import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import * as XLSX from "xlsx";
import LoginPage from "./LoginPage";
import PortalPage from "./PortalPage";

// ─────────────────────────────────────────────────────────────────────────────
// SCRUBBING PIPELINE
// ─────────────────────────────────────────────────────────────────────────────

function extractService(memo) {
  if (!memo || memo === "") return "";
  return String(memo).split("\n").pop().trim();
}

// Strip trailing "(Pet Name)" or "- PetName" from free / discount service strings
function stripPetNameSuffix(s) {
  if (!s) return "";
  // Remove trailing "(anything)"
  let clean = s.replace(/\s*\([^)]+\)\s*$/, "").trim();
  // Remove trailing " - SingleWord" (e.g. "Free First Lodging - Sally")
  clean = clean.replace(/\s+-\s+\w+$/, "").trim();
  return clean;
}

// Normalize paren suffix to dash form for consistent grouping in ServiceDisplay
// "Courtesy (Modified Stay & Train)" → "Courtesy - Modified Stay & Train"
function normalizeParens(s) {
  if (!s) return "";
  return s.replace(/\s*\(([^)]+)\)$/, " - $1").trim();
}

function extractInvoiceNum(s) {
  const m = String(s).match(/Refund for invoice\s+(\S+)/i);
  return m ? m[1] : null;
}

function categorizeRefund(src) {
  const s = src.toLowerCase().trim();
  if (!s) return "Refund - Other";
  // Bare invoice reference — default to Boarding (most refunds are boarding-related)
  if (/^refund for invoice\s+[a-z0-9]+\s*$/.test(s)) return "Refund - Boarding";
  // QB Product/Service field is a single word category — check it first as authoritative
  if (/^daycare$/.test(s)) return "Refund - Daycare";
  if (/^grooming$/.test(s)) return "Refund - Grooming";
  if (/^training$/.test(s)) return "Refund - Training";
  if (/^(boarding|activities|enrichment|transport)$/.test(s)) return "Refund - Boarding";
  // Memo-based keyword matching — grooming last, only if clearly a grooming-only service
  if (/daycare|play package|play session|session package|day pass/.test(s)) return "Refund - Daycare";
  if (/lodging|boarding|stay|bus package|chauffeur|transport|zone|outgoing|incoming|activity package|silver|gold|bronze/.test(s)) return "Refund - Boarding";
  if (/train/.test(s)) return "Refund - Training";
  // Only classify as grooming if no other service type matched
  if (/groom|bath|nail/.test(s)) return "Refund - Grooming";
  return "Refund - Other";
}

function getFinalProduct(service, originalService) {
  const s = String(service || "").toLowerCase().trim();
  const o = String(originalService || "").toLowerCase().trim();
  if (/remainder of non-refundable deposit/.test(s)) return ["Non-Refundable Deposit Remainder", "Miscellaneous"];
  if (/non-refundable/.test(s)) return ["Non-Refundable Deposit Remainder", "Miscellaneous"];
  if (/reimburs/.test(s)) return ["Reimbursement", "Miscellaneous"]; // vet/food reimbursements are income, not refunds to customers
  if (/voucher\/pass/.test(s)) {
    // Reclassify into the parent service bucket so negatives net against that bucket's revenue
    // FinalProduct = "Package Redemption" so they still group visibly at the bottom
    if (/enrichment/i.test(s)) return ["Package Redemption", "Enrichment"];
    if (/chauffeur|zone [ab]/i.test(s)) return ["Package Redemption", "Transport"];
    return ["Package Redemption", "Daycare"];
  }
  if (/(?<!non-)refund|unused portion|unused play|unused chauffeur/.test(s)) { const p = categorizeRefund(o || s); return [p, "Refunds"]; }
  if (/daycare-enrichment|enrichment daycare/.test(s)) return ["Enrichment", "Enrichment"];
  if (/(silver|gold|bronze|tlc|puppy).*(enrichment)|enrichment.*(silver|gold|bronze)/.test(s)) return ["Activity Package - Enrichment", "Boarding"];
  if (/lodging.*(enrichment)/.test(s)) return ["Boarding", "Boarding"];
  if (/silver|gold|bronze|tlc package|puppy package|activity package|activity session|outgoing day|incoming day|free first activity/.test(s)) return ["Activity Package - Daycare", "Boarding"];
  if (/lodging|boarding|overnight|free.*night|free lodging|per night|evening arrival|resort tour|spring break premium|initial deposit remainder/.test(s)) return ["Boarding", "Boarding"];
  if (/chauffeur|pickup|pick.up|drop.?off|transport|bus.*zone|bus.*package|zone [ab]|one day bus/.test(s)) {
    // Split Transport into Daycare vs Boarding sub-product based on service string
    if (/lodging/i.test(s)) return ["Boarding", "Transport"];
    return ["Daycare", "Transport"]; // daycare, bare chauffeur, packages all default to Daycare
  }
  if (/daycare|day care|half day|eval.*day|session package|day pass|10 day pass|free.*day|free eval/.test(s)) return ["Daycare", "Daycare"];
  if (/bath|groom|nail|brush|blueberry facial|deshed|de.shed|de.skunk|skunk|sanitary|paw trim|ear clean|eye clean|self.wash|gland|custom trim|sensitive skin|hypo.allerg|oatmeal shamp|rinse only|scented|cologne/.test(s)) return ["Grooming", "Grooming"];
  if (/train|day train|preschool|acclimation|remote collar|go home|refresher|e.collar|muzzle|private.*lesson|adult day|puppy.*day|puppy.*preschool|follow up lesson/.test(s)) return ["Training", "Dog Training"];
  if (/discount|courtesy|% off|\$-\d|promotion|preferred|military|friends.*family|family discount|second dog|additional dog|write.off|pilot program|credit for|referral|paid directly|test day|olive and oakley|requested by|play passes from|late arrival|previously paid|cancel other half|reduction for shortened|transfer from group|friends and family/.test(s)) return ["Discount / Courtesy", "Discounts"];
  if (/gratuity|grauity/i.test(s)) return ["Gratuity / Tip", "Miscellaneous"];
  if (/food|dog food|royal canin|fromm|nulo|purina|pawtree|hill|nutrisource|n&d|meal.bag|bagging fee|pre.bag|lick mat|eye wipe|petsmart|mcdonalds|two additional ingredients/.test(s)) return ["Retail / Food", "Retail / Food"];
  if (/\bvet\b|veterinar|medication|benadryl|bordetella|urine test|prescription|medical|tick treatment/.test(s)) return ["Vet / Medical", "Miscellaneous"];
  if (/thanksgiving|santa|holiday.*milk|holiday.*picture|holiday.*treat|festive photo|pumpkinfest|raffle|video \d/.test(s)) return ["Holiday / Events", "Miscellaneous"];
  if (/staff.*lunch|staff.*pizza|lunch for staff|gratuity \(staff lunch\)/.test(s)) return ["Staff", "Miscellaneous"];

  // PATCH: Employee Dog → Miscellaneous / Staff
  if (/employee dog/i.test(s)) return ["Staff", "Miscellaneous"];

  // PATCH: $X.XX Premium → Miscellaneous / Service Premiums
  if (/\$[\d,.]+\s+premium/i.test(s)) return ["Service Premiums", "Miscellaneous"];

  // PATCH: Free items → Discounts (derive sub-type from keywords)
  if (/\bfree\b/i.test(s)) {
    // Lodging/boarding keywords take priority (even in combos like "Free First Lodging w/ Activity Package")
    if (/\b(lodg|lodd|boarding|night|stay|nights)\b/i.test(s)) return ["Discount / Boarding", "Discounts"];
    if (/\b(activit|activ|daycare|day|daytime|incoming|outgoing)\b/i.test(s)) return ["Discount / Daycare", "Discounts"];
    if (/\b(bath|ear.?clean|grooming|groom|nail)\b/i.test(s)) return ["Discount / Grooming", "Discounts"];
    if (/\b(chauffeur|transport|pickup|pick.up|drop.?off)\b/i.test(s)) return ["Discount / Transport", "Discounts"];
    if (/\b(train|eval|evaluation)\b/i.test(s)) return ["Discount / Training", "Discounts"];
    if (/\b(medication|medical)\b/i.test(s)) return ["Discount / Miscellaneous", "Discounts"];
    return ["Discount", "Discounts"];
  }

  return ["Other", "Miscellaneous"];
}

function getServiceDisplay(service, finalProduct, finalBucket) {
  const s = String(service || "").trim();
  const sl = s.toLowerCase();
  if (finalBucket === "Daycare") {
    if (/eval/.test(sl) && !/free eval/.test(sl)) return "Evaluation Day";
    if (/half day|half-day/.test(sl)) return "Daycare Single Day";
    if (/daycare full day|full day daycare|daycare with pick|daycare with transport|daycare with chauffeur|daycare wed\.|daycare full day-individual/.test(sl)) return "Daycare Single Day";
    if (/(\d+) daycare session package/.test(sl)) { const m = sl.match(/(\d+) daycare session package/); return `${m[1]} Daycare Session Package`; }
    if (/enrichment.*session|session.*enrichment/.test(sl)) { const m = sl.match(/(\d+)/); return m ? `${m[1]} Enrichment Daycare Session Package` : "Enrichment Daycare Session Package"; }
    if (/chauffeur|pickup|pick.up|drop.?off|transport|zone [ab]/.test(sl)) return "Daycare with Chauffeur Service";
    if (/\$10 off daycare/i.test(sl)) return "Daycare Discount / Courtesy";
    if (/^free|discount|courtesy|credit|application of/.test(sl)) return "Daycare Discount / Courtesy";
  }
  if (finalProduct === "Boarding") {
    if (/2 dog|2 dogs|\(2\)|two dog/.test(sl)) return "Boarding - 2 Dogs";
    if (/3.* dog|3\+/.test(sl)) return "Boarding - 3+ Dogs";
    if (/stay and train/.test(sl)) return "Stay and Train";
    if (/^free|^first free|^discount|courtesy|promotion|credit/.test(sl)) return "Boarding Discount / Courtesy";
    return "Boarding - 1 Dog";
  }
  if (finalProduct === "Activity Package - Daycare" || finalProduct === "Activity Package - Enrichment") {
    const sfx = /enrichment/.test(sl) ? " (Enrichment)" : "";
    if (/^free|^first free|^no charge|^courtesy|discount/.test(sl)) return `Activity Package Discount / Courtesy${sfx}`;
    if (/outgoing|incoming/.test(sl)) {
      const t = /gold/.test(sl) ? "Gold" : /silver/.test(sl) ? "Silver" : /bronze/.test(sl) ? "Bronze" : /tlc/.test(sl) ? "TLC" : /puppy/.test(sl) ? "Puppy" : "Activity";
      return `${t} Package - Outgoing Day${sfx}`;
    }
    if (/gold/.test(sl)) return `Gold Activity Package${sfx}`;
    if (/silver/.test(sl)) return `Silver Activity Package${sfx}`;
    if (/bronze/.test(sl)) return `Bronze Activity Package${sfx}`;
    if (/tlc/.test(sl)) return "TLC Package";
    if (/puppy/.test(sl)) return "Puppy Package";
    return s;
  }
  if (finalBucket === "Grooming") {
    if (/no nails|no nail/i.test(sl) && /bath/i.test(sl)) return "Deluxe Bath";
    if (/^free|^no charge|^complimentary|courtesy|discount|forgot|redo|july bath|50% off/.test(sl)) return "Grooming Discount / Courtesy";
    if (/nail|nails/.test(sl)) return "Nail Service";
    if (/deshed|de.shed|deshedding/.test(sl)) return "De-shedding Treatment";
    if (/skunk/.test(sl)) return "De-Skunk";
    if (/deluxe bath/.test(sl)) return "Deluxe Bath";
    if (/ear clean/.test(sl)) return "Ear Cleaning";
    if (/eye clean/.test(sl)) return "Eye Cleaning";
    if (/gland/.test(sl)) return "Gland Expression";
    if (/paw trim|sanitary/.test(sl)) return "Specialty Trim";
    if (/brush out/.test(sl)) return "Brush Out";
    if (/self.wash/.test(sl)) return "Self-Wash";
    if (/blueberry/.test(sl)) return "Blueberry Facial";
    return s;
  }
  if (finalBucket === "Dog Training") {
    if (/courtesy|adjustment|promotion|conversion|saturday activities/.test(sl)) return "Training Discount / Courtesy";
    if (/adult day train/.test(sl)) return "Adult Day Train";
    if (/puppy day train/.test(sl)) return "Puppy Day Train";
    if (/puppy preschool/.test(sl)) return "Puppy Preschool";
    if (/remote collar/.test(sl)) return "Remote Collar Program";
    if (/private training|private lesson/.test(sl)) return "Private Training Session";
    if (/acclimation/.test(sl)) return "Acclimation Training";
    if (/go home/.test(sl)) return "Go Home Lesson";
    if (/e.collar|e collar/.test(sl)) return "E-Collar";
    if (/muzzle/.test(sl)) return "Muzzle";
    return s;
  }
  if (finalBucket === "Transport") {
    return finalProduct === "Boarding" ? "Chauffeur Service - Boarding" : "Chauffeur Service - Daycare";
  }
  if (finalBucket === "Enrichment") {
    if (/enrichment.*session|session.*enrichment/.test(sl)) { const m = sl.match(/(\d+)/); return m ? `${m[1]} Enrichment Session Package` : "Enrichment Session Package"; }
    if (/daycare.enrichment|enrichment.daycare/.test(sl)) return "Enrichment Daycare - Single Day";
    return s;
  }
  if (finalBucket === "Refunds") {
    // Collapse all "Refund for invoice XXXXX" and similar into a single display label
    if (/refund for invoice|refund for inv/i.test(sl)) return "Refund for Invoices";
    if (/reimburs/i.test(sl)) return "Reimbursement";
    if (/unused portion|unused play/i.test(sl)) return "Unused Package Refund";
    if (/unused chauffeur/i.test(sl)) return "Unused Chauffeur Refund";
    return "Refund for Invoices";
  }

  // PATCH: Gratuity always shows as "Gratuity" regardless of suffix/parens
  if (finalProduct === "Gratuity / Tip") return "Gratuity";

  // Package Redemption (vouchers) always show as clean label regardless of bucket
  if (finalProduct === "Package Redemption") return "Package Redemption";

  // PATCH: Employee Dog display
  if (finalProduct === "Staff" && /employee dog/i.test(sl)) return "Employee Dog";

  // PATCH: Staff Lunches display
  if (finalProduct === "Staff" && (
    /staff.?lunch|lunch for staff|staff.?pizza|pizza party|gratuity \(staff lunch\)/i.test(sl)
  )) return "Staff Lunches";

  // PATCH: Service Premiums display
  if (finalProduct === "Service Premiums") return "Service Premiums";

  // PATCH: Free items in Discounts — strip pet name suffix
  if (finalBucket === "Discounts" && /\bfree\b/i.test(sl)) {
    return stripPetNameSuffix(s);
  }

  // PATCH: All other Discounts — normalize parens to dash form for consistent grouping
  if (finalBucket === "Discounts") {
    return normalizeParens(s);
  }

  return s;
}

function getDiscountProduct(service, originalBucket) {
  const sl = String(service || "").toLowerCase();
  if (/daycare|eval.*day|\$10 off daycare/.test(sl)) return "Discount - Daycare";
  if (/activity|outgoing|incoming/.test(sl)) return "Discount - Activity Package";
  if (/lodging|boarding|free.*night|free first.*night|free.*lodg|evening arrival|\/night|\bnight\b|per night/.test(sl)) return "Discount - Boarding";
  if (/bath|groom|nail|july bath|free.*bath/.test(sl)) return "Discount - Grooming";
  if (/train|courtesy.*train|conversion|preschool|stay.*train|stay.and.train/.test(sl)) return "Discount - Training";
  if (/chauffeur|transport|bus.zone|bus.package|zone [ab]|pickup|pick.up|drop.?off/.test(sl)) return "Discount - Transport";
  return "Discount / Courtesy";
}

function scrubRows(rawRows) {
  const sample = rawRows[0] || {};
  const keys = Object.keys(sample);
  const memoKey = keys.find(k => /memo|description/i.test(k)) || keys[10];
  const numKey = keys.find(k => /^num$/i.test(k)) || "Num";

  // Build invoice lookup for refund matching
  // Groups all rows per invoice, picks dominant product by total amount
  const invoiceGroups = {};
  rawRows.forEach(row => {
    const num = String(row[numKey] || "").trim();
    if (!num || / \(R /.test(num)) return;
    if (!invoiceGroups[num]) invoiceGroups[num] = [];
    invoiceGroups[num].push(row);
  });
  const invoiceLookup = {};
  const PRODUCT_PRIORITY = ["Boarding","Daycare","Dog Training","Training","Activities","Enrichment","Transport","Grooming","Retail","Miscellaneous"];
  Object.entries(invoiceGroups).forEach(([num, rows]) => {
    // Sum amount by product
    const byProduct = {};
    rows.forEach(r => {
      const prod = (r["Product/Service"] || "").trim();
      const amt  = Math.abs(parseFloat(r["Amount"]) || 0);
      if (prod) byProduct[prod] = (byProduct[prod] || 0) + amt;
    });
    // Pick dominant product by amount, breaking ties by priority order
    const dominant = Object.entries(byProduct)
      .sort((a, b) => {
        if (b[1] !== a[1]) return b[1] - a[1]; // highest amount wins
        const pa = PRODUCT_PRIORITY.indexOf(a[0]), pb = PRODUCT_PRIORITY.indexOf(b[0]);
        return (pa === -1 ? 99 : pa) - (pb === -1 ? 99 : pb);
      })[0]?.[0] || "";
    // Use the first row's memo as service description
    const firstRow = rows[0];
    invoiceLookup[num] = { product: dominant, service: extractService(firstRow[memoKey]) };
  });

  return rawRows.map(row => {
    const service = extractService(row[memoKey] || "");
    const isDeposit = /remainder of non-refundable deposit/i.test(service);
    let isRefund = "", originalProduct = "", originalService = "";

    if (!isDeposit && /refund for invoice/i.test(service)) {
      const invNum = extractInvoiceNum(service);
      isRefund = "Yes";
      if (invNum && invoiceLookup[invNum]) { originalProduct = invoiceLookup[invNum].product; originalService = invoiceLookup[invNum].service; }
    } else if (!isDeposit && /refund|reimburs/i.test(service)) {
      isRefund = "Yes";
    } else if (isDeposit) {
      originalProduct = "Boarding";
    }

    let mismatch = "";
    const dollarMatch = service.match(/^\$-?([\d.]+) /);
    if (dollarMatch) {
      const svcAmt = parseFloat(dollarMatch[1]);
      const rate = Math.abs(parseFloat(row["Rate"]) || 0);
      const amt = Math.abs(parseFloat(row["Amount"]) || 0);
      if (svcAmt !== rate && svcAmt !== amt && rate !== 0) mismatch = "Yes";
    }

    let [finalProduct, finalMainBucket] = getFinalProduct(service, originalProduct || originalService);

    // Reclassify discounts within service buckets
    if (["Daycare","Boarding","Grooming","Dog Training","Enrichment"].includes(finalMainBucket)) {
      const sd = getServiceDisplay(service, finalProduct, finalMainBucket).toLowerCase();
      if (/discount|courtesy|^free|^first free|promotion/.test(sd)) {
        finalProduct = getDiscountProduct(service, finalMainBucket);
        finalMainBucket = "Discounts";
      }
    }
    // $10 Off Daycare
    if (/\$10 off daycare/i.test(service)) { finalProduct = "Discount - Daycare"; finalMainBucket = "Discounts"; }

    // PATCH: Negative rate + discount/courtesy/promotion keyword → always Discounts
    // This catches things like Transport entries that are actually discounts
    const rateVal = parseFloat(row["Rate"]) || 0;
    if (
      rateVal < 0 &&
      /discount|courtesy|promotion/i.test(service) &&
      finalMainBucket !== "Discounts" &&
      finalMainBucket !== "Refunds" &&
      finalMainBucket !== "Package Redemption"
    ) {
      finalProduct = getDiscountProduct(service, finalMainBucket);
      finalMainBucket = "Discounts";
    }

    const serviceDisplay = getServiceDisplay(service, finalProduct, finalMainBucket);
    const excludeFromRevenue = ""; // Package Redemption rows (vouchers) are negative — they net against daycare revenue naturally

    return {
      ...row,
      Service: service,
      Is_Refund: isRefund,
      Original_Product: originalProduct,
      Original_Service: originalService,
      OriginalService: String(row[memoKey] || ""),   // raw Memo/Description, renamed for export
      Service_Amount_Mismatch: mismatch,
      FinalProduct: finalProduct,
      FinalMainBucket: finalMainBucket,
      Exclude_From_Revenue: excludeFromRevenue,
      ServiceDisplay: serviceDisplay,
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// PETKENNECT SCRUBBING PIPELINE
// Converts PK financial export format to our canonical schema
// Data is pre-classified — minimal scrubbing needed
// ─────────────────────────────────────────────────────────────────────────────

function scrubPKRows(rawRows) {
  // Bucket mapping: PK FinalMasterBucket → canonical FinalMainBucket
  const BUCKET_MAP = {
    "DayCamp":         "Daycare",
    "Boarding":        "Boarding",
    "Grooming":        "Grooming",
    "Training":        "Dog Training",
    "Retail":          "Retail / Food",
    "Add-On Services": "Miscellaneous",
    "Vet Bills":       "Miscellaneous",
  };

  // Product mapping: PK FinalProduct → canonical FinalProduct
  const PRODUCT_MAP = {
    "DayCamp":          "Daycare",
    "Boarding":         "Boarding",
    "Activity Package": "Activity Package - Daycare",
    "Day Training":     "Training",
    "Puppy":            "Training",
    "Fitness":          "Dog Training",  // Fitness → Dog Training not Daycare
    "Dog Sports":       "Dog Training",
    "Private Lessons":  "Training",
    "Training Tools":   "Retail / Food",
    "Training Misc.":   "Miscellaneous",
    "Food":             "Retail / Food",
    "Treats":           "Retail / Food",
    "Food/Treats":      "Retail / Food",
    "Bathing":          "Grooming",
    "Nails":            "Grooming",
    "Parties/Events":   "Holiday / Events",
    "Photos":           "Holiday / Events",
    "Bedding":          "Retail / Food",
    "Deposits":         "Deposits",
    "Misc.":            "Miscellaneous",
  };

  // Fitness bucket override — Fitness in any bucket → Dog Training
  const FITNESS_TERMS = ["fitness", "fit "];

  // Location code normalization
  const LOC_NORM = { "04-hh": "04-HH" };

  const output = [];

  rawRows.forEach(row => {
    const bucket  = String(row.FinalMasterBucket || "").trim();
    const product = String(row.FinalProduct || "").trim();
    const locRaw  = String(row.LocationCode || "").trim();
    const loc     = LOC_NORM[locRaw] || locRaw;
    const dateRaw = row.InvDate || "";
    const dateStr = String(dateRaw).slice(0, 10);
    const custId  = String(row.OwnerID || "").trim();
    const custName= String(row.OwnerLastName || "").trim();
    const invNum  = String(row.InvoiceID || "").trim();
    const itemName= String(row.ItemName || "").trim();
    const qty     = parseFloat(row.Quantity) || 1;

    // Week ending (Saturday)
    const dateObj = parseDate(dateStr);
    let weekEnding = "";
    if (dateObj) {
      const sat = new Date(dateObj);
      sat.setDate(sat.getDate() + (6 - sat.getDay()));
      weekEnding = sat.toISOString().slice(0, 10);
    }

    // Revenue: SalePrice is what was paid. Fall back to Subtotal, then ListPrice
    const salePrice  = parseFloat(row.SalePrice) || 0;
    const subtotal   = parseFloat(row.Subtotal) || 0;
    const listPrice  = parseFloat(row.ListPrice) || 0;
    // Use SalePrice if > 0, else Subtotal, else ListPrice
    const revenue = salePrice > 0 ? salePrice : (subtotal > 0 ? subtotal : listPrice);

    // Discount: ListPrice - SalePrice (only when both are positive and list > sale)
    const discountAmt = (listPrice > 0 && salePrice > 0 && listPrice > salePrice)
      ? Math.round((listPrice - salePrice) * 100) / 100 : 0;

    // Refund: IsRefunded=Yes, RefundAmount is positive
    const isRefundedFlag = String(row.IsRefunded || "").trim().toLowerCase();
    const refundAmt      = parseFloat(String(row.RefundAmount || "0").replace(/[^0-9.-]/g, "")) || 0;
    const isRefunded     = (isRefundedFlag === "yes" || isRefundedFlag === "true" || isRefundedFlag === "1") && refundAmt > 0;

    // Bucket/Product mapping
    const productLower = product.toLowerCase();
    const isFitness = FITNESS_TERMS.some(t => productLower.includes(t)) ||
                      FITNESS_TERMS.some(t => bucket.toLowerCase().includes(t));

    let finalMainBucket = isFitness ? "Dog Training" : (BUCKET_MAP[bucket] || bucket || "Miscellaneous");
    let finalProduct    = isFitness ? "Training"
      : product === "Add-ons" ? (BUCKET_MAP[bucket] || "Miscellaneous")
      : (PRODUCT_MAP[product] || product || "Other");

    const baseRow = {
      Date:             dateStr,
      WeekEnding:       weekEnding,
      Customer:         custName,
      "PP-ID":          custId,
      Num:              invNum,
      "Product/Service": bucket,
      Memo:             itemName,
      Service:          itemName,
      Qty:              qty,
      Rate:             listPrice || revenue,
      LocationCode:     loc,
      FinalProduct:     finalProduct,
      FinalMainBucket:  finalMainBucket,
      ServiceDisplay:   itemName || product || bucket,
      Is_Refund:        "",
      Exclude_From_Revenue: product === "Deposits" && revenue < 0 ? "Yes" : "",
      Service_Amount_Mismatch: "",
      DiscountAmount:   discountAmt,
      Original_Product: bucket,
      Original_Service: itemName,
      OriginalService:  itemName,
    };

    // ── 1. Main revenue row (gross — SalePrice) ──────────────────────────────
    output.push({ ...baseRow, Amount: revenue });

    // ── 2. Discount row (if discounted) ─────────────────────────────────────
    if (discountAmt > 0) {
      output.push({
        ...baseRow,
        Amount:          -discountAmt,
        FinalProduct:    "Discount",
        FinalMainBucket: "Discounts",
        ServiceDisplay:  `Discount — ${itemName || product || bucket}`,
        Is_Refund:       "",
        DiscountAmount:  discountAmt,
      });
    }

    // ── 3. Refund row (if refunded) ──────────────────────────────────────────
    if (isRefunded) {
      output.push({
        ...baseRow,
        Amount:          -refundAmt,
        FinalProduct:    `Refund - ${finalProduct}`,
        FinalMainBucket: "Refunds",
        ServiceDisplay:  `Refund — ${itemName || product || bucket}`,
        Is_Refund:       "Yes",
        DiscountAmount:  0,
      });
    }
  });

  return output;
}

// ─────────────────────────────────────────────────────────────────────────────
// PETKENNECT BOOKINGS NORMALIZER
// Combines multi-tab bookings workbook into canonical booking rows
// ─────────────────────────────────────────────────────────────────────────────

function normalizePKBookings(workbook) {
  const rows = [];
  const today = new Date().toISOString().slice(0, 10);

  // ── Boarding ────────────────────────────────────────────────────────────────
  const boardingWS = workbook.Sheets["Boarding"];
  if (boardingWS) {
    const raw = XLSX.utils.sheet_to_json(boardingWS, { defval: "", raw: false });
    raw.forEach(r => {
      const startRaw = String(r.CheckInDate || "").slice(0, 10);
      const endRaw   = String(r.CheckoutDate || "").slice(0, 10);
      // Recalculate nights from dates (Nights column has data issues)
      let nights = 0;
      if (startRaw && endRaw) {
        const s = new Date(startRaw + "T00:00:00");
        const e = new Date(endRaw + "T00:00:00");
        nights = Math.max(Math.round((e - s) / 86400000), 1);
      }
      rows.push({
        reservation_string: String(r.ReservationId || "").trim(),
        customer_id:        String(r.OwnerID || "").trim(),
        customer_name:      String(r.OwnerLastName || "").trim(),
        order_type:         "boarding",
        status:             "booked",
        date_created:       String(r.Reservation_Date || startRaw || "").slice(0, 10),
        start_date:         startRaw,
        end_date:           endRaw,
        nights:             nights,
        rate:               String(r.PriceCode || "").trim(),
        booking_value:      parseFloat(r.TotalBookingValue) || 0,
        booking_quantity:   nights,
        activity_package:   String(r.ActivityPackage || "").trim(),
        add_grooming:       String(r.AddGrooming || "").trim(),
        locationCode:       String(r.StoreCode || "").trim(),
        online_request:     false,
        is_future:          startRaw >= today,
        service_tab:        "Boarding",
      });
    });
  }

  // ── DayCare ─────────────────────────────────────────────────────────────────
  const SERVICE_TYPE_NORM = {
    "full day": "Full Day", "full": "Full Day", "full day": "Full Day", "full DAY": "Full Day",
    "half day": "Half Day", "half": "Half Day", "half day": "Half Day",
  };
  const daycareWS = workbook.Sheets["DayCare"];
  if (daycareWS) {
    const raw = XLSX.utils.sheet_to_json(daycareWS, { defval: "", raw: false });
    raw.forEach(r => {
      const statusRaw = String(r.Status || "").trim();
      if (statusRaw === "Cancelled") return; // exclude cancellations
      const svcRaw = String(r.ServiceType || "").trim();
      const svcNorm = SERVICE_TYPE_NORM[svcRaw.toLowerCase()] || svcRaw;
      const isBoarder = svcNorm === "Boarder";
      const dateStr = String(r.Reservation_Date || "").slice(0, 10);
      rows.push({
        reservation_string: String(r.ReservationId || "").trim(),
        customer_id:        String(r.OwnerID || "").trim(),
        customer_name:      String(r.OwnerLastName || "").trim(),
        order_type:         "daycare",
        status:             statusRaw === "Future" ? "future" : "booked",
        date_created:       dateStr,
        start_date:         dateStr,
        end_date:           dateStr,
        nights:             0,
        rate:               svcNorm,
        // Boarder: counts toward occupancy but revenue already in boarding
        booking_value:      isBoarder ? 0 : (parseFloat(r.TotalBookingValue) || 0),
        booking_quantity:   1,
        locationCode:       String(r.StoreCode || "").trim(),
        online_request:     false,
        is_future:          statusRaw === "Future",
        is_boarder:         isBoarder,
        service_tab:        "DayCare",
      });
    });
  }

  // ── Grooming ─────────────────────────────────────────────────────────────────
  const FROM_SCHED_NORM = {
    "boarding": "Boarding", "daycamp": "DayCamp", "training": "Training",
  };
  const groomingWS = workbook.Sheets["Grooming"];
  if (groomingWS) {
    const raw = XLSX.utils.sheet_to_json(groomingWS, { defval: "", raw: false });
    raw.forEach(r => {
      const fromRaw = String(r.FromSchedule || "").trim();
      const fromNorm = FROM_SCHED_NORM[fromRaw.toLowerCase()] || fromRaw || null;
      const dateStr = String(r.Reservation_Date || "").slice(0, 10);
      rows.push({
        reservation_string: String(r.ReservationId || "").trim(),
        corresp_id:         String(r.CorrespID || "").trim(),
        from_schedule:      fromNorm,
        customer_id:        String(r.OwnerID || "").trim(),
        customer_name:      String(r.OwnerLastName || "").trim(),
        order_type:         "grooming",
        status:             "booked",
        date_created:       dateStr,
        start_date:         dateStr,
        end_date:           dateStr,
        nights:             0,
        rate:               String(r.ServiceType || "").trim(),
        booking_value:      parseFloat(r.TotalbookingValue) || 0,
        booking_quantity:   1,
        locationCode:       String(r.StoreCode || "").trim(),
        online_request:     false,
        is_future:          false, // grooming rows are always completed
      });
    });
  }

  // ── Training ─────────────────────────────────────────────────────────────────
  const trainingWS = workbook.Sheets["Training"];
  if (trainingWS) {
    const raw = XLSX.utils.sheet_to_json(trainingWS, { defval: "", raw: false });
    raw.forEach(r => {
      const statusRaw = String(r.Status || "").trim().toLowerCase();
      if (statusRaw === "no sale") return; // exclude no-sale consultations
      const dateStr = String(r.Reservation_Date || "").slice(0, 10);
      rows.push({
        reservation_string: String(r.TrainingPackageID || "").trim(),
        customer_id:        String(r.OwnerID || "").trim(),
        customer_name:      String(r.OwnerLastName || "").trim(),
        order_type:         "training",
        status:             "booked",
        date_created:       dateStr,
        start_date:         dateStr,
        end_date:           dateStr,
        nights:             0,
        rate:               String(r.ServiceType || "").trim(),
        booking_value:      parseFloat(r.TotalbookingValue) || 0,
        booking_quantity:   1,
        locationCode:       String(r.StoreCode || "").trim(),
        online_request:     false,
        is_future:          false, // training rows are always completed (no_sale already excluded)
      });
    });
  }

  return rows;
}

// ─────────────────────────────────────────────────────────────────────────────
// SERVICE LABEL HELPER
// Returns tenant-specific display name for a canonical bucket/service name
// Falls back to the canonical name if no override exists
// ─────────────────────────────────────────────────────────────────────────────

// Global tenant config reference — set when Dashboard mounts
let _tenantServiceLabels = null;
function setTenantLabels(labels) { _tenantServiceLabels = labels || null; }
function label(canonical) {
  if (!_tenantServiceLabels) return canonical;
  return _tenantServiceLabels[canonical] || canonical;
}

// Global tenant header color — set when Dashboard mounts
// Used by sub-components that don't receive authClient as prop
let _tenantHeaderBg = "#1B3A6B"; // default to Waggles navy; updated by setTenantHeaderBg on login
function setTenantHeaderBg(color) { _tenantHeaderBg = color || NAVY; }

// ─────────────────────────────────────────────────────────────────────────────
// PETKENNECT COMBINED BOOKINGS NORMALIZER
// Handles single flat sheet format (replaces multi-tab normalizePKBookings)
// Two versions: chunked async for manual upload, sync for Drive loading
// ─────────────────────────────────────────────────────────────────────────────

const PK_ORDER_TYPE_NORM = {
  "Day Camp": "daycare", "day camp": "daycare", "Daycare": "daycare",
  "Boarding": "boarding", "boarding": "boarding",
  "Grooming": "grooming", "grooming": "grooming",
  "Training": "training", "training": "training",
};
const PK_STATUS_NORM = {
  "Completed": "booked", "Sold": "booked", "sold": "booked",
  "Future": "future", "Cancelled": "cancelled", "No Sale": "no_sale",
};
const PK_BOARDER_TYPES = ["boarder", "Boarder"];
const PK_EXCLUDE_STATUSES = ["cancelled", "no_sale"];

function normalizePKRow(r, today) {
  const orderTypeRaw = String(r.OrderType || "").trim();
  const orderType    = PK_ORDER_TYPE_NORM[orderTypeRaw] || orderTypeRaw.toLowerCase();
  const statusRaw    = String(r.Status || "").trim();
  const status       = PK_STATUS_NORM[statusRaw] || statusRaw.toLowerCase();
  if (PK_EXCLUDE_STATUSES.includes(status)) return null;
  const serviceType  = String(r.ServiceType || "").trim();
  const isBoarder    = PK_BOARDER_TYPES.includes(serviceType);
  const startDate    = String(r.StartDate || "").slice(0, 10);
  const endDate      = String(r.EndDate || "").slice(0, 10);
  let nights = 0;
  if (orderType === "boarding" && startDate && endDate) {
    const s = new Date(startDate + "T00:00:00");
    const e = new Date(endDate + "T00:00:00");
    nights = Math.max(Math.round((e - s) / 86400000), 0);
  }
  return {
    reservation_string: String(r.ReservationId || "").trim(),
    customer_id:        String(r.OwnerID || "").trim(),
    customer_name:      String(r.OwnerLastName || "").trim(),
    locationCode:       String(r.StoreCode || "").trim(),
    order_type:         orderType,
    rate:               serviceType,
    activity_package:   String(r.ActivityPackage || "").trim(),
    status,
    start_date:         startDate,
    end_date:           endDate,
    nights,
    addon_description:  String(r.AddOnDescription || "").trim(),
    addon_cost:         parseFloat(r.AddonsCost) || 0,
    booking_value:      parseFloat(r.TotalBookingValue) || 0,
    corresp_id:         String(r.CorrespID || "").trim(),
    from_schedule:      String(r.FromSchedule || "").trim(),
    is_boarder:         isBoarder,
    is_evaluation:      String(r.IsEvaluation || "").trim().toLowerCase() === "yes" || String(r.IsEvaluation || "").trim() === "1",
    date_created:       String(r.CreatedDate || "").slice(0, 10),
    is_future:          status === "future" || (status !== "booked" && startDate >= today),
    service_tab:        "Combined",
    online_request:     false,
    booking_quantity:   orderType === "boarding" ? Math.max(nights, 1) : 1,
  };
}

// Chunked async version — yields to browser every 20K rows to prevent timeout
function normalizePKCombinedBookingsAsync(rawRows, onProgress, onComplete) {
  const today = new Date().toISOString().slice(0, 10);
  const CHUNK = 20000;
  const total = rawRows.length;
  const results = [];
  let offset = 0;

  const processChunk = () => {
    const end = Math.min(offset + CHUNK, total);
    for (let i = offset; i < end; i++) {
      const row = normalizePKRow(rawRows[i], today);
      if (row) results.push(row);
    }
    offset = end;
    onProgress(Math.round((offset / total) * 100));
    if (offset < total) {
      setTimeout(processChunk, 10);
    } else {
      onComplete(results);
    }
  };
  setTimeout(processChunk, 10);
}

// Sync version — used for Drive loading where we already have async flow
function normalizePKCombinedBookings(rawRows) {
  const today = new Date().toISOString().slice(0, 10);
  return rawRows.map(r => normalizePKRow(r, today)).filter(Boolean);
}

// ─────────────────────────────────────────────────────────────────────────────
// ANALYTICS HELPERS
// ─────────────────────────────────────────────────────────────────────────────

const BUCKET_ORDER = ["Daycare","Boarding","Dog Training","Enrichment","Grooming","Transport","Retail / Food","Package Redemption","Discounts","Refunds","Miscellaneous"];
const WIDGET_BUCKETS = ["Daycare","Boarding","Dog Training","Enrichment","Grooming","Transport","Discounts","Refunds"];

function parseWE(val) {
  if (!val) return null;
  const s = String(val).trim();
  // Handle "2019-05-25 00:00:00" or "2019-05-25T00:00:00" — parse as local date to avoid UTC offset
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(parseInt(m[1]), parseInt(m[2])-1, parseInt(m[3]));
  const d = new Date(s);
  return isNaN(d) ? null : d;
}

// parseDate reads the raw "Date" field — used for month/year filtering
// so scorecard month/year aligns with transaction date, not week-ending date
function parseDate(val) {
  return parseWE(val); // same parsing logic, different source field
}

function weKey(d) { return d ? d.toISOString().slice(0,10) : ""; }
function moKey(d) { return d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}` : ""; }
function yrKey(d) { return d ? String(d.getFullYear()) : ""; }
function fmtWE(k) {
  const [y,m,d] = k.split("-");
  return `${m}/${d}/${y}`;
}
function fmtMo(k) {
  // k is "2024-03" — format as "Mar 2024"
  const [y,m] = k.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(m)-1]} ${y}`;
}

function filterRows(rows, period, periodKey) {
  // Week filters by WeekEnding; month/year filter by transaction Date
  return rows.filter(r => {
    if (period === "week") {
      const d = parseWE(r.WeekEnding);
      return d ? weKey(d) === periodKey : false;
    }
    if (period === "month") {
      const d = parseDate(r.Date);
      return d ? moKey(d) === periodKey : false;
    }
    if (period === "year") {
      const d = parseDate(r.Date);
      return d ? yrKey(d) === periodKey : false;
    }
    return true;
  });
}

function filterRevenueRows(rows, period, periodKey) {
  // Date filter + exclude pkg redemption — used for widgets/totals
  return filterRows(rows, period, periodKey);
}

function buildWidgets(rows) {
  const out = {};
  WIDGET_BUCKETS.forEach(b => { out[b] = { revenue: 0, qty: 0 }; });
  out["Total"] = { revenue: 0, qty: 0 };
  out["Misc / Retail"] = { revenue: 0, qty: 0 };
  out["Retail / Food"] = { revenue: 0, qty: 0 };
  rows.forEach(r => {
    const amt = parseFloat(r.Amount) || 0;
    const qty = Math.abs(parseFloat(r.Qty) || 0) || 1;
    const b = r.FinalMainBucket;
    if (WIDGET_BUCKETS.includes(b)) { out[b].revenue += amt; out[b].qty += qty; }
    if (b === "Miscellaneous" || b === "Retail / Food") { out["Misc / Retail"].revenue += amt; out["Misc / Retail"].qty += qty; }
    if (b === "Retail / Food") { out["Retail / Food"].revenue += amt; out["Retail / Food"].qty += qty; }
    out["Total"].revenue += amt; out["Total"].qty += qty;
  });
  return out;
}

function getPriorPeriodKey(period, periodKey) {
  // Returns the key for the period immediately before the given one
  if (period === "week") {
    const [y,m,d] = periodKey.split("-").map(Number);
    const dt = new Date(y, m-1, d);
    dt.setDate(dt.getDate() - 7);
    return weKey(dt);
  }
  if (period === "month") {
    const [y,m] = periodKey.split("-").map(Number);
    const dt = new Date(y, m-2, 1); // go back one month
    return moKey(dt);
  }
  if (period === "year") {
    return String(parseInt(periodKey) - 1);
  }
  return null;
}

function getYoYPeriodKey(period, periodKey) {
  // Returns the key for same period one year ago
  if (period === "week") {
    const [y,m,d] = periodKey.split("-").map(Number);
    const dt = new Date(y-1, m-1, d);
    return weKey(dt);
  }
  if (period === "month") {
    const [y,m] = periodKey.split("-").map(Number);
    return `${y-1}-${String(m).padStart(2,"0")}`;
  }
  return null; // year mode has no YoY
}

function buildBucketTable(rows) {
  const byBucket = {}, byProduct = {}, byService = {};
  rows.forEach(r => {
    const b = r.FinalMainBucket || "Other";
    const p = r.FinalProduct || "Other";
    const sd = r.ServiceDisplay || r.Service || "Unknown";
    const amt = parseFloat(r.Amount) || 0;
    const qty = Math.abs(parseFloat(r.Qty) || 0) || 1;
    const rate = Math.abs(parseFloat(r.Rate) || 0);
    if (!byBucket[b]) byBucket[b] = { revenue:0, qty:0 };
    byBucket[b].revenue += amt; byBucket[b].qty += qty;
    const pk = `${b}||${p}`;
    if (!byProduct[pk]) byProduct[pk] = { revenue:0, qty:0, bucket:b, product:p };
    byProduct[pk].revenue += amt; byProduct[pk].qty += qty;
    const sk = `${b}||${p}||${sd}`;
    if (!byService[sk]) byService[sk] = { revenue:0, qty:0, rateSum:0, rateCount:0, bucket:b, product:p, service:sd };
    byService[sk].revenue += amt; byService[sk].qty += qty;
    if (rate > 0) { byService[sk].rateSum += rate; byService[sk].rateCount += 1; }
  });
  Object.values(byService).forEach(v => {
    // Package Redemption rows sort last (negative avgRate pushes them to bottom of ascending sort)
    if (v.product === "Package Redemption") { v.avgRate = -1; return; }
    v.avgRate = v.rateCount > 0 ? v.rateSum / v.rateCount : 0;
  });
  return { byBucket, byProduct, byService };
}

function buildWeeklyPivot(rows) {
  const buckets = BUCKET_ORDER.filter(b => b !== "Package Redemption");
  const weekMap = {};
  rows.forEach(r => {
    const d = parseWE(r.WeekEnding);
    if (!d) return;
    const wk = weKey(d);
    const b = r.FinalMainBucket;
    if (!buckets.includes(b)) return;
    const amt = parseFloat(r.Amount) || 0;
    if (!weekMap[wk]) weekMap[wk] = {};
    weekMap[wk][b] = (weekMap[wk][b] || 0) + amt;
  });
  const weeks = Object.keys(weekMap).sort((a,b) => b.localeCompare(a));
  return { weeks, weekMap, buckets };
}

function buildQuality(rows) {
  const total = rows.length;
  const refunds = rows.filter(r => r.Is_Refund === "Yes").length;
  const mismatches = rows.filter(r => r.Service_Amount_Mismatch === "Yes").length;
  const pkgRedemptions = rows.filter(r => r.FinalProduct === "Package Redemption").length;
  const other = rows.filter(r => r.FinalProduct === "Other").length;
  const byCat = {};
  rows.forEach(r => { const b = r.FinalMainBucket || "Uncategorized"; byCat[b] = (byCat[b]||0)+1; });

  // Row-level details for clickable widgets
  const mismatches_rows = rows
    .filter(r => r.Service_Amount_Mismatch === "Yes")
    .map(r => {
      const dollarMatch = String(r.Service||"").match(/^\$-?([\d.]+) /);
      return { Num: r.Num, Customer: r.Customer, Service: r.Service, svcAmt: dollarMatch ? `$${dollarMatch[1]}` : "—", Rate: `$${parseFloat(r.Rate)||0}`, Amount: `$${parseFloat(r.Amount)||0}` };
    });

  const other_rows = rows
    .filter(r => r.FinalProduct === "Other")
    .map(r => ({ Num: r.Num, Customer: r.Customer, Service: r.Service, Amount: r.Amount }));

  // QB Miscategorization detection
  // Rows where QB's Product/Service doesn't match what we classify them as
  const qbMiscat_rows = [];
  rows.forEach(r => {
    const ps = String(r["Product/Service"] || "").trim();
    const svc = r.Service || "";
    const sl = svc.toLowerCase();
    let reason = null;
    // Rule 1: refund filed under wrong PS
    if (r.Is_Refund === "Yes" && ps !== "Refunds") {
      reason = `Refund filed as "${ps}"`;
    }
    // Rule 2: reimbursement (positive income) miscategorized
    else if (/reimburs/i.test(sl) && ps !== "Refunds" && ps !== "Miscellaneous") {
      reason = `Reimbursement filed as "${ps}"`;
    }
    // Rule 3: training service filed as Boarding
    else if (ps === "Boarding" && r.FinalMainBucket === "Dog Training") {
      reason = `Training service filed as "Boarding"`;
    }
    if (reason) {
      qbMiscat_rows.push({
        ID: r._id || "",
        Num: r.Num,
        Date: r.Date,
        Customer: r.Customer,
        QBProduct: ps,
        OurBucket: r.FinalMainBucket,
        ServiceDisplay: r.ServiceDisplay || svc,
        Qty: r.Qty,
        Rate: r.Rate,
        Amount: r.Amount,
        Reason: reason,
      });
    }
  });
  // Sort descending by date
  qbMiscat_rows.sort((a, b) => {
    const da = String(a.Date || "").slice(0,10);
    const db = String(b.Date || "").slice(0,10);
    return db.localeCompare(da);
  });

  // Service display breakdown for import summary
  const byServiceDisplay = {};
  rows.forEach(r => {
    const key = `${r.FinalMainBucket}||${r.FinalProduct}||${r.ServiceDisplay||r.Service}`;
    const amt = parseFloat(r.Amount)||0;
    const rate = Math.abs(parseFloat(r.Rate)||0);
    if (!byServiceDisplay[key]) byServiceDisplay[key] = { revenue:0, qty:0, rateSum:0, rateCount:0, bucket:r.FinalMainBucket, product:r.FinalProduct, service:r.ServiceDisplay||r.Service };
    byServiceDisplay[key].revenue += amt;
    byServiceDisplay[key].qty += Math.abs(parseFloat(r.Qty)||0)||1;
    if (rate > 0) { byServiceDisplay[key].rateSum += rate; byServiceDisplay[key].rateCount += 1; }
  });
  // Compute avgRate for each entry
  Object.values(byServiceDisplay).forEach(v => {
    if (v.product === "Package Redemption") { v.avgRate = -1; return; }
    v.avgRate = v.rateCount > 0 ? v.rateSum / v.rateCount : 0;
  });
  return { total, refunds, mismatches, pkgRedemptions, other, byCat, mismatches_rows, other_rows, byServiceDisplay, qbMiscat_rows };
}

function buildReclassification(rows) {
  // Group by original QB Product/Service → our FinalMainBucket → ServiceDisplay
  const map = {};
  rows.forEach(r => {
    const orig = String(r["Product/Service"] || "").trim() || "(none)";
    const final = r.FinalMainBucket || "Uncategorized";
    const svc = r.ServiceDisplay || r.Service || "(unknown)";
    const amt = parseFloat(r.Amount) || 0;
    const qty = Math.abs(parseFloat(r.Qty) || 0) || 1;
    const differs = orig.toLowerCase() !== final.toLowerCase();
    const key = `${orig}||${final}`;
    if (!map[key]) map[key] = { orig, final, qty:0, revenue:0, differs, services:{} };
    map[key].qty += qty;
    map[key].revenue += amt;
    // Third level: ServiceDisplay
    if (!map[key].services[svc]) map[key].services[svc] = { qty:0, revenue:0 };
    map[key].services[svc].qty += qty;
    map[key].services[svc].revenue += amt;
  });
  // Group by orig → array of finals
  const byOrig = {};
  Object.values(map).forEach(v => {
    if (!byOrig[v.orig]) byOrig[v.orig] = { orig:v.orig, qty:0, revenue:0, finals:[], hasDiff:false };
    byOrig[v.orig].qty += v.qty;
    byOrig[v.orig].revenue += v.revenue;
    byOrig[v.orig].finals.push({ final:v.final, qty:v.qty, revenue:v.revenue, differs:v.differs, services:v.services });
    if (v.differs) byOrig[v.orig].hasDiff = true;
  });
  return Object.values(byOrig)
    .sort((a,b) => (b.hasDiff - a.hasDiff) || Math.abs(b.revenue) - Math.abs(a.revenue));
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────

const NAVY="#1B3A6B", NAVY2="#2a5298", NAVY_BG="#e8edf5";
const GRAY_BG="#f4f5f7", GRAY_B="#e2e6ea", GRAY_MID="#8a94a6";
const WHITE="#fff", RED="#c0392b", GREEN="#1a6b3c", AMBER="#d97706";
const FONT="'DM Sans','Segoe UI',sans-serif";

// API base — empty string on Vercel (relative URLs), localhost in local dev
const API_BASE = process.env.REACT_APP_API_URL || "";
const fmt=(n)=>n==null?"—":(n<0?"-$":"$")+Math.abs(n).toLocaleString("en-US",{minimumFractionDigits:0,maximumFractionDigits:0});
const fmtQ=(n)=>n==null?"—":Math.abs(n).toLocaleString("en-US",{maximumFractionDigits:0});

const BUCKET_COLORS = {
  Daycare:"#60A5FA", Boarding:"#2563EB", "Dog Training":"#7C3AED",
  Enrichment:"#059669", Grooming:"#D97706", Transport:"#0891B2",
  "Package Redemption":"#6B7280", "Retail / Food":"#92400E",
  Discounts:"#DC2626", Refunds:"#EF4444", Miscellaneous:"#9CA3AF"
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function Widget({ label, value, qty, accent, sub, prevValue, yoyValue, onClick, period, invertTrend }) {
  // For invertTrend (Discounts/Refunds), compare absolute magnitudes — smaller absolute = better
  const effectiveValue = invertTrend ? Math.abs(value || 0) : (value || 0);
  const effectivePrev = invertTrend ? Math.abs(prevValue || 0) : (prevValue || 0);
  const effectiveYoy = invertTrend ? Math.abs(yoyValue || 0) : (yoyValue || 0);

  // Period-over-period change
  const hasPrev = prevValue != null && prevValue !== 0;
  const prevChg = hasPrev ? ((effectiveValue - effectivePrev) / Math.abs(effectivePrev)) * 100 : null;
  const prevDir = prevChg != null ? (prevChg >= 0 ? "↑" : "↓") : null;
  // invertTrend: magnitude down = good (green), magnitude up = bad (red), flag if up > 10%
  const prevColor = prevChg != null ? (invertTrend ? (prevChg <= 0 ? GREEN : RED) : (prevChg >= 0 ? GREEN : RED)) : GRAY_MID;
  const prevFlagged = prevChg != null && (invertTrend ? prevChg > 10 : prevChg < -10);
  const prevLabel = period === "week" ? "vs Last Week" : period === "year" ? "vs Last Year" : "vs Last Month";

  // YoY change (only shown for month/week, not year)
  const hasYoy = yoyValue != null && yoyValue !== 0 && period !== "year";
  const yoyChg = hasYoy ? ((effectiveValue - effectiveYoy) / Math.abs(effectiveYoy)) * 100 : null;
  const yoyDir = yoyChg != null ? (yoyChg >= 0 ? "↑" : "↓") : null;
  const yoyColor = yoyChg != null ? (invertTrend ? (yoyChg <= 0 ? GREEN : RED) : (yoyChg >= 0 ? GREEN : RED)) : GRAY_MID;
  const yoyFlagged = yoyChg != null && (invertTrend ? yoyChg > 10 : yoyChg < -10);

  return (
    <div onClick={onClick} style={{
      background: WHITE, borderRadius:10, padding:"16px 18px",
      borderTop:`3px solid ${accent||NAVY}`, border:`1px solid ${GRAY_B}`, borderTopColor:accent||NAVY,
      cursor: onClick ? "pointer" : "default",
      transition:"box-shadow 0.15s",
      boxShadow: onClick ? "0 1px 4px rgba(0,0,0,0.06)" : "none"
    }}
    onMouseEnter={e=>{ if(onClick) e.currentTarget.style.boxShadow="0 4px 12px rgba(0,0,0,0.12)"; }}
    onMouseLeave={e=>{ if(onClick) e.currentTarget.style.boxShadow="0 1px 4px rgba(0,0,0,0.06)"; }}
    >
      <div style={{ fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:800, color: parseFloat(value)<0?RED:NAVY, letterSpacing:"-0.02em", fontFamily:FONT, marginTop:4 }}>{fmt(value)}</div>
      {prevChg != null && (
        <div style={{ fontSize:11, fontWeight:600, color:prevColor, fontFamily:FONT, marginTop:3, display:"flex", alignItems:"center", gap:3 }}>
          <span>{prevDir} {prevChg >= 0 ? "+" : ""}{prevChg.toFixed(1)}% {prevLabel}</span>
          {prevFlagged && <span style={{ fontSize:12 }}>⚠️</span>}
        </div>
      )}
      {yoyChg != null && (
        <div style={{ fontSize:11, fontWeight:600, color:yoyColor, fontFamily:FONT, marginTop:2, display:"flex", alignItems:"center", gap:3 }}>
          <span>{yoyDir} {yoyChg >= 0 ? "+" : ""}{yoyChg.toFixed(1)}% YoY</span>
          {yoyFlagged && <span style={{ fontSize:12 }}>⚠️</span>}
        </div>
      )}
      {prevChg == null && <div style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, marginTop:3 }}>{fmtQ(qty)} transactions{sub ? ` · ${sub}` : ""}</div>}
    </div>
  );
}

function PeriodPicker({ period, setPeriod, periods, mode }) {
  return (
    <div style={{ display:"flex", gap:0, border:`1px solid ${GRAY_B}`, borderRadius:6, overflow:"hidden", flexWrap:"wrap" }}>
      {periods.map(p => (
        <button key={p.key} onClick={() => setPeriod(p.key)} style={{
          padding:"6px 14px", border:"none", borderRight:`1px solid ${GRAY_B}`, cursor:"pointer",
          fontSize:12, fontWeight:600, fontFamily:FONT,
          background: period===p.key ? NAVY : WHITE,
          color: period===p.key ? WHITE : GRAY_MID,
          transition:"all 0.1s"
        }}>{p.label}</button>
      ))}
    </div>
  );
}

function DrillRow({ label, revenue, qty, depth=0, accent, onClick, expanded, hasChildren, note }) {
  const isNeg = revenue < 0;
  const indent = depth * 18;
  return (
    <div onClick={onClick} style={{
      display:"grid", gridTemplateColumns:`24px 1fr 110px 160px`,
      alignItems:"center", padding:`10px 16px`,
      borderBottom:`1px solid ${GRAY_B}`, cursor:hasChildren?"pointer":"default",
      background: expanded ? NAVY_BG : WHITE, transition:"background 0.1s",
      borderLeft: depth===0 ? `3px solid ${accent||GRAY_B}` : "3px solid transparent",
      paddingLeft: 16 + indent
    }}>
      {hasChildren
        ? <span style={{ textAlign:"center", color:NAVY, fontSize:16, display:"block", transform:expanded?"rotate(90deg)":"none", transition:"transform 0.2s" }}>›</span>
        : <span />
      }
      <span style={{ fontFamily:FONT }}>
        <span style={{ fontSize:depth===0?14:13, fontWeight:depth===0?700:500, color:NAVY }}>{label}</span>
        {note && <span style={{ fontSize:10, color:GRAY_MID, marginLeft:8, fontStyle:"italic" }}>{note}</span>}
      </span>
      <span style={{ fontSize:12, color:GRAY_MID, textAlign:"right", fontFamily:FONT, paddingRight:8 }}>{fmtQ(qty)}</span>
      <span style={{ fontSize:depth===0?14:13, fontWeight:700, color:isNeg?RED:NAVY, textAlign:"right", fontFamily:FONT }}>{fmt(revenue)}</span>
    </div>
  );
}

function BucketAccordion({ tableData, bucketColors, externalOpenBucket, onBucketChange, accordionRef }) {
  const [openBucket, setOpenBucket] = useState(null);
  const [openProduct, setOpenProduct] = useState(null);

  // Respond to external trigger (widget click)
  useEffect(() => {
    if (externalOpenBucket != null) {
      setOpenBucket(externalOpenBucket);
      setOpenProduct(null);
    }
  }, [externalOpenBucket]);

  const orderedBuckets = BUCKET_ORDER.filter(b => tableData.byBucket[b]);
  const productsByBucket = {};
  const servicesByProduct = {};
  Object.values(tableData.byProduct).forEach(v => {
    const bk = v.bucket; const pk = `${bk}||${v.product}`;
    if (!productsByBucket[bk]) productsByBucket[bk] = [];
    productsByBucket[bk].push(v);
  });
  Object.values(tableData.byService).forEach(v => {
    const pk = `${v.bucket}||${v.product}`;
    if (!servicesByProduct[pk]) servicesByProduct[pk] = [];
    servicesByProduct[pk].push(v);
  });

  return (
    <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
      <div style={{ display:"grid", gridTemplateColumns:"24px 1fr 110px 160px", padding:"10px 16px", background:_tenantHeaderBg }}>
        <span />
        <span style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.65)", letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>Service Category</span>
        <span style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.65)", letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT, textAlign:"right", paddingRight:8 }}>Qty</span>
        <span style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.65)", letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT, textAlign:"right" }}>Revenue</span>
      </div>
      {orderedBuckets.map(b => {
        const bd = tableData.byBucket[b];
        const prods = (productsByBucket[b]||[]).sort((a,z)=>Math.abs(z.revenue)-Math.abs(a.revenue));
        return (
          <div key={b}>
            <DrillRow label={b} revenue={bd.revenue} qty={bd.qty} depth={0} accent={bucketColors[b]} hasChildren={prods.length>0} expanded={openBucket===b} onClick={()=>setOpenBucket(openBucket===b?null:b)} />
            {openBucket===b && prods.map(p => {
              const pk = `${b}||${p.product}`;
              const svcs = (servicesByProduct[pk]||[]).sort((a,z)=>(a.avgRate||0)-(z.avgRate||0));
              return (
                <div key={pk}>
                  <DrillRow label={p.product} revenue={p.revenue} qty={p.qty} depth={1} hasChildren={svcs.length>0} expanded={openProduct===pk} onClick={()=>setOpenProduct(openProduct===pk?null:pk)} />
                  {openProduct===pk && svcs.map(sv => (
                    <DrillRow key={sv.service} label={sv.service} revenue={sv.revenue} qty={sv.qty} depth={2} hasChildren={false} />
                  ))}
                </div>
              );
            })}
          </div>
        );
      })}
      {/* Grand total footer */}
      {tableData.byBucket && Object.keys(tableData.byBucket).length > 0 && (() => {
        const grandTotal = Object.values(tableData.byBucket).reduce((s,b) => s+(b.revenue||0), 0);
        const grandQty   = Object.values(tableData.byBucket).reduce((s,b) => s+(b.qty||0), 0);
        return (
          <div style={{ display:"grid", gridTemplateColumns:"24px 1fr 110px 160px", borderTop:`2px solid ${_tenantHeaderBg}`, background:GRAY_BG, padding:"10px 16px", alignItems:"center" }}>
            <span />
            <span style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT }}>TOTAL</span>
            <span style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"right", paddingRight:8 }}>{grandQty.toLocaleString()}</span>
            <span style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"right" }}>{fmt(grandTotal)}</span>
          </div>
        );
      })()}
    </div>
  );
}

function buildPivot(rows, mode) {
  const buckets = BUCKET_ORDER;
  const periodMap = {};  rows.forEach(r => {
    // Week groups by WeekEnding; month/year group by transaction Date
    const d = mode === "week" ? parseWE(r.WeekEnding) : parseDate(r.Date);
    if (!d) return;
    const key = mode==="week" ? weKey(d) : mode==="month" ? moKey(d) : yrKey(d);
    const b = r.FinalMainBucket;
    const p = r.FinalProduct || "Other";
    const sd = r.ServiceDisplay || r.Service || "Unknown";
    const amt = parseFloat(r.Amount)||0;
    const qty = Math.abs(parseFloat(r.Qty)||0)||1;
    if (!periodMap[key]) periodMap[key] = {};
    if (!periodMap[key][b]) periodMap[key][b] = { revenue:0, qty:0, products:{} };
    periodMap[key][b].revenue += amt;
    periodMap[key][b].qty += qty;
    if (!periodMap[key][b].products[p]) periodMap[key][b].products[p] = { revenue:0, qty:0, services:{} };
    periodMap[key][b].products[p].revenue += amt;
    periodMap[key][b].products[p].qty += qty;
    if (!periodMap[key][b].products[p].services[sd]) periodMap[key][b].products[p].services[sd] = { revenue:0, qty:0 };
    periodMap[key][b].products[p].services[sd].revenue += amt;
    periodMap[key][b].products[p].services[sd].qty += qty;
  });
  const periods = Object.keys(periodMap).sort((a,b)=>b.localeCompare(a));
  return { periods, periodMap, buckets };
}

function fmtPeriod(k, mode) {
  if (mode==="week") return fmtWE(k);
  if (mode==="month") return fmtMo(k);
  return k;
}

function TimePivot({ rows }) {
  const [pivotMode, setPivotMode] = useState("week");
  const [openBucket, setOpenBucket] = useState(null);
  const [openProduct, setOpenProduct] = useState(null);
  const scrollRef = useRef();

  const { periods, periodMap, buckets } = useMemo(() => buildPivot(rows, pivotMode), [rows, pivotMode]);

  const COL_W = pivotMode==="week" ? 120 : pivotMode==="month" ? 100 : 80;
  const LABEL_W = 220;
  const ROW_H = 40;

  if (periods.length === 0) return <div style={{ padding:20, color:GRAY_MID, fontFamily:FONT }}>No data</div>;

  // Flatten rows for the pivot based on open state
  const flatRows = [];
  buckets.forEach(b => {
    const hasProd = periods.some(p => periodMap[p]?.[b]);
    if (!hasProd) return;
    flatRows.push({ type:"bucket", key:b, label:b, depth:0 });
    if (openBucket === b) {
      const allProds = new Set();
      periods.forEach(p => { if (periodMap[p]?.[b]) Object.keys(periodMap[p][b].products).forEach(pr => allProds.add(pr)); });
      const sortedProds = [...allProds].sort();
      sortedProds.forEach(prod => {
        flatRows.push({ type:"product", key:`${b}||${prod}`, label:prod, depth:1, bucket:b });
        if (openProduct === `${b}||${prod}`) {
          const allSvcs = new Set();
          periods.forEach(p => { const sv = periodMap[p]?.[b]?.products?.[prod]?.services; if(sv) Object.keys(sv).forEach(s=>allSvcs.add(s)); });
          [...allSvcs].sort().forEach(svc => {
            flatRows.push({ type:"service", key:`${b}||${prod}||${svc}`, label:svc, depth:2, bucket:b, product:prod });
          });
        }
      });
    }
  });

  const getVal = (row, period) => {
    const bd = periodMap[period]?.[row.bucket||row.key];
    if (!bd) return null;
    if (row.type==="bucket") return bd;
    const pd = bd?.products?.[row.product||row.label];
    if (!pd) return null;
    if (row.type==="product") return pd;
    return pd?.services?.[row.label] || null;
  };

  return (
    <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
      <div style={{ padding:"12px 16px", background:_tenantHeaderBg, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <span style={{ fontSize:12, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>
          Revenue Over Time — scroll →
        </span>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ display:"flex", gap:2, background:"rgba(255,255,255,0.1)", borderRadius:6, padding:2 }}>
            {[["week","Week"],["month","Month"],["year","Year"]].map(([m,l])=>(
              <button key={m} onClick={()=>{ setPivotMode(m); setOpenBucket(null); setOpenProduct(null); }} style={{ padding:"4px 12px", borderRadius:4, border:"none", cursor:"pointer", fontSize:12, fontWeight:600, fontFamily:FONT, background:pivotMode===m?WHITE:"transparent", color:pivotMode===m?_tenantHeaderBg:"rgba(255,255,255,0.7)", transition:"all 0.1s" }}>{l}</button>
            ))}
          </div>
          <div style={{ display:"flex", gap:4 }}>
            <button onClick={()=>scrollRef.current.scrollBy({left:-(COL_W*4),behavior:"smooth"})} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:WHITE, borderRadius:4, padding:"4px 10px", cursor:"pointer", fontSize:14, fontFamily:FONT }}>‹‹</button>
            <button onClick={()=>scrollRef.current.scrollBy({left:COL_W*4,behavior:"smooth"})} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:WHITE, borderRadius:4, padding:"4px 10px", cursor:"pointer", fontSize:14, fontFamily:FONT }}>››</button>
          </div>
        </div>
      </div>
      <div style={{ display:"flex" }}>
        {/* Fixed label column */}
        <div style={{ minWidth:LABEL_W, flexShrink:0, borderRight:`1px solid ${GRAY_B}` }}>
          <div style={{ height:ROW_H, background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}`, display:"flex", alignItems:"center", padding:"0 12px" }}>
            <span style={{ fontSize:11, fontWeight:700, color:GRAY_MID, fontFamily:FONT, textTransform:"uppercase", letterSpacing:"0.06em" }}>Category</span>
          </div>
          {flatRows.map(row => (
            <div key={row.key} onClick={()=>{
              if (row.type==="bucket") setOpenBucket(openBucket===row.key?null:row.key);
              if (row.type==="product") setOpenProduct(openProduct===row.key?null:row.key);
            }} style={{
              height:ROW_H, borderBottom:`1px solid ${GRAY_B}`, display:"flex", alignItems:"center",
              padding:`0 12px 0 ${12+row.depth*16}px`, background:WHITE, cursor:row.type!=="service"?"pointer":"default",
              borderLeft: row.depth===0 ? `3px solid ${BUCKET_COLORS[row.key]||GRAY_B}` : "3px solid transparent"
            }}>
              {row.type!=="service" && (
                <span style={{ marginRight:6, color:_tenantHeaderBg, fontSize:14, display:"inline-block",
                  transform:(row.type==="bucket"?openBucket===row.key:openProduct===row.key)?"rotate(90deg)":"none",
                  transition:"transform 0.2s" }}>›</span>
              )}
              <span style={{ fontSize:row.depth===0?13:12, fontWeight:row.depth===0?700:400, color:_tenantHeaderBg, fontFamily:FONT, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{row.label}</span>
            </div>
          ))}
        </div>
        {/* Scrollable data — both axes */}
        <div ref={scrollRef} style={{ overflowX:"auto", overflowY:"auto", flex:1 }}>
          <div style={{ display:"flex", minWidth:periods.length*COL_W }}>
            {periods.map(period => (
              <div key={period} style={{ minWidth:COL_W, flexShrink:0, borderRight:`1px solid ${GRAY_B}` }}>
                <div style={{ height:ROW_H, background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}`, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 6px" }}>
                  <span style={{ fontSize:11, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"center", whiteSpace:"nowrap" }}>{fmtPeriod(period, pivotMode)}</span>
                </div>
                {flatRows.map(row => {
                  const val = getVal(row, period);
                  const rev = val?.revenue || 0;
                  const isNeg = rev < 0;
                  return (
                    <div key={row.key} style={{ height:ROW_H, borderBottom:`1px solid ${GRAY_B}`, display:"flex", alignItems:"center", justifyContent:"flex-end", padding:"0 10px", background:WHITE }}>
                      <span style={{ fontSize:12, fontWeight:row.depth===0?700:400, color:rev===0?GRAY_MID:isNeg?RED:_tenantHeaderBg, fontFamily:FONT }}>{rev===0?"—":fmt(rev)}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Total footer */}
      {(() => {
        const periodTotals = periods.map(p => {
          return buckets.reduce((s, b) => {
            const bd = periodMap[p]?.[b];
            return s + (bd?.revenue || 0);
          }, 0);
        });
        const grandTotal = periodTotals.reduce((s,v) => s+v, 0);
        return (
          <div style={{ display:"flex", borderTop:`2px solid ${_tenantHeaderBg}` }}>
            <div style={{ minWidth:LABEL_W, flexShrink:0, borderRight:`1px solid ${GRAY_B}`, padding:"10px 12px", background:GRAY_BG }}>
              <span style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT }}>TOTAL</span>
              <span style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, marginLeft:8 }}>{fmt(grandTotal)}</span>
            </div>
            <div style={{ overflowX:"hidden", flex:1 }}>
              <div style={{ display:"flex", minWidth:periods.length*COL_W }}>
                {periodTotals.map((tot, i) => (
                  <div key={i} style={{ minWidth:COL_W, flexShrink:0, borderRight:`1px solid ${GRAY_B}`, padding:"10px 6px", background:GRAY_BG, textAlign:"right" }}>
                    <span style={{ fontSize:12, fontWeight:800, color:tot===0?GRAY_MID:_tenantHeaderBg, fontFamily:FONT }}>{tot===0?"—":fmt(tot)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function ReclassificationPivot({ rows, isPK }) {
  const [openOrig, setOpenOrig] = useState(null);
  const [openFinal, setOpenFinal] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const data = useMemo(() => buildReclassification(rows), [rows]);
  const diffsOnly = data.filter(d => d.hasDiff);
  const display = showAll ? data : diffsOnly;
  const diffCount = diffsOnly.length;
  const COLS = "24px 1fr 1fr 90px 110px";

  return (
    <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
      <div style={{ padding:"12px 16px", background:_tenantHeaderBg, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div>
          <span style={{ fontSize:12, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>
            {isPK ? "Product Reclassification" : "Product Reclassification"}
          </span>
          <span style={{ fontSize:11, color:"rgba(255,255,255,0.6)", fontFamily:FONT, marginLeft:10 }}>
            {diffCount} {isPK ? "item" : "QB categor"}{diffCount!==1?"s":"y"} reclassified by us
          </span>
        </div>
        <button onClick={()=>setShowAll(v=>!v)} style={{ background:"rgba(255,255,255,0.15)", border:"none", color:WHITE, borderRadius:4, padding:"4px 12px", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>
          {showAll ? "Show Differences Only" : "Show All"}
        </button>
      </div>

      {/* Column headers */}
      <div style={{ display:"grid", gridTemplateColumns:COLS, padding:"8px 16px", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}` }}>
        <span />
        <span style={{ fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT }}>{isPK ? "Fin File Category" : "Fin File Category"}</span>
        <span style={{ fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT }}>Our Category / Service</span>
        <span style={{ fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT, textAlign:"right" }}>Qty</span>
        <span style={{ fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT, textAlign:"right" }}>Revenue</span>
      </div>

      <div style={{ maxHeight:600, overflowY:"auto" }}>
        {display.length === 0 && (
          <div style={{ padding:20, fontSize:13, color:GREEN, fontFamily:FONT }}>✓ All financial file categories match our classification</div>
        )}
        {display.map(orig => {
          const isOpenOrig = openOrig === orig.orig;
          const finalLabels = [...new Set(orig.finals.map(f => f.final))];
          return (
            <div key={orig.orig}>
              {/* Level 1 — QB Product/Service */}
              <div onClick={()=>{ setOpenOrig(isOpenOrig ? null : orig.orig); setOpenFinal(null); }} style={{
                display:"grid", gridTemplateColumns:COLS, alignItems:"center", padding:"10px 16px",
                borderBottom:`1px solid ${GRAY_B}`, background:isOpenOrig ? NAVY_BG : WHITE,
                cursor:"pointer", borderLeft:`3px solid ${orig.hasDiff ? AMBER : GRAY_B}`
              }}>
                <span style={{ textAlign:"center", color:_tenantHeaderBg, fontSize:16, display:"block", transform:isOpenOrig?"rotate(90deg)":"none", transition:"transform 0.2s" }}>›</span>
                <span style={{ fontSize:13, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT }}>{orig.orig}</span>
                <span style={{ fontSize:12, color:orig.hasDiff ? AMBER : GRAY_MID, fontFamily:FONT, fontStyle:orig.hasDiff?"normal":"italic" }}>
                  {orig.hasDiff ? `→ ${finalLabels.join(", ")}` : finalLabels[0]}
                </span>
                <span style={{ fontSize:12, color:GRAY_MID, textAlign:"right", fontFamily:FONT }}>{fmtQ(orig.qty)}</span>
                <span style={{ fontSize:13, fontWeight:700, color:orig.revenue<0?RED:_tenantHeaderBg, textAlign:"right", fontFamily:FONT }}>{fmt(orig.revenue)}</span>
              </div>

              {/* Level 2 — Our FinalMainBucket */}
              {isOpenOrig && orig.finals
                .sort((a,b) => Math.abs(b.revenue)-Math.abs(a.revenue))
                .map(f => {
                  const fKey = `${orig.orig}||${f.final}`;
                  const isOpenFinal = openFinal === fKey;
                  const svcList = Object.entries(f.services).sort((a,b) => Math.abs(b[1].revenue)-Math.abs(a[1].revenue));
                  return (
                    <div key={fKey}>
                      <div onClick={()=>setOpenFinal(isOpenFinal ? null : fKey)} style={{
                        display:"grid", gridTemplateColumns:COLS, alignItems:"center",
                        padding:"8px 16px 8px 28px", borderBottom:`1px solid ${GRAY_B}`,
                        background:isOpenFinal ? "#e8edf5" : f.differs ? "#fffbeb" : GRAY_BG,
                        cursor:"pointer", borderLeft:`3px solid ${f.differs ? AMBER : GRAY_B}`
                      }}>
                        <span style={{ textAlign:"center", color:_tenantHeaderBg, fontSize:14, display:"block", transform:isOpenFinal?"rotate(90deg)":"none", transition:"transform 0.2s" }}>›</span>
                        <span style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT, fontStyle:"italic" }}>↳ {orig.orig}</span>
                        <span style={{ fontSize:12, fontWeight:600, color:f.differs?AMBER:_tenantHeaderBg, fontFamily:FONT }}>
                          {f.final}
                          {f.differs && <span style={{ fontSize:10, marginLeft:6, background:AMBER, color:WHITE, borderRadius:3, padding:"1px 5px" }}>reclassified</span>}
                        </span>
                        <span style={{ fontSize:12, color:GRAY_MID, textAlign:"right", fontFamily:FONT }}>{fmtQ(f.qty)}</span>
                        <span style={{ fontSize:12, fontWeight:600, color:f.revenue<0?RED:_tenantHeaderBg, textAlign:"right", fontFamily:FONT }}>{fmt(f.revenue)}</span>
                      </div>

                      {/* Level 3 — ServiceDisplay */}
                      {isOpenFinal && svcList.map(([svc, sv]) => (
                        <div key={svc} style={{
                          display:"grid", gridTemplateColumns:COLS, alignItems:"center",
                          padding:"6px 16px 6px 46px", borderBottom:`0.5px solid ${GRAY_B}`,
                          background:WHITE, borderLeft:"3px solid transparent"
                        }}>
                          <span />
                          <span style={{ fontSize:12, color:"#888", fontFamily:FONT, fontStyle:"italic" }}>↳ service detail</span>
                          <span style={{ fontSize:12, color:_tenantHeaderBg, fontFamily:FONT }}>{svc}</span>
                          <span style={{ fontSize:12, color:GRAY_MID, textAlign:"right", fontFamily:FONT }}>{fmtQ(sv.qty)}</span>
                          <span style={{ fontSize:12, color:sv.revenue<0?RED:_tenantHeaderBg, textAlign:"right", fontFamily:FONT }}>{fmt(sv.revenue)}</span>
                        </div>
                      ))}
                    </div>
                  );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReconciliationTable({ filteredRows }) {
  const qbTotals = {}, ourTotals = {};
  filteredRows.forEach(r => {
    const ps = String(r["Product/Service"] || "").trim() || "(none)";
    const bucket = r.FinalMainBucket || "Miscellaneous";
    const amt = parseFloat(r.Amount) || 0;
    qbTotals[ps] = (qbTotals[ps] || 0) + amt;
    ourTotals[bucket] = (ourTotals[bucket] || 0) + amt;
  });

  // QB side: positive revenue buckets first, then adjustments (negatives)
  const QB_POS = ["Daycare","Daycare Packages Income","Boarding","Activities","Enrichment","Dog Training","Grooming","Transportation Income","Transportation Packages Income"];
  const QB_ADJ = ["Discounts","Refunds"];
  const qbPosRows = QB_POS.filter(k => qbTotals[k] != null)
    .concat(Object.keys(qbTotals).filter(k => !QB_POS.includes(k) && !QB_ADJ.includes(k) && qbTotals[k] > 0).sort());
  const qbAdjRows = QB_ADJ.filter(k => qbTotals[k] != null)
    .concat(Object.keys(qbTotals).filter(k => !QB_POS.includes(k) && !QB_ADJ.includes(k) && qbTotals[k] <= 0).sort());
  const qbTotalRevenue = qbPosRows.reduce((s,k)=>s+(qbTotals[k]||0),0);
  const qbNetRevenue = qbPosRows.concat(qbAdjRows).reduce((s,k)=>s+(qbTotals[k]||0),0);

  // Our side: positive service buckets first, then adjustments
  const OUR_POS = ["Daycare","Boarding","Dog Training","Enrichment","Grooming","Transport","Miscellaneous","Retail / Food"];
  const OUR_ADJ = ["Discounts","Refunds"];
  const ourPosRows = OUR_POS.filter(k => ourTotals[k] != null)
    .concat(Object.keys(ourTotals).filter(k => !OUR_POS.includes(k) && !OUR_ADJ.includes(k) && (ourTotals[k]||0) > 0).sort());
  const ourAdjRows = OUR_ADJ.filter(k => ourTotals[k] != null)
    .concat(Object.keys(ourTotals).filter(k => !OUR_POS.includes(k) && !OUR_ADJ.includes(k) && (ourTotals[k]||0) <= 0).sort());
  const ourTotalRevenue = ourPosRows.reduce((s,k)=>s+(ourTotals[k]||0),0);
  const ourNetRevenue = ourPosRows.concat(ourAdjRows).reduce((s,k)=>s+(ourTotals[k]||0),0);

  const QB_MISC_ITEMS = ["Miscellaneous","Retail / Food","Package Redemption"];
  const isQbAdj = (k) => QB_ADJ.includes(k);
  const isQbMisc = (k) => QB_MISC_ITEMS.includes(k);
  const isOurAdj = (k) => OUR_ADJ.includes(k);
  const isOurMisc = (k) => ["Miscellaneous","Retail / Food","Package Redemption"].includes(k);

  const COL = { fontSize:12, fontFamily:FONT, padding:"9px 12px", borderBottom:`1px solid ${GRAY_B}` };
  const HDR = { fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT, padding:"8px 12px", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}` };
  const SUBTOTAL = { fontSize:12, fontFamily:FONT, padding:"9px 12px", borderBottom:`1px solid ${GRAY_B}`, borderTop:`1px solid ${GRAY_B}`, background:"#f0f4fb" };
  const ADJ_BG = "#fffbeb";

  const SideRows = ({ posRows, adjRows, totals, totalRevenue, netRevenue, isAdj, isMisc, padLeft }) => {
    const pl = padLeft ? 16 : 12;
    return (
      <div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 120px" }}>
          <span style={{ ...HDR, paddingLeft:pl }}>Category</span>
          <span style={{ ...HDR, textAlign:"right" }}>Amount</span>
        </div>
        {/* Positive revenue rows */}
        {posRows.map(k => (
          <div key={k} style={{ display:"grid", gridTemplateColumns:"1fr 120px", background:isMisc(k)?"#f8f9ff":WHITE }}>
            <span style={{ ...COL, paddingLeft:pl, color:isMisc(k)?NAVY2:_tenantHeaderBg }}>{k}{isMisc(k)?" *":""}</span>
            <span style={{ ...COL, textAlign:"right", fontWeight:600, color:(totals[k]||0)<0?RED:_tenantHeaderBg }}>{fmt(totals[k]||0)}</span>
          </div>
        ))}
        {/* Total Revenue subtotal */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 120px" }}>
          <span style={{ ...SUBTOTAL, paddingLeft:pl, fontWeight:700, color:_tenantHeaderBg }}>Total Revenue</span>
          <span style={{ ...SUBTOTAL, textAlign:"right", fontWeight:700, color:_tenantHeaderBg }}>{fmt(totalRevenue)}</span>
        </div>
        {/* Adjustment label */}
        <div style={{ padding:`6px ${pl}px`, background:ADJ_BG, borderBottom:`1px solid ${GRAY_B}` }}>
          <span style={{ fontSize:10, fontWeight:700, color:AMBER, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT }}>Adjustments</span>
        </div>
        {/* Adjustment rows */}
        {adjRows.map(k => (
          <div key={k} style={{ display:"grid", gridTemplateColumns:"1fr 120px", background:ADJ_BG }}>
            <span style={{ ...COL, paddingLeft:pl, color:AMBER, fontStyle:"italic" }}>{k}</span>
            <span style={{ ...COL, textAlign:"right", fontWeight:600, color:(totals[k]||0)<0?RED:_tenantHeaderBg }}>{fmt(totals[k]||0)}</span>
          </div>
        ))}
        {/* Net Revenue total */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 120px", borderTop:`2px solid ${_tenantHeaderBg}` }}>
          <span style={{ ...COL, paddingLeft:pl, fontWeight:800, color:_tenantHeaderBg, fontSize:13 }}>Net Revenue</span>
          <span style={{ ...COL, textAlign:"right", fontWeight:800, color:netRevenue<0?RED:_tenantHeaderBg, fontSize:13 }}>{fmt(netRevenue)}</span>
        </div>
      </div>
    );
  };

  const diff = ourNetRevenue - qbNetRevenue;

  return (
    <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
      <div style={{ padding:"12px 16px", background:_tenantHeaderBg, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <span style={{ fontSize:12, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>
          Financial File Reconciliation
        </span>
        <span style={{ fontSize:11, color: Math.abs(diff)<1?"#86efac":"rgba(255,200,100,0.9)", fontFamily:FONT, fontWeight:600 }}>
          {Math.abs(diff)<1 ? "✅ Reconciled" : `⚠️ Diff: ${fmt(diff)}`}
        </span>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1px 1fr" }}>
        <SideRows
          posRows={qbPosRows} adjRows={qbAdjRows} totals={qbTotals}
          totalRevenue={qbTotalRevenue} netRevenue={qbNetRevenue}
          isAdj={isQbAdj} isMisc={isQbMisc} padLeft={false}
        />
        <div style={{ background:GRAY_B }} />
        <SideRows
          posRows={ourPosRows} adjRows={ourAdjRows} totals={ourTotals}
          totalRevenue={ourTotalRevenue} netRevenue={ourNetRevenue}
          isAdj={isOurAdj} isMisc={isOurMisc} padLeft={true}
        />
      </div>
      <div style={{ padding:"8px 12px", fontSize:11, color:AMBER, fontFamily:FONT, fontStyle:"italic", borderTop:`1px solid ${GRAY_B}`, background:"#fffbeb" }}>
        * Misc / Retail items shown separately for transparency
      </div>
    </div>
  );
}


// ─── CUSTOMER KPI HELPERS ──────────────────────────────────────────────────
const CORE_BUCKETS = ["Daycare","Boarding","Dog Training","Grooming","Enrichment","Transport"];
const CORE_COLORS = {
  "Daycare":"#60A5FA","Boarding":"#2563EB","Dog Training":"#7C3AED",
  "Grooming":"#D97706","Enrichment":"#059669","Transport":"#0891B2"
};

function getCoreBucket(ps, service) {
  const p = String(ps||"").trim();
  const sl = String(service||"").toLowerCase();
  if (p === "Refunds" || p === "Discounts") return null;
  if (p === "Daycare" || p === "Daycare Packages Income") return "Daycare";
  if (p === "Grooming") return "Grooming";
  if (p === "Dog Training") return "Dog Training";
  if (p === "Transportation Income" || p === "Transportation Packages Income") return "Transport";
  if (p === "Activities" || p === "Enrichment") {
    // Lodging in enrichment building = boarding stay, not enrichment service
    if (/lodging/i.test(sl)) return "Boarding";
    return "Enrichment";
  }
  if (p === "Boarding") {
    if (/stay.?and.?train|stay.?&.?train|board.?and.?train|board.?&.?train|day.?train|adult.?day.?train|puppy.?day.?train|remote.?collar.?program|acclimation.?training/i.test(sl)) return "Dog Training";
    if (/enrichment|silver|gold|bronze|activity package/i.test(sl)) return "Enrichment";
    return "Boarding";
  }
  return null;
}

// Resolve true first service using business intent rules:
// - Board & Train / Stay & Train / Day Train -> Training (boarding is just lodging)
// - Grooming/Transport never primary if anything else present on same day
// - Activity packages tied to Boarding -> Boarding primary
// - Standalone enrichment passes with no boarding -> Enrichment primary
// - Otherwise: Boarding > Daycare > Dog Training > Enrichment > Grooming > Transport
function resolveFirstService(firstDayTxns) {
  // FinalMainBucket is already correctly set by scrubRows for all cases:
  // - Board & Train rows already have FinalMainBucket = "Dog Training"
  // - Lodging (Enrichment) rows already have FinalMainBucket = "Boarding"
  // - Activity packages already have FinalMainBucket = "Boarding"
  // - Standalone enrichment passes already have FinalMainBucket = "Enrichment"
  // Priority: Boarding > Daycare > Dog Training > Enrichment > Grooming > Transport
  const buckets = new Set(firstDayTxns.map(t => t.bucket));
  if (buckets.has("Boarding")) return "Boarding";
  if (buckets.has("Daycare")) return "Daycare";
  if (buckets.has("Dog Training")) return "Dog Training";
  if (buckets.has("Enrichment")) return "Enrichment";
  if (buckets.has("Grooming")) return "Grooming";
  if (buckets.has("Transport")) return "Transport";
  return firstDayTxns[0].bucket;
}

function buildCustomerProfiles(rows) {
  if (!rows || !rows.length) return [];
  const CORE_SET = new Set(CORE_BUCKETS);
  const custMap = {};
  rows.forEach(r => {
    const cust = String(r.Customer||"").trim();
    const amt = parseFloat(r.Amount||0);
    if (!cust || amt <= 0) return;
    const d = parseDate(r.Date);
    if (!d || d.getFullYear() < 2019) return;
    // Use FinalMainBucket directly — already correctly computed by scrubRows
    const bucket = r.FinalMainBucket;
    if (!bucket || !CORE_SET.has(bucket)) return;
    const svcStr = r.ServiceDisplay || r.Service || "";
    if (!custMap[cust]) custMap[cust] = { txns: [] };
    custMap[cust].txns.push({ date: d, bucket, amt, serviceDisplay: svcStr });
  });
  return Object.entries(custMap).map(([cust, data]) => {
    const txns = data.txns.sort((a,b) => a.date - b.date);
    const firstDate = txns[0].date;
    const firstDayTxns = txns.filter(t => t.date.getTime() === firstDate.getTime());
    const firstService = resolveFirstService(firstDayTxns);
    const firstYear = firstDate.getFullYear();
    const bucketsUsed = [...new Set(txns.map(t => t.bucket))];
    const revByBucket = {};
    CORE_BUCKETS.forEach(b => { revByBucket[b] = 0; });
    txns.forEach(t => { revByBucket[t.bucket] = (revByBucket[t.bucket]||0) + t.amt; });
    const totalRev = Object.values(revByBucket).reduce((s,v)=>s+v,0);
    const firstDateByBucket = {};
    txns.forEach(t => { if (!firstDateByBucket[t.bucket]) firstDateByBucket[t.bucket] = t.date; });
    return { cust, firstService, firstYear, bucketsUsed, revByBucket, totalRev, firstDateByBucket, nServices: bucketsUsed.length };
  });
}
// ─── STICKY CUSTOMER WIDGETS ───────────────────────────────────────────────
function CustomerWidgets({ profiles }) {
  const [tooltip, setTooltip] = useState(null);
  if (!profiles.length) return null;

  const total = profiles.length;
  // 1. Overall retention — avg yr1 across cohorts (pre-computed from cohort matrix)
  const cohortRates = {2019:0.625,2020:0.713,2021:0.508,2022:0.493,2023:0.557,2024:0.549,2025:0.388};
  const avgRetention = Object.values(cohortRates).reduce((s,v)=>s+v,0) / Object.values(cohortRates).length;
  // 2. Cross-service adoption
  const multiSvc = profiles.filter(p => p.nServices > 1).length;
  const crossAdoption = multiSvc / total;
  // 3. Avg CLTV
  const avgCLTV = profiles.reduce((s,p)=>s+p.totalRev,0) / total;
  // Best acquiring service by CLTV
  const byService = {};
  CORE_BUCKETS.forEach(b => { byService[b] = []; });
  profiles.forEach(p => { if (byService[p.firstService]) byService[p.firstService].push(p); });
  const bestService = CORE_BUCKETS.reduce((best, b) => {
    const custs = byService[b];
    if (!custs.length) return best;
    const avg = custs.reduce((s,p)=>s+p.totalRev,0)/custs.length;
    return (!best || avg > best.avg) ? {name:b, avg} : best;
  }, null);

  const widgets = [
    {
      label: "Avg Year-1 Retention",
      value: `${Math.round(avgRetention*100)}%`,
      color: avgRetention >= 0.6 ? GREEN : avgRetention >= 0.4 ? AMBER : RED,
      tooltip: "Average % of new customers who made a purchase the following year, measured across cohorts 2019–2025. Excludes 2026 (partial year). Based on positive-revenue transactions only."
    },
    {
      label: "Cross-Service Adoption",
      value: `${Math.round(crossAdoption*100)}%`,
      color: crossAdoption >= 0.7 ? GREEN : crossAdoption >= 0.5 ? AMBER : RED,
      tooltip: `${multiSvc.toLocaleString()} of ${total.toLocaleString()} customers (${Math.round(crossAdoption*100)}%) have used more than one core service. Core services: Daycare, Boarding, Dog Training, Grooming, Enrichment, Transport.`
    },
    {
      label: "Avg Customer LTV",
      value: fmt(avgCLTV),
      color: NAVY,
      tooltip: `Average lifetime revenue per customer across all ${total.toLocaleString()} customers with core service transactions. Includes all service buckets. Highest CLTV acquisition channel: ${bestService?.name} (${fmt(bestService?.avg)}/customer).`
    },
  ];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const stickyEl = document.querySelector('[data-sticky="customers"]');
      const stickyHeight = stickyEl ? stickyEl.offsetHeight : 60;
      const top = el.getBoundingClientRect().top + window.scrollY - stickyHeight - 56;
      window.scrollTo({ top, behavior:"smooth" });
    }
  };

  // anchor ids per widget: 0=retention(no scroll), 1=cross-service, 2=ltv
  const anchors = [null, "cross-service-section", "ltv-section"];

  return (
    <div data-sticky="customers" style={{ position:"sticky", top:60, zIndex:90, background:GRAY_BG, paddingBottom:8, marginBottom:-8 }}>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, paddingTop:4 }}>
        {widgets.map((w,i) => (
          <div key={i}
            onClick={() => anchors[i] && scrollTo(anchors[i])}
            style={{ background:WHITE, borderRadius:10, padding:"16px 20px", border:`1px solid ${GRAY_B}`, borderTop:`3px solid ${w.color}`, position:"relative", cursor:anchors[i]?"pointer":"default", transition:"box-shadow 0.15s", boxShadow:anchors[i]?"0 1px 4px rgba(0,0,0,0.06)":"none" }}
            onMouseEnter={e => { setTooltip(i); if(anchors[i]) e.currentTarget.style.boxShadow="0 4px 12px rgba(0,0,0,0.12)"; }}
            onMouseLeave={e => { setTooltip(null); if(anchors[i]) e.currentTarget.style.boxShadow="0 1px 4px rgba(0,0,0,0.06)"; }}
          >
            <div style={{ fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT }}>{w.label}</div>
            <div style={{ fontSize:26, fontWeight:800, color:w.color, letterSpacing:"-0.02em", fontFamily:FONT, marginTop:4 }}>{w.value}</div>
            <div style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, marginTop:2 }}>All cohorts · {total.toLocaleString()} customers{anchors[i] ? " · ↓ View details" : ""}</div>
            {tooltip === i && (
              <div style={{ position:"absolute", top:"100%", left:0, zIndex:200, background:NAVY, color:WHITE, borderRadius:8, padding:"12px 14px", fontSize:11, fontFamily:FONT, lineHeight:1.6, width:260, boxShadow:"0 8px 24px rgba(0,0,0,0.3)", marginTop:4 }}>
                <div style={{ fontWeight:700, marginBottom:4, color:"#93c5fd" }}>ℹ {w.label}</div>
                {w.tooltip}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CROSS-SERVICE ADOPTION MATRIX ────────────────────────────────────────
function CrossServiceMatrix({ profiles }) {
  const [viewMode, setViewMode] = useState("pct");
  const [tooltip, setTooltip] = useState(null);

  const filtered = profiles; // filtering handled globally upstream

  const matrix = useMemo(() => {
    const m = {};
    CORE_BUCKETS.forEach(fs => {
      m[fs] = {};
      CORE_BUCKETS.forEach(ls => { m[fs][ls] = { count:0, rev:0, days:[] }; });
    });
    filtered.forEach(p => {
      const fs = p.firstService;
      if (!m[fs]) return;
      const firstDate = p.firstDateByBucket[fs];
      CORE_BUCKETS.forEach(ls => {
        if (p.bucketsUsed.includes(ls)) {
          m[fs][ls].count++;
          m[fs][ls].rev += p.revByBucket[ls] || 0;
          const lsDate = p.firstDateByBucket[ls];
          if (firstDate && lsDate) m[fs][ls].days.push(Math.abs((lsDate - firstDate) / 86400000));
        }
      });
    });
    return m;
  }, [filtered]);

  const rowTotals = {};
  CORE_BUCKETS.forEach(fs => { rowTotals[fs] = filtered.filter(p => p.firstService === fs).length; });
  const sortedBuckets = [...CORE_BUCKETS].sort((a, b) => (rowTotals[b]||0) - (rowTotals[a]||0));

  const getHeatColor = (pct, isDiag) => {
    if (isDiag) return "#1e3a5f";
    if (pct >= 0.8) return "#14532d";
    if (pct >= 0.6) return "#16a34a";
    if (pct >= 0.4) return "#4ade80";
    if (pct >= 0.2) return "#86efac";
    if (pct >= 0.1) return "#fde68a";
    if (pct > 0) return "#fca5a5";
    return "#f8fafc";
  };
  // White text on dark backgrounds (diag, ≥80%, ≥60%), dark text on light backgrounds
  const getTextColor = (pct, isDiag) => {
    if (isDiag) return WHITE;
    if (pct >= 0.6) return WHITE;
    if (pct >= 0.4) return "#14532d";
    if (pct >= 0.2) return "#713f12";
    if (pct >= 0.1) return "#991b1b";
    return GRAY_MID;
  };

  return (
    <div id="cross-service-section" style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
      <div style={{ padding:"14px 20px", background:_tenantHeaderBg, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>Cross-Service Adoption</div>
          <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", fontFamily:FONT, marginTop:2 }}>% of customers who started in one service and later used another</div>
        </div>
        <div style={{ display:"flex", gap:2, background:"rgba(255,255,255,0.1)", borderRadius:6, padding:2 }}>
            {[["pct","% View"],["abs","# View"]].map(([m,l])=>(
              <button key={m} onClick={()=>setViewMode(m)} style={{ padding:"4px 10px", borderRadius:4, border:"none", cursor:"pointer", fontSize:11, fontWeight:600, fontFamily:FONT, background:viewMode===m?WHITE:"transparent", color:viewMode===m?_tenantHeaderBg:"rgba(255,255,255,0.7)" }}>{l}</button>
            ))}
          </div>
      </div>

      <div style={{ overflowX:"auto" }}>
        <table style={{ borderCollapse:"collapse", width:"100%", fontFamily:FONT }}>
          <thead>
            <tr>
              <th style={{ padding:"10px 16px", fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", textAlign:"left", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}`, whiteSpace:"nowrap" }}>First Service</th>
              <th style={{ padding:"10px 12px", fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", textAlign:"right", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}` }}>New Customers</th>
              {sortedBuckets.map(ls => (
                <th key={ls} style={{ padding:"10px 12px", fontSize:11, fontWeight:700, color:GRAY_MID, textAlign:"center", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}`, whiteSpace:"nowrap", minWidth:80 }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:4 }}>
                    <div style={{ width:8, height:8, borderRadius:2, background:CORE_COLORS[ls] }} />
                    {ls}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedBuckets.filter(fs => rowTotals[fs] > 0).map((fs, ri) => (
              <tr key={fs}>
                <td style={{ padding:"0 16px", height:44, borderBottom:`1px solid ${GRAY_B}` }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div style={{ width:10, height:10, borderRadius:2, background:CORE_COLORS[fs] }} />
                    <span style={{ fontSize:12, fontWeight:700, color:_tenantHeaderBg }}>{fs}</span>
                  </div>
                </td>
                <td style={{ padding:"0 12px", height:44, fontSize:12, color:GRAY_MID, textAlign:"right", borderBottom:`1px solid ${GRAY_B}` }}>{rowTotals[fs]}</td>
                {sortedBuckets.map(ls => {
                  const cell = matrix[fs]?.[ls] || { count:0, rev:0, days:[] };
                  const n = rowTotals[fs] || 1;
                  const pct = cell.count / n;
                  const isDiag = fs === ls;
                  const bg = getHeatColor(pct, isDiag);
                  const tc = getTextColor(pct, isDiag);
                  const display = viewMode === "pct" ? (cell.count > 0 ? `${Math.round(pct*100)}%` : "—") : (cell.count > 0 ? cell.count : "—");
                  const avgDays = cell.days.length ? Math.round(cell.days.reduce((s,d)=>s+d,0)/cell.days.length) : 0;
                  return (
                    <td key={ls} style={{ height:44, background:bg, textAlign:"center", borderBottom:`1px solid ${GRAY_B}`, borderLeft:`1px solid rgba(255,255,255,0.2)`, position:"relative", cursor:cell.count>0?"pointer":"default" }}
                      onMouseEnter={e => cell.count > 0 && setTooltip({ fs, ls, cell, pct, avgDays, n, elem:e.currentTarget })}
                      onMouseLeave={() => setTooltip(null)}
                    >
                      <span style={{ fontSize:11, fontWeight:700, color:tc }}>{display}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Tooltip */}
      {tooltip && tooltip.elem && (
        <div style={{ position:"fixed", top:tooltip.elem.getBoundingClientRect().top - 140, left:tooltip.elem.getBoundingClientRect().left - 60, zIndex:9999, background:_tenantHeaderBg, color:WHITE, borderRadius:8, padding:"12px 16px", fontSize:11, fontFamily:FONT, lineHeight:1.8, minWidth:220, boxShadow:"0 8px 24px rgba(0,0,0,0.35)", pointerEvents:"none" }}>
          <div style={{ fontWeight:700, fontSize:12, color:"#93c5fd", marginBottom:4 }}>Service Journey</div>
          <div>First service: <span style={{ fontWeight:600 }}>{tooltip.fs}</span></div>
          <div>Later service: <span style={{ fontWeight:600 }}>{tooltip.ls}</span></div>
          <div style={{ borderTop:"1px solid rgba(255,255,255,0.15)", marginTop:6, paddingTop:6 }}>
            <div style={{ color:"#86efac" }}>Customers: {tooltip.cell.count} of {tooltip.n} ({Math.round(tooltip.pct*100)}%)</div>
            <div style={{ color:"#93c5fd" }}>Revenue from {tooltip.ls}: {fmt(tooltip.cell.rev)}</div>
            {tooltip.fs !== tooltip.ls && <div style={{ color:"#fde68a" }}>Avg days to first {tooltip.ls}: {tooltip.avgDays}</div>}
          </div>
        </div>
      )}

      {/* Legend */}
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 16px", borderTop:`1px solid ${GRAY_B}`, flexWrap:"wrap" }}>
        <span style={{ fontSize:10, color:GRAY_MID, fontFamily:FONT }}>Adoption rate:</span>
        {[["#14532d","≥80%"],["#16a34a","≥60%"],["#4ade80","≥40%"],["#86efac","≥20%"],["#fde68a","≥10%"],["#fca5a5",">0%"],["#1e3a5f","Same service"]].map(([c,l])=>(
          <div key={l} style={{ display:"flex", alignItems:"center", gap:3 }}>
            <div style={{ width:14, height:14, borderRadius:2, background:c }} />
            <span style={{ fontSize:10, color:GRAY_MID, fontFamily:FONT }}>{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── LTV BY ACQUISITION SERVICE ────────────────────────────────────────────
function LTVByService({ profiles, cohortLabel }) {
  const [hoveredService, setHoveredService] = useState(null);
  const [multiTooltip, setMultiTooltip] = useState(null);

  const filtered = profiles; // filtering handled globally upstream

  const tableData = useMemo(() => {
    const byService = {};
    CORE_BUCKETS.forEach(b => { byService[b] = []; });
    filtered.forEach(p => { if (byService[p.firstService]) byService[p.firstService].push(p); });
    return CORE_BUCKETS.filter(b => b !== "Transport").map(svc => {
      const custs = byService[svc];
      if (!custs.length) return null;
      const n = custs.length;
      const avgCLTV = custs.reduce((s,p)=>s+p.totalRev,0) / n;
      // Multi-service: used at least one service OTHER than their first service
      const multiSvc = custs.filter(p => p.bucketsUsed.some(b => b !== p.firstService)).length / n;
      // Avg services OTHER than first (so min=0 for single-service customers)
      // Round up to nearest whole number — you can't have a fraction of a service
      const avgSvcs = Math.ceil(custs.reduce((s,p)=>s+p.nServices,0) / n);
      const bucketRevPerCust = {};
      CORE_BUCKETS.forEach(b => {
        bucketRevPerCust[b] = custs.reduce((s,p)=>s+(p.revByBucket[b]||0),0) / n;
      });
      const totalRevAll = Object.values(bucketRevPerCust).reduce((s,v)=>s+v,0);
      return { svc, n, avgCLTV, multiSvc, avgSvcs, bucketRevPerCust, totalRevAll };
    }).filter(Boolean)
      .sort((a, b) => b.n - a.n); // sort descending by customer count
  }, [filtered]);

  if (!tableData.length) return null;

  const maxCLTV = Math.max(...tableData.map(d => d.avgCLTV));

  return (
    <div id="ltv-section" style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
      <div style={{ padding:"14px 20px", background:_tenantHeaderBg, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>Lifetime Value by Acquisition Service</div>
          <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", fontFamily:FONT, marginTop:2 }}>How much total revenue does each customer generate, by the service that first brought them in</div>
        </div>

      </div>

      {/* Stacked horizontal bar chart */}
      <div style={{ padding:"20px 20px 8px" }}>
        <div style={{ fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT, marginBottom:12 }}>Avg CLTV Breakdown by Service — hover for details</div>
        {tableData.sort((a,b)=>b.avgCLTV-a.avgCLTV).map(d => {
          const isHovered = hoveredService === d.svc;
          return (
            <div key={d.svc} style={{ marginBottom:10 }}
              onMouseEnter={() => setHoveredService(d.svc)}
              onMouseLeave={() => setHoveredService(null)}
            >
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:3 }}>
                <div style={{ width:90, fontSize:11, fontWeight:700, color:CORE_COLORS[d.svc]||_tenantHeaderBg, fontFamily:FONT, textAlign:"right", flexShrink:0 }}>{d.svc}</div>
                <div style={{ flex:1, position:"relative", height:24, background:GRAY_BG, borderRadius:4, overflow:"hidden" }}>
                  {/* Stacked segments */}
                  {(() => {
                    let offset = 0;
                    return CORE_BUCKETS.map(b => {
                      const rev = d.bucketRevPerCust[b] || 0;
                      if (rev <= 0) return null;
                      const w = (rev / maxCLTV) * 100;
                      const seg = (
                        <div key={b} style={{ position:"absolute", left:`${offset}%`, top:0, width:`${w}%`, height:"100%", background:CORE_COLORS[b]||GRAY_MID, opacity: isHovered ? 1 : 0.75, transition:"opacity 0.15s" }} />
                      );
                      offset += w;
                      return seg;
                    });
                  })()}
                </div>
                <div style={{ width:70, fontSize:12, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT, flexShrink:0 }}>{fmt(d.avgCLTV)}</div>
              </div>
              {/* Expanded breakdown on hover */}
              {isHovered && (
                <div style={{ marginLeft:100, background:"#f0f4fb", borderRadius:6, padding:"10px 14px", fontSize:11, fontFamily:FONT, display:"flex", gap:16, flexWrap:"wrap" }}>
                  {CORE_BUCKETS.filter(b => (d.bucketRevPerCust[b]||0) > 0).map(b => (
                    <div key={b} style={{ display:"flex", alignItems:"center", gap:5 }}>
                      <div style={{ width:10, height:10, borderRadius:2, background:CORE_COLORS[b] }} />
                      <span style={{ color:GRAY_MID }}>{b}:</span>
                      <span style={{ fontWeight:700, color:_tenantHeaderBg }}>{fmt(d.bucketRevPerCust[b])}</span>
                      <span style={{ color:GRAY_MID }}>({Math.round((d.bucketRevPerCust[b]/d.totalRevAll)*100)}%)</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {/* Legend */}
        <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginTop:12, paddingTop:10, borderTop:`1px solid ${GRAY_B}` }}>
          {CORE_BUCKETS.map(b => (
            <div key={b} style={{ display:"flex", alignItems:"center", gap:4 }}>
              <div style={{ width:10, height:10, borderRadius:2, background:CORE_COLORS[b] }} />
              <span style={{ fontSize:10, color:GRAY_MID, fontFamily:FONT }}>{b}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={{ borderTop:`1px solid ${GRAY_B}` }}>
        <div style={{ display:"grid", gridTemplateColumns:"150px 110px 130px 160px 130px", padding:"8px 20px", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}` }}>
          {["First Service","Customers","Avg CLTV","Multi-Service %","Avg Services"].map((h,i)=>(
            <span key={i} style={{ fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT, textAlign:i===0?"left":"center" }}>{h}</span>
          ))}
        </div>
        {tableData.sort((a,b)=>b.n-a.n).map((d,i) => (
          <div key={d.svc} style={{ display:"grid", gridTemplateColumns:"150px 110px 130px 160px 130px", padding:"10px 20px", borderBottom:`1px solid ${GRAY_B}`, background:i%2===0?WHITE:GRAY_BG }}>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:10, height:10, borderRadius:2, background:CORE_COLORS[d.svc] }} />
              <span style={{ fontSize:12, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT }}>{d.svc}</span>
            </div>
            <span style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT, textAlign:"center" }}>{d.n.toLocaleString()}</span>
            <span style={{ fontSize:12, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"center" }}>{fmt(d.avgCLTV)}</span>
            <div style={{ textAlign:"center", position:"relative" }}
              onMouseEnter={e => setMultiTooltip({ svc: d.svc, data: d, elem: e.currentTarget })}
              onMouseLeave={() => setMultiTooltip(null)}
            >
              <span style={{ fontSize:12, fontWeight:600, color:d.multiSvc>=0.7?GREEN:d.multiSvc>=0.5?AMBER:RED, fontFamily:FONT, cursor:"help", borderBottom:`1px dashed ${d.multiSvc>=0.7?GREEN:d.multiSvc>=0.5?AMBER:RED}` }}>{Math.round(d.multiSvc*100)}%</span>
              {multiTooltip?.svc === d.svc && multiTooltip?.elem && (
                <div style={{ position:"fixed", top: multiTooltip.elem.getBoundingClientRect().top - 160, left: multiTooltip.elem.getBoundingClientRect().left - 180, zIndex:9999, background:_tenantHeaderBg, color:WHITE, borderRadius:8, padding:"12px 16px", fontSize:11, fontFamily:FONT, lineHeight:1.8, minWidth:220, boxShadow:"0 8px 24px rgba(0,0,0,0.35)", pointerEvents:"none" }}>
                  <div style={{ fontWeight:700, fontSize:12, color:"#93c5fd", marginBottom:6 }}>Revenue from other services</div>
                  <div style={{ color:"rgba(255,255,255,0.6)", marginBottom:6 }}>{Math.round(d.multiSvc*100)}% of {d.n} {d.svc} customers used multiple services</div>
                  {CORE_BUCKETS.filter(b => b !== d.svc && (d.bucketRevPerCust[b]||0) > 0).sort((a,b2) => (d.bucketRevPerCust[b2]||0)-(d.bucketRevPerCust[a]||0)).map(b => (
                    <div key={b} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:16 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                        <div style={{ width:8, height:8, borderRadius:2, background:CORE_COLORS[b] }} />
                        <span style={{ color:"rgba(255,255,255,0.8)" }}>{b}</span>
                      </div>
                      <span style={{ fontWeight:700, color:"#86efac" }}>{fmt(d.bucketRevPerCust[b])} /cust</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <span style={{ fontSize:12, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"center" }}>{d.avgSvcs}</span>
          </div>
        ))}
      </div>
      <div style={{ padding:"10px 20px", fontSize:11, color:GRAY_MID, fontFamily:FONT, borderTop:`1px solid ${GRAY_B}`, background:GRAY_BG, display:"flex", flexDirection:"column", gap:4 }}>
        <div style={{ fontStyle:"italic" }}>
          <span style={{ fontWeight:600, fontStyle:"normal", color:_tenantHeaderBg }}>Avg CLTV: </span>
          {(!cohortLabel || cohortLabel === "All Cohorts")
            ? "Total lifetime revenue per customer across all years — includes every transaction they have ever made."
            : `Lifetime revenue per customer in the ${cohortLabel} cohort — includes all their transactions across all years.`
          }
        </div>
        <div style={{ fontStyle:"italic" }}>
          Multi-Service % color thresholds — <span style={{ color:GREEN, fontWeight:600, fontStyle:"normal" }}>Green ≥70%</span> · <span style={{ color:AMBER, fontWeight:600, fontStyle:"normal" }}>Amber ≥50%</span> · <span style={{ color:RED, fontWeight:600, fontStyle:"normal" }}>Red &lt;50%</span>
        </div>
      </div>
    </div>
  );
}

function CustomerRetentionChart({ rows, cohortFrom, cohortTo }) {
  const [viewMode, setViewMode] = useState("pct"); // "pct" | "abs"
  const [chartType, setChartType] = useState("heatmap"); // "line" | "heatmap"
  const [tooltip, setTooltip] = useState(null);

  // Build full cohort matrix from transaction rows
  const cohortMatrix = useMemo(() => {
    if (!rows || !rows.length) return [];
    const custYears = {};
    const firstYear = {};
    rows.forEach(r => {
      const cust = String(r.Customer || "").trim();
      const amt = parseFloat(r.Amount || 0);
      if (!cust || amt <= 0) return;
      const d = parseDate(r.Date);
      if (!d) return;
      const yr = d.getFullYear();
      if (yr < 2019) return;
      if (!custYears[cust]) custYears[cust] = new Set();
      custYears[cust].add(yr);
      if (firstYear[cust] == null || yr < firstYear[cust]) firstYear[cust] = yr;
    });
    const cohorts = {};
    Object.entries(firstYear).forEach(([c, yr]) => {
      if (!cohorts[yr]) cohorts[yr] = [];
      cohorts[yr].push(c);
    });
    const years = Object.keys(cohorts).map(Number).sort();
    const maxYear = Math.max(...years);
    const maxOffset = maxYear - Math.min(...years);
    return years.map(yr => {
      const custs = cohorts[yr] || [];
      const n = custs.length;
      const retention = [];
      for (let offset = 0; offset <= maxOffset; offset++) {
        const targetYr = yr + offset;
        if (targetYr > maxYear) { retention.push(null); continue; }
        const retained = custs.filter(c => custYears[c]?.has(targetYr)).length;
        retention.push({ count: retained, pct: n > 0 ? retained / n : 0 });
      }
      return { year: yr, newCustomers: n, retention, partial: yr === maxYear };
    });
  }, [rows]);

  if (!cohortMatrix.length) return (
    <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, padding:40, textAlign:"center", color:GRAY_MID, fontFamily:FONT }}>
      Upload a transaction file to generate retention data
    </div>
  );

  const maxOffset = cohortMatrix[0].retention.length - 1;
  const COHORT_COLORS = ["#1B3A6B","#2563EB","#7C3AED","#059669","#D97706","#0891B2","#DC2626","#6B7280"];

  // ── LINE CHART ─────────────────────────────────────────────────────────────
  const LineChart = () => {
    const W = 680, H = 300, PAD = { top:20, right:20, bottom:44, left:52 };
    const cW = W - PAD.left - PAD.right;
    const cH = H - PAD.top - PAD.bottom;
    const maxAbs = Math.max(...cohortMatrix.map(d => d.newCustomers));
    const offsets = Array.from({length: maxOffset + 1}, (_, i) => i);

    const getY = (val) => {
      if (viewMode === "pct") return PAD.top + cH - val * cH;
      return PAD.top + cH - (val / maxAbs) * cH;
    };
    const getX = (offset) => PAD.left + (offset / maxOffset) * cW;

    const yTicks = viewMode === "pct"
      ? [0, 0.25, 0.5, 0.75, 1.0]
      : [0, 0.25, 0.5, 0.75, 1.0].map(v => Math.round(v * maxAbs));

    return (
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow:"visible", fontFamily:FONT }}>
        {/* Gridlines + Y axis */}
        {yTicks.map((t, i) => {
          const val = viewMode === "pct" ? t : t / maxAbs;
          const y = getY(val);
          return (
            <g key={i}>
              <line x1={PAD.left} x2={PAD.left+cW} y1={y} y2={y} stroke={i===0?"#cbd5e1":"#e2e8f0"} strokeWidth={i===0?1.5:1} />
              <text x={PAD.left-8} y={y+4} textAnchor="end" fontSize={10} fill="#94a3b8">
                {viewMode === "pct" ? `${Math.round(t*100)}%` : t.toLocaleString()}
              </text>
            </g>
          );
        })}

        {/* X axis labels */}
        {offsets.map(offset => (
          <text key={offset} x={getX(offset)} y={PAD.top+cH+16} textAnchor="middle" fontSize={10} fill={_tenantHeaderBg} fontWeight={600}>
            Yr {offset}
          </text>
        ))}
        <text x={PAD.left + cW/2} y={H-4} textAnchor="middle" fontSize={10} fill={GRAY_MID}>
          Years since first purchase
        </text>

        {/* Lines */}
        {cohortMatrix.map((cohort, ci) => {
          const color = COHORT_COLORS[ci % COHORT_COLORS.length];
          const points = cohort.retention
            .map((r, offset) => {
              if (r == null) return null;
              const val = viewMode === "pct" ? r.pct : r.count;
              const normVal = viewMode === "pct" ? val : val / maxAbs;
              return { x: getX(offset), y: getY(normVal), raw: r, offset };
            })
            .filter(Boolean);

          if (points.length < 2) return null;
          const pathD = points.map((p, i) => `${i===0?"M":"L"} ${p.x} ${p.y}`).join(" ");

          return (
            <g key={cohort.year}>
              <path d={pathD} fill="none" stroke={color} strokeWidth={2.5}
                strokeLinejoin="round" strokeLinecap="round" opacity={0.85} />
              {points.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r={4} fill={color} stroke={WHITE} strokeWidth={1.5}
                  style={{ cursor:"pointer" }}
                  onMouseEnter={() => setTooltip({ x: p.x, y: p.y, cohort, point: p })}
                  onMouseLeave={() => setTooltip(null)}
                />
              ))}
            </g>
          );
        })}

        {/* Tooltip */}
        {tooltip && tooltip.point && (() => {
          const { cohort, point } = tooltip;
          const tx = Math.min(point.x + 14, W - 160);
          const ty = Math.max(point.y - 60, PAD.top);
          return (
            <g>
              <rect x={tx} y={ty} width={150} height={72} rx={6} fill={_tenantHeaderBg} opacity={0.93} />
              <text x={tx+10} y={ty+15} fontSize={11} fontWeight={700} fill={WHITE}>{cohort.year} cohort · Yr {point.offset}</text>
              <text x={tx+10} y={ty+30} fontSize={10} fill="rgba(255,255,255,0.7)">New customers: {cohort.newCustomers}</text>
              <text x={tx+10} y={ty+44} fontSize={10} fill="#86efac">Retained: {point.raw.count} ({Math.round(point.raw.pct*100)}%)</text>
              <text x={tx+10} y={ty+58} fontSize={10} fill="#fca5a5">Churned: {cohort.newCustomers - point.raw.count} ({Math.round((1-point.raw.pct)*100)}%)</text>
            </g>
          );
        })()}
      </svg>
    );
  };

  // ── HEATMAP ────────────────────────────────────────────────────────────────
  const Heatmap = () => {
    const CELL_H = 36, LABEL_W = 72, VAL_W = 68, CELL_W = 64;
    const offsets = Array.from({length: maxOffset + 1}, (_, i) => i);

    const getColor = (pct) => {
      if (pct == null) return "#f8fafc";
      if (pct >= 0.9) return "#14532d";
      if (pct >= 0.7) return "#16a34a";
      if (pct >= 0.5) return "#4ade80";
      if (pct >= 0.35) return "#86efac";
      if (pct >= 0.2) return "#fde68a";
      if (pct >= 0.1) return "#fca5a5";
      return "#ef4444";
    };
    const getTextColor = (pct) => pct == null ? GRAY_MID : pct >= 0.5 ? WHITE : _tenantHeaderBg;

    return (
      <div style={{ overflowX:"auto" }}>
        <table style={{ borderCollapse:"collapse", width:"100%", fontFamily:FONT }}>
          <thead>
            <tr>
              <th style={{ padding:"8px 12px", fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", textAlign:"left", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}`, width:LABEL_W }}>Cohort</th>
              <th style={{ padding:"8px 12px", fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", textAlign:"right", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}`, width:VAL_W }}>New Custs</th>
              {offsets.map(o => (
                <th key={o} style={{ padding:"8px 8px", fontSize:11, fontWeight:700, color:GRAY_MID, textAlign:"center", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}`, width:CELL_W, letterSpacing:"0.04em" }}>
                  Yr {o}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cohortMatrix.map((cohort, ci) => (
              <tr key={cohort.year}>
                <td style={{ padding:"0 12px", height:CELL_H, fontSize:12, fontWeight:700, color:_tenantHeaderBg, borderBottom:`1px solid ${GRAY_B}` }}>
                  {cohort.year}{cohort.partial?" *":""}
                </td>
                <td style={{ padding:"0 12px", height:CELL_H, fontSize:12, color:GRAY_MID, textAlign:"right", borderBottom:`1px solid ${GRAY_B}` }}>
                  {cohort.newCustomers.toLocaleString()}
                </td>
                {cohort.retention.map((r, offset) => {
                  const bg = r ? getColor(r.pct) : "#f8fafc";
                  const tc = r ? getTextColor(r.pct) : GRAY_MID;
                  const display = r ? (viewMode === "pct" ? `${Math.round(r.pct*100)}%` : r.count.toLocaleString()) : "—";
                  return (
                    <td key={offset}
                      style={{ height:CELL_H, background:bg, textAlign:"center", borderBottom:`1px solid ${GRAY_B}`, borderLeft:`1px solid rgba(255,255,255,0.3)`, cursor:r?"pointer":"default", position:"relative" }}
                      onMouseEnter={e => r && setTooltip({ x:0, y:0, cohort, point:{ raw:r, offset }, elem:e.currentTarget })}
                      onMouseLeave={() => setTooltip(null)}
                    >
                      <span style={{ fontSize:11, fontWeight:600, color:tc }}>{display}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Color legend */}
        <div style={{ display:"flex", alignItems:"center", gap:8, padding:"10px 12px", borderTop:`1px solid ${GRAY_B}` }}>
          <span style={{ fontSize:10, color:GRAY_MID, fontFamily:FONT }}>Retention:</span>
          {[["#14532d","≥90%"],["#16a34a","≥70%"],["#4ade80","≥50%"],["#86efac","≥35%"],["#fde68a","≥20%"],["#fca5a5","≥10%"],["#ef4444","<10%"]].map(([c,l])=>(
            <div key={l} style={{ display:"flex", alignItems:"center", gap:3 }}>
              <div style={{ width:14, height:14, borderRadius:2, background:c }} />
              <span style={{ fontSize:10, color:GRAY_MID, fontFamily:FONT }}>{l}</span>
            </div>
          ))}
        </div>

        {/* Heatmap tooltip */}
        {tooltip && tooltip.point && tooltip.elem && (() => {
          const { cohort, point } = tooltip;
          return (
            <div style={{
              position:"fixed",
              top: tooltip.elem.getBoundingClientRect().top - 80,
              left: tooltip.elem.getBoundingClientRect().left + 20,
              background:_tenantHeaderBg, color:WHITE, borderRadius:6, padding:"10px 14px",
              fontSize:11, fontFamily:FONT, pointerEvents:"none", zIndex:9999,
              boxShadow:"0 4px 16px rgba(0,0,0,0.3)", lineHeight:1.8
            }}>
              <div style={{ fontWeight:700 }}>{cohort.year} cohort · Yr {point.offset} ({cohort.year + point.offset})</div>
              <div style={{ color:"rgba(255,255,255,0.7)" }}>New customers: {cohort.newCustomers}</div>
              <div style={{ color:"#86efac" }}>Retained: {point.raw.count} ({Math.round(point.raw.pct*100)}%)</div>
              <div style={{ color:"#fca5a5" }}>Churned by now: {cohort.newCustomers - point.raw.count} ({Math.round((1-point.raw.pct)*100)}%)</div>
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:0, background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
      {/* Header */}
      <div style={{ padding:"14px 20px", background:_tenantHeaderBg, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>Customer Retention</div>
          <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", fontFamily:FONT, marginTop:2 }}>Cohort analysis — % of customers still active each year after first purchase</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {/* Chart type toggle */}
          <div style={{ display:"flex", gap:2, background:"rgba(255,255,255,0.1)", borderRadius:6, padding:2 }}>
            {[["heatmap","🔲 Heatmap"],["line","📈 Line"]].map(([m,l])=>(
              <button key={m} onClick={()=>setChartType(m)} style={{
                padding:"4px 12px", borderRadius:4, border:"none", cursor:"pointer",
                fontSize:11, fontWeight:600, fontFamily:FONT,
                background:chartType===m?WHITE:"transparent",
                color:chartType===m?_tenantHeaderBg:"rgba(255,255,255,0.7)",
                transition:"all 0.1s"
              }}>{l}</button>
            ))}
          </div>
          {/* View mode toggle */}
          <div style={{ display:"flex", gap:2, background:"rgba(255,255,255,0.1)", borderRadius:6, padding:2 }}>
            {[["pct","% View"],["abs","# View"]].map(([m,l])=>(
              <button key={m} onClick={()=>setViewMode(m)} style={{
                padding:"4px 12px", borderRadius:4, border:"none", cursor:"pointer",
                fontSize:11, fontWeight:600, fontFamily:FONT,
                background:viewMode===m?WHITE:"transparent",
                color:viewMode===m?_tenantHeaderBg:"rgba(255,255,255,0.7)",
                transition:"all 0.1s"
              }}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div style={{ padding: chartType==="line" ? "20px 20px 8px" : "0" }}>
        {chartType === "line" ? <LineChart /> : <Heatmap />}
      </div>

      {/* Legend (line chart only) */}
      {chartType === "line" && (
        <div style={{ display:"flex", gap:16, flexWrap:"wrap", padding:"4px 20px 16px", borderTop:`1px solid ${GRAY_B}` }}>
          {cohortMatrix.map((cohort, ci) => (
            <div key={cohort.year} style={{ display:"flex", alignItems:"center", gap:5 }}>
              <div style={{ width:24, height:3, borderRadius:2, background:COHORT_COLORS[ci % COHORT_COLORS.length] }} />
              <span style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT }}>{cohort.year}{cohort.partial?" *":""}</span>
            </div>
          ))}
          <span style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, fontStyle:"italic" }}>* partial year</span>
        </div>
      )}

      {/* Data table */}
      <div style={{ borderTop:`1px solid ${GRAY_B}` }}>
        <div style={{ display:"grid", gridTemplateColumns:`80px 110px repeat(${maxOffset+1}, 1fr)`, padding:"8px 16px", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}` }}>
          <span style={{ fontSize:10, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT }}>Cohort</span>
          <span style={{ fontSize:10, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT, textAlign:"right" }}>New Custs</span>
          {Array.from({length: maxOffset+1}, (_,i) => (
            <span key={i} style={{ fontSize:10, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT, textAlign:"center" }}>Yr {i}</span>
          ))}
        </div>
        {cohortMatrix.map((d, ci) => {
          const inRange = (!cohortFrom || d.year >= cohortFrom) && (!cohortTo || d.year <= cohortTo);
          return (
          <div key={d.year} style={{ display:"grid", gridTemplateColumns:`80px 110px repeat(${maxOffset+1}, 1fr)`, padding:"8px 16px", borderBottom:`1px solid ${GRAY_B}`, background:ci%2===0?WHITE:GRAY_BG, opacity:inRange?1:0.35, transition:"opacity 0.2s" }}>
            <span style={{ fontSize:11, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT }}>{d.year}{d.partial?" *":""}</span>
            <span style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, textAlign:"right" }}>{d.newCustomers.toLocaleString()}</span>
            {d.retention.map((r, offset) => {
              const display = r ? (viewMode==="pct" ? `${Math.round(r.pct*100)}%` : r.count.toLocaleString()) : "—";
              const color = r ? (r.pct >= 0.5 ? GREEN : r.pct >= 0.25 ? AMBER : r.pct > 0 ? RED : GRAY_MID) : GRAY_MID;
              return (
                <span key={offset} style={{ fontSize:11, fontWeight:r&&offset>0?600:400, color:offset===0?_tenantHeaderBg:color, fontFamily:FONT, textAlign:"center" }}>{display}</span>
              );
            })}
          </div>
          );
        })}
        <div style={{ padding:"8px 16px", fontSize:11, color:GRAY_MID, fontFamily:FONT, fontStyle:"italic" }}>
          * 2026 is a partial year. Yr 0 = year of first purchase (always 100%). Each subsequent year shows % of original cohort still active.
        </div>
      </div>
    </div>
  );
}


function ImportSummary({ quality, rows, onGoScorecard, authClient }) {
  const isPK = authClient?.clientId === "petkennect";
  const [filterBucket, setFilterBucket] = useState("All");
  const buckets = ["All", ...BUCKET_ORDER.filter(b => quality.byCat[b])];
  const maxCat = Math.max(...Object.values(quality.byCat), 1);

  const serviceRows = Object.values(quality.byServiceDisplay)
    .filter(r => filterBucket === "All" || r.bucket === filterBucket)
    .sort((a,b) => {
      const bucketDiff = BUCKET_ORDER.indexOf(a.bucket) - BUCKET_ORDER.indexOf(b.bucket);
      if (bucketDiff !== 0) return bucketDiff;
      if (a.product !== b.product) return a.product.localeCompare(b.product);
      return (a.avgRate||0) - (b.avgRate||0);
    });

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior:"smooth", block:"start" });

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      {/* Sticky KPIs */}
      <div style={{ position:"sticky", top:60, zIndex:90, background:GRAY_BG, paddingBottom:8, marginBottom:-8 }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:12, paddingTop:4 }}>
          {[
            ["Total Rows", quality.total, _tenantHeaderBg, null],
            ["Refund Rows", quality.refunds, AMBER, null],
            ...(isPK ? [] : [["Pkg Redemptions", quality.pkgRedemptions, NAVY2, null]]),
            ["Amt Mismatches", quality.mismatches, quality.mismatches>0?RED:GREEN, "mismatch-detail"],
            ["Uncategorized", quality.other, quality.other>30?AMBER:GREEN, "uncat-detail"],
            ...(isPK ? [] : [[isPK ? "Reclassifications" : "QB Miscategorized", quality.qbMiscat_rows.length, quality.qbMiscat_rows.length>0?RED:GREEN, "qbmiscat-detail"]]),
          ].map(([l,v,c,anchor]) => (
            <div key={l} onClick={anchor?()=>scrollTo(anchor):undefined} style={{
              background:WHITE, borderRadius:8, padding:"14px 16px",
              borderTop:`3px solid ${c}`, border:`1px solid ${GRAY_B}`, borderTopColor:c,
              cursor:anchor?"pointer":"default",
              transition:"box-shadow 0.15s",
              boxShadow: anchor ? "0 1px 4px rgba(0,0,0,0.06)" : "none"
            }}
            onMouseEnter={e=>{ if(anchor) e.currentTarget.style.boxShadow="0 4px 12px rgba(0,0,0,0.12)"; }}
            onMouseLeave={e=>{ if(anchor) e.currentTarget.style.boxShadow="0 1px 4px rgba(0,0,0,0.06)"; }}
            >
              <div style={{ fontSize:10, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT }}>{l}</div>
              <div style={{ fontSize:22, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT, marginTop:2 }}>{Number(v).toLocaleString()}</div>
              {anchor && <div style={{ fontSize:11, color:c, fontFamily:FONT, marginTop:4 }}>↓ View details</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Category bar chart */}
      <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
        <div style={{ padding:"12px 16px", background:_tenantHeaderBg }}>
          <span style={{ fontSize:12, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>Services by Category</span>
        </div>
        {BUCKET_ORDER.filter(b=>quality.byCat[b]).map(b=>{
          const pct = Math.round((quality.byCat[b]/quality.total)*100);
          return (
            <div key={b} style={{ display:"flex", alignItems:"center", padding:"10px 16px", borderBottom:`1px solid ${GRAY_B}` }}>
              <span style={{ fontSize:13, color:"#333", fontFamily:FONT, minWidth:160 }}>{b}</span>
              <div style={{ flex:1, height:6, background:GRAY_B, borderRadius:3, margin:"0 16px", overflow:"hidden" }}>
                <div style={{ width:`${Math.round((quality.byCat[b]/Math.max(...Object.values(quality.byCat),1))*100)}%`, height:"100%", background:BUCKET_COLORS[b]||_tenantHeaderBg, borderRadius:3 }} />
              </div>
              <span style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT, minWidth:36, textAlign:"right" }}>{pct}%</span>
              <span style={{ fontSize:13, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT, minWidth:60, textAlign:"right", marginLeft:8 }}>{Number(quality.byCat[b]).toLocaleString()}</span>
            </div>
          );
        })}
      </div>

      {/* QB vs Waggles Classification pivot */}
      <ReclassificationPivot rows={rows} isPK={isPK} />

      {/* File Reclassification detail — hidden for PK (data is pre-classified) */}
      {!isPK && <div id="qbmiscat-detail" style={{ background:WHITE, border:`1px solid ${RED}33`, borderRadius:10, overflow:"hidden" }}>
        <div style={{ padding:"12px 16px", background:RED, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontSize:12, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>
            {isPK ? "Reclassifications" : "QB Miscategorizations"} — {quality.qbMiscat_rows.length} rows where {isPK ? "source" : "QB"} Product/Service doesn't match our classification
          </span>
          {quality.qbMiscat_rows.length > 0 && (
            <button onClick={() => {
              try {
                const COLS = ["_id","Num","Date","Customer","QB Product/Service","Our Bucket","Service Display","Qty","Rate","Amount","Reason"];
                const dataRows = quality.qbMiscat_rows.map(r => [
                  r.ID, r.Num, String(r.Date||"").slice(0,10), r.Customer,
                  r.QBProduct, r.OurBucket, r.ServiceDisplay,
                  r.Qty, r.Rate, r.Amount, r.Reason
                ]);
                const ws = XLSX.utils.aoa_to_sheet([COLS, ...dataRows]);
                const wb2 = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb2, ws, isPK ? "Reclassifications" : "QB Miscategorizations");
                const out = XLSX.write(wb2, { bookType:"xlsx", type:"array" });
                const blob = new Blob([out], { type:"application/octet-stream" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = isPK ? "Reclassifications.xlsx" : "QB_Miscategorizations.xlsx";
                document.body.appendChild(a); a.click();
                document.body.removeChild(a); URL.revokeObjectURL(url);
              } catch(e) { alert("Download failed: " + e.message); }
            }} style={{ background:"rgba(255,255,255,0.2)", border:"1px solid rgba(255,255,255,0.5)", color:WHITE, borderRadius:6, padding:"4px 12px", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>
              ↓ Download
            </button>
          )}
        </div>
        {quality.qbMiscat_rows.length === 0 ? (
          <div style={{ padding:"16px", fontSize:13, color:GREEN, fontFamily:FONT }}>✓ No {isPK ? "reclassifications" : "QB miscategorizations"} found</div>
        ) : (
          <div style={{ maxHeight:400, overflowY:"auto" }}>
            <div style={{ display:"grid", gridTemplateColumns:"80px 120px 100px 1fr 150px 150px 60px 80px 90px", padding:"8px 16px", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}` }}>
              {["ID","Invoice","Date","Customer","Fin File Category","Our Bucket","Qty","Rate","Amount"].map((h,i)=>(
                <span key={i} style={{ fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT, textAlign:i>5?"right":"left" }}>{h}</span>
              ))}
            </div>
            {quality.qbMiscat_rows.map((r,i)=>(
              <div key={i} style={{ display:"grid", gridTemplateColumns:"80px 120px 100px 1fr 150px 150px 60px 80px 90px", padding:"9px 16px", borderBottom:`1px solid ${GRAY_B}`, background:i%2===0?WHITE:GRAY_BG }}>
                <span style={{ fontSize:10, color:GRAY_MID, fontFamily:FONT, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={r.ID}>{r.ID ? r.ID.slice(0,10)+"…" : "—"}</span>
                <span style={{ fontSize:11, color:_tenantHeaderBg, fontFamily:FONT }}>{r.Num}</span>
                <span style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT }}>{String(r.Date||"").slice(0,10)}</span>
                <span style={{ fontSize:11, color:"#444", fontFamily:FONT }}>{r.Customer}</span>
                <span style={{ fontSize:11, color:RED, fontFamily:FONT, fontWeight:600 }}>{r.QBProduct}</span>
                <span style={{ fontSize:11, color:GREEN, fontFamily:FONT, fontWeight:600 }}>{r.OurBucket}</span>
                <span style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, textAlign:"right" }}>{r.Qty}</span>
                <span style={{ fontSize:11, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"right" }}>{fmt(parseFloat(r.Rate)||0)}</span>
                <span style={{ fontSize:11, fontWeight:600, color:parseFloat(r.Amount)<0?RED:_tenantHeaderBg, fontFamily:FONT, textAlign:"right" }}>{fmt(parseFloat(r.Amount)||0)}</span>
              </div>
            ))}
          </div>
        )}
      </div>}

      {/* Amt Mismatches detail */}
      <div id="mismatch-detail" style={{ background:WHITE, border:`1px solid ${RED}33`, borderRadius:10, overflow:"hidden" }}>
        <div style={{ padding:"12px 16px", background:RED, display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:12, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>
            Amount Mismatches — {quality.mismatches} rows where Service $ ≠ Rate
          </span>
        </div>
        {quality.mismatches === 0 ? (
          <div style={{ padding:"16px", fontSize:13, color:GREEN, fontFamily:FONT }}>✓ No mismatches found</div>
        ) : (
          <div style={{ maxHeight:300, overflowY:"auto" }}>
            <div style={{ display:"grid", gridTemplateColumns:"120px 1fr 1fr 90px 90px 90px", padding:"8px 16px", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}` }}>
              {["Invoice","Customer","Service","Service $","Rate","Amount"].map((h,i)=>(
                <span key={i} style={{ fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT, textAlign:i>2?"right":"left" }}>{h}</span>
              ))}
            </div>
            {quality.mismatches_rows && quality.mismatches_rows.map((r,i)=>(
              <div key={i} style={{ display:"grid", gridTemplateColumns:"120px 1fr 1fr 90px 90px 90px", padding:"9px 16px", borderBottom:`1px solid ${GRAY_B}`, background:i%2===0?WHITE:GRAY_BG }}>
                <span style={{ fontSize:12, color:_tenantHeaderBg, fontFamily:FONT }}>{r.Num}</span>
                <span style={{ fontSize:12, color:"#444", fontFamily:FONT }}>{r.Customer}</span>
                <span style={{ fontSize:12, color:"#555", fontFamily:FONT }}>{r.Service}</span>
                <span style={{ fontSize:12, color:RED, fontFamily:FONT, textAlign:"right" }}>{r.svcAmt}</span>
                <span style={{ fontSize:12, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"right" }}>{r.Rate}</span>
                <span style={{ fontSize:12, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"right" }}>{r.Amount}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Uncategorized detail */}
      <div id="uncat-detail" style={{ background:WHITE, border:`1px solid ${AMBER}55`, borderRadius:10, overflow:"hidden" }}>
        <div style={{ padding:"12px 16px", background:AMBER, display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:12, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>
            Uncategorized Rows — {quality.other} rows with FinalProduct = Other
          </span>
        </div>
        {quality.other === 0 ? (
          <div style={{ padding:"16px", fontSize:13, color:GREEN, fontFamily:FONT }}>✓ All rows categorized</div>
        ) : (
          <div style={{ maxHeight:300, overflowY:"auto" }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 120px", padding:"8px 16px", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}` }}>
              {["Invoice","Customer","Service","Amount"].map((h,i)=>(
                <span key={i} style={{ fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT, textAlign:i>2?"right":"left" }}>{h}</span>
              ))}
            </div>
            {quality.other_rows && quality.other_rows.map((r,i)=>(
              <div key={i} style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 120px", padding:"9px 16px", borderBottom:`1px solid ${GRAY_B}`, background:i%2===0?WHITE:GRAY_BG }}>
                <span style={{ fontSize:12, color:_tenantHeaderBg, fontFamily:FONT }}>{r.Num}</span>
                <span style={{ fontSize:12, color:"#444", fontFamily:FONT }}>{r.Customer}</span>
                <span style={{ fontSize:12, color:"#555", fontFamily:FONT }}>{r.Service}</span>
                <span style={{ fontSize:12, fontWeight:600, color:r.Amount<0?RED:_tenantHeaderBg, textAlign:"right", fontFamily:FONT }}>{fmt(parseFloat(r.Amount)||0)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Service Display breakdown */}
      <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
        <div style={{ padding:"12px 16px", background:_tenantHeaderBg, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontSize:12, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>Service Breakdown</span>
          <select value={filterBucket} onChange={e=>setFilterBucket(e.target.value)} style={{ fontSize:12, padding:"3px 8px", borderRadius:4, border:"none", fontFamily:FONT, background:"rgba(255,255,255,0.15)", color:WHITE, cursor:"pointer" }}>
            {buckets.map(b=><option key={b} value={b} style={{ color:_tenantHeaderBg }}>{b}</option>)}
          </select>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 130px 80px", padding:"8px 16px", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}` }}>
          {["Main Bucket","Final Product","Service Display","Revenue","Quantity"].map((h,i)=>(
            <span key={i} style={{ fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT, textAlign:i>2?"right":"left" }}>{h}</span>
          ))}
        </div>
        <div style={{ maxHeight:400, overflowY:"auto" }}>
          {serviceRows.map((r,i)=>(
            <div key={i} style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 130px 80px", padding:"9px 16px", borderBottom:`1px solid ${GRAY_B}`, background:i%2===0?WHITE:GRAY_BG }}>
              <span style={{ fontSize:12, color:_tenantHeaderBg, fontFamily:FONT, fontWeight:600 }}>{r.bucket}</span>
              <span style={{ fontSize:12, color:"#444", fontFamily:FONT }}>{r.product}</span>
              <span style={{ fontSize:12, color:"#555", fontFamily:FONT }}>{r.service}</span>
              <span style={{ fontSize:12, fontWeight:700, color:r.revenue<0?RED:_tenantHeaderBg, textAlign:"right", fontFamily:FONT }}>{fmt(r.revenue)}</span>
              <span style={{ fontSize:12, color:GRAY_MID, textAlign:"right", fontFamily:FONT }}>{fmtQ(r.qty)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Product Reclassification pivot */}

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────────────────────────

const LOGO_SRC = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAfQAAACECAYAAABmg9n/AAABCGlDQ1BJQ0MgUHJvZmlsZQAAeJxjYGA8wQAELAYMDLl5JUVB7k4KEZFRCuwPGBiBEAwSk4sLGHADoKpv1yBqL+viUYcLcKakFicD6Q9ArFIEtBxopAiQLZIOYWuA2EkQtg2IXV5SUAJkB4DYRSFBzkB2CpCtkY7ETkJiJxcUgdT3ANk2uTmlyQh3M/Ck5oUGA2kOIJZhKGYIYnBncAL5H6IkfxEDg8VXBgbmCQixpJkMDNtbGRgkbiHEVBYwMPC3MDBsO48QQ4RJQWJRIliIBYiZ0tIYGD4tZ2DgjWRgEL7AwMAVDQsIHG5TALvNnSEfCNMZchhSgSKeDHkMyQx6QJYRgwGDIYMZAKbWPz9HbOBQAAD8IUlEQVR4nOz9V5flSpbnif22mQE4wrWHh7i6sjKrq7vZI9YscsgHfnuST+RiD6e7eqqyKtVVoV0eAcDMNh+2AQfu4XEzr+iZlV1nr+Xh4e44gMHEFv+thD3taU972tOe9vQjKeCYk8ngOqg9iIPoIC1BAohCvAVWiGQAFEAd4AGo6zldB4cXn2nHjPbtW3Brwd9B3oJCSOCogQU9Leo24MrNHLgMOUP4P2Qe9rSnPe1pT3v6K6dMBMDPZxydnbA4OVPVhsu3LZs3VxCjQI1IxPuWmDLOgeBJSQEhpUR9cMjz50/xswu+9jWry28hrkahrUAmo0TAFa3A/kYSVBSRvUDf0572tKc97eknUEZcRnMmbVouX24QN+PFixc8PTun/wwu313pd9/+Hu1eSVQPrMk5I0RAAEdKEXKk73uOzxZ8+eWveFmJvn/TCd2mPGlKDlwATaBm5asq4nQv0Pe0pz3taU97+ikkAuoykCFl3r98JfQzffr8iMOj5xwefYa6Oe/fL7RdvxQ6BVpEMqqZuq7pukTarPj2u2/An3Dx9DOePf0CQfXdN1eCb0FAU2bE2FMF4u3/ABpRHX/a0572tKc97WlPfzllcupBE86BcwJty/vvX8qbV2/ZbDPvbzrOX/wtv/p3/zPHz3+jsAAqvK8QIMYOSECkv7uS92/fkPvMrD6lqU7BnwALFAEpigOAzJHqDL88A6mBGnLYC/Q97WlPe9rTnn40CeAUBHICjQok0MR2dcdqtYJQEaVB6yNOn/+Ko89+o7glfQ/BB3LOJqi9ApHbuytubm5o6iUHB0/AHSi5soc5wEWoA+fPPtPf/N1/0NPzT1QWpwoV4PcCfU972tOe9rSnn0RiPmwvMKuF2hmkvrr6Tn7/X/4joXFscuRq1eLqY55//htmp58oNMWyxhSDHEF6dHUtL19+R85weHCOhCPsWmcC3Udc5VgenPDs2Zc8ffoFB4fno5W+F+h72tOe9rSnPf0UynkIQafvlJwTQguyBVb88Zvf4uvM/PCIXgVfHfPsxa8Is3Pto4ATxGEG+MyBtNy+/Y7r60s2bcdscQL1gYWwewAlb265u7tju+k5PH5CsziFMFek2gv0Pe1pT3va055+NKnDUsgsttyNXxH0GvI7Wb38J2K+I+UNMSnZ1cwPLjh/9itgBuLR4hbP7RakBenk+uYd88WML7/8ivMXnzGGuwngIu9fvZRvvvmGzTby9NknzM8usLz4Pe1pT3va05729ONJBZOyOxIA14O7g3Qp19ffInSIU6BG3ZJqcYZbXih58lkH+B7yipvbN1xfv6OZLTg8OoPlod148Ntry831Fdu2B1czXxyBq3Qv0Pe0pz3taU97+klkIjTjdrnio4xvIV1x/er3kDdWI0YFlZr68AlPXnwJ2uxupYAkkA3r6++5unmDC8JsvqBeHJvwV0q0e2J1/Y6u63C+YrE8gOXh3kLf0572tKc97emn0SDG3eBKH4u4mVDfwuV3Iu2aWjKqSsTjmkMOnnwC9RKoSsU37NPSQ7qTtrshxkgmEPwSchj99dADW7m9vWW12bJpe1hv9wJ9T3va0572tKcfT2Ypg6IImUAqv91Jd4W+I6/vqIEcE71C9BV+dsD88EJhPvrh7cMRZIOjA1Gc1DTVEbhFiYinVJrLNIuKg6NDDo9P4HBvoe9pT3va05729BMpQ6nnrniUgCom0BWsqhusLi/xquScURwtEMVzfPYCZGbQey412gPgIjFtyDlT13MOlmdQHYKYNe9IQM/1zSXbrkVE8KHZC/Q97WlPe9rTnn4S3YuJc1iBl2q00CUK4Ll7f4XLiqjivSdh0PvJ8QWiM4WaofuaUcdqfc3V1RU5OeazE8iV0pvQtysjXdciArPZjMVisRfoe9rTnva0pz39PBo85w4ICK7I+Qgu0q4uybmzK5xDxJNVqBYnqFQgAe+8KQEJ0AzrG3nz5hXbtiPjoQMIoA7vBUikviWnnpwjXb/3oe9pT3va05729DPJoHchlx5qRioZuIWqk/fXr1nMGnKX8H0muJoVgdnzF8Aa1S2NQp3EbifK4niJn1VIVcPsANwM8GSsVOzBfEa/WSMp0m02e4G+pz3taU972tOPpnvp5xYg5+4nsJW88QwSydqRc7biM8W/nqVBQwNOLQ0dCEOAHJn1dot4RwiBw9Ozkvfu6JIFxR0sKg7nFe12haZ23z51T3va0572tKefTMIkV+0jVALics6ld7mgqjhxVFVDW4zyjLtnZcduC2RUE7NZw62UJi0ZxAnv3r6kahZ8+/Wf4PZS9gJ9T3va0572tKdfhPKHv1L7J6VkQh1KcRhBBarQlDKyQ3C8jD/nvqdrN7gcWd3dQOxxlSdnQBNNBfMZpHhnjVv+d3nHPe1pT3va057+m6ahrIySybuqsMWCT2oCXByIKKoJgLquASErmH98+IzCZkXft4QK1nc3IJkcO0JwQCLHlaTuFqdbkTrvIfc97WlPe9rTnn40KTu4/c/C7g5Vc7qLCKkIbEXxVVV84yA44mjlK3hHXTkWswYfhCQKOl7O3fvX/H67lrS+K0/Z0572tKc97WlPP48EBk/4PVJnfdPFI+JL2pqgmsjJ0tiMHDrNRRfl4OyIyivru2uCy+MzUhr+n4jr2xJll/YCfU972tOe9rSnX4YeCnNMmFOanoNh7lhwnJIQkQ8+YlFvmZPjA3K35dX3f6Tf3Akp4qpqrETngwMioQj6vUDf0572tKc97ekXoyLUFctDUwdJOD05p4sW6R5CQLxQec92vQa3C4qD8rmc6NoN81ng8o+/l7y5A0nkvh11g9RH88/HWErC7mlPe9rTnva0p59F8qgP3VvjlVCr9xXOBTJKUhP6Kff0saW0WivR7wMp5I52fYtVnMtWQe6+QT/+KLAPitvTnva0pz3t6SeRugdC+CEVqH12gAsN+IqcFbJB6poisWuxtqkGw4NisW+RzfqW970Vhndq0fPBOWLKk/sPtI9y39Oe9rSnPe3pJ5CzLwV5LP8cSnfVQN0s8a6xFqsa0ZwJXgmVQ1Nvt0qW8EYp7Aqwun4vt/EK6Mm5Rch454jp8fHsBfqe9rSnPe1pTz+Jhsrtjwl0N7ZQXS4PsVKvRQkolnUTAmgcDW3ddVQHILdrSB5I+FInXnIR91qC64ZnsYfc97SnPe1pT3v6hWkiuF3gYHkECJqLx9vZd82Rvt1AKTIj04A6AXLCstN1jJPPST+a874X6Hva0572tKc9/ST6mHU+odlcl4dH1rYlZ2ut6hw5Zbbdmm51BzHeu+NImgCPlMYvAOkxYV78+EGohk+ixEeu/OAR919gSK175CFTKGD6UPM53L9uWnBn98z8wd9/iOSR3+nDP+juvg+v/3P3331meKd87zP3/za934cLfv/Zj28K/eC6P/eZx5IW7o/xJ730R5//4bN+6FEP6WOP/tGfm2yOj332R73mw835g/QDZ+MXpo/u7zIOud/W4cO9/+GHfvBdf3gdQrk8lvl3k8/cnwO9d3j/a8zX7jyOJA950vRcPn4e7bw5tECbj58/9+D7w3vlH7XXhvvrB79wj143vf+fW9rHmOYPrenDce/4WeHDf/aB/0ponIcSdV5osKCH4q+IA1cj9QGhPmabZ+TsEJ/wKJo70vo9bN/LYKEDJS99sMIzXhQ0k0hUYgJdRErN9/tDC45DIOLJZNeScm+DC5ASow9geAvBIu7GF3NQeq2PN/cOskKvriTRs6tp6xSi4otQH+rquKJ/7Lbx7n+5JMzfOzrlXZw3VMLZy0AqI3UQM+ThncWB9xBtorw403u0nzznkYMF94SFKzDKuHBSavZm+5uUudLxfooPgZQ6yxN0tgcClmoYy/3GYgITuj8fU9oxb+eUlJUdxGOj9CIkjWX2JnM2zXHQDxWxD97/AceRcT8M71nGIRaliUa0cEKnH35+uKWIQ0WIOe3G4uz+0zF5sb00HWYGZGgZXOazrmv6riMgCErlKvrcA45ERpxVYIrZDk4VHDHm++/7kGHtTudkgqaMvFSAQhDBIlTHvZIRxPZnVpuTyXyKlHco5/hRZlmug928THstZyC6MnfqqWUxrrkToaO3jfZw8iavmx/sB7IxC3uW3r+Y6Xw0tr9lJ9AFj5XQ6Bn2XaihjfZ55x2pt30jZc5yGe9PJ0fwta2rMhoNIoUv5WG+HBmPki1ByJXrsv1t4C87PpQfPX8ynrFQpmPgYPfV93tKww9IUY+xpS7aHpAQ0GjCwDlfbqb3gq6GEz2029w90+6Rp+fODhvO2P+jAdmK8aWohgTnvBubI5DpqBrY9nbeci4fyOXJf9VC/S/J3H6QKjb5v1QB7XtQaCpH7POOLwEw4+nn/yfW+YBe54SqhnSLblc0suGf//P/Q9AbBgGegDQwCwVIJN2O6z9Y5xYNP4mMK78PA4tI9BZOT9kU48LvhIR9Lt+XdRni5AqHfTYNVzk3vdk9vGD6p+GKPB4/+3f3+909psxv+vmBOTruP3IccM7jSHOZrPE+PHL99OeRkd/fALkwuIe1fmSiHudhMDK9ghEPeew85AffH2674TP9ECBBxuPH3+s4SR+BhCaC6i8AjR4ZnY2oco6YI1kTaB74ByKjmwjY6XWai4DWTFZwQcjCLtADijJQug1NNWDZaa/DlA6vqTr4mMq8Z1tnY4UOzfdnOecfeOOPCvZHDn9JM1EFES0SOCMqKEpK7Jo0fPixcV4+NgadyAovpsqJPlD/HLjkUM1F1QTVOCo7VtyifJnmUfoxF8aj9rvhOcYsQmEij89TnihzuwFPz4h9ro87Cz3l3XXKkKLzcykTU8cgfkwbVHvde+eynHuKBlSY9CD7Br4ziMpcfnr8/LnJb4Yryr6cqAYfSM+HFnOZcp3IRE1pHJHxM7mnT3nxSFGEd9zs3i0RDyre/pgSaFEvJgphBThxSGjoU0csvDAVBUfIqASyBiCTejM9dtlSg7IxnYO/RvpLUE4+BteguR/v0fc7/md8SSAstV6c0GmFD3OcU/q2Z9bA5v1r0A1O253snT5vHN9fjvoEJ5FeO0y85FF+j1YtgaGpuhS4WhCQIo4GuY0HdSTVkQmbwO52A1QQ50AVFSHhUaf352lsIyeT2z8C2zMwCjslIo7shJwSDsV7j+aIjgJhODUeJx7VnYXHAI8Aog4dpc+EHRSzUQcrQDMqDicVOSdUM+IEr0oq8ImxDl80ZnNtZOdMC55ALPqDeYzj6B/5TVnoiYGeOmNWw9zcMzDHH0bVi/uq0oRR6ceZ+XBlJhFzDwKVN2GbkpKzQ9Xsh+Hu43YVRfxu82oe/l7Wvax/UtMKmrompURKvemCOhiMQqgcfWrt+tQj1saInB3ROfrsCaFCY0FhvEB6aBFOGPNwaEfuOrnsUQg037fEJtcPv/Xe77aROluxvNuTurtZgSgoGrWp+PVsSc6Z1PX0RWGyI5oZI2IVMn1RXQpMhU4YCyAN4O3ZmoDeHpuG97KJNWUsgFZkVUQySLebJgU0ILmslzeGLxoQDSPcmF0cpYcpFh5y2Ck85LHb1M8ioexVBxJMmrkM2o9KU1KH04ASCnqX0WF8H6Aw7v69C5lAHHig211fGPfIwId7yPipB2Od/KimWGoWvHeGrqgatFWs3x0w4k1BHPiZE7yvyb0U42nYi1rmoyAWVbB7ZoEsaJ5U/Fagj2R0onQGcBWaE0kFc8kqKpFczp4qEIsS+N8A3ZfTH/I8e8thzR+sfYo479G4M3OcL7eRGn96wXxxyGpjFrWmiGhH7TPfX76GnH4hxdYoRNmApIk6Oyz2h0JtEI6G3ReN2FeQtGyqQUgU+1NA8s4vH4IjZcgaSITCQCP3dvk9bjocLplM5qR4PYyCWPPOYsijdluWQvLo6hAyiC8Kg3/AkIs2k+UDBm5CNxfdP+8Ui1zZuKQnk40BDtaPYHObJ5ZLKtr/2Kg+8THr3xSHH4CE7BVGFT+mgplpsWzvIQvD/Yd1CuV92mKJTSH7yVgeqUxUXtzsmLIcFtOh4+YXqRDfIIht2Fz2GAP8nMEFY8LT9zbzncF8abuBgQ3WV0ZzssYGfW8WfjKL3aH0avGgKYNS06fyXqImyOQxPKIc0sna3aOBiZf3nsKfVTCrRae3FDGlEUdKxQyfup8QCKFgre0DBeK+wOg2w00HC9ShpfVi1mS/TjYuZYBwHZs0QKMCWkNqQMuZo8MM98GiL/CyE0Q8KXtUPRDKUtSgbeETbjcW8g4JUcazlMY5y4WxVZDKOms2geWSya2fI9MFqhr6vsydiikNBpeQXS4mpStCrExqdrt9MAAT41kb7N7hHbh3DsfzP/yeVL5P1pZhPA951X3lz0bTFLtebDJEDdlyg8AvQx7RxKIsiCcOPHjwq7gCi8kE1ol5p7AMCA1CHlOeepx3qLa2mfum3C9hcRL2c0ob27beEaNjpwH8AkrZ/4H0KGv7yDXK4GodYcGiBOuObUhxMbkKqgOePPsM8RXOQR83QEddZ7r2htXtJeaifFygWwOXHyfsg7quMGVf1rCBJOX//j52Pd7b24JqKAKtbCxXhBQR0oacNwQK9KogOdPnANRQH0HXgWt31v4H5O4rFuLKYSx/g3K2HLga8Ipmw6joy7UbvJtaglp8FBXUwQSISzsFRYdnTg9jHl6gjFWKcPEQS1ChJDKmxfqp/C4ImjRLY/qxCDItioyruE8T7Rl2jGfceZP1GC2MgjAMPowhqCJ2xoQHRjrefyrYB/+9n/ytKD73cN/7NFjeXj0pp1GQ+xDI4tCYiakt9y1rHhoIMyVUtsmlpqkPbAyq5ByJOZH6jY09bSC1Ah1IC3S2rhP577wOmR1k9fY8qVFfFUXKBBZeIGRoN4WZxgF93s3LR5Snh4d+elWOxaqlbFExaypLsPd2xWrE7+ZcPISgSEVzfm7Kj8gEBUqlcUOkXd9A3EBcC3mLElHpy17crYuUtReBXm11fHCkVEE+AF2AeoUkuAanDtWNQfhlvXMeLMAKaIAFxVwUpAj0UZH1dgZcLGPxO+VfKkaHvwrIjPE8VRHodjDzA6v1x1Iatqko+Bn0ZXVcBdoz+hbyRCkTYehOdX9xZXfNdC+MAnsquMv1MuyxiRKg0zM2pemLmpLg/ZzUteQcd89Q7s3P7lMOqWYmUFK2uXXlWdUcqRfqqwXON8gEhYupJ7Ur6LeQNkJuzUAp/picxfapmwE1aFWYdgXaaNU4awxCi+ZqHMtfN9S+ox8wmSaUyQUBy5O1Vd3JlgyohPK3OfXJCz06fkrXJqrakzYtznfMqszr77+H9bU8xlt/DgVczdGz51pXB6Q+UIUFST0xQVV7Yiw+giHYpGxYp0ImoMUiyq7Ce49zmRTvWF99R3/9UkS6nW6cATyEA5599m+0y4Ky/QGBDjuYqxy2e9YSeFGDZ31FVE9UtPKKxDXd7Ss2l38UyXGnZYkjqYPZgvPnn2kSJhD7DlITnSIOoIN16CJ50IDV4/KSSjyaLlnffCfbu9c7HSiV99U5T5/9Wl11wLpLVE3Ntt8CeQx6MrIf3AdWddkwIwMvjF92oFzlPaKKywlo2dxes3n3UuyzQ7GCPF2IP0PDGCYWyQNSIGYFAj5U5MEiHRSs0HDw5JmGasFsdsxscWgVk2pjOCIVdOW7U1SVqJGUOmLakrVn295ou7lidfOGePdO6G6AHrBmBHGMaRys/YqTp59ovTgxC8Y5YkpUwUPacPv+O+lu3poe96h6Lo/8NLDUjHtsLrTwcSDnQRkMIDVQgZupOzxleXDMfHnIbDFnNlvgqzl9a9ebQB+YRCr+8Ij3iW17w/ruUterKzabS+L2WmjXhjhkW1vnG2Ju6TQzpKk+//RXutUlmUPQAxyBSpJqumZ1/S2r21Yq3GhZy0S5aY4u9PDkE1JuUBE1JaIH6UC0nA8dz67kXQasBYpmskBMjqZeop0gJBq/5e7mW1ldfv3onvqxlMtRXJ59wtHxl7rpK7J6pIaYNjixNRP1k/dTg9zJD9xdU6Xu/ndD1XaukvH7PavboH27j62lTK38yT4SwGnG5cSbr/9g8re4J73jXmyQuU4CZI/GYpHP5rj5gT599ikuNNTVAVU9x4cDnKtBfVGSMzm29N2avr1jvb7S27t3xOu3QuxBM9I0aF7xxb/5D4o7BmksJkUD7V2klpY//e415FyMIRlRqr9m0F1wxUU1UOGRH6FhJ0zDJkV2a+V8U4yLBpZP9PT8M3Azui4xr5QQIt619Nsr3r3+GvK2IIePP++nQPGB+ks9O/97DpdP2XZCcIcggS5CVXmUzg6tFL+wOrNmtRxg6cA7q2MjGeeV2F6BRq5v3hDzzjYZHtksTvX8k18TmSMPYCl9ILRAdpHV6oqwM2ErJLxbmxBwM6IEEkLlE3l7yeayYnP5HTF3u4R7ZwejWRzrxYu/Q/0ZmWY87HbQdsxtMDpy8YmpFH8T2TTZNGNeOfrtS16mWrerXlTvio8wACfgT/X09D/QLJ9w10dm8yVtv0F9wtGDxN3BH5WKgXYmjE6sMhXTFxUr+O9zJuctLncEt+Xt66/ZvH9vSID0k/sVBqx9cVHctzruA+67DT6uyg5/sou9gASzlLIHGlic6cXTrzg5f466ClyNCwFxgSyZVq3jUM6JSj1oQtWbD9wFNDQ4DnGSODg8Z5m2nFx8SWzvNK5vuL15x/ryjbC9w6BgU66QANWC42dfcHj8lKgBlQpVpQ6ZbnNF2m61u7sRgzcnB/iBEiXlrXe/va90DvMhRe+z4Mhgh1kamB9raI548elX+PqAqjnA+QbFfJ5JnYE1jVWPckoJZCwIidoat11HmB3y5PBznI/Efs3N7Xu9ev+G9e17tL0W1lfEYgFHiUACFzh9+hlu9pykh6R4gMNTuZ5+85IYb7i7fW1MiQl4KkAz5/DJM54//w1JD8l41PWoGwR6QorfWnJjiu0QxDjuT0XxJA1UYY524OkI3JH6oKt37wVW/GzKDtwhR0d/pxfP/j1tXJKo8DNPTGuc9EiJPUB37DsXV59KARGmmGm51tAr9+BxkxgMmSq7DpcFKQJddHIvwKlO9lsmi+Jp6bevefPyJeQVQ+T4EEDu3OAx8MAMi2dq4PBUz56/4PjsBeIXqDQ48WTnUari4toxNNfULBZHeKecpJ71+o67myttV9fcfP07tO0FNyPMnjBbPAdq2tTisuPwYIbLd/zp64X5f9R28M4B9dcs1B1QYTkAhrze53b8AMhi1zgnxYgpAYTiIRxy9ORzDk+e02eP857Yb/C+h3jH29d/IN+8FOgfRT9/DgXaCpVjpH5iKLUcgNSk4hVXOnSwiMgIBUoUYyBeOhDospBypA6Cqw4M/lJ/D1ZTEVBPlx2Jil4bnM4pveLGaNgsO012mFSnDkQYc23V4YioOKL2RK2IEmxTSyTlWzZRylgrspr2G4tPrY1K9guSHpFY4tSEpWgBoSea95Dao0U3221gDynQNBVJbulShfYDrDokpplbIrMgy4KUIy0LOhGcCElb0DwyRPnAQpiQ7oKwSlIQqg1KIKeO3DuqqsJXc0J4Z8+fpg3euxf3LY5Jfv5u7u9b5/dSq1z5x1XmotGG6vyFPn/+a45OPgGWbDolzA7JKnRqAlwlIy6hlZVL0OyRrCSFrFIgrADOUpvIPU4qXLVg2ZzjDiIHR7fcHF7q9vYNt9/91rZkn8b1SLIg+wNyruiypSgSHOIi2TVF8XAGcet9pvzIpPMxYW57O5QocWMO+AOq4wt98vQLDk+eErXG+RkaGno8MYlF2IsvUfEWHGYQuIlWwSNq7++qOV2OtOsecQ4fahaHR8wPPkfyhvXld/r9t78jr64FNkX5thG2zGjcIX2/JOa5pWq6gIY5ydv5TYaz4cLkpXIiqSeHBTHOSVRkoiECg9uj+JUdDVCzy+owX36WjBJQKsgzck40rkadkvMA61YjwvCTSIv7ghqROSoLYp7T0xDUEyUgtIiUqH6RieJsynkeF3KHzhvZmuYh/gHKWhWrvhg4U1QDBD8ifA8sv1GY7wS74gn10uYg7/bhcM+MHV9NRZDPTvT0xVecXXxK1SzIUtO7JTlXZjnnYtXJgNtl88OmTNtFnHME3xBmM46bc+Ss5fDgCTc3r/X2+z/S5TnEmkxNzEKFI0fwirlHNU/OS/6Apfy10n1ps0Ospp6UiX15T4NRQtG+KzMqaGBxqocnL6jmx3StBfau726Z1YnUXfP+9R9Bt+DzxLfyy1DABZII63ZLzBVVsMhoqQIdnckDLyhmVTkH3pdoyT4TpKJve/yiJmfHpt8af1WBqoEukDHIO6nBRZoFCQ05Onxds15vERGqJhTYtWj5ytAmlsGSdDokxJmVoDjUz+mSmI9WPFE3SKgtP9t5ywMe1iNUaHYQFecbus6RRSxACTFNrVhv4kHEAq5SVHxV413NtouoCrPZnJg6NnGFCwn1HfgWXG9pUjnYwukKV2WS66gWFR1rkjNotHJzui5SVRVktRz1EJC8Oy6bzYrDk2P6vmfTrpkvF5beogYxkz2xU+r6GNE71qtrY6RDXMBjgWAfnMbBhzekf5VDe8/PXC7xJbgqe0gz3OlzffH8Kw6OnyLugJ45Kc/IVUObPIihN2a19ahGSJGM1TLOxX8bNRtk7S3PXvA4qUk5EnNPjAmvFb5qOHnyhOric1bHZ/r6+9+xuboWqpkShSwLujwjSY1KIDlltd7ShCV9NAUPLZr1eGiHg+WR0TJXnFh63WAxwUSpDg2qVVkHD0fn+uzZ33B28QmuOqKLgnMNMQdScmQC3nvEO7JGYupxkmEoEkGB6wfoVqWEHAWkmoEoWcSEiiaQGYuTmt8cPqPbXOvvf/sPsL2SIfivZUFKDYQlKc9BYJtvCXXNumtHpSzmfhebKbkgbpjyIYGYLUI8ak9VW9Rz226oqsoCXWNRjMpWS8XFI05KsGpPJlJ5JbuO6DrwPaYY8NMZmuTyDhuS78mhQ3MDJNrcoy6RtcOJ4lwwpSlFMoL3gveBrJ62banrQNZEStHWaECgCmmBYrIMSJkp+oZYyuAsGzeJlE2Sc2Y+b1iv72gWDW27wQUhxsQsqIFnanvAYoSg74b3A9U5MIf5ub741X/g4Og54hqQiqSepDM6FSQrzjmcs1gUKYI3+IATSBJJqohUhBBIMZKS8OTpr6iaA2anT8kckHRpgaUaCN6BV2J7awUFIqYw6iPs46+SMlalwhDM4C1L62HNCPv/oNiJISYDIpXUIjOjAga1f/rV33N08oJNJwQ/4/b6hrPThtxd8Y//9L/A6q1QC3T9Lw5vBOPzDu89uQrglJgiRZrRa8apmDYdMik7G7sIEoYAFPP/OUww+sFrHg3GUAY7ZzdDKSVytoANFwTnAiqeRLYJLUh8Ui2Q2M5vDHZgnEBUiygWb8EdfdrShExVVzg3eO/NujftdeeDV7VDoAh5sBQcSHCICFkyXddb6pG3aOqkhgrkDG3bWgS9783CHypZaGE0TgtcJmTXktOGTrfk4PHB43xDig4XZljsdMZJImkmR3vfqrIAsm3X2pv4hrZXVMUCWmNCXEJcJjjzbYbK03m3g8Vht3Gm34e/jUz1cR9xGubdYQuTBuRjyfGX/04XB0+ZH5yS3YysFepnZAJ9VGJOyKgcgbhSFKd4b/u+xyE4JzS+QsXS/BJivmQsWty5CvHB9iJKnzMpZpYnz/nq8JDvX77Uq8tbiBs2LcxkRqYiEfBDdD0GAzMKTx5wpswH1dZ08J9OSii48v4pgMxhdqxnLz7n+OQp1eyY7Oa0faDLDlJAXI06b/u9S0AkVK4IEUWIRaAPYsHGK+JsfyoldU8NORPB+4raBcTXKDXLoyM+/3XN1fUrvf3uj5CVyBJocDREF1Bs/4tIiRHLpFjS14a4vQSoFv+dpcbJ8FnsrKhTJCzBBzaxRZwjeLtv0t4CvERxpLLe5lbKksnalWI05bx0P5OjSQmGcImsPVm2ICBei3WrY/GnLA7xFaJKZ8UyqH0gVGKGfvE5i2rZk8GUN4FdLEv+AGpn/F8e4fvRVUdPn4RIxmm2NL/gTIFFDIovV6I6FkxCQZNB7f7wuZ69+A2Lo+eomFLqNZBKcaZhTMquPoMpW4Gr95c0TUVd16hq4S0mwKSu6ZIQZqfMdY5WNVlmeFeRdUtMCTdRb0dJ/teLsd8jJRPp8N7O9pBj75oZKh7tuiK4h/icUECWSRyXJOjV/j4/1JPnXzA/fEqWmZ2tkFnOa7Z3V1y+/T30twa1t5vHB/UzKZBBu4xbQCUZzT2aFAkWeUtRvkUrcA4VS48ScXgB1RaRHpcF1RZHpKJFcgfYYSs8AjtpAl5xtDQhEGNL5RtjmIVpWZ0LX4I43S7NiSG2OuPEkTVbhKFkgndkicS4xTul8dFSMTSbwuGcWbVjGSRHQEnao1qjmsyyUEHEETXRp2wRyK5G1HLcNQuNC6ZApB7VHpcjEO3ew2IPRWziFuol6BaRBi8B74SsCY1KHwNNfYAm83X7YAfUBYN5k7bMFoG23+K8p24Cbd8XRaFHXI+XjHjLQyavIW9IccsIFeeJhT7EI+CKZTCBlx45qEMROtsKNWZBVrA8Znn8uT55+u/w9THiPG1MqDi8dwVabVks5qO/nJTRpDgRsyaAFKMFhGVBXRqNZUHHAzYwJ3Km7TtyMhRjMZvTbracHh/zSXVCWL7j7TffU80OQRr6HqsTICDEyUF0w0N+gMr8l7mZuo5K9C/oDJYXevD0S86ef8ZsfkjXe7ooJKnxVcV201kGi3MWk6LJqjGmjpx7gvNFgJY5onjwtbcCIjETQk1dW+BcypBzImcTFKFq2PQ9VI6jsy85PHvGH8MBq3ev6LVBpCKJMX8hFQtzgte4cjbvIcQJL5HK9aS4MhTBCxVSYiWMyXW9wcbeWWYBmtCspmy7aBlzGhHNeJ/wms01lAocEH8B6ZBN89eU0dQSqImuGzMFquCMl+TiBlNTEJFISkoXwUuFSo8rTgJyxItDcrL8+ZLgB5gCb/W8DMkaa3SMA7r3vanrEjwl5KxkdTipUWdoYEIZ885VxxzmpM72Vzjg7OnfcP7sb8jukD46U7CyIJLQfEfwppg4PDkqbdvTbTf04jg/PsI5oc89bbeh63vEQ90EKl+zXWWqakkdavqsJDXe5nIgl7Pp7r3X5Dz8tQv2chZi+X/x/JL7IZNpAQSoDzUszlgujpjPD2nqOU01wwehj3esVrf0WVkcnbA4egZhwba1gN3KKfPa8f2bl1x9/VuIt0D/A4P6eRRohEq2uHSLSCCxoXKCCwvTIJ0zrU5dCfgAMFjCiSLS4tlSa0/SDnJHkC0zn1j5ZBM0qq2AS3iXoLulqgA6nI+k7OhjJIQGX81RLGgo6c4fZH67wZNtP8+ain67QREWTY33LZX2hLyhohTMkUweuFbOQMR7RfsbAkLlMxmly0p2Ae9qU0TUkAtTIIQQlNhtSHlF5TyBhHMdQovTNTMfWTtM1RPAZWMOVSft5p1WuSepR6qaPma67Gj1kOAr0FwsGkVTxFdmoV69u+LJs6e42CESTDEh4p1A2hI3b0m6pfIO1UiOW7xE6O6MSQ9jueebLxYmGWh3u+ED/1CBmkXIoS4pQXNYnOrFi7/h7NmviHpIn5uRWYHVbtFS0jN2K8TCqvCu2HglOjvnRF0Z/ExWclZETGFywSxy8YH1dsv2zpSYulnim4ouJe62HQ0NN2tFteb84jOoj5gvj4hJLQPDOUN3xgCAqZXF/Zcd6QHzKihbViBVkGs77IsT/fQ3/wN+dkT2M9ato09i6IOrwAUODxv62BL7FblPVAHmHgswy4ngpMxFtoAy1RLx7sEJjTiytuQYzIoXhxdP8B6cZ9NGsqvpEvS5Z3lwyKef/R2vl6eEakki4AaUbOLHdTCuF3L/lQF87iDeUhFRHwsoU9FHiATm1THbmOz3BebVGPEkvMtAT4pbghO8CN4bc9O8ofHKKmApnD+XJEMQgnR41kjlCNLS5s6KV0VFpMHpQalgCeosl98H8DEzCxDjBi8tddVBMpg+ZxC3c10NikBm50OHEhA4ZAE92EqqnhwzVVUDhlLmoSiPGFQ+1jxWKQqHApW5VC4+14Oz52S3ZNuD9xVOAjm1kHuCmHIo2eHFkqrEWUEtEeHm3UvEO6qqpqlrFgcVKo6271hvt1Qc0OWMZjV+S8aV4lxOxHSNB+jefxPCvJALOxvPgOVgilR1QHP6Qp8++5IQDvDVAic1ORc3kjqSSzSLQ9zslCRQ1TOSNHTbjFKzWDQ43bC6u+T2/bfQXwtswSfLvpUPjt3PpkB8JbfvVDd3NTgrVhBxuGpJl4RPP/+V+YKzNysdN1o2jsjb7/6F3K5o6oCmLQ6rgrO9fgW5Ax0KijBCVbm9lsvXv1N8Q58zUs1o+0yWwNHpcxb+lKwVVht6UCKGQAxjxk4LSJ06rt69ZLNesVwEfGrRtCa4NZur7wTXgw6VJxit0ry5katX/6J9VnyzIGbPtndQLTk4vqBeHOHE4wS22zXz2lN7pd2+4vb6DaIR77IVFUhbGh/Z3H4vpNWYBq45AWvSes0f//FSoLLetqEpES8HOv/V/428PAEyXhIpdoj2uODYbFdcfv87Do9NyUg50Re4XQikuOG7f/h/2kYJpbhE7JHao6mFVKz0wRKfBOqM53FgRg98hlNKisVCSQOLJ3r+4m85vfiSZnZK7JWu3wIOF2pUoe97PMKsbtDcWSpd3pLiiq67pdtcsV5f0bYb+r41iycBIjhfUdczqnpOqOYcnzzFVQccNoeIb0h4UhJcbhBfUTeH9N0dXb9mVgXmB4fghJgzvlR9cVIcP1IEuRjsfX8ibEfbvx/GHBjgErD87Dnh4JmePf+KxcE50c0sVUx9KSLhSSkTuzUaBCeJWYgIHZI3tJs72s0tfbfh+v0r0CLQJzUfnAQUz5Mnz/F+TtUcUM2OaBrL208x0eVEUpjNl3iE7SqxuuvxVeDg8AxXNxZbIlYAVxjOzUSfkQDa3Y/004725h1X/g/06iFUtCkiVUWbK+r6mLMnFZIzPpREfDVh4iUT2LJdv+Xm5iVoh9OMd47aCblbs7n7TojrX0YoaIbuhrvrPxHzCnUN2QldbKGqyCmwXD5hsfgU5+aodiR1iIdKMOEVM1ev/0AfL5k1Cc0bglNiF0sKmFXOg4JuTLN+CkIwTKiUCpJg3ErcjITn4sUXqJvjsyA5IeJNgR9hbJtDhVIGtILFiT795G+YHZ6zzZ5tjFSSqMSCEyuJHC4bbq9uePvue26v3xBXV9BZzjgoNDNwlfr5EcuDMw4OnzCbn9LUS5rZgpwDbc7E3BbfcERTQiVSe4fEaAbU4D+eBOwN2+WvmXJkZ9vExiLUX/xKT84/I1THxFyhfo6GhkRAxVxO5gKEu/UVIRxBpayj0seIEqh9cUNn5e2rb+ivX9uaaG/eJj9Ex/+yFOiuef/qdlfrRtQ2cFhC9MSLJyr+gCAzYsqIOoIb6m8nbr//A6zfyAqMMdCbNRPXjNGwMDYdEOnR7TWvv/7HUi8xga9N0M2OddHUSD0zqE4ag28Hv/cQjKFDXbpMv11zc/WK/Pp7WjorHtJfC9KDbhgr1g3QopiFru0dr7/9rcTuDuraCsToAuYX6qQm1GY5iwYk2eGv6NneveHuu98WbSuX3JLeOsN0a3PpTVxNUur1aFyXAxOgb+0Ah5plU1O5gBKpAuTeqoAJmZvr93D1huurc56cP0fE06VI5WdjnII/cKTLm1KK0R6qndh7U1KYxoCvwUIL7CK3C/QOj55O50oZVipojvXJ819x/uxXOH/I1W0kzOZAR1KzxJyz7E4vMK8yfd+y3bzn7vIVd9cv6VdvhfbG9ooMgXg7ygpb8WxdDVJx+d3vdHHyjNOLL5gdXKBuSZYK5xcEX9P2LT7MCU7oclcs0UkwpQwRHLl8j4x1D+5ZHubbdxMbK2NLNtavzgLU0Jzq+dMvefbib7mOWAlgVwNCKp56EbUA07wleMVrS7e54ubqO64uX5LWl0K3tbkvFcIYnwpD8Yo3199CtSDMT3V5dM7h0VPq+RFVWFC5GlctURJtny190WGwNxk0lBRMi41wRATFax7rSViBI1NgLA1e0BRZX72R7d2tZYU4tevEYgY2J1/o2fEFQSqiKpIFp5bTO3eQY2Z9+Ybbb38rpFVR7Mtk5gx5u5v8n8PTJp+9efNSbt68LR4VsfGGGnQOz1SX1Rmz+ZLt6M6JqEBNRvs7rl/+C6xfy9qvQFdQuRK0VCLyh/xygV2BqQH5GRTAyYBkiDOpoTnSJ+fnhHkgiym9Lu/qDpQuNeWDpfC+bzg+u6CaL+nxJHFQeTI9WZQq9FQ+8qff/iPr27esb95CdyPIBnwHruyp1jC2tGm4eTfnxh2oP37G0+dfcHr+OfhgMS5leSww11DQ4KQEHk8pMM22+aumwQUXPciM6vipnj35guMnn1E1p0StEYwPWZwV5qJFiWS8CtXi3Ao4aQfa4mrBSY0kR7fZMgsdd1evoV9bVHsq9pU3PvNLhyQET8YnpdcOdYX5SamypTNWl9csDpY08wXkQIqCBI9ziqSWZtnQ3m0wv0BC6PDR4GPz3ZQ9X+A+T08kI7EvEKJtELRBQuB0fsSiXrDtA646oOtgjN2TPKaXQUKkZT6LnBzWvH+1ATbiYoeyxmlrpbt1gI2LEUgemba2rVUb7yOqDWiNhBlHi1MO63Oy1sSkNAiNZnLb0l7fwPbWoBOJkGqGFkdODd6cO0/KLVmVGIeuatA0FV2rpVGIo5kfcLw4JktNzpnKB7regoycKrdvXgGZ6/eXnJ5cUNdLUgbnKnL21KHi80+e8Ke7P5C7breqWhha8RPfi9hVGFtrjv7kQUX98JAavOahnnP27AvOLj4jhCM2fUWfQPsKxFv8RUy4kAhVInW3XN3c8Pb7P9Gt3sPNWyFvIFsKZHBW57wbS30NQqjUNs5rlABxJetXV6xf/QnOnumTF7/i6PwZiTWrraOPNYv5AVVVk/uEryzSWLKSU8Sr+RotnatYViNcyniadl20jDkPTTbu93AJsDjWJxdfcXb2Cc4tUOlQsbShlDOSFXGK94ITW9P1zSvev/mGu9e/h+1l2TsdaEEakFJW2RQwlYL8SNG14i3x9lKub7/m+tsZzflzffrsUw5PP6F1nq6viG1iVtWEUJNJVKr0KVmmAOZScjnjyRb/ll3xPxuyI6IFVBZDTHSL9ltTUVRNQGDWvHDLssn0KqwiiJ/jcyAkpRLQ2CKbBJsIriiV6krZRICaytUgiZi6nyEcHDPX0KUEWhkUrtmUBlELyk0R2SbqVFPlmpwCLaYwOSK1c1T1zECGHDHLtoeh50bubPyYwjbizVLO17T73rR4hUwCcuNGfHbquoDXhsQMLxUuqwV5jsJ810VR6jlnF8/p1dH3PRpqXDA3nPeJ1K15+/Zb3n7zH4X+toyxpBROtvhYM8NtjG/mTtL7Fd9v3+nrN9/w9Mv/AdcsCTMz6ST1phiKwfAeS9UzTXlQ/ocIykFR/msV7h70EGTB4cVn+uKzv2F+cMGm9dyuPBIaog6ZDcnkoyjiczEYPP1WkT6TxGo/eFcyWdSBa0dkTOmw9SkerpjZKYK/HAUhkYtfWkSICdsNIYO2rG/fM5+fWxW4HKwyWB4sGeH0+JSX1zVstybMS9MH+1JTdJXBeQUaEDoLngsGI1njEqWuK3zdEHEFXirWpAquaIZCX3yBDouADiwODnnfeGh76pCIfTvGMg8l3e915fKgSS3Ip0xrX1JHZvND6vqArI679YZFs7A85qz0mzXt+hq0RSq181EqljlcCZbBImhLnfgAxQjOaIrFGA44v+Do8AkhBNZ9Sx9b6npOihDEobqC9ybQuXpJ7D6jmR2W4CmLlp7VlVmtvsEQiRIBLZgPDRmzBEYajAJlwkgnUZvld6NNmw1inh9/omdPP6eZH3K37ehTxfLgmNW2pWlqnLT07QqidQ+6vf6ONy//iL57KebySBZP4ErLllyCw0YxsquR7ihGi5jikbUI4qtW3t6+5f3ZhT7/5EtOzr+g7eYkTXRdT0qZug6k3qK0gzML1O7bl9V+cIDGIMHHQcQx40LNbz4/fM7JxeeE+THv1xu0qbGGK+ZvDE4IztwwGle8ef8tN1ff0b/7k6BboLWsuZgKcLIrvTqgKQOoMGZ0iJUw1dyDtrTv7uTru5f4xe/1s7/9PzM/uCCEGbFPbDYrA38ab/Ev5bWclnkt6JEfEJshtqV0XNvtib64HiLOQ5/B1ab8Ou3Q1NP2lqnhyhrGnIhZ8U6oBhdQCTKz0qIWTixQSp3+PGYmQJ8iMp49ixLHezR2o4uvqQLiKlKuSsaG4rGMic3WUk69JqL2iLft6p0ZA0NhZFd8UqYaTSvM6a6IjFKURoq/XSAsQCqtfU1Uh8Yhw8ChTgr6NcQYZZyrSUmowpEeHVywco0ZRQpSHJCezO3NG17/y/8C6QpkY/sGG7eW9ZaCVLVDCQHaYgz1sNlK2t5xszzW4yefEWbLUktELD4DIUYLbBxL3+pDxX8QSvCxtZRx5ob87rxbPJu+H0Hu3kenNGJqD//46P2HMTfgjvTk03/LJ59+ga+XrDuhV4+rFogPBOfJatklY1EvLL0x50xdz00+OSsNm1Om7TMBT+1rVFtm80M2twGyIE7H8veVeJM7vyCFOFQOI+BS0dc1k7pbcBW3l7+XT776StfdJa6+MMvdW+5qEM/B6Qv4w/+m1GvRuCIqY6/zAekVKCkYNTGZNZZpaWNCqAluRp86nnzyHJ3Puekz4WBOG3tEE6IVjQRiimS/wldCGxV1QpdqDs4/h9k/QJfZ9nfUBcfIhVkroCmPfDumiIW0WXBQPxiJXpgdn+MPj7je3OFngV7uQNYIQre9Jd2+FkJE48SyGIIbxNIRLRCpdFcjImrRxSlFO3HiybHi9LO/Lal0PfWi4Wa94WhxSt684+bNt8CVQAddw+r2tTbLI1xVWdSwehxLcvWcxfmvdfXtPwqsqSSOgetKZeWBFHC7Nn/oBOiuHClGSkNFIKOOXYe4fASHn+qTF/+WZvmUdYxoaGiCZ9uuLRTAB8hb5mFLxS1vvvtnXn/9TwYp3yscYoJ8sH+H31F2xL2trYWJScGoyJivd0N+fSWX3VvS9Rs9/eR/os9L81cSyJ31kXbO0JKcW7wvriA625elm5ihBVUJGTNGvGvXOpTwjEADaQ4HT/Xzr/5Hem1YO0dfO6pFRb/tqFygQvB9z0Hj0bTm1cvf8u4P/1/B3WG10Mt7dTD4ZC0MacogJ1MwGHwJE4pj8FUPm5a0vZU/tit9/vnf8+zJbxCZ0Suob8jiSsO20jGrFE3yeAt2EovUNqshm8I8WQcd4yoqUkxmqbU2qMrXdBrQMLOAyZzsWbUikqgll456XYHXSxxL6ZFgYjbxaBndH03W4dxkarEmI/asGMF1tHED9ZyVKlpnUkp4AkKAcEByt6UrZIt2WzMEehvroGLaHkkIpWPeUAc+N6NCavEqMik8k8wVVjUSY1TXOFxd0jVlA7LFhcoUHW+4Qd9XwJLT069ou5oYElWzYNtntBdc9lSVsnr5J9i+lVFIF70slu55Q77EIDyEoTNjMuU/t0Bk9fv/j7TXr/Tzv/v3zA6fkLFiTF12VN66odMp9OZ2E3oz/nYqlD1n2ldgFKKOQFUqXJjSJa6z/ktu5xmx+TUXRGaXMjncA6w4lIjVFLGqDRbfZJ0txz5zhXZ8OVSe2CdQy4yJaZBIAjLj2b/7n6kXz2mrBtTRi0KwWhRZO0rik41iQGdweGpwbtJNtOA+zhnv10BUUOZ88jf/lt/nS83v/0VUrTrirKosW+kXJjcijVDguVIkQbFDmNfcrd7hfELVrONEtmhs5xG3wB2cMRSWwJXCYQ8etIM0ayCMzXocVrQFHwh1TZRA9IGOTJKMC4L3Sh+3VLUx+KgdyWWy82Rp6LOnPjgeN5UMyvIIMLrpQCYWq7NDjTPhFRqtDw7YxoRrPJmI0pYGIFs2q0vTwHUiHHe3GgPId0CUWS6W7z5cV3yXZ081+8ZAC03gFBc8fRcJznP7/rUdOt0Ca1a37xEXSakffV05O7rcsDz93Mbv3Ije7Sp3lfzJ0f93f9h56IZX/Hm5DNE+7sAt9fDoOfXiCX2uLCIWRveM90Lb3kDckuMt7179gbff/lbYXkNeI8UyFj6sLJXHfbJDBaZfNiyxv2vEUs9ayGs277/l7Tf/JO+++yck3XI4c1RiuelDINNmazUDhvKdVhiuaMjJFaE6ifS+N8Ch2EuAHMDNOT3/HHVztDpgHTPUgdvN1oLvspJjR+2UbnXJ669/y9vvfgvccS+TYPBqFXhVB4uvMNyH73/vc2UejAuatc7qtbz+4z/Id3/6L6TulsU84CTRx9byjnMmaiZmE3sJyDq03NzNvZHbPUoAN2i6bmfVl4El8bvYlpLnbWcSyzefRNTvpjUX364peT9XnO/QhOKGkzzhZQNFskSyCFmcFXRycYKOi20Fl0A6dlnXw74Y4MUB6Ym7501/nr7jQKNVYxHzfcTaCyOkwiHyCONHQ0XLma3CAudngCMmS2Gsq4rKB+KmZXX1ugiR+MhmMWVk97VbQykIkM1PC9yh23dy+epr4naDpt72pqtAGpJmy3Wvay21HSdo0uBC2c32MK+2MzJOAr4UFRGEIfCP8qYlmQPIVuNDC0pV0IXZrLZSuJpJuZ/wkUTWOEHdpvO+4/exT9SNN2MrRVxo7Ny7JWdf/Butl+dIc4SGJVFqSt3E0rvDvpxOv7B4kdLPxJ6XrC4/xSVcpI+KoL4iuoqD02fGo2XXjOt+9NAvQ3/+ninx7t0bg6npTcsq6UWow/mKs7NiuasJjSHW4/4+G150N/k6/iVRHz3Rqj5Ek8W251hSd8ho3nJz/QpHb1pZVCwr1mDFnDOHx8em6Yp78NzHMZ0RUh7sRedhvmSxWND1W4KvAYdmofJC6luur6/4sHTa5Ibjr/O9r4RO9pjVHL+4eIFzwYwJsaWovLUp1Lhh9faN7O6hdFdvyN0GVdNOxTtizqg6Tk8uYH6oVj2qsAQZIM/HmI49z97PfhoDdGB3PiVQnZxxdvGU+XxJzIqWSM+UTUnxbhd1u7p5z8vv/kTerEe4876w1nGa8seX5v60PphrtysdSGrXvPnDP8i7V7+l3bzBuy2htkIiyTtSVRP9jORmJFeTXYOTyiz0IRVhGJs8+D7OkbHB2cmZPnv+CU48IQS6NlJVDR5v1oP2eJcIPnN9+Yrvv/kdsbuTycv/Re/748gEfF5f8+b7P3D1/ns0rqmDEnypGObKfDiKQPMkF4iutmBU+a/BVv56KA+W9BhfYYqm/dwzxl7QYwIwMeYRC4Z8yQalNf44VQoVqOZQzdW7Gd7NcDLHSUPlZjhpSiyDxRhM+1qEuhrlccy9KVcugUt0aY12dzItPzt+kREGQVdcXejjCqMAJNL6iqs//k7a9ZocUwl8bvA0pE7IyZdJyWR6Ej1JUnE7lOC7j3QqjGrzYq6KYRQ2gCHcIWG17SfHbeQR23Zr9UaGhj9EsvR431NVQ3De5GvsdscgV1GSGUE4chRwS44u/lZffPL3NLMDQrAKpT+9L3lBKR77i1hVubOzc2iWWFdQa2r1XyPy4N4otEz7PcqJ7dV7JJe0l1J7eicAHIfHp9juM4tnrHZ0796PC5fhABweP6EKB2j2lrKjRbCnns36mvdv/0DfX5e22EJwFU4NpklJOT4+gzADFUqRtY9PmO7+msrIyIGD4zMrYALYTvC4wrC37S3d7SW7EoA87syBB9p7skjxVJ6bHcwO9PjkCYjl+LtQkzN4ESpJXF1aus841pxgeyd3t5d4Z76tqqpMe8bhQ8PR+QvIVjTDnj6s5pBis4PbH6NhIyhmwQ2NRs7On7I8OAHx5lOUCtSRUsL5DGnLvFbi5pqX3/0R7m6E0pp06LW9U+Z+PE0VjeHAjZ3JXIZ0w+XX/1m+//Z/I+cVs7knamQbE6FZ0Ksj4slU6IjWWK30e2WFHx1eCRj0c55cfEIzP7AqgQo4C3yZNzNy26M5UvnEevWGV69+D6xlhF2nVKxzizr/ufWwswkdWuhu5eU3v+XVd78jxxWzAJK31m2suHNzEexJHHGofPURRvyviaxBS76P3g2IzTS8BBizQ+Th18R/O13UPkI/BHoqKfXEzr409tZquTxUh2JUOCtcpWp1J0TIkmjjllgQQ1yEUqJ2sMPdva88Cvcdz833xwzUM4+rLWx3XntmlcOT0L4jdluLQxlbrdp+HlsXOO5B7Y9TJA+fwwyCe5M6jKX0VpLKWRnuBwcjVJ5Q+bEoUpegTdbRL0/XDYrGYsEE3lkpXWu1UAMN9fEL/eKr/x7cIZodMUZiNJ/4wFum3Q//Ynqkba6VSw8s5keExbGWwg2lp8gvT5OtumO6OjAaxTjBdi3t9g5HhxshF1AVslogGc2RDnXFh8YtAzu//4yJcBl9lsJyeQ7MSgEbh8ser+A1sV69g5vvWK9fE5xpPVLKr6rapDX1krA8VajJOgTM6eS53LOSBus8osVKqTg9eUpKiRAcuc8mzLFuYJv1FaTNTgo8hGg/sMBsE2cyzvsSXGW+28XREwuAU09Miq8COVlBmdpH3r35I1PQ3u7Vcv3uFZU4Up/xtS9VpmraLVw8+8KgJKlNN9dUtNdJzvVkrKPtPKzxdJWyzYfMj/Tk9Bn4mk2fUAI+zMhiRX+8E5yuyd0lb17+Ab18I9Ah3qE5Tub/p9PDg5Vz3mnT2uLDFtIlN6/+We5uXqJpjarVfu9zMltFS4W1VFz6KaMaS1Tzbi/ep+FwVlTLYz05f842qtVpyEpdNfRdKinticYL3faab779R+LdS4EVUuKpp8DUrk/1L2OyixY0lDVs38u7737H7ftv0XhL8NniBqQvVlIiaSz/G/bxz1Mp/tsgt7Ps9IFweEDjsR/Y5XQJp1iyDvcFgqOuhKaCWQVVSMyqyKyCoHZeTP8f8GqvWRIZK9Uq3tYqa0RJhMZBU0NO94T4Ix61ccwf0xy7bUvu1qARyS0utbi0pqKjlp5ZA3VQQwcGV8MHD8y7OXxgqWZAShdOw9Yd0IAugCVjOmAOaKzQWAo3UWrY10cgc2IfiGPjK7E4H8qSPQAXrb1RUXIGtDiCKecHev7s18wPPmXbzYg5jDUgpFSwlFIm/C+22O9VoHTje0MR6OLJyXF8fG7X+rrMyy+vTIf7Pz6iNWiGHLm7fst5c4qX3nzk6hmKIHoJzE8u2Ly6wco/llrBO1Sz3HuY+R3sYrEJh9rMTogpIFKRVUqP8Z6Kns3qLaQrVnevOTt/gadGs+UFWql0BxI4PDrl8v33oLFkWNwPBPvAfzyMRCqYHeliccomRppZxWabqX2N5ETuO+5u32NBPhMNfTzAgYc93UeliAlsrDW4huPjpygNWa0xjIq3krsSif0V6eqVBI9FBIuY0iOR9uoNsd2i0qASi1UR2HaZ88MnyMET1VUvQiZqV+Y/Twf1URmy27yWVYDUHJ0+o5pbgaEugYSKjMfK40LOHbMqcvn6azYv/wi0SHC43JLZlQbf7asfv4FTSXUaoPZp8RUUnOsskrtzvP3+90hYsjj9G6QS2nZNU1UEMf+XFyveY3EQ5llPj03IuLYCfsbhyTOq+RG3twqVdV6tmoa+b+lyzzx4Gh958+oV29d/FGSFrxKpn9Zrvs/s7BEPfH8/kgQrh577UmYZB/FGLt99o34WOHr6gnUs0Kuj+PkiQazYTv6Y++hfE6kvEsHKwt7zST9gVzAEd1kFy3Hppudq6Fo47CHvILfSd7fqMvRajVA3uWVz+45ALrlBw3OtQElDps8JF2pShlBVaEz4UNMcXWj75kqMq+54z73VfCjEH/6sBQ9wnuQdPrf4fEfwGe8zLijd+h3a34BurLbHsGce3bZu8j0WoWX2XdZUugk29iVzc5JLSWXVcmG9oJ4vtWnmllnlHG27sYZT25WQitvDYRXX0gTJZNAzdrMQe3BByDlAajj/6u84Ov+Mm7UQpSlF+lJpbLPjMVM08C+i4m7eobdDJz+LNeujsjw4413J1nrcDfrzKUx3QNGz7lMy3831u9ecnX+O8zOz0jXbIriKpIHj06ds3nwDuS3CLD68E4zW5tQqciyPT6jqA7Z9QJrKqjk6T+43pLgm3r4GuZX13SvNqUVTQL3i8GRNVL4ippajowsuwxz6HnEWRKHjm/GBMDOebW+8OH4Czrq1SemsZGFLmb7bsF5dYjmeyuhTGBewYFBT+El2xq/BKwGSwx1f6PzgHHXB0Aixph0ewWvi5vIl5DWRDQ0mzI03RNhcy+rmWg/PTuljB640afEzkvZcPP+S1//8liwWUaxTiO2HKA+xzVKC9gLUS87On5O1pldBQo2KJ0aL5KxqIccNmu94+/J3kFaCmJafUz/aoA8e9GcG8nHSUhJ16kNHMnEIFJWWePW9XM2OdT4/Zj4/J6eeIDXeRXxaE/wWsH7e6K4vwMh8p/NU9ib1Uk/OntMli7OwOiI7bTyQqZ2yvn3P1bvvQFbAmqFP2k4mTIMzHyBVP5MspanH+Zqka7bX38vNotbFofls1XVIMgUmSIfXlkBP91FXw78WcqXwTo3kyjrnYYFXpTjuPVZlJ90byqIZixgvaYaEyVXJjArJEDcQMy+/+c90KRAloAGya6k00rQ9Ka7HUrOIQOpl26700IGoR8QT+8xitiD3W5SKJ8++4Nu7S3R1SRz3UkI+oiSOiOu9tzfESHMCbfnTb/9XtmqNhADIPbOZZ1krpCHXnY8jE5PdPlqo0+tFQCvk4EKXBxfUzZJQmRtCs8OFivnskPnsgLqeWcOw0uin79Zc37zVd29f0t28FfoOclsCN6cFqoagxknuSDao3R0/15OnX0C1ZHUXqeYHdjbyfQY5WOf3eM1H6L7PevqTSdNBlqAVzewQ5sfK6o2AED8o2vPzachVuWdRjtthFFJKvLuEvMVXEZ/NJ2lqv5CS5+DgAtxM0a3sJni6sabCbmKxSWB2cARhRuw9QTw5ZZyzilX99hZW7wW3JW7fi6ZOyRWiS7z3JE04X9G3MFsc2xhYi/NiuajTEp+TdRsn0lmzkcXyhKwe7xtU1Y6tKGgixy1sb8Q0wo9N5eR9J9b76D8PDcSG5cG59UB2VhdYgiOmjtpB0J7b69fgIj5HgnOW/wulElHP9u6Gs6eOVbdBveVAh3rBdnvJ6ZNPef3b/xUTWgWdeOgWmPz3AdCOObIqyAFXH+ny6II2WzCVC5Zy2PcbmsbaZvbthtXtK/L1S0EjoXbE1moAHC4bblft7rHT0rPCXyxIrDmPPg6BSbFSPaSUIbe0N6/ZXn7DPHaE5NBOwWVUt2Tfk+MK1CoYfjCED34RoJoxPzin7cE3c/okuFCUMHHMQ0D7G96/+Y54U8o7SiTG0lVxDHAflIBfVisfqyoLpFQqI/aRze1r7t4vaJYnqKugz4jLOFe68/W3ELu/wAf63z5N06xkaPM4KmA7i13KNYOqrzBBwMzKnwoTUagbR9ttubn6VvI2GkMIGdiQNLONlaXmjk2STFFouxVajJWMszK080CfhehqTs6/YH1zrZerTnYVIWPBTEtA36PvuBNCBZcgoniJbK5eWts5X6zN3LO9jXSzCor7yIu3ttTY2cz60HDb7fEhk2nHbKww08n5Jzy9+FsWh2e03doEWzbjxrlZqYip5vZ0DicZPzvmbP6E5fEL3l++0vfffwerN2IIS2vKFUPw4o68nxNTBeGIi+df4cKSbczIrDHFKu7g9oHHmN/b+nik9FGGz86XNszmQwaSSzdPh/MNQRfMD07ZrN/hQ0X8r5C2FkaI6MHw7o2ZDN1Krt9/rxefnqCYFeuC5aNLFubzI6qDJ/TtLahHnEVhO8dYbUs8JfCDoq15CDUHxxe0PSgGuUNLt10zC8r7y9cgliPK+pq76zcsT45Y9x3RZULtLfdUKurZEfPjp2xe39L22w/fdvBrUd5JAkNP8fOnn6GuoesTtTNXee47Khd59+57oIfc4VyFd4E+9RN9dPjPxEovf0wZ8JXlcUrNxcWnOD9jGzMqAec9282Ko3pGe33D5vU3UhJ+S+GVAlspKInbd9/DF3+PD8EOQunI1VRzGi8snn2h6ze/E7SDkOBhveDJHBhIKyVYJeN8Zf198Tx99iXbNpM81PM5N5sOXGC5OKRrV3Rpy6wW/vkP/wiVQt8RWytE4YHVquUD+gnC4x7E/pCKv3MMONRIvnkr391cM1b2Gt+0VNGSdsfsBEoPXivfWJqF+BBI0eyX5598xd02Us1PSPhS8rhk1uaEcxFyz+2rb6x2/pBvjjWp+bjy8vMF6XheR6aZTVlR6C+/l+8u34NYtsYYpKk95lAcGOC/XoHuik94VmPdDrFqBkLR2x/ArSaXPsQxLdd5uKcFpYaC0HWt7Ye8fY9VJsM2rCvoiCaqEnC6c8NE1qsbnCTiVvGLOceHJ3Rbq1hnjazmPP3k70EbXd++p716C9oKGg2KLvxqQIIGmToksRUHjcX4kEl5YuVmChyeDcnatCM8n1MqcSB+rHj5Z0mwgOVY8emXf8fJ+d+y3db0eU47BAQ686VnLVVKkeIuF1LqCJoRF9imnief/B3V/AmzBo2bG7793X+G1TtpFtCt3yHAfFax3iZSrkAOOXz2az04/hx1DWDlbtu4soY2xRIfDAZryMWfEeYfe9GBcgm2dPR9wnlPzJ6j43M2r/9AjNuPJkz9HCo+9B+yHNQiiVxHu7nBaYfld2c0WwcoqSpiVI6OL3j37msovZ9dGfDoXtKdxSh4lBoWp4pf4MIMR136EWdCEJx2rG7eMOZbIqxXVyyOOio/w7lAUvMzzaoZbbfi4OiczeuvsaYsjBuxvMmOhl/6GczONFMhrsIVGNyJCQFNG/ruFos6TxZS8ADCtgPyEVxbKZZpDQdPFL8wxcWFcp4TwWF57neXjFXVCpCmMOaNKhHSlru7t1RH5+X1AuI8WT1ddhwcXbB++02xtLv74/iApnENk7cJC0K1QIK1D0y5+JLEotstMDHTrq4hboS0LbEF+X6Z2ekE/Qir/EeTTq3fFqEHzH+t478DNFfgyYEPjUUsim9rOnbnqWeHSJiTqEhqLXs9IGpZH5Ij29WV1WqeWnOTefjwtX+JcMH795bhmWSUdnLohsoiQwlW+25JjfkDfe9fFykpt8Q08JdSt09KHMmwH2RXcbIA6rt9JVhKVbHcrdjI7ty6ctlQYQ6453sfmk2NiJ5ksvRod8fd9VtmR8+JfU/MHUJFcFZquU8RTTM+//V/x93NO969fcn11RtldWVnkhKc7MV4SuyKw9PcewJkkhVBFEq0fdkPytjAYJpX/nDuHpcYY2TSTmsQD1Goz5/r4ckzotbg5qy6XOKBTPCZcSVkmWRfSEZdjfMZ5wOuSbimwc06cML88JBf/f0hr77+T7p683vxrkFzy3rbk0qb4+b0hZ4/+xXV7JRNynQ5WnXAMCzszwlOewTrlGEe7N5jPQsJeDez/cUvL8wBghRGaLBoWQp9yIMjZGF9956cttZ1rcAu6iyYISbh9Pw5737nxwFb4NQj7+5As+mLi6MLxM8QF3DVUG5QqV2iX98Qb97JDkZR7q7fcvZ0hdTLUknS6m37Zka3WXF8+pQ3oVZkIw8T4j+QKaWBwsH5J8RsgXhZy9jECk107TXbzaUJx1KJStMj0SWj/yzfX2PBkAganlx8hgtz+ixQCZoyKWUaJ2h/x9X77xmsJmWs7QRQWiZn6Fdy+fZrfXFyjEvmqbbiKRUxdhydPeX1twdqgnZ9742Nb+wOirladn/Pwwms51rVC3ywoj0xJ5wzNpBipAmC08j76zfQr8yXNXlldCdCP1CiftFN7Mrc7m7v6YtvcOdLNEY2DYJ85FZ5V1DJilt4qCrqxSFUSyJhUrQn4fBYs5OtuUlSJ3jdKZGD9j2+7zCWXYnbX2wOijUwbrmC5hgN362G/mClTasU/GuV6VkyvlYiG7NqJVuwm2IKsWSoim86G1/IQ8yMwNCdLJaNkUWLAeMsC0d3FRFHk2lARNPu98BkRxSUpbuV96++1q+OL9CcCrQdShS2B+ZkhKvVmjA/5vnfnPEstqzXV9xevta767ek9TVs7mS34hlEyEWRUDrSwFsHYLFshlAs1yG96v6etbM1KDfD28nk7+M5E+vUiTQ8ufiSanbCauvJviFmsaJdhS+oG9xykVxim0QT4iCR6GNPzj1JK6qmtvTmGDg4OsR/Hvnt+1dkOoRU5E4Dfqnnz7/i8OQFW62s+ltJJxWxlMCfn7qpyIA0TH0bYkqTOkElIK4mVHNDbH+s8f8XUvhzF1jDDEGlo19dSd+ulDAHzLeKCH1SvAQWiyOYL5XWCmoMGoj5QSeLrBRYpeLo6DnZzeix/HZrUqLAlrvbN6BbXO2sSqdkurv30rV3WlVnpGQ9jxFHyo5ExWJxCMtjuL269x6u/GuJYBMffm44OnlOUss316zEUurSS8fd+h26vhJIhNJ5axCCqm6y0R/ZFMO7qgNp9PT0GeLn9MU/p2rCp6qE/vaa9vo1A3sVX5A5Md3JxJZCWrO+fImk3+BdIEuySnsukMTTVAccnD3j7vu3RdgZg5r6CafsQwrkbmNNgMOHGhcahKHLULZe7ECOPcELQs/11Zti8XHvfiYk7m/se/SLSxB7J/NqxlEZvT+uyS/vPb8oMVMrf5DcTaPBN/QSSNkVJHKoty6QE07KPqVEmpep9LLLQto9Z5iZXcDOz5sKgz5HXy+m+BnFibA2y1wmV+/JKDShVACu7dBZ6KB9ObGKgtKRczKeVaxI2yoKIRijkwY0FIVZrJ68rklMrdxdMOU0lXRA/FRAtAjYaOe8vXuHa86Y+YY+96SouBBwvsI5YdO25CTgK3wVmB82zGZHPHn6BaIt7epWN6trbq7e0V2/E01xwv+klP+L4/YczZICzA6xAkVl+eg8ysf+nkyYw4JmfsKmhSTWk128N4ViWoRK3D1VIaM4n4kpst3eEfuWSE8TPMF7OvXEdcvJ4Rnnn3yp7/50KxpqUyRSxZMvfs3y5BltCqy6iKsCVQiobiao1s+gaTzYmD83nQeHk1D4QiDUM6gavaf//IL8MNxjfVMuqIMINKtCVaHfcre6ZnF8ZptPhOyE2Cecb8hETs6ecPXtq/G1BBPoMeYHDDVAfazLgydEaYg5mf9KrHlK7jfcXb8GegLJalSrg7im7W6oJJI1knGEUNF1CV/NUd1yfHLO9e2ffuC1i39IHPilNvMT1FV2GFUs4rKKeE2sVm9BN6ATOFkpwm54y4lfbfCjj8Lc/PTh4JTF/IStBjR7slpRhEocLm/Zrt5DfztgJGNAydiIiaGY6xa272W7vtLmeEkk06mCq9Bc0ectT55+wt13/8XmWKeBFw/0t4cWc9HAXKgRV1trzKLkiGScOAtCFCHFDXl1yZQxycgObJFNgZsGQ/KLbt4dGfydJ6zgoeUzIE/3xjD8spSHs2CnTCwbdzZbWJnUbGU6REBEEVc6UUmP5i3t9grT+MuiTf1MkzH+V0EoRlfBI+mUUxqEhu7O5b9Wy3wgFcf17YoUe3BzmJ0rvZq/N3lroap3IJ0M1dzIFeBMbrtogbKqoDO1wNcM9GS/FnOsl+px6sj4ItQzQhgVO5kgZVlNacx5A+17+f5P/6QXL37N4ZMlkpRV15NygxfrLrc4OEJTS9v15BRxKHVYUs8OqJygcsvi8BkXLzKaWl2v77h8/47bd28t0FcDSIl3GZqwiJoCwxjr/yGNG+i+gjJeX96ZAnsvXnyl3h+y3iTmR3PWPfjgIJeMKQZ5mMocAWRzcYlDnZXe1dSzutniD5eE0FA1NX2XiFJz8clXvPv6t2WdHO7kmZ49+wzfLLnrEjE7GldbzEC0jJlpV4mfQh9VCEpr6EQ2wys7MoFQzayt7+AN/YUP4Y7DPzBplEGbF/KYrhC5fPeW+dFXFhUIqIp9OU8f4fT8gqtvMe32nlU4eU5Z8fnBMSEckP2cPkWcS/jKWvf1/Yq7u/dAVzbqoLNF7laXHDzpESzgx7lAmzrqpqHvthydPOH6T8JQVWDXn7xoz6OPx+GPn+D8HPWW/+5CXfpHJ3Jac3f7jqGvex5cr45SfMXh6CcM8hHY3VpfcXb+DJWAagDHWJM9AH3XGmw7lmosWvvwle3eNgcdqOfm6g1Pj56Cs3rwCcU5T4yOw8NTWB4r642UwbITV/dhZ0sHYyJ8Hb6qEW++c0O0c1nFjBeDwWK/he1Khvu4Mp/mM9JyUIpg+4hQHw7/T9/TeTrjMLF8B0TrgyIh+uD7+PfByt8NsWkaK56UKcqfomL96oc3jf0a+rVARHPCiUc1jq6m3VzfD5ZUfgHrYBw3u/3GdIrvv7yK9VnYNe+ZolX/Ckk9s+UZwS1YOGUmHdoLVb3kplVcFaDqcLLVKtmaSzbFv3eQXdYY15YaqXOCq8l9h+iWvnutf/iX/5/k7aUpXMXSHVT/nZ4u5EFr14mlW+Dn9Zs/yc38UOfLY3xzRF0FokSSGHLW97Z+IdS4eoZDSCmxbjty37Fcnlpnw9SSXUO1POBiec6Tz36D0Ou3f/ot3eYGvb0S0hbVjqSJ4APiMn0/CW59zFX1EWRy594LuOW5fvriV4T6yOqfh5rc95B7vE6V7zz+b7dGPUJFFRwybwgo19fXaIIclegSs+WCu9tr5qGxAmfaCUn54qt/A9WcrSrqA6GqEJwVhMoB66cgP53/jBX0Bjem7n4/UeClrJWKUIUa70Op5f/LU/ihcDgwzXGQK+pg8/6t5C+TWpcwyKr4qjILO8PBwSEym6H93U5uTrz/4gZkt+L46JyUPVJVBjVJafmZe9brG2hvBcloKrAoAiRW1+/IcYsLC1sQSw627wqL5SEslmp9y3f0wX4U4ez8GVkdzju6tidUh+S4JaXeNvrqUiDhvJBLLq/zO4G+owz4h08AHC40en72jBhBRRHv6VOiqSznfnt3x/rqLbtYAWdQ9sCZnRQXBQy1pG/evuT8078jaU9VzYldJKgQQkXMHRfPP+XNv7xk8Nm5jxw8W5/J5DhHFWoLOMSVXthiiyaO4Byae1JbgsDyoC0N0fLDjcvOeowJyBQa/ulkT9kJ9TT8UiYXDDRucncPRxgOlhTo3SMM1QuqqrFqTuImgZBDcJPgPGzaW0itHeZsmQJWHzDfj2K9h36Vb7+ImTx9m8dosg7leSoQh25vQ1e1f4WkODYbIfiaujrHe4tH8dWSoA7XVES9Q6VFpTcgRCqyuFKhLBNcIvgZTmel7e2GprZ657kXrBlHOdc6uFsUN7pEqkH6jalrA7fLtEDF1Zs/kcVxdPE51eEZzokBAU4Q1bL1ssUuO49ITQg1BNj2PYhHCLiQS5h7QnNGcuJXf/eMdnPD6vJ7vb38jvXNd0J7ZYGCgwXzAaoFOxdVcevBYwwWCBwePWG+OCa6GVUTaLtI1dS07YZKhKEHvB/PxU6wizhImZyFytf4mef68gaXPZqFqC19jiTNxOyAGnzQ0xfPOTq54C55+pzJXi3SvI247AgsrOqMv+eP/Ik0EeoPfz+saS75EZXFQYy86hem8MFpnixeHvvklMF5hXiH5DUu9yRXAZE6OPImmuivD5H5iWp/LYJZ1i7p+ALBQS8O/IL68IxtTFS1YM0JsAIL3ZrYXUFelVxwkGlgx+1bSf2den+ApyKlvuSkZ5w0uDDHzw5InTwIPpCdwJEA1CyPzumzUKmnSz1VnayrWb9hu74tuUcZhzAoMc4xWjb3glkeLqoCVFa97OiMq01C64zlyIvB7WmFbq/M2nWu3DAXqH6Imh5O1eTGt5cica3qK0K9ZLOJiDjm8yXt7R0XF5/z5p//E8ZM8ghHj2PdOcZ26w5Q8i+d90WVs4IuWpxqXgRyQlPPaBZms5WFKYSliJP7Ak354LljkN5fpLG6e9+HSmvKzg95T5jLg48OpT2ZWANFg0atF1n2YgqkVnhXgjXVXEFKRlQLY/c4cXRdSQ0aYY7JI8UKHw3PeXiA78c1DB9/fB7uy343+e2Qd8xY53q42a5/woMb3btPMPSpdIti8vkPGrdMgodk2Jc/mxm6ybDyB7tBP2CUeXL99Mqf9mzz40J2gR7oYyI5T5cqamZksdKdkYhzGbQ2gS6ZJJByIlEjyZP6DiJUjeVTk6x7+S674uFbYNBsMUic86Wj2E4RFEno5kqu//iPIFHPqgz1EtEFwS3KjbzNhxQ0qRRhEhGcn5NzJEsiakZSRkqaVhSl65T57AkXnx1xdvGU28snev3ma26vvhf6O5D+wb4ODzTRHcY2IKFu3IemDNfzOduoRaBVbNdbjs/mpHa3iqKU7n0ZGVOpHd7ZGYs5U1UVQTySBS8B7xzqhe12y+HBMUF7aD3MD/j0y3/PpgeqGblTsmQ0d+SYqOsZtfNstr3Jl2mNjA+k7A/v7/Fq3QWmOh2aIVH4i5IkWnVPp1j+dsUHJbkfu/mjj39oSE7+YkI3jzVvh+utSUkA5ggzCwiPHTQbvv7jf2IeMj71OG1x0ltJTVdzneacfvHfgc5RqgJqRLufWqF8JEBzrP7oKWE2twIKNAStcG1H4yNXb38nsCm13h2eJY7GIA4fuXr1BxrZMvcKsSPlDd4LSSpWrePJJ59OkXWsKF7xMMxqkAZ58plKWFI1B3RRqOuaLl4R+/ecHja8+/oPhfs7NO7q23ctGAzf40rOonMQLFN5x+O8zd+LL/6edarIPqBVS5ItgQbXO45dy82rfwYUN9Q1JuCyhxxKQIU1LRRfGEQWXK28/+5fCKJoSjS1WVx9C83ilNuVsHj+G4U5VPXYraiqikUruyIaQ6tCc4VFZrMFXRcRPKpSaucXlpuz1ZQeCmqUPH5K8xOT16Ud6FArfZArRZibFyEwFicavqZuhvIViiFZVU25ripfc6DZdUJ9DDAZnjme1WKh+AwHZegAmsjSkyXTu2xWVThCOSAng6WddrjSMEekIudAjCWjQ4fAqEzKpb+4M2ZvaEu5JocSWMWY0mTtf3bFSh66p5zY+sxqIQxJxOJsjA4IG6h6O6oeq7MxzsfQMWwYBxNXQ491CpuBzJGQkRIH5pAy3l1PBh6OTR2i8gu4DTxSeiZUOGocTfm/H0L4ShdFO0+GsMwr9yge9uMoUwUl5hXZR7ayIdVK7xOhFlIyyzoJ9B7a4GiDo/f2u+QgVQ5mng1bZJ4JTSLmLXVVxlwE7LAhd0G0w94vAgUhpcGRw+gqUjU/PNxy/e0/yB//8f/F9bf/wKy75qx2eJXSpc3OquZdgRQl0caWqKUMqxvaolZk9RYbUjdsxHEdla1bcPz07/nyN/93nn/yf1WqL5S8JNTzosiD0waHdQxDou0hF/HVsHe4B6ND6WjQBHIF225DUwfiakVVYl+m73uPH+AMzc1CJRXzumF9t2J7e8Ph4RJSxCsEV9H3wvUqcfLv/i88/Tf/E1s9JIVjUqoIYVaOR6SphKwtbVzjgqISrdWvjBFftsc0IMMeZ2BHefwCEHVorwT1VF4IpWuf86bw5+SIKRE1QehxdSLTk8RBcwY6Z+f1vv/ej/FCO4jDNRWPhbiOPnQRb2GN3hhI7DIW+FUq3WjROFJLjHfE7YoQKmt7V5oE4IQoc6iPoTpU2o04a39SUoEKlKQOd3BOcg2qQ9epCkmZ4JV+e02K6wk0O9gOg3nVkbbXSH8H4cAgL7OvUAKhmVPFQ/ANaHvPBgAtSk3FfHmC+tJQRm1RhRbvelZ3K+h32umHaNJ9C93SyoxjOlfquWSB2Ym66gCVmuR6XClq4qTGaeLu7pIUt7ZdXFM+KKgPljtatFWbpWEBhZwSq7sbjlJPlIhTj4q3I6I1oTni+PgZ61e/Y0grU2fNn4b1znmXPW89cuynocyqwvh9t09+BE70SCGZnXCYmtIflwqxFGfp+2w1A/JQc7tsZDcvwmmDQQWl33FJnbQqUuU0uFCcyF2p89+Oe2xsmzpapvWQcW4xFZJ3EctFe8gjajR9jY9ZjCMEwtRXv4P+H8xDuVycrU0XtaQGVxDm4Bq7fkzDGRSsXCAkN5knNz7XbmrlNE2TO4B+Q84rcxop5p4pjao1PV6i1k3e5edQse1GJqmj48ajo8h2ZU1Mu1HNpOSxyKKfPgYBq41eUrREDUonmSVrJzGWXgq5GKZ5p8goxJRwLpllnR3eiVmOXYah8MrYN5uRhY2rfQ+5GZj57n1MvFv7UeKWfHMnV+2azfVaq9lLnn36d2TfmILuhOSKQM+Gjh0uFqSsdCmSogW7Dda7+kDOkJ3DUYEIba6o/ZzDs0DdLPnmT/9vjdtXxfYcULtJrlDZwDEW9BoT7EldURYrenV0CZwIzglOBK9i6X7lQ6r3ufRw4xgjJycnXL+/JKXE9999w8mTU7p+a7VOFJxaB0Tn58yPGkIIJFcTYyzo2xQDK/usWGe7nPH7yupuDI/49SdUVTPImdhHa4glGacevCcEa75lwsH6vadkbZfbToxPjUHLwxge7OeJ2Lr/yx3nmFKw93WkpEWjzLi6siArMR+NTltYJNDtWtbrK50dHyL44rvB8u0QmnpOODohvn07uljHuSow9NnZE5xzdNna6lUi5BzxDVzdXaFtN3kJawM6pkIlYbO6k3a7UWYRVzsgWU1gEesl3M9hfgS3dwxxAONExQjVAcdHZzjn6GMqvEILJOO5evUOYiuPLeRubrO5luHeQoiZZ6A1h6dWs1idRc9r9ogPVgHJwfdv39P2ytAnF1cBinqzxE2fKZDQUJYRk0D97cZEQ+mtK2JaekqZup7RnJ3z/exA2a7knqtLdgrW8D66WxxyjkUpMdgcsNx85yzafahbrXa4Zey5PN19E9/sozrAdIfm3fUPr3UBsuDnJ1w8/1xDdUjWQFKPaoOwKM/YmEKWQzkovtx5g/eOnBXvKnJ2KC3iN9xef83q9e8lszGE6p5LwFRZGS2o6To/pAFfo7gZptHBH/OvDbNUXAZMAtQmc5AHHVbMnsctOH3+N1rNjlAahAMygaFp7mAyS+lxKfegiyKUxIqomGLTcnv9NZuraxmKiYyOjJLdQEk51dGK+QVJwNwdOr6/4okIvplDl00xz2IpYilDjiTviXmFMGnA9GNJHdo7aj/D9REvhpKJWqAtmPGRRgUOXKliJmq6U+0qQg5UmpjhCERCjHRdwqRVkNHiYhAkuRy1ndtnXKMBxRqNmWTPK6gXGdhc0a6ytOFK766+pTk45vT0nKPjU5rZ3Oavhz4q6+s7xNWIb6h9QMJ8fFYS6LO5zrybgWb6aO6XerHk7PBT+vye7/+wKqkjWhS8SKgcOabd1AvgigDTCmvAcsDBJ7/Wo+PnhDArjsshR1+IKRN8+MH1S6kfO6C1bQspcX5+DkDTVKy7nlA3Y+31uq4Rscyrv4TcdO4Heqws9MjadtC8gmVfIYgLVL5CJROz+fxVzTUgqqVevLnzFs2MNqbRgNqp8g/m4aN2zg7xeUhhFxBlZQmrxSHL5ZKrzRu02A1Tv5UqsO1Y311ycPIpkUDMar6B4aahZnl4yvVbP6YQ5SIslBr8gqOjE9L48HKQ1do93t1eQdqlWxksUyxbb5Zl3tyyXl2znH9mr5dj8eMGAxp8w/Lgia6u34nKRAsaYIvZgS7mJyDeijY48MVP6lGur95zr1PZx2gUBK48IeOdNzyOiuOzp4R6bj17VcnJW063MwH4/PPf0F08QTWpFyWmluCEUDli7JCsRYhDLvC3ikdV6XPAhzmqHnJhQgp9sqIJTTXj8PQJt9+VnHRJaLndo/14SwBYjAYbOb3veddBhpcOcWY+Ptgf925YxNoHG/Phsz8UZLuHGrycpOHw7FNmizMSNUkqYIHGpT2nWOgywNvqGYpnBGdpKt77AqP1ZO7wCKvXf7JHTwU6gCbJOSpkXOk7P7WkBjeid4NP8cFUyk7FM0F1HyAexPwu25adZTaZSPM4SWkcUkFzok+efUWzPCOxJMdjlKpYuIkhwlamVoaWZKkh+hazOB0tXu4Q1nR335LyagwwHaqkqeRfAFb/OA2xCamYrgXjKkjbAWeffqYarM2lDxWSEqRIJbC+fsPV29+JPKjf/Rc/m8zhojEEaHNHiiscNaIVTjaGVDlD7LJEPA7UqkkO5MTjs4fYITmDJrJuyf0dYe6I64Kg3LNqYAhGnM7tVJ8d2OnAfz3WStt+3YJsjSHcXdGuvuHlu5rXswOdL485ODxjeXBK0xyxPDggZ0g50uVE7nsyHucC4h1DEZZcnD9ZEylF1JmL7+TZl9xsbnX15o9WqEosEycOaRy5oCcqJD+4xRrC8XM9OH7Ks09/jWsOyVKz6TMp21mwLmx/rgxKJoTA7d01dRO4unrP0flJKVhm98k5jzXXU7I6JilZbY7ZbPYXlG8dFKmd0nufpgu0E+bDZ1MpICRgveMLNm69y7zVC9CIasCXU7+cL7kc7/OQdxbO8JhxA/y5INaQGGDGCl/POD4509PTU65evZOhV/kAhw2GJyQ2q7do3iDerHQRNaEtIOJYLE+4drW9mJZnqAWi1fMzbeoFd9nq8w5RFOKUtr/hbv1uN28jLtWXyR/i+CLru0uOnwm92gK7YEyzT5GampOT56y++UOZ8OIDL91v5osTgm/o1d4uqNVmq7yQYkt3d83OCfvx+d2RY8iGjkmxlqwnOl+cgasxG8QK4PjkEc1WvEFO8YsjvBckJ1yOeDG3IalHsiLBfNgxm29MvPnffLKI+WlshYppvL06XPScnb/g9vt/NmvEGww4bZqgMJboHSyJrm8NmdHB9ypjRSV1Br0RmpJPeR+S+tCKvQ8hTdGNKcnkj6N8HKpyUUGnJDdnKzM6nZE0oMxwYogGJaBm0DrHYh7OLJGYMqFAb85nNCc6HTqiPcBCATQS+w3WNdCga6fug+NeVVVBEcr+zBYI+pcIwXF3DbDfRzaYUKE5AA30jiQH9Lqg09pg/2ntg+F+u9w0E95aQIQi1J1mPJmZVHRRSN1QumxoJzpYATqJgZpY6OrKefrz7/lDpCQQHY+5IqhzIDV+dqRPP/0bsj+gzQHvqwIuJFxOVGHO1ds//uRnO3razTUpXfLd1/+RtHplpct6wcmCHCNSdajrZQyc0kaHAF7LwEm4EMhFcIhmvFNRWtLm2nzMU9fTQ0aiu9MyONYGsmNaJl+0BI6Ve+gKdGuGQc7QeXJ3Kaubl6zeLqmWx9o0hxyfvcDVS+r6gKpaIGFG1p6YHRodIQT6lK3zo/OGYklPRGlzZtac8uST39DHqN3b3wuuNcQmF76BmEFBBe4IOTjTUB/y4tPfcHz2grbL9NETU7KmLuKQYAaBCw7rLfAYgmXzUNc1bbchzGruNnfMm5pYelx0fWI2W6KqtG07Qux/cR9z5BEhPSxSGcW9RmIPP+PwVUBjJMaO3NnzBY9zHicVOQHicQRrX+wC8/kSfKWk7WBeTt54+J+blIYd1P+8O9v3/rajnYqkQmoz63Xm+YtD/PyQtL5FiQxeVMfg4k1s1u/pt9fI/ACRQNaOmHtCsF7lzfwYvzzRdPte0pif50AbDo+eMlRcMp9sJudI7a20qxU8sMmz4A6sxrFmNBUmr5G720tS30OTEacl+hxSl1A3Y3nwFJmdqW5fi9IjUjQL8RwfXZC15J47h7iIpkhwyvrmFtoVu4KhAxT4of42uCJHKTkoXTLj4PQTXLUkqSOVjj6oQ7MQnHX1SdERwgxNg2ujJFIlULX0EpdNoCe11BTJUtJTgBjxOZNLIIxpvh7NFQnH4vAEd3Cm+a4VVHE+7fJeBzk2tU4Fttu1ISJSmW+rBMXlIizViRVIaJZKz30n+71N5h58/zjiMeVzw3axLTBEeznwDVkai0dghlKhzuFUC+pd4GXdWaUJUG+FfFCPU0rBjTDZ1Y+AMTnRthscCWGI9x+uL24IgaophSL67lHGtLPCP2Qy94KFH+cn9idxKCUAL3miOrI6ogrBu4lSYIJ9l2K3sz7G1ytQbhZwuabPsTQWKQiCeHSq3Nwbo83hx97wp9HkZWUCkjiPVDMSDZGZlZfKFaqGHLncs+5l944/iTLzmSP3idS9h+0bc+f3mewWECPab+4rS7kSa02MbRpXdDk1d6UCUQofcEMmyINXVR6ssTHrh2+y229W0InBKCljh2zdQwHvMriKmDpyn+ivNtLzlrvX34Cfa3VyweHRKfPlMc1syayeI9WMLDVdDqRUgtNcQTnxtElInWNx/ILDkyveXb6CtBr3hJdAyoMjtObs+Zd68vzXJFkyP7hgq55+grR6D+IGV5haZsXoWnicUu6p61BQQ+H6+pqLi3PTOzuD/lfrlvV6PULzdW3FZvq+/wtaoD7USJXHYn8evzaTU4vzUBdhH5zDiadPjhh7pFQcdM663ntXEao5i7NnrN/cMTV4HkaDjLbF6Ftnd8VUYZ9QCMHRx4FJedY3G0EabWZLXXetaNqStVgduWCuorC+lM3dW53Vz3D13DayZmbeEyM01ZLl0Tk3d3+wh2ew5vZzTk4+IWXB+VA8RKC5x/vE7c3r0uiCwiBNwLkA9JidK56sPXl1Leu7K52FQ7wTsmqpeewsfc0dsDx8wt32mmnFJqo5R0dPrHqPZCpvUcqaOiqfeXP9BvrWcJOP+Cqmsy7Kju27Eh1cH+vZxReozOiyoF7AO3yszKdCjxBNMJobHBBcCFCgIx884j3ZWCzivLWVVUVF8CLkGC3WAcwVIZb/7F2Fag1+zsn5J7y/ewcpkp1BJkWPsvvKEGsqoInUrREtrQg1IJ4xGM4ULMGHGb5ZkO7u7byPsFf3QPvk0XP0KOWp1a3kmME7S6vLMhlnX/CRwZdfYu2LwpVl6G5vDThiSnT/f/b+q82SJEnTA19RVTM7zHnwjMws2gwz0zMA9gK7V3u5v3V/xe4DPFgAMwNMo0l1V2VVssigzo4fZmaqKnshaucc9/DIzO6a6UJPhebj6R6HGFFTFfKJyCdpALwFV7x6EUplRaZrVwxVIIlsRmgxyob7aeopUo1V00ZIhtL478PEPjTuKvF9PTdkSQ82o3fgHE2oiL0lhmnpAZB3qwWzCgfW9kG5+wKrW35M5WrLfDYNVEgw0vsXcfuh2KHk93bQt4aDiOnHXcWS8aYnalPq6hEakIx3iqoQZcgU/6cOR+pLcW5MdgHZfnvaQuJ0N65pibI70JVSRi67TCMdINM9a2irzIdqhh0K8iF3wapyhnvMDN3yfPm6p8wZtk0090X4D4a9M8a52Ep/PufiXICAHB3r6ekp04NTquaMUB8T3CF9tjSK7EBFC5wcqLNjevCUm5PX2l2827JT5TQkdzpSVXPy8BnHjz7jcqV02jBfbJg1E9CEl0TlzCnoc9oyZf7Q00vZoICUEtPplPXFBfPlgtGoRlVYLBYsV1Z9MhqNtgo8hFBKSr9/DKEpLW3CBwRraxTfhx4UxAsUTSuCs0TNvu9RVyH1mCAVMfa4ILgiEfokhBDIvuHk0TNW774Gvd2/b/9sg5q3lVKM14IM3TK090aIxlBAaCZE9eDHWjdTfBgZvr6FYylQRpEquaVtrxmrUbYORIGu1D/5MGY0OWYupd5OnSk7Hen44Ix1drjKlcYGCqnDhch6dY3VPprxsD3j1luw5AdNEc0dq5sLmsNH1vIyReM9F49qRUwV4/EJC6mxDM1kXk6Y6Hh6xKINqFrmJVnpU4sTR7u8sWveay85bLXtHA6S565EE+uqQzXD+okHUrbd55w1A3ACgZ5ERNVbk5YYLYRNIOeIRiNycd6TYySrEkJAxJOyUY+qJLwmahdIAm2KxKR471FxJAkkrZkenXFRHyhdJxS4KlgS/dYg2dVRZ0gtSotohcpt78useA+uwlcTEoEhv2EAJ3aLMnObbvY+T728JpYoJLc+VRKBgoM+UTulp8cRcdqTcmcpRxrJrnSTkkGwKloSNhVj51MVSJbsl9QylLf3NYAWUso8NJHT4KFHnFbo4N0O1yfgfI3zNUlKoiCuqNXdnVos//173zbZuqvM9+ZAtt+xhhLoRlJcq0sNzjmCFI+nlCWW6qLSvWqocxtg9B1U7JTSmnJDn3Yxm1twpfeFh2F/3JUi32Ps/tihe/JpQI/SkKltaITmQHYeh1i9vUsl/PP7DCEnTx0mOD8iawn/aWeZ7rC73QG0UNhxLrI1ioc0qhCCrdo0SC53yyMfSFSGFMhd+uS+4+AwdV2Da0zDDgqdUGrV9ymX7f9bhItkhncsn3HeYk+Fkljnczmff8251Bw9+3M9On7O+FBxTIjamBcdLF/H5Yq+V8bjM2t8ddVA7qh8jaaMIxamSqvhn682zJeO6TQwGs8KB35GSmOrPMD03hmn+g/0BK/rmvVmSXCe8XgMIrz+5humJ0eEumG1sjk5Pj5mMpnQ9z0pJarQbNugfnjc4/lux101u1trruwRkY5xldG0Zn5zzdXFFd5XnJ48YjQ9ZVwHoqYSBjCHt8MhLjA6egCuLlwF/R25aecfWvEOz/Y9/X3Pi84PIkMjdB2j6YSmmeLrKWYdmhmesb3mEMgRQubi9bdMRp7l4pqqqsg507Yt3lW0vXJ09thKjXQgWAiMTp5ANSbjramIOmrvqIIyv35NnL+1nr6q2yWqAl3ceQSpUMFCz9XFK8a10LdrRnVTlKbHVyNS9hyePjLBJq7EBysePH5OyqaUTKglVBOTUcNmccnm4jW7NqZpew335D7tGeCDF+SAmgdPf0LMFVUzpe07qqqi6zqbv9hCWjGtMyG3VCkyFhhpJvQddY6MnBJSQroNVY40qvi+x3UbqtRT544qdTR0aFpBWuHEQgZKps9KzI4uOY7PnuJGB1ASq5z37O8j1YLBhgCaIG7kZn7OqBGCE1K5buec0U2mCFJx9uAJUDGZTrdzVFW7reHdsKFk7+fOghTYEpjcedu2UYJ+RTNrtLu5YFYl6rzAtVdM2TDNK/z6kiOfkc0NEwdV6sltTy0Vo3pE3yZIQhXGBi+rZzKesVqu3ztnToWbm4gurmS1vLRwT45mKKll24bKvNlNzDx6+plxfxMQqcq9G0/QDw2jnxx+dsr81t9DLXJe4w5qJS+YVD0S59BdMHIbfFwwlkjQFvqWaVVBTORojT29jMi9I3VAF6AP0AmVD6zn17aOh1iwYgog7pTG7Xg/3wNL/iNHCY+IDgx+5QTF4LDGTlZGOQho1WzcHD9EzPEjzi06QdOY3I8gN9BZPDirx1Hh9hSyKfOSwU2DcS/s/oaKGCHH4R4UNONq2Xp+ELEufRHvo1mIvrzvIIwaEI80h1AdQJgpbqrUD+yneoCEE5SGLJ5k5m05ouzZDreMQEgryGusL8UaWENecv3t38nX/+f/JG9f/C2ea2ZjiP0ScleaZQFS0afA6fEzqEbFkXXbZxVzD7Gn7zu8F6rK06UOCWId6Iqh6XzJDZJocfz4vjK/G43o+56qqnDBI87x9Gc/4+TpEzLKcrlkNptxdnbGZDLZlduq0vf9P67EFrBwVd6Lm5sT5r2n37QEcVTOEfuWulZq3xHSNTdvf8Or3/xHNl//Dcvf/RUvv/prFhdfIXmJZ03fL5Eg1OMx66QkP2ZFwD35VJGhNJwtMdQ+mrkTIbeNig+NMDjzORrN4GazYbFa46sxO8t+f8ILSK4R0kbWN5dah2NSSpY5CTgR81JpCAePNV52VueRhdnxKW1UXKjNOxdHSpHGReabS8jd1l26ZXwUa8Ts3iGWlKFfsFpd4KspKaVSumWxfucbAmOYHCrtjZkDUlGPjog482LENpPkjGjPenENuZOth/4jqDFV2BGtJAfNlGZ0ANKwaTtCPbIucpVHYqRymYuLFxDXjEdHgLeM8u1UD9b9fSe2110JfaxTixLwoyOmx4/ZJOtynNURQgN9R98nDo4ecj0/ByI59gUNuHOGWJLgcs9qccXBcYuXkZHbkLdkFZaYVzOeHENzyGr51paSJLo4VKpi8OUt4oS95I7tGJS5YyiP2rdWPYlIR7s4lzfffaHXlxdsciCro6oaQh+tMmDsWfWZKFMePfsl9eiUnFrEG/+8qpJzEaQiVvvfre9cy3A9ykCxu15f08zOcNJs11cIAeseqLaeJkcwmijrVoycP9LG/j2L+z6A0e393z6R77w3KHdjYsyLd/Lu1Rd6dXVOH5VKre65j5mI4OqaLjnwEx4/+Yyq9pYZrj1VIWfxIogmQxuihSuQhOidch29+6zKy/KfxS+/FTne+SK7Y29tB919PpFBIiJDudrvdyW6lQPDCTM7Ipj3r3ggUd7v2LjD8IbQyG3kLseSMOcgBCFHQ4g0g2sq8+ZLgmaMCaTiybOf6uT4CdXkmBgtK137Du2Wen3+DW9f/EpUM7tOZduz3b3BW5t8X8SYQbQCEovrr6maiuPHE2ofLLSJIm6g1K5wMqaqJtqrE1XZu8UM9HTtEu3X1H5MlJ7YRRpvyJWmSIyJ5DKIULkwAGHf/3xUrARTFO8D3nvqJjCdTskJsoZCgsaWUMcS037vYBAAXReZNJXB+QKp7/AuEnC062tevfgrbt59I2xa0yXZEc9b5rXoyckx6iv6nEj9huytvE29QJgyPX7MzasvCnV0xqqQ8ntI57ALP6QR9kcYlmfMnV1Qu5b5zVKnsxPOR4fKphUkIi6Rt5OfzV3PG64vXnL6/IhNSoRQF2pBT3YeGHF8+gnvLt4CEXzg8OwBrSq1r9BsdKU59Tjfsrp5ywAJs38DsvfCrVtViHO5uXqlZ09+SZsSLlSkLMSYCb5CXM3hyRnzl69MEjUnOpqeGUtSOa6oCUzRyM3lO5vgoQRIubU59+PF25dKKMA285j66JE2kxOyq4h9ohk1dF1LHRxON2hecvHqS3T1Sixf4M4mHLoe3SdUhoxmdYZ8+JlCTX3yjM/HI9pYQTUlZWvb511N1204OXvG9etvlbYV8Dgpi38wkobsUDFD5ubmnEdpDX6GqFGesvWYIiI1VXPI8dknevViIbhoPaXT2rynuK+g7kuk2kvq+J695xxITigdNxffyQ3vyhcGVr0OQVFnqAEcavXJJzTVMet+DV5wklAimiPedzifadcX0F3J7jnvFIuJ5gh0LObnHJ5+QlXNiDGC9/iS14AqUQP1+JDm8Ix2c4OSqZuGru0/+Ag/eK9ltvZVhC9TZMqshwyb8+8E9w5SopVkhpgrJDLVBKIDJto8/wTvevqoaE54F6i8h5xRSQgtm9VV2XO2/gtwzJYUpdzn7rkNmybzo8o6f3DcffiDgiphAd1PmrI6ksEfdXT8XgpdzDjIroXhR1u75Rx3R96CBhm0LUbI8O4QOd/lOOx/Ryq2HYZVXUHGisgWR24VXN7BMdlCJG50xOz4KYtW0XoENISgjKcnrFZLLKu8xZgAPzAN9yjy2yalZYcoLd3V1/JGs06Pn1OPz+hRNFm+AlnR7PFhxGR8wPVVIJK3u1oLGrBZXhA35zSTBwTnaJOV6YZgJXJ9ital0HtUjInP44ugv31lViaaCyqT2eZuYQ6jC1VZpjuSqZzzNmS0Jc/5URnve8bY/kSqJdwapb0Rr4n2jGtB45LzN19x8/Zrob0EhVA3xD5CblmefyOXh4d69vhn1L6h0xZNAYKzpFZXMz18yM3kRLlZi/WAF5Bu+zztlu/qgvL3B27LOawPuC2MHrJlDB4enDA7fsJOcO5WhQxWrCbmV98RXI9qIhQAX9VaDSo104PHpnS0xk8OtZnO0OCICioBj8WQc16yWr5j4C9/T8bfeiHvriOtWFy/Ng6jhC0QdcSk2+Shg+Nj0wxUhOkp9fiYqG4b11RNeJScequBJ5rHcl+M/NYllZYngydTSroOjx5T1weGI3iHFKg69x2envX1a3T+WuivQW5A5nd+FvbbLXY/sti9TvktNxAvhHgj3c0bJK+pfKYOYmVw3uhuowam0xPGx2flWo0hSvaMEz8wqznAJeJ6Lim2lnQm1qtenBKC3U9MSs4Vx2fPoTlWcm2ghni2uZPCVj3uDLEdpLqN2e97EXJ7a/XZyF28KNACa2ADLIEbPCu8bAxOzC3+cErTNDgpXQALwuSRUr+sBCLd+griiqHt6O39McCVPTfzd9L1S0JwW9jUQg9Wq5vEk/2YwwefgGuUqJaLgiP4eu+YH/LOb59VcSRcyUkpdRaDhyrY9cYV9DcGo8Y5VsK0tHmJa3N7ZhPGTWWsVcPRS1vMlHtSakE61qtLSEspGadFrxQrr5DTfOiKPxiG+r3H7mm4Quqyr9ztt+5c999jZJcsT0QKTe4HkE299Xfe3vvQT+A9o7+sae0cpAaqU+AI5Aw4Bf8U/ANojtl2sd5uyMCqTSw3SpSGJGPaHFjHwCYH1n2xgveV1T0RrdvD7f0EpMh1h8M5BWnRxRvJ/crA1FgY5ZyxBIg4nK8ZjY/Ah3J7htdaaKljef1S3r36kvnbr+zn4gUXF69Yb24MdnfBfsSqTbiPC+PWpDvLacFQghSV2Gdi3P0Mme373vmQGPfDyvz7EB6bq6YZk/pM6uMt+bGYv2X+6ivbbxJxrkfTAtENsIH+ipdf/o3ML7/D6YqR6XJjpUyABupqyvHZM7Yhm1xA5FvA+D3X+D23FQQp3hfge9DEar2gqmecnjxm8fI34GTr8gMFzrDMvW5xTuxuwFtnKs328JMoEUdojmF8CjeRw6NTkgNxga5PeNdgLT2UdnUJmytBt3xCtmDuesR7ELjZlxva1QWpbxEmZkmRTYEKoJlmMrYex70ynp3h3MTIE2xrGu+ZCH27RNuleW0aS5nb3uRuN+o+fDwowgLP+6lOpqdkvHVn80IfO4I4JPc0VeLFxTel/KM1WPruRrxvY973ELVnVDVs+iXkWvrNpYaDEeojGr3Fn8Shzni/j08esX71JbhgzG+i7+M4YsdlM2dxc87Jw6fUBPqSZOPC0KxFSOoYTR/w4NnPefe7vzWhWAl0q52ASYNS3/GCbxOBdO953mmxektWuXKtqaReyv6GdXhfG1RJ4NknPyVrTd86YFSYrbzJ6dxRuwBxRXtzPeys96Z1C+2SYL1gvbxmcpCpXE2bi1enVjIDFX1cc3TyiOtHz+heLcl9C+JIdyHse4fdd7oFsPui2OPWcQOT95baaUavc4IrqFlS8PWY2Am4Sh8/+wWx82SpETUyI1SI2luVhbNk0MXNO8hDVclOn+2eht+9ohaiGnQ9P8r7+f47H46QJW8rEnbVJcMoaJTFMyzeLhgU+/uOWwaL3IGo73hDAtsGUXsek+B2bjh706IVuANcc6y/+JN/h3NTCCPWfQLxqC6R/i1f/fbfC6m3BMRC2evdCFdNcG6EhCmaHLltjYsCZ/shAHeX8Ht/7wO3O1doPzZbeU+bDJGKMRL6ZFWiwaOSLCgrgaRC1YyxnJe0awEq2F5qb7j+5h/kunmp5BG4EfLgU0JVUdUTI8TKVoPuXWn1eQfl0Vv7wBLtoMDozls4dzuspbVq3kHt22oc89h/XBw9815OSOHAcBKMHtiZaEM71osrzt98A5trudUVMxYzIESS9uTNnNcvfssjCRyczUilZNnuB4TA6dlzbt691rR8LeZctDv6je0zHBLk9nAWvd/ZdFbqk0pzlgzas1wu2ax7JtMTGM3UmtTvvK7teRDol7K6ObcEiVQskBJnzQjiGkazR0ClB0cnRvUaoMtqpDI4vDjmV28hrcvlShHBt57tbq73X5YI/Y2sb27wRYmaVWdeZUap6pHF0f1YDw4f0SeHOn/b7tHMYn5NyZqyOP19cPB7ZQyy9aBwyvjkAaPmkJTMaoy5J8YO5zyVD+S0ZjV/C7QljcUIIocuftuQ2CDt7vwMn/FaVGRaIWygu+bt62/I3QrNfWHxNRpCJ4GY4GB2gkyP1OhlC7yrlpOTc1kuKZri1Mi7N6/JqSc4M7Bi6izDdtvJqUFkzOnpc6tJpypCv0yV3G+H3HJmlN1c3zUsBHCe7IS8ZUGLqHaIdAiRTKaNvQmZyZTZ0SkxCZGAC2PLXNbS+1hBSPSbJTc358U7v72RbyOmVhJ3dXlOu1ltLf9cGrFkIDtPoqJqJjx4+NSShooStAqSYeyUwL6Stnf2vLzC1kfJQ1HxWzYEV9Bvm6uI5DT0ZAFVYpfMyp8ccXL2iE2HtZR0Feq8gcNOkNqBh5hblqsrGJrOvIeMOXbS5X2E4T/PKL7eXW9f7Fby9hocqOyobIcuNL/v2GfPu7UA3fs/uqtk2F2mNUYZ2hO77fMBw4SFHD31+BQ3PkXGD5DRY2T6hHr6lIOjR5Drkntb7tU3KNaTYLXuWLY9fTICrZixPu0SjU2zyIuhv4jsy5Fbs5z3ftKeOnYWw3cAxnNhlaIVzlcFicrgS0WSr2x9urztwqIZQuXNEchLWF+KhbM6Hp7OmM7G+LpCXGWOTlY0Ycjwj3lEYnSxUhr5DImURnOdCmvcP3V8KP/AiKRiZ0hFU9fG0Z8iN/N3bC5eYo4f7CU/mekbQVMLPtKev5LzNy9YXF8gqcdpphJHLbY2JtNjTh89Azeyb7tSLS3v++bvgUf3CFcnQ2rKEKyTBIsbubqa492I0fjAHmK5WucNaoEiuKXn8uoNImqJcWIeN25Iq6iZzU7x02PG0xk4sdrCUodmoVnh8vI1O8qz+5Iaijcs+/8uo18zv7reGoqeEj8hIWJMa9ODQxjPmB2c0UfMWhR2Qjpnrq6usIryUl95n3H3nnGxV3UscHr2mFBN6WM2yB8sZpSM/Ob68g2012IKfef/yPaupShMv13Asu3J5ct/Q160XbcngkQWF69xknCSGNUVdW3wFs6bB1c1nJ6cMZDgDMaxWYulqkB3ZDrx8q3ML69IKRXa1OE+LcCj6shUuGrCw+c/M6Op8OJT+t0wGIrvL72taNy1nGHPWiuKzQdwji2PfxH8doWKDtKrDjz+/DPNIZDE4erGsn7Lcx4IjISO9eqCePNOkPYD17b/UDKb83dcX19u+e8HmM8MJgFfs4nK7PiE0+efKZVlKn/osPtjq0aGRCrBMEznDGP3wbw5tnTae3OV9wCWbLHwuuHR0yeWFeyt7j47IblMlET2GSql0yUXN69hfSNIb13dGFSXlkvx3O4AZ1efpXjU/xkcZDvpwDE/LM0hv2LPmNDSCEfDVqBz69r+qWNPG+6tBTvrEFQbfvaUebl3R8ARtp+8re4j1B5cQmto6VjTs/YdfaOk2tH1Cbpk95eH+/TEmIh9ZjKZEUKw1qGhxnkYjWoYlWoKDQQqKio8YfszdO+TrVrY1/r2YyRWnpg9+0IsE0CqQhnbWx6LF/oYtyWhuOK9ldJBh1IF8NIjrkUa4ZOffMKD01Mmo4Yh1Od9gzEfOktf+QGjzPuq8Oobc1pKmZzVfie2zsUtnoy9OPoPDTMkBy9qGM6SjtV6QFQu4NQS4oITgmToFmIuuTBkGIe9Pk22YqzKYPXqhZy/fEHaLKmyEYEFEXx2aHacnj6FemQ7O9RFD29V5Hsuh3zPmnctFR1h2/PDspk2zFeXqOupRw1IeSB5eHRFGBchuVrOcdKS06Y8aNk+BPGBajxjdPAACWOcC6YggpjxkNZUdLBcyoBdKKb8bsVXszUr2S28PThTI6vVO0TWZNYgPQRL91BfgRvTjB9Cc0w1mtKnvL0+j+A1orqmW74DeoZyGAf3sBgNGwKGZBCbZwEZMzl4gIQRvQrqa/DOmI7yEtKKi4vvoPQ8NjRj/6eQouj7PxZJHtjkBgIVm7FA2VebpaRuSbte0LdLNpsNiCdhXmSiYXb80DLxSzOYvEVdMs7tNgZVDRq5uPiO2F1RubZsWPNU+pTZxERyFR3Cw2efMzl6BDoGf2DH33oqbjt32yYcew0Q7gVuB9g1tYaaDEl53m87kJnWCeAqZHaqT5//gi471Fc4H1hvNjsyIVG8i6AtsV0YedGWQGXvtLeuAfNE0kLWi7dWhiIdQbKtnFyRouJ9RdslQnPI42c/ozl4pMgUZMSOGKSsnbI9tz7h1nDY26QDW4i6bZgBLJae2G3wjPUbS4C4CTBmcvqJPnj0E7oUcNUExBXbIqK5xYnlcfSbFTcXF1ZCuScxhqszpV6qAkpAwDzoolS3U+SK+WsezbZPevFcQe6ErG7D1bfmeh/q3hJ+mBlmW99Zq9yBEXDvGt4/xnCeoW1yeaNA98kpyVlJlW5f99tjGMC5P9ty+7hbL3ivnSbvOWzWsbHvzGn3HlcHpDLK3k2MxvdBQWRcuYkcEYHQGGW0xYsN9XS+YXJwyvTsiRJGpGIOGyjrS8jR6Ed3bXn3xrDePOAydfBsPU2aEoM2r7iLSlLruicu0WtrynJLTAI+TIFA10f6lLdhpsnkQB8+ekbqlc1yw2Z5bfz2rqfymVDO8d46uDOsdC6+FyMfjBwnYWtg2+f/MQpdTQcV49Sem629IV8jOGuItYk9i26Dazyj2XTn7WWDrnO20mqKDe4EcrI2vOiG5cU3pP7KYuzaG4eRetrkaKYHpsilGPLl0vZ/7yijEsptFb8/nHv2VE9+8ktt2wafqlLvtWH53d/KKr7j0599zsOnnyt6BnqMyBTBUwGaLVVf5+eyvH7BwTTSaUfy3kSCGMxXjSc8+PTndEzIyVOpN9avvGDk58zffQF9NuipCiTp6a2LrlmZec8qL8IksbdxnNIvvpE+fkdV3ZD9EgnGR9zriFUcc3j2cz7/xb9jsWxxwaMxE9QhCYJEFvNvoX8jhA1IRKjIGqwt4V0oeJBXAbL0uNpDnjJ98EuV6ojXVyvGJw9YdpGYhXW/ZDJRUr5gffmd4JSaeojQUYxVu5U9wT4AeXY65S6T+NbWIVjykAjfff07TmdjunbFeNzQq5LciHUekcOU6clTGB8axumKcnS2YGIuG1Y8tB0Qad/+Rrr1CzRdQFqUUEqFUhs1ZwWxcmzwPP3sTzn+/F8r3QzaGfiyXqQp11uSj1zezmGSXbX/Vrmxkzs7AWvGhOQAqQYa0CkwYfTop/qzX/x3rPsxmRlZRqxbq19NSWnq2pAL7VndvOHd669BjXHw9nzuFMVgyNU+ATcsXv+NrG9+R5VvkL6lZoLXCXVpSFFVhyzXDnVnfP7L/xuHT/5M0aLUJRQs9n1bbYs8DTiFBf/sR9NWAboglizj9n48aDMlMSPrAePHf6aPPv1Lkn9AkkP6XOHqhj5uEGmpQ4d0S+rYEbqe/vW74iUFs52w52EMXpZFXtFRlRJOVSupyq42aBalE+M6yAnrC11g5pSsbwNSbfeMyE5vGc4UzPDL3roWKlT4rTc+JDSqU5LLJBdJhUBHJVtfaed2wrWEooJCVRQW6hDXbBscaa4RGZOd0EuP1kL2HvoK0gGiDZUO5khR1kPz+kLp6lAqHdKFLfZsibT7oILtL3BQT7TtM941xC4gqSZ1Hu9mtOoNAdVkWK20oBuQjkRnJClVNXTgJCaPNGdMHv457vSXiquJUOiPBlyhKsjB0Ge+pmLPwB72WIAUr6noIVeE0ZlWVSBU0CfFuzF1OKLdZGJa0tSJdmWd16qmgejIGxAdW46GYIHxesLo+DlRD8mx4aCZMnYt03pFJRe8+OZv6Nc35JhQcQwtuvWWYt+5ObZuCrslagQ1ORX+kEJAlG3yt7A8fCApbs+FkmwdOl1CvaNXDFnNjpACIQrkhATlOi2Rw4aVA5pRIYRpiqwq3JTms9NG6ItBmrobYAHdS/n2q//IaLRhE+dI09C5hp5AGx3NdGbrZd1DsiYrroRhbG4yKnEPTXof7QNwnQbGhw+AhtpPMOhuDe6aLl4jruLo8BNgrLgpfW+duDK9Nb0v5Ant6hKn1nc7qW1KzdY1iTCCMEX8DE9jtqMKXjNel5YQV7z/IclgiPG4Ynn6LYRUHksRiOZcOsgtq+UFaFeIOChx8hrVMYkxyshYzijnwlntbY6s1pdY2YrFE03Y+61QkYKsvGdMCkZHKmNOTj5BZEQzntB2lgXtnKMOArpkOX9tWZG5u1XItWfwbhdhET9FsRk8NozMzpix1jliXM/a0S0u2SwvaUIm9pstGYzzFdk1tEk5fDRULxiT2hBBAQsPCLrzpPKSd6++IHfXTCeOvmuRrExmUzYx0mpFZExkitTHHD/8CSef/bnSnCrRF6avcv0F2Rjo2XfPIeBcsF7SUgSlmjweVzvHLqVkLHnOg9QgNZNHP9Gjk8/An5DSGNUGxaz2UDnq4FkvLwlE6ipz8fZb0vLCtt49RbCDtwkmx7u2VH/EK968/HscK0YVxHWLZCGlSJc6upRIuSYyxtcPOX30C04//9eKOwIZg9a3zjMotwL1mKWfs/VeJ1MLNOIYBcsmyVHtkewJZHDk3kM4pnn0Uz169BlSzVhHoUsO5xv6PlGH2nI0UizXfsP87Qvobsoa2jaKRdnxI94bNFBTxFtoXoy50YmCJlLssLar+2753jovnu2AOJFv5xXknAqkqCU2OlxRQexuQeN7gWOESnbhGzshJdxkrZlTtrVrSXWWwR2HNeAbIOAlbKHzgTzRqnYGxHBo9WqfyaX/gmK+0NDu1uTisEll2wUMVbw4a6sp2028N7+3Y7quxLYtZ0JLZ7SKevyIkyc/Nc/OWxmiFibFTAf0xVdPQETpCMgtI1mj1cWbpB1T1cdUTWMomHhysp4R3ntEerzraVcrQOnXGzy5zLeipWMlkmFUU9UjqhBovGNzc470l/TLF7z59q/ZXHwNdNTNDzG57Y97cbzfexiBnpLUsvCDb0wWSZGsmsEprq7IwdGLdTdhNFVK3tEtR2vriRQZQgJa0CWarmW1vsQHI+fKzpFdRR5CBjlvjdIhZLJ16YT3f+4ZgW7EbHwC3tG6rmywDKyYn7/mePRz6tEBNDX074AWcZmUQCQaFI6wvLwmP4kEbdAESUyhIyCqeGdxply0kVlSkdxHbuaXbLPBygYfEu+Hkff+vx3lxpMaf/r8fMHhiZU6iAsENbhTJBULoEAxbkhksX/H2DO/utoeEy3KW813NEEzbFR22Nrwbxz1waGeHB3T5siomrDsljRBIK9xuaPvFrw7f2nWN5FYttF2mZZjGuguRdzl8tfdeWBrEQIEDykahz3dQpaLS509OGbVtQgNog7vzNvICR4/+oT5yy+gb0GgL96RKc60vSdyC6J056/k4vBUH40mjOoZfV4T+xrvKlJXEdwMaEl5w2TaMH4e8D7x7sUSug14aEY1Gnu6Lu0nBBcF4az1JB4TPrsw8qa3m/WVWA/0WB7QyOPqQ332+Z+g/hDvxyRtQKqylHocCc0ddUgIPdcXL5hfvBLo8T4Xzvb9YSbk3eGCJ8dMd34uFyev9OzRASE4urwwI10yijcPSiucVDSTpzysZyyXUdvlG1i/FXC4OlOFSOwTOUIt6Zas2q55tdc1G2g/6Hw3JFoJxFxBOGDy6FN9+ORTJgen9Dj6viswc01O1vkp9T0jP6J2a15efMviza8EKSVulPrdvVu3cIDf8rpnyoITRfsWYofzG5Aa5yt8Billjt5FnM9bVALZKchtwpg6Uy8BNPaWm5MGZW9/1IEd7K/WvlQ0IWJr2Rduc0rW/74RkiwOAc7hRRG6Ih6j1RW7iGgi9huSay3/IBuJ0aDQs9oaHMowByhWoNQHm/zJ2BoYwhImaspkOgcuIWp8boGWJCZ5nG5w0mIe/mAJmDM09GVHMll7y03yZiRoFupqxvjkIfz0cz1/9RthuUC1h6qw9SqIj1TOZFcqiMqg0DWbotioA2pojvXw7CmhmpFyQakKbGOlWonctayWc5vXrBbm04GzY2/DbK65ufqaII6+y7z+8h9gmvENpPlSkAPVeIOmMUJVQih/mOEL8YsmcD5Y2DEZ54OIEQBJGhjoICeh9jVPP/uMd1+u6NvVbTm+HSZLtrk/ZGhbLi/ecfLkEW3s8NTbkjs3UPP+niOEVqFVRicPdHP5jZlwNRAzizffyc3BQkeTE0bHp2zevgFXYk4BciqtJV2iv76gX68J9ZSoBvxkhJQVkUAmDboaLTa4F6XdLOhvLmWArMiWoLdPYjOk699SfkUBWqMTe289v4GYCMETS4N5EbezDgajQbTAFyYw2m4Di/ng75ckY/usA6SE7/sM1qykwpGs81xWoOH49AnON8Q24n3Ga8Jh9eB1yGzWPWlzA3UFmxb1znZ+MemlJH5pcWPy4HuL27LQWdjY/PXh2XcoGnt7IGIexMW7V8xOnqJZca5DXI0jE2NH5Svqesr46LGu324EZyUZmq0qwDtfeghnQlXR90b4fv7dl+LqkT759C+Yr1sWN285PH7GcqXgasvoTx0bdYyqQ04f/ZTReKxvvvuCbnkh7XJlD02E0FgpSOoHopDhYe8UbMbKVM22CCVxxxksNTnk4ZNP9PTsU6Q5o09j45T3NU4dKeVimGRIS06Ox1y//ZZvfve35pVKX7L199eTwbM23PZanIPcJ3tNIu+++QLvpzx8/AvSpgOxJDxxDidCzJ6cEl4rvJ/ysz/5t8yvXvD25W+0u/xWcr+h7RM4RxWE1KctmrutGNfdWhdAXMBRkXOiH3Bd9bjmkAef/bk2h48YTQ9IEsgKoapI2ZP6iBdP7CIVQu0d65tL3r75EnQOVYbOYnO3rH4FxdGpxWTNU1VDRbAyp9o7XF2zTD2IWtMgIt6pkZGQ96CnAhkOm3i/LicWRC5tT13+yPRFCLiyRkyxmzJ3ZKtFrwR6kzfBVaWToBgKlB1kRyLhXMZ7xbkESZGYwSWautREB4HKIcmcDlcgg/4WKqIDHfoeqrYf88zve08pQVDrBikZddkqNLYNmgrFtA5SrrZwlvqSk5BBjXrZhxqXhBwz2YELY55++q8Qgr779iuhXZgR7qIx0WmZ3lsIIOZMSSBLAzlAmHL67BccP3xOlopNF03BOIfGDU56glPeXb2G1Vy220PAmiIxdMC2k/Qblq+/keXFNSHUSroSlkvSRiFXUI/o+xsm8oAPGdH/XGOLkuiQo2WEN6IJ54ciaFuvqU+Wf+trDh4+5Orb32jffshXphidQ9YTEDvmV+c8eKpo7HC+QhCSCr5qhi/9KDDiQxQMQVcLltdznjz7BV9evTHYGWye25arqzd8cvSA04fP+W7+SmnnknKBTTOgLU4rcr+Q9eKdHpwdoZIZyAgypVkKipZGD1by5Ahkrm4uzDqWHSR72yPdf+D3JQMYc1QqNcNxs6GeWdvRiJUcUKyrrcdAstpmteYC7WpuiVdet5OV1KglVQxKM4FjcRONviSjgMUJRxyfPaOLCjiyRuqqIpUEiNi1zK8vYWGMQDhHvyVVKOC6Ct47UrQmI+K0oAJ7yX/WQqmgGLukIRseRmPohPXNNanvmNQzNn1HVdfEPpFiZjIa07ctJ2fPWb97A1gcaWjk0SdDCIxcZKDXBNo5715+xWx2ymjykDwKkOdIULJUNlcuWTyrb6irRxyenTAZf8Ll+Xf69tXvrJxFW+KmK0LSoESwPiA4O1scENah21Q2nmxGhzo5ecLJ6RMODk+pRkfcdA0pV+AErx4RxUtCJFK5Hu8j3eIVb199AatzgTWuTsYwDPdCV7tQTy79jDEYRDNsruX89Zc6mxwwnp6yysYWZzBsMMHmjH444xBXc/DwCdPDKVfnD/Xd62/J83MhZfrUgmxIstdoY1jqRSBvcXCyrTUCfnLA6dlDnRw/ojn6FKopGaHtE1kEHyqcMy6ISsCJMq6EdnXJN1//Cm5eCbLcITHbkzOAJKAVaLUT0ihDZDn12ZoHhWgcFmoImHeyZSBM2+dXl73d753Pjm37vbPfQSAmVDxIBa42msxBwA3dsLYJe8PDisUAEbpsPOwmPIuQEkdfQj6h1JSlnHBSjuEyfWwteS1Zg6BIQQoSJRa+l+NQkuK0JEOhdVlDmdKsvfzb272rp0yGyaJCuiVkvEvl/ofnPyTnVDpk81sXutJ0yjlELHSTopDzBK8zjk9PEf9zPX/zO/LFtwJzYE3GWOTElZwCFXKEPNSTqoPmVMcPf8LR45/gxqdsNna5wRmymSUS6HDacfn2tc23s7anQyNEynwNr9mxe+iuSB0CG0YTx6YvCPBmIVfv3urh8adYCe0fbvS5PA/xoIGci4HtM84rUYHg8L0DrfC5R7Wl37SsV4sPKvP94ShLQCLx5kpSt1EnsZBl2SFGoxE3Q6JJQWrvFU4/MELO1zK/aPSnn/5b5PQT1av50DoNSNzMv0Pcz5gdnPLJz/8VL/7uJYhl6eHAZ9C8BBzzy1ccnn2CSGPesWtMmTpF8lAYlSzRTCzjeH51zjapZIAZ010LJO9v4d0YvHRK8wpdy838nZ7NjinFa7vEmm17vF2bSY+iuWNx9Y4tAXw57wDHDpauCYcJMDEFI8WMd7UyPaaePCiZxSNUretc6q2tXtt3LFf91sPBNSbgUHxTk1ZL0DzsexAjRMgDDF9+O5E978ZZ/IzBNHbQ26Jk2XN5ecP4YISrDqG0PFUCWRxt9EwOn8LBU2V9DrRCtEYRwasRBZHJMRO8lK5kLbq8kK+++Fv9yZ/+JQ9PPuHVxQXVaELKHYonhApPRU6Jdcp49YzGY558esajJz9lfvVaz998xer6HaS1uKDk/tqqYpNY9j2uQCSNJQtVE/WjQ2ZHjzg8fcxkcozzI7oINwuHCxOyNxqimDNeEsEVZU6L0xW/+fX/web8W6HK0PfkPhOqW71H7llYOyOyqSvaZJ4dIsSrl/Lia9HPf/GX+OqM2lkJX8qJrKUpi7Ma8LbtGNeB8eSUR5MDTk+fc3V5rtfvLlgvz0GuBF2Xiynd5SjP05dSJqmgHhNmp3p0+ICDwwfMZoe4asKiVYQGY05IxBSKaQGVZDSvCa4nxyVvX/+azZuvBWmpxxXdqi+g4A4YNLCkAjeCago9ZshoslwYDYofkZMntQnXhK3JqUgxfgWlgTBVUrY0dy2ReQ3FeKzt31S2/zOm+NWZkkw1q9Zon/JQezw8F5UdTLt9iDVVOKaPtUIWvKglZHgkjAy4z4nKDZLBIdT0fSTFAG4GVVY6EdzYyBn63s6Xi0Lfek95z9AK5belpu2Ir4oxWk2UagrSkLKQxYJp4txeWeww+277oxg1tXNDno/F4CUbahdKVvSq9YzqY07OjglhxnxyrOvlS/LqjdDNwVviWJco+6sCAr6a4JtjPXj+p4yPHyOjGavesI+qquxeUqJxEVLH5uYd8eaioF5GqtR3FkPf5kakwqdhwRQDXNmgRNKmPG4yqOCkNsqP5kN78J9hqOUXFb5Uo6VFCHXAqXFu3KzWNNMDVGtqHwgENGbevn3N7djh+2PIg3IDEKsKfct6eU19cAo5lXnyjEZj+9KQrf8hhf4D3nvwLFjeqFxfzvXZ05/yYvUGNufbY2t7Lpdvv9Tx7HOOTz/nxeyZsvrOcC7snKIKvmNx+Z3k9OdKGJGjUa/mgSSjdAFzYuUgmiJdt2C9uJAtH56mrRG8LU16707u89LBKEGF6+uXnD1+ZpCReJKYIZG3cHuxsF1CciT112yuXpv3PkB8Ra+LKy1XfcVo+oij00+1rs7ooiNpxIeaVUo040PWOgZfk6lBArnPIBWhEpyHg6On9M1MSZ0lycVE5TLaXfP6y18XrF+3z1DL3/vPz2JzhgJ4X+PHxzz6/E90HY26NSWlCiNWnXJ4/Jh6fMymd/TRYHFXVazaDj86RbXn5NmfkfMKoVenHSOfqXPLzeUrbi6+k5zWlkRksQ00bsiXL+XNNxP1GWrXQB4hGixEIRYeUe+ImumjklKBQ2XC7OynHJ58Rt+uWN3MdbOa03dLYuroSttDfKAZTWjGE0LdUE8OqJsJVTUju8A6CTk6vK9oxiPaKCWzpbMYokaC69C4oG2vuL74js27r4W8xjndGmo/1BRif+SMkXgQTGKlJZvzr+Vl0+jJk39FNRIkNHQp0RdhrliizeTolHazYL1sCQiT+ozTx485OulpuyXXNy81phVdt6GPHTmVfeUdIoHDo1PE19RhSjM+ZNQcggSWPbSbHl+Dy4JKgzgrXHJFOYq0BFmxXp8zP/+S67e/FWQFmkibVJZbaaYzrLUSU/VHj/T09DniGmIGJBOqhi46muaI0fSUNjpy4Z8Q5yy0WoiZxocPmT3PjEeolJa3NopCL9Uq2fVk3RCcJa750n64jRXjg0ckGjJW5uU0oESyBAvJiGWIe+84PvpcD44+Q/OUdVZNHlJItJrwzYTsrA2oVwFXIUDKHuca6skph89MeelmroFEVXk2fYevG7I4FM9+IxlLfC8yZfDOZcfhkAkonj552k7xo2P6Xqyvuy+EQdrhhwoHcQXGH45XwmxZca7c+5Cb5zzizcmRqqZD8BIYHT9kfDih3zxkcf1aF4t39NfXJXalVihdzTiYnXF89JAwO0IPjpHRjK7t6PvIuG7MOO7XNF7xdHTtFS+/+R30Kxkcr1ySiobW77sUDDMazGBKNFij11hKuggTZPJAnz//JYQZaZvZ/IcazhKgs+XxWMgos2nX3Cyu2XSZajxFVK3SJCs5Rt69fc2giwbe+fvuYwinOS3sk9noxR8dJPrcg3jEeZrRxJ4rfpsLcjd/6seMUPnIOt3w6sVv+dN/9W95NTrT1COkucVu04pX3/wDD58f0DSPOXn4Sy6/WoHMLV4DIAbp9P26QPCRrA4RLaxa5iG7gdBAEyluyO0CuiV1I3Stxba9lLraPWV2q+7u7qRtcwky0NMvzyXrRoUx4iyWmqTQtwxp6pJLr+WI9gto5yKVdbrZj+aId0M2CX50qIcPnjObPWfTO2ssUDccidU9LxctdTOi7xTvHV0XGU9qNpuE9w3jySMePnhO37XUdU2/WTPyCdm85vzNS+Lqxu5tj7rw1hiUeYEtXahpRgc6Of6MICOaqqZbb5hNDliuWnAN6w24qqHd2LWICPN1y4OzE9bLOQcPPgNv5VyenrFEpL+h6yLXF+ckepz2hobmkrjjaxYvfidfrjf6F//mf2DZ9VShJiF0bU8k4quaUFdQ1aYsCvowQJ2uGTFrzpiRrW3vnjW6feZi4GqoTaF0CqhHvEfEEXOm63ujkSwMcpI7vGtx2tJtLllevebdb/9GqBXajtwlgnfEQkrhw/t00nft4roa0fUb+3vk6do1ljeizL/9e6mrqR6cPKeZHlrfbvV4Z/m/PZn5oqWqxjSzA1xObPqMxEzwDaPDY0YHZ6galWzOnYVYhtar4umSUSQ7X4PWdNGRC5Wrb5RQJ2LsDQl1jtoHE6GpRfMNVdhwcf0l19/8taBrqlFFv46QYFI1tL3tYecGZNhDGHN0/JjHz39B1Nr0gVOqqmG96dFUk/2YzaZFfCCrkNVbFzvA+8D0aMJ4doyT0vhlH3nYeumgLpFSi221SPAeRQid0EyP6WNdlKNlW2QsrGL0T3acRKCZPuD4wefgTxllJbpEDha/bvuE1BPSpkXxVvERbT22feKgmTI9esrRpCa2CwKJug70fW/hE3EFz9tVP7iSCrUlbhn44MvrKgIaCPWYq+s11eiENrVoVlSNCz2pR0rb1Z37b6ZKlqwq0PaJqh7hgtXS5yg4Mqo9STNZrGQwu0xdVQgTKqecjKc8dD9DS2kXGUQqhBonFgroqWixNrF9TqUbnKfbLEjtkvqgJm0WdMtL1m++kS3rgWZitErAVNBBS+5yluiF2xqK21wUQIJsKSWa8RGLVqFyd+HYf7aRBZy4wgCXSbmzig2NbNoVV5fnTI9OaZqKFD1eldx1pM0GVksryfkhPnq41VwIMu16aSGfnFBnScFV1YALqLrfy74JWcwj2Nx8K8vrZ/rZsz/jd7/+lSJRrI9uhnYjb3/z73HVf6fPnv85Tho9/83/LCKJPFjeBQK7vLzk+PED6rrwXpcSiJQizgdi19IEI1v53RffApGuLR3H8i4uMyTF/dCjjmJ5UiSKVdFyffmOh08fMN90ZAmEZmQ16X2PDxVBAlkjTltevfiK0CixXW2B1u22igbH4GuWXWZ08IiLJYxGh0TXs2w7XF0bxN4cEHFIZRZWGI2sCVYYm+ipxmx6RWRqmdvugF4XBD0nrlvTLqV1rFQOLbShLlAYkbC4XRl91zP2Y6I7InJAjhnnZyw3gBtbUp0LJPWEUaDLhos10ymLTY9zY5JkYu6pmwNyWpPjioMmQHVATI4QpqS4sMxlhEiCtAEZ08/P5T/9z/+T/uwv/gd801BXtdWUZuvShAvgA23XISKkkjTmCiRpySiOnEN5xrezQYaQQ8RtjTbVIXZpyis4RybSxw2eDU2VaaRjffOWN9/+htWrrwVaq6kv2dxxj3o5lcKAu0P3TMiu77avd20xYMklqaLj3Zf/XtbLV/ro6c+YHj4m6oy27xHxjMKMqGbUtp0d0TtnHbjIxL5nJCMsfTTj6EmUNqbJqAK8q0vs0zwZ5wJOjRM7JcXlQBDotSd3azR4qhDB3dB3F/zd3/yv0F0IsgCN1tfZeVz2VtImNZ125O1cWCJZjIEuBZKMSM6jooaGhDH4ilUvSD1DyYjPFqVTEB9IkmhztrjkVpnv54yErbwwG8JCYIRMN2QwjAJt8mStEF9jhayK81bvnHK20FU1hW5NDgdofcS6a8i+IYfIOi0sWS9k2g7CaErMHslifA/BU1eZdd8RRmPWOZWQWbRkOO+Ld15arA5Jk0K5J4dsyTCsPMy89LKOROi6QDUeM18pyAi87EpOZYRqA8lb+CyuMSKjFh9KEms9ZtNZGWPtAqIJTR3iNzhfkkqdJ+do8kWTGXsuWPeuwgFk8tSMIhFv+wtL4pS+MEHmRN+taHxiNHbEzSXX777j3esvscZIfXGSClWzK8C6GA2rYnvZ8AzbJ6kEZLJC6hVcw/jsCfN1TxjNSOz21x9i5Ky07YrD8RSXlH4zJ4Satt3A69cc/eTnAKSuRzTy9PSI/+V//B8LUjcYcHtK/R6FlRTqAOsEBEd7c41zihchD/z2KPXBgXYXF6KYURzTLiH8/rFL3h1G6BLmIccbfveb/yR/+e/+X+r/9N/ym//0/7EYXmwhWVeqm6uXnD74jAcPPmPx+ktt5514vyblRCr0hW9fvmF69BNoslnS0TLKc8wGB6oz2KLryd1AvfnDVs575PnD5MleRnwCsrJZrSFHmmpGp56UFHUGU0vJgPaAz0LcLMj9AmvHWBIYhskaAkEK9Jlll2mTGAeEePLQ0324flXLvOW2l5cQnPoSEx/ecYgGtC84WkqWGIQhG1tPtfwhJfa2mw/bSCkHojR41a3S206PWn16KjS49j07hs9moSZXWf/s3uF6xafMulOgJsaSmV4mwa7Ach+IAnnOb//233P65Cc8evIJB+MDOhybmOk2GzNwfIUWfmLzXTLeYaVHIkRx6LbNoZ1n30btk/Uvd8UgEF9grxxJMZJ1w6Rx1F7p1te8fvuCyzdfozfvTJnrwKWWy3MZihWH7OS4e1jb+OcHsm73chxK+h5oYvnmC3mxutGThz/h9NHPOZg+pI2JZbvAVyMclqCWs0H+UghKfAjE3vZE3lK/WvgiOzuHuNJJKlOMEsuKVrE+0Ztlh4hSB6EaO2rf0m4uefP6C67e/g66ayGu2O9iaEareVBRd6yIiJjDIc4MB6nZJIjiLdFVLH7tVJGs5Tp2aatpO8fmQVtq4ZA9O4xh3ofdtqcQ92rMjZhKSJpxhWAlZCFLZ8QyqaNLEctaqmh7ZdUqqz5aJYlzZK3JyUovJSecelJpTpOGEjzJZMlYH+pkcPrePrN1aWpJRd/L7XG53u2RLcI2zIjdZ97O+PC6MZM5VSrXGFqRyv1nq5xAOqpaaIE6BBpXUYlnvV5xdfmCFG44OD6kbg7R5BF1VHVNFcaAo+0TXd9bm1EyUgyJob+FWXDDTvAEsex7lzs0bdB4Q+4XvH31O/LiXLZ5Tntzk5LuwoRq+eCDNDQDNZV8gSEpzFgdp0fHEAzB+kOPuq7x3qF9y8lhzfx6juYN87/7G3jwkJQUqT21N9k4v3wFq0uMUSfe73Fu1/bu/rYhB4DYGW10irgKQ1nUiNh+1NhXA7f/SVCFrrMHk1ZvePHi15w9+gmjBz/VzcUrRvVC0I5NXLN6+zs5P3muZ0ePOHvwGd/Nr+2h0gEjcGNYJ2qpqOsJ6xxIriFqJuGoxDLftW9ZXl+j11fcnRG97yqHN+TuB8tvBaQufaAd8/MLVg+XVOMp3jn6rsdXI0KoiHFjoRwR4mpDv74Wsr1mdZV7pUuaLe4kDqqKEAKj0QjjFs9Uzu1lmdvGl4EYZk9xA0guGbvI9hwBtyOOQLfae0v2Iru37BQ7/GAguvHe48XgIKd2XldCEFm01NEa8UWWHfzjHVZuhSsIsnFKeS9G9+sslknaiSdbouaZmpzLQlpy8fJSu/W3HJ48YXL4mMn4mGY0oY2CuMroOrWUb6j5WhReq1Rqa3fP1m2b3YhCVdeWUR37kpxoJAw+Ky631KOeuJlzcXPJ9cVr2qvXsL4WtL+lzKUcW7dKpGw4vb3xbLzfJe2+JWnPxBRid/NW3rQd6+VKDx88Z3LwgFlzwHx1jVQVTT3CVxVJzbO20jrzZ5xALkxYtjR0l0uhhfaSQnspqRg3VmZTiyv0nSva9TnXN99xffUt6+uXwvoGMzq0wNwZV7rAYQVwu/UJpKy25mNvFSmaqP3I6JplUEI2fz7ZxstbBcZ2hnbm0D4V7O13tv/etnf1FgqTIQZd9ohqyX2IeO+KU5hJzoyYtcUmaJoxofYEF8wLdgnvFOe9KWmxFqy2CXfXm91+g54BHr7PoCuGi7JjqNSAy67UiysMob0htirD/Zl3n2VYa/YZD8RVPzxoew4uotqT4xpJA9VqJpFo6oZKWm7mr9DVC7rNGZ88/SVVOMS5itTDarUhqyNUY5p6Sp86Mxb3yJ1UohnUGhkHT79ekfqWpobpSEibNa/efs3Fi98K3QIzIgtL2V3uBsfOECuKTHXnmYOxSNNh8zM70NnJkYnr1G57afyhRkoJTT2bzZwKT+0jf/fv/zc4OmL26ClSWDiVlspnrt69hLy2YnUn7DKZPzy8lJAhAAp9LzlnTVuP1IT50CoWBkPy+8b98xaa0YS2wM2hEV5/+deyXPX653/63/PFP/wV8zf/CWFVIOENub2ANOPxk2dIavXFN/976bASzDqNG67efsex1HQ54JsJqBJcpgLqYJbwOm0MntpvP7d3z/eOrYc0/F3UjAoDNzlkWM0ldUudziIiieiUytc4iWTtGQUHqbeSubRm1xQGBm627YliZ/eVOjSuCUzo4hLV1mTQNlU1b613d8soKMqjdPwqnyx/ragrhzQV2vZsC/VLUa5ze+jDrVFyEXKHxqXV2hfFL0PLziIcJUvhxcdizSVGs4UdAUlCcBkvEdKGnIsyzH05mymYXezQoG4lG6rQrWTx5oLFxbdMzp7p6YNPmcweMaonrDbGpy8uEEojBwMqDPbrhuw03fU13i5VVaTrEE34wrBVB+NQyvSorllcvOT66hU3b1/CpvT1lgJ/6r4yv29ZFQNJ2UOA9qFhbv0tdxS9qC0NJ0LWTO7mzF9/IfOrN3r48CkHp4948OCJGbS5Y7NRUnSo1DTVhKoxJrftlYgtbBXQbJ5j5V3hpy5KIvfEbElJEuGgGpPWK5bL15yff8X68ish3gCtwYK9MmSIC6EYBzuhHBjIL8o8FMO0conaZWLcWE2/0y2CIeqQbMLHb71NyJJvK7ytQr87n8WT36NyHrj91Q0xaVvLgsNnJcfO6GJdQolI6hFd2/4ELIGzxSMkosHS2pYuhhmn3miDMSM8FyUkeXddA3nMoHL3r9fpwBOwl52uPbLtB1AMgz3Ifae2TU4J+/OUEWkReqiCklSG0q+E7UUvHeOqIqce7VoCkdFYmVWJm/Wl9N9d8uV8qccnn/DwwRMm0yNq52l7JaeO3EHlC1OeGn4iTgnO4XxJ3WyvmVWeUAt9u+Di5Rsu3r1gfflS6FcM+8lCfmm7/p04Qzf2ZfIe2mqraO9xiyXLnTx4St2MScGU6f3G0z/PcApJM6Mq0K1bZtMZf/e3fweTiic//Rmz46es1mpeet/iq47N/B0468JI7vggmmdnADLeiyFJQ0mAUMJp1p3SKK+Ny+JW8tiPuwv2HZLQbjq8N0ao1CvIisXFN3Jz/XP9/POf8m36Vq+vl0JKqPbcvP0S+pZnn/ycJ58/493F39EuVrsEObKsrl7pkyePGY1nrLs5qlbb57Ipobi5YXX5LehCPgy33/Gc9qXygPLsK8ztNozAGu2ucPkQaKkIuNyTY0b6DXWoSf2K5fULKDkEBlcXyBvz5VSK6KuUqgbfr/DVxDK/fWtEETo0n9jrVru9VlcW8nCNRaEXAehlyXp+jhuIbrftz8o97rPbbb1z7KHnDu2XjP0NlURc8UBMaOTCkWCNEnUvXCFD2KMAi01pq1kB6AaXO2rXQi3QFdKLMru7+9qLccQCFdNDWrN6cyWryy8ZHZ7peHbK4yefgpuAjIERmj0pe2K2SNvYDXS8O4UqQ39jIqGyZyG5x9GT12uWqyXz+RWr1QWby6+s7GsLawzlj5n3lfm+sh7+Hgyu8v5Wkg9hlL1Y79ar31uO6khaQhGDVdW2Mn/xjvmrwKvZsU6PTjg9e8rh4SP86IA+Kn23JrYtrqoNKSkwvAwIjqpB9Z3VcDonOIHgC4egJsT1vP7mC9aLt6xv3kJ/LbAuUGAsj64YcNsUpWEN2TLqM+XdQYpEnI/4vEE3V9RhhpOOJBlIpUrFqiqkrP0spcO9sP1tk1NgpH2fXTPbmnIp7VC1KIci6LZzLzYHDqUiUrmBSTChLhOrzJV2UNXUrsPlaxrXoi4Yj4QbWOYsvDVA/IqW2n+HZKuJN8fdWXWJ7lALCgS/S2zaM4fE6sjd0KMA3RoK23WmO68LwElBziyCTZvmuJAkxx5XpsY7kG6D9GuEROUCfWzpFobOTPyGG11bDPdqLVc337G8eqinZ084Pn7GdHRE1oaYIhoDQytqFQe5QukRBJd7Amto19xsFlxdvub64jWsLgXpwSdI0eLmW1IEe54Z2ZYu722Q3ZYa7teXULNMoDri7OwTY6YM9t7Os/9DDWWzXvD08RHfffkrVlff8a//+/87F6tA7BVKw5sQwOmGzeoc8hqk4y6yc1sRmxDPQJY9V6A4bZYP4ks/99Lr3YWC8phM+aeMAEJK2eLofYHccstv/vY/8JOf/5TPfvkTvvlyzdWrt0BPe/VG2qtr5qsbPTya0MaFKUW1DV/VFf3mLZvFt2g7QUONZMV5oY8tObfkzYLUvcVKzdLeRNwDyd1R+ML7nxWHWeQISgcErs6/pM9rJkcPSTSkWBNjRFJHcjXd+gaN78AXyCsbguy2AtCy3slA29FvXsqX6a/Uu2P6PuGrDQQlbZ17hbubGbbe0V0PxSlWVqRrUl+KNL0gqoWWMZdwRjnMwCUNFrbIia6by29+9T9rpsZla/m3ZYUqgtYaWdg5RQ0e9Fm2KEKuPX0s/PwxUrlI3y6E/qYYO7v5H6gwKTFUZe+ahhaYqYe+ZXM+l83Vd1x+/Q/QzLSenDCdnjEZnzIaz2jqKcGPyNpa+HTLa21VEFqU8vr6htRvaDdz+vWSzfqGdr1AN8sS19sAXYHiQbMp1tse1v7iuRPP3XaEuQ96hx2D3PseJuyLd4fxB0R0YNdIQpovZX7zhvnrb3DVgTbNEePpKbPJA+rxFJEacZbAKNuGAWwXuq8dqe9oN2vW6xXr5ZzlYk5c3hjrna6FuGRIynKihexluKoCgzIwju+Mll0zHAshuYIW537F9fm30vdJF70ni5CcKS2vyYzRbHM3rLO7YxdeGhQp5bkMe6QEP4r214I8DpwnwxQM7TZStGY7JGvh6YOwvLkQtIVuw9uXv5Hz87faExAfiNpSeYEUS96O0UAP3nkuBFouN6BDFn0oeQKuzF0uIWLzqLdPX3b3QzF0dqQy+yhO2fs6wPK7e6d46c458vrS6LHLPeesXJ2/o0u/YZE6RqMG+g7alhrYtDeCdoai+QBxTX+xkNcXL3g7/VJnR4+YHTygaQ5omineBUKotghY33ekviXFNevVW64vX7G4fCsWvy+KPPelVPM2ym5NUnzRS47b7DJ7C2BAUaWyNRAOmB5/qqPJKTeFLdY592F/7p9xVHXg8vw7vvvVf5T67ERHtWOsY1Zdxvkp3kFdedJiSd9dickc49nXkuW+TQHaGxlrE9P3eXjB9vfRoQ57L21N7SIDDaIpSNq9Umxv3KMfKzelzy1hbA0Ecusw0gcPTcXocMJmeYmsFhxUI3KqWGS1Sgvfm2cuUEiJQD2qAfzIsjddIWQQNa/BW1asbhb20vbB8wHBOSTa7G5h+GsIL4fakzrr20vRwSrAeGbJmVLuZzimZOg3ZfHGnWOvAU9ThI4l7GUPoYKuDaAHwIFJam4oqfXlqgbIbV9oDgpjuLed57H9vHNWSSCmLQcSiZ13UIgo9gBf54L1QW9G1gxDgIEadZ9Wc5j3fVrTPCivoQVm8WwHFivB2lXF1lr+7GXWD89JynFUlVC4o3dQHKgOTW18OY8w9KayA3j7cYpvarWM9tJjnEJ/mwBNFs4ZWqhShKdmcOatBlFi7KwkbljUeytowBJ0/419BKVwyes2S/nuZ4bneHtdbu3vYcmKlHB9AWbFU1WVlT4xLPSBcazazctIyrP3iHg1UvPdPtC2wyyDrkjWWEIh2dZvYRlzQsl+33XxDjgSEcshT7t9sV0b2N7IdlwRSocnSDTAGGP+cOCt4ccW/dhfZ8PY41HYDzXtTjgYgAM0nXf7Ys+4KDGZMtVu9919TmgfIK2p3dou34+JWpSHEywhshg2KmxlGth9+LYgGUPIZfCkffEQYEBjQIviuhuOcaAD01l5f9tbex89ZG/97N3/VkZ0VPRUZfoyI9ZYW1J8IazRISTnkEJiM7R5NuZNI64xIiJvIchqBDjFOZyrCA5rEtN3otEIY3bPJIHLbJV6mZIBjBMxUaXZodkXUTAIzmF/5dtLQp2hc6mB8WP9yS/+kvHxUzpqOixh9seUff2XHCLCbJT4m//v/xv6C6EK/Nm/+39orJ6T/Rl9rJG8RlZfs3j7K85f/CdBVyCWXjXQRtySL2rryCSgtTvNw5Jqzjj+/C/19NF/Q69TlBmSNkyrS7794n9l8eYfhO6mmOC2fuzYe6FpBdnK730yKggxdwQfiJvNjn+TSD1xdJtrNhcLi+oDm35jMKgEK2GqgNaSSrt+uJmEL/XnKlLKMbK11YsbGOoisWSJ7fd+5NhtqQHvydsXg7MchW1N7WZh2A797rNOSlw8Uzf+vfMPDHLbB57LotYIbBDLQUbYlImMu2NLEXbDxJcrtQOxExL7VlVypbuKQN7NjSulIPufNadHCs88pH7N1iWXIY2yKIPtxtoLwm9jqQ7Hzvs2rutALt2gLHPYsuGGhnrb+9k6f/b/gSvalQ1v4X8tyxlESotEbXfNULbCUkirKINC2yb9bc9VvBqlEK2IeeA5FkRl99FBVN+6VLl9uPfH3hqyWb813/v3vDPG8u6lfaU/ELA7uzbVSNe1e1C9fdcRLNGtiG42g1J2qHgzl1R333GV3a8OIYVCfVriK+LtGWjGuPilKrSzSrcV/HtraPCAB0PF7d2zmggwIZ5I2zVSNoEU5cKQBeLen9v3YMe9+9/OOeUe7+wFZYdQbBXgrlVRUL/lxa4kE2m33mNO1vTIQEcpikmLAWsMlRg3ZPncbfTpljE3XPr+vQwRr1uGHmypzmTPIBzmdgu57937VuEP5wkMxlRKww6W3fW5bKWianXiksXkk2SiFsZcVVLc7F2oM1i4vRnuhKyOTvY2TLkGR00uyInL4LyQBga1vf0/dPXVwbBRZ5BxGhwuo6rdVtlI2VsJrJviiNnRAxZtpjmYsFm31ulN8p4s33/uHx67HI3hu4BamZ4rBqLsoYfGacGWXVAK34jTiNeWv/vf/wPklZVpd8oXf/9/cPiwpZksaMbHLK7ecfP279m8/bVAT/Cm0uKdJbN7pnlvf3mzWVyFNcE506OTZxBGxNa4GwCsRWrRv7d0xd64tbfy+y8Bsi9s7p3Q7QIeWsW5ITS3u/Bbgm8Yw3Hv97g/LGXvhzbvjrv77dal3nphz5rej3nonS/B1jOSO4JmiyJsvU24pSjvHvu9Y+6Pe+5pi078E6zVW+fav57h/X2BuRemKNd8/2MYNiU7IfZhrfiBy7pH2N/7wfuv73s/v3fg7wOlPnj+vYWy+3NvnewJjNvCd/cR9j/yQ+cZPlcsd4vKua3fvAtd7K2dfcXwnjesewfdN0bu2Wv3ONG6//n39vewNkwkWvly2h1L2bYHt7Pc7WO9d/57S02LUB0QiP17+N59c9+4myMxzMGeUrnVcOcHru37xr2LyWEKPYO0u9MPj3OPQGeroPeRiO1x8z1zWr43GABbAT/Ip+/bmvfN3f3yxWD+W+z4e0cv3t/2mu85agH2XAmOuMG3FCEJJHXAhE/+4n/QyfFnLPrA9PCMNipIwrtI17dob53Pgh8BUlrnYuWEhT7ckkOtu4XV0ieCdNZSVseIa3AacDlbsrN2jCYV83ZFdBW+biyrPUcmDka65u1X/4H5m99KXF9S4gDFYqiR5gDdtGUuCkL1niE4eMoFLRn+t91npW96NeP4+Z/o6ZM/IVcnSDih7ZSmmpD7OT695d2Lv2H+8m+EeAVqhkNK+2t7f9y/fsP7CvbDVoFt8Hzvx94fw3H/sUrqx33+vv11v7ewp2h/8EAmXD7sdXyIu/eHLanvHfdZYz923Cto7vEy77yuP/q6PnSOH/rqj7yff9JcffCf/4Rz7p+7KM73DKO7Rhp78FcoFt+e0tv/2f88FM80AanIfrl11+9f2r7CvhsPuKvE974zKKz3rNz9l+4zCHbpcbuq4j0Z8d4F7ucrfMCI29M6AyHLverlH7sW3rucezz+D+3/f9LCuTsGxoQPGMY6fGZ3XVt0ZDDidff6zkX4Psfn9rm+1yD/gddurwN7/juzLu8MNb2zlvaPWu7FqSO4iqBC1EivraGk1QyaMeODI3wzxqt1wuz7DueF4CuEhA+FLU0dfW8llaGqCo9JthOJGD9C4bVQtHSGdGZ6JMVptJZA4gi+InYJL4EonqiZEIQgHt3ccHP9guvXv5a0frubkzxwYrRIt/wBOba/J3eho/1KDwkB1RoY6ezgMTBBU8262xCqEbFb4SVRhXC7haqwLXrabZ/9sM39I3zwnY/j4/gjGHpL6RbBthdotvBEEbw6eE/23r5C3faULHXDO5Tofcs67W3I/CGlfK8xdZtO8/bGdnc/+iPGngGiRq6StnBx+S13r2/Pc8cqEQxruOfY33MN/0Tz9f/6I7Nn+Mj2xfcMGN1fZ3+YoVikb3j+9swGiAE+bMztjD8A7zxOhD5vtmQx2UE9DnSbHj+aqfNTNI3wBCRVSOwJoaJbJkI4wInSrqyUOQRTbOvVmlHTkHK2yxNv6Q1l7nLK5tF7h5MGzY6cslHiki3LIAm+GuPFkWIkeKV2PYvrt1y8+A1pvdjOh3PuVv33D9eCl4kZ5q9o3rz3WDUmQ7hcktPjmeYQSE5Iq57peML84oqqSXifLCy0rdbZ6fbbI7+HUu6Pjwr94/g4YM/7GHIi3FZl29smwDwDPO/eU8u7T/utXN+FdsorxXNOWw8/31bS9+Ko+QN/3/7MvfDrPjpw5/PvDbnz3hbyvxtrGO7u7jF+DAp293P/klX7HUTgFtRiSaCyWwlbpX5rXfwhxy7Gsqew70M59kfAkuFMdTjvSMnaVwtQVeaixghdG8EdMpk9QXVK11prXFFBiNSuJoURVsfQ09Qe0QjSIT5RBYANDkuiTcXgsCqYUmqZxXK1PJZb5EvZa1bLNZaASIPLoFlxbOgW59y8/Yr+/IWA9X4XkeLtl6kpBFbfq9T3Feu9oU92yt5lYr/CuxEp9qQusVm0TEaeJiS8tmhusVwtwXF36u8x+u/Z8B8V+sfxcbw3bgu1IXfExs7X8uTCerdLEjMPdoCUB6+5bHy3h6ENmzFzi65X4IfDKN8z7tXdeu+f77/wnjGRd0odYaeO9uKFd67sQz7n+4DDv2RFvj9MCdyq2MFyJWxN7DPRFTjWvoGhKvcyR/3zj/18mWHs/Xu/MtFtDdps9fxVNsWaSpKxOKjH0Bzq8cM/4fThn+CrUzYbwXvrmCfSouqpXc16tca5lnqUWC7fcn75HaPa8+jRI3J2eGlw0uDVk3NvNdwScN7Rp4yqo03WrEYEKu+M3z4qQo2mAKrUUhHykvn8gvXFC7GyabuxfWU+jCGO//3zdr/xszXehhBb7rh4+x0njxzeTzg5GJHzxip74pp+c0VcXYJGnNNbBQP3XsUHNtpHhf5xfBx3h9wWYEPuyOBJu639bIrNiILej0cPPrylveUdLHc3FH7v+IEkyQ95BPceZ3/cPqbsQ6n7ylzvfn64nnviqT/C27TEq/9alPgw7hh+CrvE2X1/PG9Jd9DhiZTX+QM769+XJ7MXjtLtnsjFfC0Jczp03JxidW4N7uBMHz/7lJNHn7Fqa5rpI/q+ZtuLIieCUyS1dG1iUtektOLViy+Yv/w1tNeyqR3XFzP99PnPqcIE8VPrfZGz1eyHgPcN3o/R4EGUTTQCnJwH2Nuh2S5cshJqocoQ13O0u8HR2c7cU9xS+kb8KGW+P0/3ImvDyBA3vHn5pfQ56Wg8Y3Z0RoyRy8tL+s0C+iWLq9diZanpnmTPHzc+KvSP44977NUHb3WU7FDm0qTq1ka1spfCbFac1zQoxiL0htIYuAdqzfCeR3QrPrmPCNwWLBm+V5m69/7l7j9KEdB+iyaYN/4h5TIkdm1DwzJ4ILevZ0t/vHcNufy2W/5wst6/xLFTdMMzk72IeUaHUjaxvK5dCOf/AmPgrbh1MfcYbcV+tfVreSbbPBAHSAB/wMGjn+njxz9neviIPjtWnZKrEZtY0cY4pNSTtcd7j6Mj+A7PDTc3XzF/9bfQvhWkhZjRyzfy9fkLcGNwE5s9K5sTvIIf6+Nnf8bo8BHV7AhcoC8LNOehhC7ixVkcO1s9v2gE7akEuj10bIDeh2z63384nDjrepkSeXnF+e8Wgq+ganZB8q6wO2Zj3PF7TYD+sfjNR4X+cfwRj12s3MadOPa+J72X3KZqHNDvZbPvKf6hbncAqwca551iH7qQZW4bDB+2xgfRc0vU3FGmQ77y7bEvsXeevx3Hal+VvEMStp8zT3OXS2Dtkt87/1ByqfeH7N3WqPjhLN1/iUPFaPOHcXeGhwkZ6qGH5kn/GM/rv9SwEI8twA+tq9v/tnACWtlrByfMTp/ow9NfMh09JaUpi40nO4/UxjIYgSiJJlQImdwXhaU9tW/57a//isWb3wpuA3XpIZHKqVSNcCJupFCCmnJOEQjy+tdzxo9+rmfPf06YnhGkQsp/WSDnhIRE7nsSPUpnLJRA0rTXrOh9iP0f7amzv/4LDqOZyhsb63g2Y71c2f21hUNESlhGFOcymvJ2Hw/e+a0w1Q+UW35U6B/HH/XYT3wDqIKRl/QpQzUu2WsOCODKdkmFTSdIoYpStvXCIRjLHhFfCxVK3xXnpHjyqh6lIg0drO69Kt3+BTtLXTzbKiLnqtJaNRctYRo1Y5eWb5FtfwDCl4ZeS2KRZrZZ7aMJbDYYvasv3NJD05vMtgnJoJyCQ0ho6R/vikd6+07+hbvjd8e+MTcgM3vZE4mMC6X8qNz+ANA0zhHznQS0/xKXOBCX3ONxChnPUGcRwDn6HLfKJFSFoNEDvoY2gzSgFaPjh3r44BmHjz8BN0PyIes4JuWKLIKUEnznYbNZEsLIWGo31mXROevD/ve/+l/oVi8F5qD9rkPrYOtKRvMeQ+G2zS72eTLr89/K6nCmZ5NDNtGBq6hCxbrf4EOgS2uyRNRZQ6n5co7luoQd2dU94x+rzIcxXP4w2m4DCOvF1d7k6y1Ej2SNbjwfWhHvv7rjlNiNjwr94/gjHrn4oduiM3JUcpFGzeQB48OH2klNovS9zmL84NnaUTpnLXD7nOj7DTmuYWk9yGNsibnfkt7u4qcw1J/fhqz361qHz+0lWzmsIxMOsierJftYWQwIES0UuHn48vcNgagJpObgyTN11QQNDV2fqaqGvo1IynhxBFcgSe9Jmlks18TFFeSF0K8h9QZxhgrJPTHfhpdvhenvQTT+pY/BdLJUsVLIJ3mnzKVQYGdTrn2O/yyw+w9Bx5M60HYRJVq3aPEkgZgTsXNDn2WIFVDhZg/0+PQpJydPqacnRD8l6ghlhHMNznsg0eUl/aaF4AnBIxlWqwWNg+nY062uOH/9Bd3qtWh7yTbRNJclfUt/DVksGe+tG+GWMVY6SAu61RynmVGo6JKVn1VVRaZHvIImsvS0XUvqrd8HVCgt/+RxHxz13hiqT/YN6nxvAuJgH9755G5sDZkPn/ajQv84/shHyTkunKgZyOpBpkzGz/TRsz8jugnJTxEcHo9V5iRzvdSRNZKdEHPLYnnNYvFOlxffwvV3AisSa5rinW91mVgHr8KU+8FrGxTzEOpP0QEVaHMr/k9qTXQUm2OoqLEItr/v4CUWoBAqTj/9nHpyRg4z+t5ikA5hUje4lKyFZ+FETxKYtZl2c83y4re6vnmJzt9Y7FOME9ya6wzz+4eHlv/5xp0YtJjHSC7JcgPmrj3eZVL6EFnVP8/YdHFnV0npZZcd0IBUGJd+BX6m9ckTHjz6lKOjxzg/pdOKxIiUHTECucf5llAJ4yYw9oG2jzT1iNgJUXrGVSR317x5/dfcfP3XQpxvsw5Q8MmY5natagaeBwFSyUZXxBWmcrGuaLE3Dm/vKuisG6GvPCmr9X731iOjzV2BHZx1ecw/kHz6Y8Y9+/f2S/eq59t5Ody/S7ZMfT9yfFToH8cf9RjU3S6CWGKE1Hh/gPgjxE1BGlKytoaZhIugWah9Td95qAOuPmBydMjs6BGbozPOv5vp5s1vRDO02hJKUpmSzGN4L+Xl9pbexsxvbegK/ARXHWseFLrrob0R8gpKjHtwAPI2K/8DQqvEI5Or6WVMn8ZkacgC46ZiuVlYh7Vs/NWqgoQRYXLI+OARR7MR3eKYly9r7c+/EdJye18DsiBbpX7X//gXPr4ns3n3UgXNEXTBrLcASE/q5+T0Ierlf8ZR9KbiiEmssRaFd9yNoJ5omJxwePqIg8NHNONTkozZRKFPQpRMVVWMxwE0EfsVfdfS9x3iISfHprWY+HTkqV3H2ze/5eb1rwSuQSJOh9URME5zuVUjAqbYtGSjKKaI05Y7viJmRx9z6edhCSwOX9owC26fU1/suBZL/y+1Fu8ed+/f+3DVB755F7bfr2HbV/53qyQ+KvSP4496qLNeNENS07YzuHr6rMREiTGbRe9Tb2UzPlIFoRIFVZJAHxOtOkbjGQcnDd57XqdW27dfSiTjSgzaNmG3yxhXkPeU+V4W8fBbKwhjDh98rg9OPwXXoJpIecPFu691cf6VkEuzFyn6RodjDcGFISmPnQepWElQquljAN8Y0UbMeC94MoFMLY6UHJ1C13V0ZE7GDxmPKjLwIraq82/EurLlW3dzy1jZthL9r2B8r0YOMD3lJ7/4S1WZEVyDSy398pyvfvMfRbe1z3+YuVCB3tVoUkyBN/jpsU5np/h6Spaag8NTwmhG0xygfkQfAyl5nGuoa0/ulyRd0HWWN+GJ1CHhgxGzSO3RlFEPtXS0y1dcvP01dK+hBtkMK7MiqzA0KLE9YjHzvK2u8AQ/JmlAkzfGODKMDrSZnNCrJ2dFfAAsU93WtYU/XGkjSwggEdV/ZGeweyfxzr8/5E3r+2/rewahu/OVvcTbHzk+KvSP44937G2WhCl1Jw5RQYmk1OOD0GvE+USoFG039KsrRNdUVcWi7fD1GF8dgqvpkrCJieyE0Bxy/OATXi+ulHUnURTvAinFXS/y70ktVkqO2v7Gz4F6dMDB8VOcr1FVYr9ifnNRMo/l1r1pBtkmx5ndf0upq4AKVQ40boT4KcmNcJJZLV4zrTfE/pKoG7z3eH+MlwPUHaE5sFonRtWI2dEjTlcXnK/elNa7Ge3/UbLoX97YJsTtC+L90j6BKFSjE5Rj6jDCpzW5j2j6R0rqf+olfk9SHFqhaQR+QnX8SI+OHzKdnTKeHONCQ1ZPKq2Oe3XkZFzv2fmykjLNxBM3G1JKeBFGTaD2Sh+XtOsFJFOkqY9cry65vviO/vLVtvOqUpo73mpFXf4viaQRCWX550yMtX2uOtbR6JD6cMr48ITR4RNcMyPjEXGQErm3LHbNnqiW0+ollO6hPR/uzfHPMwT26F13JabD3H74S3DbR9+Njwr94/jjHnsyNSs4p2iKQERcxPsO+p6YlsQYubl4yfW3/wDLc6Eq7ZAmh1ofPGZy+gmTk0/Aj+hypvGB2fFDrg9O2KyvyNqTrTXVnexouG2dF8BN9qx4AQiQhL4X+uzRaG9kPH10t48jpk92jvI9sWwF+mQfnrd4DyELSo9zHZfffcXbt38DeiHI2jLZmzOdzp5ycPZzDo4+pesqblaZw+mEo7NHXJwfqt7MLSXhXji6AIbb+Pq/4KGuWFx3axHYPa8c1PkD+n5MHxuDk8MUfI1zkdyv/stf5h1lPpCnJDfBH/1UDx/8lIcPn1DXM1IS2j6TeyEVtEoFcimrEu/IRFLqSF1kpAnvElXlqUVI7YK389e8ef339FcvBJ/BOZAaNi0DZ7y4BlK7l/Q55B5k6+DmIjn3li5iDneZ3owfH+mjRz/n+PQ5yVf4yZQYxnRJyOqMfCZnnDokVJaMmq2GXbKzjU7aVmL8PuN218Bbb5TJv/uNncGS77yW9452z4nei7nfNz4q9I/j48DgQoUSVxOQSKh27wfvqIKjDS2kawHzPpAMi7l0y0u6dqXN+IjRbEKXPUmEajShnk3YzJ2V/QxMJD5Y9ppzoAHNFUM031o1lv7eQ0Dc1VirzpFKOEDDhC45/MA5Gg5ADxTnBLeEtNru/10sG3ZJRiWrGMX5gMNT+YboApIh+ERcXUB/LcgN0vRoD9otZTG/QNTpZHRAM/uc1eUNqjWHo1Mm4ZBlaooREncqTn25/mFSSwnc/t+i7Ldp3oISDElbgoksX6Q85Xtp97s00fHlXrf0uxLLrTvIJamQwgUgmW1JVOkhv2tRusup2IpRUdChGXYFzqHZOsgnhp7xFu8lZukF7V1AqxrNiV7sGnIqyWfbRLr9yOltBfGhgoC78dTtdJf714E4RkuWvTpUAskF8Af6/Gf/LVqd0EtF2xrTWuUClQM/xJjFasmy5JIYCeoEcYnN8oqUOuI6sZpfs5xfwuoc9NqMQFHoO3tevoaoeB9wUtHHHpwZrpKHvWetUTPWHF6cTTUAYczJ8z/Vswc/Z9Q8IzGmT0oXa/ouWTe1CpxmKp+pfSDn3sJH9EjaQFpAWgtkxPNfzkn/AUPhrjI3u31nFrz3df3ef27HR4X+cfzxjj1tITIIzWRCSHqiZlabhG9OSDlCyky8h3ZFFRyxWwMgwZN1DecvZTl5o0c//ZQ2VjBSfKO4xkFa0kw97TIBY9zBM81uxPT0lFF1QB3H5HVC+5Y33/0d1HMhXgBQe+g7T1Odqk6fUTUPuc4BmgaviXbZQ/MEZj2kGz06guuXfyuEFqTfQnSK31r2Q6NMiNYjOjR0LrDuepxPTOoNTVjSug6nvZFYCcjIoZuWmxe/kaef/Lkuuw2zkzP6xVt0nfj89Bf87duXVo9ecqvogDhmdPQLPT36GVI1JLcBadncXNFurlnPXwh5CRo5aCbEtsNT0ZPpUZJ30DQQjnT64HOOJs9wCDfLV8R4wfLia6G7BM2m8qN5T8KENT2EwlQ2O2B2+FOd+Z9RhyM6WXO1OGdzdQ43bwRdIG7NKHT0vRLVIaMzZg9+qjfLBLFjNGvYzL8T1kuQCZw90vFozHRSc3Y44u///f8iBCEcnWpkTC8Zpo7rzZKT6YjlysHJY3VxSl15NosboV0AESqF2OLEOounnMrzcncUtj2/ETKA/HhxdKXTn7iKmAEChlmPCqJQQTPTs2efcvzwJ2z8CfgJKoqTjtr31L5D04oUl/SbG/puyaZdsF4v2Ww6Ut+XCo9sJZpEblVc7BlGdEOpVYa8ARwp9YVpLu8MVsCJMSySY4HcHaSalAT8FJk81kfP/1uiTLnJDb6aknxpoCI9lfYEeiqfqKVHUgea6NoV65tL5jfnrBfn0F+DlNK3D1lKP1qEfDhz/Ud9/oNfzfe9CHobkr/79keF/nH8cY9BmOi+1bz7a9QckNyYlDbEvqVte4O9U9ySQGhOmMbrGQWPF4+I1Wuv2paDwxOusqNdduCOOXr0Mz19/if0oUEmDcGPkFWDtHA4qTj55BNWm6/1m9/9B2FzThcdk+qYP/2zf4OOn7GuDpn7ilXfMwqO5uCY44MHyONPaNyaWuf81c1r7ZYvTWe/l3hzR6g4JeKIzpnzSqZLG3LfQVIyAecjWUF7hVJZv1yvyCNluVlxLI7GVcznq5KtXJv3FROTT3+mj8/+DZV/BukYCQ1atcS05vC4I6Uli9Un+vb1F+TLFzJvW+qijoUGxNM8ONGDJ58yPXpMCGek1YjUJR6fPaGNF0xPn+n569+Q3v5WYm5pCAgVLcULPj7h9PlTbSYnNOEJVfecuAnMppHp2RP0ecfq+rW+e/E3xOtvZNO1OAehnnHy5DN98Nm/paUhpUTqrnn1ZadxrRKe/ok+fvY5k8kE0UjuboCZTs8e8ad/8d+w6D2pmbGIQlTPzSYzmpzxiz/9b5mGSIot5+++05ff/FaIa9AWVMiaUHbcevcsWfu74MaKo1MhESyxzI3MI04Co0OtZ2dMDk+ZTE9oJgeEegx+Qi1j2s6UaFWBxJbzi5csr17St1e0V28FbY3dLN+tysjwfZnieuezd/YWGPKVUt5DkHYrNKtDowdG4A70+Wf/DVGm+OaI5TKhrXnfdeVpJCM54tmg7YJ35y+5fPeSbnEj5BZS6WQmxdBwaqw38Q/dHOdHhJ1+sCxuNz4q9I/j41DYwq/bWLP9xJjpNZEJVNWE0BxCmFD5Gu829HpDSrqFbaNE2rgk5QZz6ltGfgr5GBQePPozffLpn5FHh/g6sGKNEsBVqBfQir4+QRtl+mmvy3dfweWFrPrMBvC5p++WBAeTUJNzJOfMulvj+zU+dATXAVn2ezTvxq7QZQjPJ4HshaxKdgEhs8mO5EbAFAjk1JkQTB7CAfXR5zqenbFyPbnbUDcVXuHq5p2hHM6DTuDBJ9oc/gKdfMYmTcgi+JGdL3bCbHSIjxMOZsfkZsZbp8rVN9L5nq5vQWeMHj3X2eNPqQ9PCONDNDrEJSaTCUkC0PDgwU84OfgFX/P/0/WbX8tKboxhjApGx3r6+E85PH1OGz2ZQ6rRAcFlEh0guGpM8/ApdZ15/Y1qe/GtEByxd6gbg1RUbkKohE4zzeQEZaR/+vN/S8wV68UKL57aHYKcoN2E5bVQTY+ZX0EKEw5nR6R2Q2BN4wK5v6IKkZxaaJcYlGHMgyFYLHiQ9/eJfRXYFDIf8JALJevoSI8ePufs8SeMZw/ootJFY0aTUJHFsVElbVockVHl8RLpVnMu33zN/O23sDoXaM3A2A+PDEmXwwX8niPFIfSzW6bbAITolnIwHBxxdHjCKkHqM02orW6ku6GpPKlfcXXxksX1G7Rb0K3nsLgsF9iXeyioguH7lv/yX9n4qNA/jo9DMF9ogP8KfCjqcBJAA+KEpJkuBUhB+5ikx5kXJBlokKMnOp4eIkFogoOQ8Klm/nYFOtGTpz/j8ZNf4OpTrjc9ThJBOvruBkkjRmEGLqBaEcanfHp6zBdZ6K86g7vrgAQImsEJKUWcJpoqgFcqJzQCTju824M098agxLF3TT6LMy9dlKiKE8FLzWj6gNXVUsmbbayTEOD4EUcPfoaGCVkjVRUJHrr+hkV7zlA2x+yx/sW//n+y6SeQx1RNRcprNv1bYtcSQiCmRIyOzIjx4XMe/6zj9f/5EnRt0inMePqTP6c+fEYbs8WguwWuS+SqReWQ9SqSY8WsOebw+FPWl29Uu6U18JCGT37xr5g8+gWtjkATirDeXNGvFtSN4r2nIyAhMDl8wtnTyMtuo+nmWpAa8SPaTaYlEuqKpDVHp0958qyh3SQEz6QZUwehW15b2FlhMpqgrmFSVSy0gihoB+ICOVpoIDihCX7HlVuSw4auYft4yr0FEUNOXhaQivrBp3p89pzZ0ROOjp9ytWjJUkPtCy8BIBkRYeQT2q2Yhpp2dcWbr37N+vXXoBvB9YY6OeNO3zIcaomj/2doXrIf/9/PHkAwKuKhSL4e8eDRYzZ9pGoOmC83jKcTgmbqkaPbXHD57iWX3/0O1lcCXUEO9n8GM2FQ7PdN6r/88VGhfxx/vEPAMq6HxCfFqSMhoA5RhxcP6vC+IuWIhCnMHkC7UmNFE/x0xmx6xOHJM8azZ/TJkdmQ05rxFC7evYEw4eThJzA6oAV0rOQ85/yL/53N9Tk5NRyfPePwwTOqwyOWy57lsufo5BnvZufK/EKultd4H6knZzh3iHQRJ4qXTNctid2CmK5xeU6OLVsWim1GeUlo4i4Aqqh39ESieII48BVHp095cPCAgCK1Q6pAL4IfzWiaR7SpJ+cbRo2wXl1wdfENfbw0gSoVT579lPbGk3NF5RKar7l89ysuX38BuZNwdKZPn/+SyewTVmtH7msOj57y+uBMubkR6gCjUxWOWC0CWZUqLLn47tcsX38LCT75s/+Og9kTvNSIgwcPTlndnHHz8hVUFeODZ3p8+hk37ZhOPUfHM+YX3/LdV78iXb5lfHTGo6c/YXT4hE2GpI768Cnjk0tWiwjq1VdTQjUh6wjKearJMdNJTVz3aFqb4u17Vqu3oDe0q8hy/pZlf8Xhk5+z7jraNhOkx9Exv3rJNGxYzK/YLG/MYBrQk1woY7nfM7+1foMYHO4CNBMeP/8JJw8+Z7WpeHWxIYQJSIUiqCbrNCYJkYSTnrHviTdveffyW9avfydoi6sUJ5nYtVgrz3xH/90u0/t9RuUb+mSc7Ao4X6E+lOYrloQxPnumk+MHZDxNNcZJS7fpqV1kPGq5uviKy29/a+RKrnjjqbuzyoeSzfxeh8D/msZHhf5x/JGP9/nT9832nLPF+HDgag5OHuB/+a8Ye6X2gS4FQj0mJ4cPDb0GUt8TmkTSNZfv3sLNJc2D5zSTIzp1RFGkSbz5+gva11+KpCVQcfXiUrO2fH72l8CIZQuT2SnnR8fo/B3f/cNfC9Lo0ed/wdnohMrXKD3dZsG33/yW9PYbiNdQdUJ/bWVr21vZL1sralz27tcVyF0SWYyNazw7pUmZCkenCa18AV9rcnTcLFvGhx6nSy4vX3Lx7mtgCShudsbZ6TM27YhJc4DXFRcXr7h+/RtYvhLIxO5avlmt9Jf/5pRxfcZitcHlhsnRJ6wW30GvPPzkOeP6CG0nNOOaGJcs3721skHJvPj1/6qf/fl/j9Rrrq6XjMIa7zpDTTrP0fETvExpl5Ew8mi34uXXfwdvfiVoz/r1FW806aNwgDaHKA2T2ZSTRwtW55fKak3KGEOeeDRLKT0bsd6sGPnIy+++5N27N0BkFAC3kri55h/+6hLckY5PzwjuACeByiW6xTkvfvN/QJoLri35Fx0QzSPGKNRz3j2/973z4run4R1DRdrlgna6ofI1k6ZGxZNTQnPGS0KkJ2sH/YaUVrw+/4bV9VsW8wtBN4B1JstEhHgLCh9WjmDxZxEpe+OfPkyZY2VtquRUurpIA/UBTE/18Wd/gvMHqB+zWLVMpwcslhuaGuYXL1hefAubC4HOeh2kFqVHgLqqUbWuaarue5ux/NcwPir0j+PjKCVrohgcqZSEpIxzrpRNO9ou4kZCPTmg8hnRhipPcYzp+rVxwJOpAozHsFwv+PbrX0Fc8vjxQ1zwxtwmLV2+pH39a0hrGhKRSCTJ/M3Xuv7kMxgfczCaEfsFR5MZV5IwoZ8FiZpzRp1QOWPk8nQkaYFWhrinwFYa26+dx3Ir/KmKaiZrxPkK5yBHu/dAReVrNpsVKQnRQ5+Vpg7U4xH9+pz51TfcvPwtunojFq8MHB89VPKI4Ed4cayXN1xevCQvLwUigqIxws1c5pdXenbyOSM/Jm82nB48YcUMYiStItIq9Maxt9kk6ANGM9bC+p189+X/prEDljeCbEoCl8M3hzw++ZRGpkyrnmbsWV1/De++EPKC0SiwWd/8/9s7syc5jiS9/zwiMrOuPnERJMFruNqZHZlkpjU9SHrWn67XNVuNNDYkl0OQQDcafdadRxyuh8iqrgYb3NkdyqQl283aUF3IzjMyvvDP3T+nnp2zWs6YDI7xFLRJSW4A5QDWTRZnSRFrhahQuoKijCxuTvnzd38gLs4le4SBRn2v4ROhmIA4XLHq1QYdPtRouoZ0KVCDKmINGgNm0y88ZiC/n9V+J6lx89EGqG84//4raVaNHj/6FFftkwIMByOqQe4COJtecHHxmuXFGwjrfA59RroR+pBEwJBw9jYP7k4iHn2Hv5/FsjixNUVmyrSAck/Low8YHz5lvP+EanxE0wHqaLtIWUHpLDEsOX31DWF5sb0O9ZlSF2BQODrf9EfZKQ+TW/mA9P9WW+ZntwdAf7Bft0li0887odjtCj5PDElzu0YxStAs22pLh8ZA6ztIJc46xCTKwiIm0vkZs6sLTk7+CLO3gg4YDofaeUHKiqR50YAxUOxrCEFwCWudpnJESImuDowKQZIyKCuoBJoOxGAkYFCMM0gEIZJi05cF9bHPDbX4z16/ZPCRTPlak9tOx+hJqqzrBjcWRBLOOdQJbRvoQoukyPXZGf71N9CdCTRbQZWDo4/wVNRJ8CkSRElFBYMDJTnpA+RQHGnXGbqQ9x86ZVzuI3ZPNS0khY6yhBYFZxgfPuWjv/l7uuUbvTj5BtJawtUsK4+JZ1v77MYMB3vqGFKvPdEHJEXWi0sIDVaUUC8opaBrbyS0Cy1KS9vBqvZURcX+06fMZzfE1BJToLCRmJQUA816zpvTl+jNm150RyA2fey2N7+Caihts9LgCqqBowsd1gVwEXybx14CUNJ70OVe77zvvjOwEALEpCgthDnzsz/Lcnqtqc7lZOX+AXuTISk0zK7PSd1SIFJYCLFB+iQx1ZiV0q2Q4m3O2K6egaiQ7kS/4V9LuytQVAVNGwkx56GMnn6iR88+Z7j/FCn2kWLIdL5mOBgTQmAwqKjrFc4K52c/sL46E3TK5n215nYx1PieYRCDwfR6DIIS+aU66g+A/mC/cks7qk7hbsxZIil1GDPC+5aycrgi0axWVGWBGI8zU4wsMUMhhZbV/JrZ9JTF9KWwvskTbzFgVO7TpQMWa4MblajWPPntf6VIFUVCjWkwTkgU+FiB2aPzGdAdvZAJbV+b2+DbFcNilLugpcho4JhrEKTFmvijhKofXev2q0y3l8YRROhCbu6yP6p4+923uC6ymhpWbc2zT18QRSjLEjUNeM++c1y1Da6C0IUsm6slo8mHTEOJLwoaK9jJPk9/95/olp8wGVS6XjUMBhO6zlCUh/gAhkBZFFmOngJVWCzOaNM5bvwR8yZgdEBx+DmjgxccP/v3+PmF+vaK08sfiIvTnHVmEkRhtHcIziHJUQ0NIdTEzoM4JDosDoMHajTUNIsF1eQRXewoC4srBKQTTZ0OKktTN5iixNnEyekrdHqZkSNpdmWlh764GUNZNKhyIyJV/3lIt7oBv8OWbFTSNqVbOwpmd9X2dvXw84+GTRFhVj2PNBAiabmSTaVGN33L1TTHkTONnrDELMu6GQLcDv0Q764Et0l52p/cz9g9r+k8RVXiY4EM9vXJ888ZH33CuiuJYQSxwriKugsUriJqw3BgaOsl0zcvuWWj8lnuEgcqOSKxXdhu3PPdN+OvrEP//80eAP3BHqx/uQ07E+jGY3WSS1aN4kPLvL7g8vTPnBMgNhSSiMHT+Q66BvxKiGuQZd61EYxzqinRNhFn9ogh4OwIO3AkLfv5cUXna1BHUQ2AAg2ew/GI2jtIfeauegoHRelQVZwtidrkWnhtgZBjpPwFeT/biS5htNfTESAGQtPQrm+YnZz0smuBVm/46Mu/QQmEYBi4guHeATeDCaE547bovSREQ1GO8Nbi1YNTOhWkPCKaAimGYAa4wlAUlmgihg6rHSNn2asqZvNAbKdydfVS3WHCjp6RKJivIgOx7JWHjMYFMjminDxm2X6sV1ffEObnQhQChnUIUGSFW2uETiMUhhA9hoBXEBMJoaOwGUlLY6lXc3TDeEhO2srhmAjakXwNoZatq/cOKORhZCAaTdFkZUAtMOqynrgYjBSk1N7541x1sPOI7hEW2YzN7a87x8wJbO3tF9tzSxvtujvgvbENDt7XC+92u76cs/d0+9jUX2Ui4LucRHl49IijR8/6lqyOweCAJgiFE9AWIy1tW9NGj29moCtyuODuGe+Gk3Rbgrr7Nmyu4Zfnpj8A+oM9GLfOFfQTgiSSQEo5DcyIYE2gbmZw+YoQGoGOYJoMpKq3k4TkriTWZCq0kCSQNMZIOSzwPktpem2wLhFpsVrjiBgTEa9b8G5mS2IzpxSl62VNs7Y2xJgYWUOKZLpWFHo68e6EfY84aP+fAqhuOlMJTlyWLw0dvpmSJW69QKC7WCCfP9XSHhJDjS0LhsNDHj95wfmr7zBich6BKQidx1aJgfNIbClMxK8XlBTYoIyMYFMLJhG6GSm2WJvQsCbGNdKtESLaLbh687WMxOvx2FAUELuEo8QHgxqDK/YoigmHB0+JruIyfq34a4lVSWcT1hk8CXWWziYwLbiWrl/EqDV47zEIGgNlBT52DAvLjWwAQ3MdPokUWzRked1b7dD7wMECDiGDeUoGiYaUMi+cNJK7+20kVrfs+09Y2kFxQ8QSuQ0Zab/wuINf/WfV3mPldnzcqhLcXsWPD7/pD/DzeeYbqwYFTX3b9UxDpI0tbd2RtKQoRwTfkVJDWQYmlWIJLNdL8HPZ6OdvyYPNv3dYjfctbe9bvvzbtgdAf7AHu28C3emglVLCSsI4obQpe2apzcAQ1rnu2tILwueYqNnR4vBtg0rAlgk1nqQtsa05++FPGfzbOdBQVCVoga8jxAoKC37GZNiRui4DJhnAlZyVnntC98d2fXbflvKF21f8/olL+8y5rNGdy/SsdRmKRIkuCGEBpYG4Zn7zhifP9wgixE7psEyOnnHxdp/YdRhxqEY01NiwRlPOOo7rmrfffp3zAGLIC5/QYvcnxHqZS6lGJdY3jCSxnl1KQU6x87M5s9WfZHZ2ruw/4/joGUdHz4lB8MESdUwbLE4GVJMPqA5XtKuVtikgpcVLh9oCry3VeEKXmvy8LGhHTnhzJWAIncdZYTwoWHa+v3NKEkHFoqaXz9VNZvqOzOl9vq0IxjhEBE1Zy2DTL36zvYjkZ/jecqrbTly7uwaIW8J9s/D4CZDaGR67+7hntzvevdn5fadSQu5Ujv+rbQvmmlgubpjNLtg7HPDocEwiEeIiL/RMQ+UiojW+uaFZnkNYceead1cqu9exCX/8aJsf6zT8W7cHQH+wX6/deZk3Xk5v4lAM1hWIF1JKGDxWQ++Rt1lz20DaBOs2AbuNMwfZ6/INTVhgBnu0footIjZ0cPUamEqmDreFNsAQGCkNQC3LpiZPw6bvXOUwRV/eoy4DBrY/dtxO3LoR5ti5vndNNtuqoskimpXirBicSbRxDTQ9AyFcfv+dHB99piM3om4LGhHKvWMOnnyi05OFJA1AQ0pzKt1nxJC9ssSEwHmzIC7ORagpyUIt8arsxXks2iZCaplrwub0Q/bKQ1YpZS39xUxYXnN9+ifmkwMNyfH0s98yOf4cwz6KUBYTxuMjWhWa2Q36oqXrDFKUdD4wGk9YbGjtTc+VrmK0f4yzA0JIhKZlPCo4nU6BnCEdpURNtQUDI5FbBbKw4bvfGVoBNOSmJoBIwlhwzoDt2YHUUy6YnJz2U3T77nPbPcaPPGfzfjr5Xc/9fYvZ91H9W8r/5zNBUA346aWcmW+0bWtseUCI4MoBe3vj3CyoWXJ1ecLs+pSuXfTVHPDeRcw7bW23F7Lt7PbLswdAf7AH003zC7czEeZ6461HkiKqHome3HasRfBZjEyUuIkrYhG1SF+alQXAlOnimr3HjwgKVVVgncspynVNVSQ0RLwaClfxwSdfajV5yrrtSH6pq/kb5pdNJmR7ALdkyj1JlvQMmnJG0G7s4I5n9x6ZSwNo6iU3BO1lrnVD32qbGYhEZgCaFfPpgqePSgozoAG0gv1HHzE9/RZYgUTqZs7wwLNaGobVGBcNlSlZ02FZAS2CQSl5+vnf6mT/MXWzoq0vKYzn7fcvJQbFd4W64T7l3oj94xF1d8H89UsJN28FO+D86z9o/Lsx4+EQkZLCWCprIQWhXqjElsoNwViCVjgzAdkHX982RZOKwfgYYwoy1nYQE8vZDMSSxOVCLikRk3Jztzwo+pHyDuV75xEYRCyCJWofck+pH0/vxH53vPTbZkF3TXaOm6mgdAvouvOz7aq363Xv0vU7O7xTk3YPrb4B8E0sfjc89eOt/0VWWEMMebSBp7l5JW9uzrIWPQZ3dKiL8RhCSwhr6sVUUr0kJ4nu3r9/nj6XbZAhbbf+hTnoD4D+YL9y205+GxfL9MCcy6pCCECJNQaNmj10yfBXSo49bydRu3H7Nl9pniBFeHv5Frf/EWILxGhWOfviN5z88ZQ2KGgBVJRHH+noyaeUe0+grhkPhDevYH5zniexlGlhQgvqiMFSGEdZVjS9znZRWVKbSFG5Gz9/Z9KT3VBjX5yrDk1+kzOMsfn6blOw4Or8mr0qYQdDkmmJWMrxMYMnH2tz+Y0gibc355RHn1FNPgXGiF1z/OHviLrQtmklhJbcevSxVuMvKCfPsHsdh8WUujmD8xPoLL/5u//GYPwEr2CKjuViyPx8Ct0KooE2ZsW8IiCiWOlwsYW4AnGyvHqjj58d00ZlYsYk33Jw/B919uaPQpzmcsBnn6ir9kipwBApbMliNofGC1IQKYiUeLGICqjNzz2Z2/uq99G3DlJulqLY3vO1YMr8kxqcsajGzEDseL5bNbOdXIfNSN1Y3Kw3Jd2GWXQjmJoXc7vAlcmAe0D9Jy395Dn8NaAogAbF5AASmJxPkuvzl2CEcHMus5v+QJsqDY2UYoka2bbH3dg9yYmbLw23nvlurvsvCdQfAP3BHuy9ltCUdcoLCzEo7Ub1AyWqblu6ALkfo0Y2PbQFzcpUxsDNBdotqPZGEBJt3fDkg0+I6T/r1eUb/LpltP+Exx98AdUBdQgkIrPVglW9ZFuHBviugdQysBUxBoqqZDR5ynzvuVrGMqwS3XJFs1z/9Ly9cVjEYASMKMlG0CwyIiJ3yoDwINUIf3nG8vAN+4MKVAkhUrkhh08+4mx6ArQwvZR6da1lscRHxVjD0Qef4LlhsRjo+uZEyr0n+vz537N38DGrJqGiiCg31zNoOyjGXM5ueDo8JmLRYJnsf8jzz1SnJxfUqzV7H77g2dOP0WTxbZMT7VLvwfnA+cn3PDl6QQqB4fiYpnN8+PHvSS26WF3CWPjks99Tuorga4gN4jw3l2+h81CWfUlUTjaLGkEDmu65s/fR1GEtGlZqxGGLEa6oMO4Ye/BCS13hJKJhLW03x/vVbd2VCFYMcZv0lZAekjaAKrrj1W++2zn8LvBuv38XzP9CNPuL8f9faIqQRQQSmrpbhmm3sqywvWAMWMl5/HHLbtyfqHeXyXj/dr80ewD0B/t1W5+ZrgZSUlQDuQNKTgpzJFQ92iUGhbAmgXNolxtq+I03q7D7OsUeLQVBowedy+XL/60ffhLZO3yOH42YLgODR7/jkw/+A6ENuV2oCD54qrIjsWQ+e83y9E+SE7AMmERbz6Fb4oYl4gx1Hdk7/pLJ4QtINzowC775n/9DiiLlFqg7aVibmTlXHPU69naItktGwzVN8BkMjSf2HruxFRpj9jRbD7qUq8t/0GcvxuAn1F1EBwWD/adw+Fy5OhHUc/PyH3n++xFSHFHHAWtfMHn8twwef0HxZdKubanKPRarGcNKqFzg4uwHZi+/E0IC6Zief83RwYBHzz9l0cCihuHxl0we/562CQwGA6aLOaVdMxkJzeKMt6ffZu9ZhDS/kouzr/Tpx1/QhjVeLNFNePbb/8JRDIjxhNjiWTGqapAll2c/sLg+zTJiHZQoha2JyePEUJWWwjlqydXfel/pmkB+ZktmV1/xwYvf0aWOVZ1lYz/7/X8ntQsOBh2nP/wvXZ7847Y7Xh6SstH645YfyXCedtFuJzS8+WfDrrwbZNn24r5Dsb/7QrwnefI9W/w13m1fM7Dz+04K/m5BRnercBP7I95e9k744d7zSjuffnxtvyTvHB4A/cEe7B73o49xRk9ls2CHT57ofabgQ05fD0l3/vQuEZknik1OcYJk6BYXMr/eU2MM5fAx470jVkHxUuClY1QWDAtI3ZzQTFlMX/Pmn/4g6Bqskltkgp9eyPX5K33x+T4Qqb3iW4vKAOv2cKJ0SVDfYfv6Y9AfXWcuWbMQFU0eow2OQCRmpbiU47MJ2wckDEYzLRrDpcyn32k1+RIjRZafLQYMhoc0cpYz21dXcvLtP+ij57+hOvocHe6xqKHuhL3xgNG+INEzHkdSO+f8/DU3Z6/ANzns0TaQZjK/fqW2tJjBMeVgjJgJXgtSGVFbMB6BZUqzvuDq8ge6xbXcUsXKxdlLSdLqow+/4Oj4OeswYFk7klZMnKUsVzgzw3dXLC5PuHr7HbGebm9b8is0rDBaIaYghr5XvO7eyHeGkJq8apKO2fUJ1WDE3uMXTMZHrLuCVkeUwyFJZqit+jr1PFRyBaTZeuR3xugO5m2H2LuH3h3H77O/Asl+ThC8d19/wQH+omu8Y+kXB9732QOgP9iDAarvlB2lRNu2eO8JarN6mOuwVnCjEdpBYSJt824d8m7MugdT6T3/dsX12SsWK8/eYcPk0JPcCHEVBE+nHWHVsJ69Zb04ZXX5nRA6pBA0eCAgRYH6wPWr7wQ70qfP/x3WjhBrEWtRBE0R55xGY3vO/P1TmTgDhcVIJARPTJC0wxqPK6CTiCEh27Ks3K4jNivO357wvPwQ31lULK6I7O0PifWhhnkn2rVcX72RRp0WK2F07DHVAdYZYmxZtS3arEntgvX8LdPzH6DJfbidhRAD+Jqrs9eybKPuP/IM9z9ArSFpLtda10ssNb55y/z6B1ZXrwW/yX4WrDHE9Yzz7xtpvOj+U5DiEaqHFHaIr2uSzgndOevlKfO3r9HFVCBgjSNVFkcihjbHoGNEQ5s1A0pHaj27i7nbzmSbBUWkubmSk1Tp41gwPiiJjEjJkSysuhVd193/bP6vEd0P9ku1B0B/sF+39d6V9jVCuYwrg3PXNXz11VdELSFFimEitG/Q9RJSIPaFZrd2fx2w6QVm0ARtLb491+ulZ7b27B0+YzDZJ4VIs1ozvzwnXr0Gf0OWetWsO97vq3Q2J9GlxPX3L7medgwOXzCaJLCwXJ6izSl+NhOS71P03m8aPcQ1r169AjcnRgeSKKoOf/4mJwZva44NuiFyY2J5cynfNF8rncmdLisFfw2LqdDVZF1zw/r6UrgJOltHDp59ymCyR9d2zGfXaL1kfXkCq2vQtWwWQimGDMZJoWtpz9/KRXC611mK8RHWjjHGUM9mtPUV7c33sH4r5Lx7jDGklLXXQSB45q9fynza6uDJF0z2B1AIqVmzmJ+znn0H6zPoVrJJldIU0XrN9dkp12sDIbfzNKxJl2/6Y713SO3cZCXOp/K2+14Hjw0Hjz9DbMl8sSItr1gul1k4XcBYtg1D7qOIH+zBfsr+D6ReMrb5vUn3AAAAAElFTkSuQmCC";


// ─── MARKETING ROI TAB ────────────────────────────────────────────────────────
function normalizePhone(p) {
  if (!p) return "";
  return String(p).replace(/\D/g,"").slice(-10);
}

function parseDuration(d) {
  if (!d) return 0;
  const s = String(d).trim();
  // Plain integer = already in seconds
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  // HH:MM:SS or MM:SS format
  const parts = s.split(":").map(Number);
  if (parts.length === 3) return parts[0]*3600 + parts[1]*60 + parts[2];
  if (parts.length === 2) return parts[0]*60 + parts[1];
  return 0;
}

function fmtDuration(secs) {
  const m = Math.floor(secs/60), s = secs%60;
  return `${m}m ${s}s`;
}

function getCallDateKey(row, mode) {
  const d = row["Date"] || row["Call start time"] || row["call_start_time"] || "";
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  if (mode === "month") return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`;
  if (mode === "week") {
    const day = new Date(dt); day.setHours(0,0,0,0);
    const dow = day.getDay();
    const sun = new Date(day); sun.setDate(day.getDate() - dow);
    const sat = new Date(sun); sat.setDate(sun.getDate() + 6);
    const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    return `${fmt(sun)}_${fmt(sat)}`;
  }
  if (mode === "year") return `${dt.getFullYear()}`;
  return null;
}

// ─── CAMPAIGN / SOURCE NORMALIZERS (module level — used by MarketingROI + ScorecardSummary) ──
function normalizeCampaign(raw) {
  if (!raw) return "Other";
  if (/boarding/i.test(raw)) return raw.trim();
  if (/daycare/i.test(raw)) return raw.trim();
  if (/training/i.test(raw)) return raw.trim();
  if (/grooming/i.test(raw)) return raw.trim();
  if (/pmax|performance max/i.test(raw)) return raw.trim();
  if (/all services/i.test(raw)) return raw.trim();
  return "Other";
}

function normalizeSource(raw) {
  if (!raw) return "(direct)";
  const s = String(raw).toLowerCase().trim();
  if (/google/i.test(s)) return "Google";
  if (/bing/i.test(s)) return "Bing";
  if (/yahoo/i.test(s)) return "Yahoo";
  if (/facebook|l\.facebook|m\.facebook/i.test(s)) return "Facebook";
  if (s === "(direct)" || s === "direct") return "(direct)";
  if (/waggle|waggles/i.test(s)) return "Organic / Direct";
  return "Other";
}


// ─── CUSTOMER MASTER TABLE ────────────────────────────────────────────────────
// Built once when all three files are loaded. Keyed by PP-ID (numeric string).
// Every component reads from this — no more rebuilding lookups per render.
function buildCustomerMaster(rows, propetRows, callRows) {
  if (!rows || !propetRows || !callRows) return null;

  // ── Step 1: Build phone → ProPet lookup ────────────────────────────────────
  const phoneMap = {}; // normalized phone → propet row
  const ppidMap  = {}; // ppid string → propet row
  propetRows.forEach(r => {
    const ppid = String(r.id || "").trim();
    if (ppid) ppidMap[ppid] = r;
    ["phone_1","phone_2","phone_3"].forEach(f => {
      const p = String(r[f] || "").replace(/\D/g, "").slice(-10);
      if (p.length === 10 && !phoneMap[p]) phoneMap[p] = r;
    });
  });

  // ── Step 2: Build PP-ID → QB transactions lookup ───────────────────────────
  const ppidTxns = {}; // ppid → [{date, amt, num, service}]
  rows.forEach(r => {
    const raw  = String(r["PP-ID"] || "").trim();
    let ppid   = raw.startsWith("PP-") ? raw.slice(3) : raw;
    // Fallback: extract from name fields when QB sync broke (Nov 2025+)
    // QB stores missing PP-ID as numeric 0 — SheetJS outputs "0" — treat as blank
    if (!ppid || ppid === "0" || !/^\d+$/.test(ppid)) {
      const fields = [
        String(r["Consolidated Name"] || ""),
        String(r["Customer"] || ""),
        String(r["First Name"] || ""),
      ];
      for (const field of fields) {
        const m = field.match(/\b(\d{6,8})\b/);
        if (m) { ppid = m[1]; break; }
      }
    }
    const amt = parseFloat(String(r.Amount || "").replace(/[^0-9.-]/g, ""));
    const dt  = String(r.Date || "").slice(0, 10);
    if (ppid && /^\d+$/.test(ppid) && ppid !== "0" && amt > 0 && dt.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const d = new Date(parseInt(dt.slice(0,4)), parseInt(dt.slice(5,7))-1, parseInt(dt.slice(8,10)));
      if (!ppidTxns[ppid]) ppidTxns[ppid] = [];
      ppidTxns[ppid].push({
        date: d,
        amt,
        num:     String(r.Num || "").trim(),
        service: String(r["Product/Service"] || "").trim(),
      });
    }
  });

  // ── Step 3: Build call history per phone ───────────────────────────────────
  const phoneCallHistory = {}; // phone → [{date, campaign, duration, qualified, source}]
  callRows.forEach(call => {
    const phone = String(call["caller_phone"] || call["Customer #"] || call["Caller ID"] || "").replace(/\D/g, "").slice(-10);
    if (!phone || phone.length !== 10) return;
    const dt  = String(call["Call start time"] || call.Date || "").slice(0, 10);
    const dur = parseDuration(call["Duration"]);
    if (!phoneCallHistory[phone]) phoneCallHistory[phone] = [];
    phoneCallHistory[phone].push({
      date:      dt,
      dateObj:   dt.match(/^\d{4}-\d{2}-\d{2}$/) ? new Date(parseInt(dt.slice(0,4)), parseInt(dt.slice(5,7))-1, parseInt(dt.slice(8,10))) : null,
      campaign:  normalizeCampaign(call["campaign"]),
      source:    normalizeSource(call["source"]),
      duration:  dur,
      qualified: dur >= 120,
      name:      String(call.Name || "").trim(),
    });
  });
  // Sort each history by date ascending
  Object.values(phoneCallHistory).forEach(h => h.sort((a,b) => a.date.localeCompare(b.date)));

  // ── Step 4: Build master keyed by PP-ID ────────────────────────────────────
  const master = {};

  propetRows.forEach(pp => {
    const ppid = String(pp.id || "").trim();
    if (!ppid) return;

    const name  = `${pp.first_name || ""} ${pp.last_name || ""}`.trim();
    const createdStr = String(pp.created_at || "").slice(0, 10);
    const created = createdStr.match(/^\d{4}-\d{2}-\d{2}$/)
      ? new Date(parseInt(createdStr.slice(0,4)), parseInt(createdStr.slice(5,7))-1, parseInt(createdStr.slice(8,10)))
      : null;

    // Find phones for this ProPet record
    const phones = ["phone_1","phone_2","phone_3"]
      .map(f => String(pp[f] || "").replace(/\D/g, "").slice(-10))
      .filter(p => p.length === 10);

    // Gather all calls for this customer (any of their phones)
    const allCalls = [];
    phones.forEach(phone => {
      (phoneCallHistory[phone] || []).forEach(c => allCalls.push({ ...c, phone }));
    });
    allCalls.sort((a,b) => a.date.localeCompare(b.date));

    const firstCall = allCalls[0] || null;

    // QB transactions
    const txns = (ppidTxns[ppid] || []).sort((a,b) => a.date - b.date);
    const firstTxn = txns[0] || null;

    // Revenue windows from ProPet created_at (not call date)
    // You can't book without a ProPet record — this is the true start of the relationship
    let rev30 = 0, rev60 = 0, rev90 = 0, cltv = 0;
    if (created) {
      txns.forEach(t => {
        const days = (t.date - created) / 86400000;
        cltv += t.amt;
        if (days <= 30)  rev30 += t.amt;
        if (days <= 60)  rev60 += t.amt;
        if (days <= 90)  rev90 += t.amt;
      });
    } else {
      cltv = txns.reduce((s, t) => s + t.amt, 0);
    }

    // Calls before first transaction (conversion journey depth)
    const callsBeforeFirstTxn = firstTxn
      ? allCalls.filter(c => c.dateObj && c.dateObj <= firstTxn.date).length
      : allCalls.length;

    // Data quality source
    let source = "no_qb";
    if (txns.length > 0) source = "qb";
    else {
      const propetValue = parseFloat(String(pp.total_value || "").replace(/[^0-9.-]/g, "")) || 0;
      if (propetValue > 0) source = "propet_only"; // Layer 2 — confirmed but QB sync broken
    }

    // New vs existing: created on/after first call = potentially new
    const isNew = firstCall && created
      ? created >= firstCall.dateObj
      : false;

    master[ppid] = {
      // Identity
      ppid,
      name,
      phones,
      status:    pp.status || "",

      // ProPet
      created,
      createdStr,
      propetValue: parseFloat(String(pp.total_value || "").replace(/[^0-9.-]/g, "")) || 0,

      // Call history
      allCalls,
      firstCall,
      firstCallDate:     firstCall?.dateObj || null,
      firstCampaign:     firstCall?.campaign || "",
      firstSource:       firstCall?.source || "",
      callsBeforeConvert: callsBeforeFirstTxn,
      totalCalls:        allCalls.length,
      qualifiedCalls:    allCalls.filter(c => c.qualified).length,

      // QB revenue
      txns,
      firstTxn,
      firstTxnDate:    firstTxn?.date || null,
      firstTxnNum:     firstTxn?.num || "",
      firstService:    firstTxn?.service || "",
      cltv,
      rev30,
      rev60,
      rev90,

      // Classification
      isNew,
      source, // "qb" | "propet_only" | "no_qb"
    };
  });

  // ── Step 5: Summary stats (computed once) ──────────────────────────────────
  const all     = Object.values(master);
  const withQB  = all.filter(c => c.source === "qb");
  const layer2  = all.filter(c => c.source === "propet_only");

  return {
    byPPID:   master,                              // main lookup
    all,                                           // array of all customers
    withQB,                                        // confirmed QB revenue
    layer2,                                        // ProPet only — pending QB sync
    phoneMap,                                      // phone → propet (for call matching)
    ppidTxns,                                      // ppid → txns (for dynamic calcs)
    stats: {
      total:       all.length,
      withRevenue: withQB.length,
      layer2:      layer2.length,
      totalCLTV:   withQB.reduce((s,c) => s+c.cltv, 0),
      avgCLTV:     withQB.length ? withQB.reduce((s,c) => s+c.cltv, 0)/withQB.length : 0,
    }
  };
}

function MarketingROI({ callRows, setCallRows, callLoading, setCallLoading,
    marketingPeriod, setMarketingPeriod, selectedMarketingPeriod,
    setSelectedMarketingPeriod, callFileRef, propetRows, rows, monthlySpend, setMonthlySpend,
    customerMaster, selectedLocationCode }) {

  const [hoveredCard, setHoveredCard] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x:0, y:0, above:false });
  const [chartMode, setChartMode] = useState("roi");
  const [chartTip, setChartTip] = useState(null);

  // Filter calls by location if selected
  const locationCallRows = useMemo(() => {
    if (!callRows) return null;
    if (!selectedLocationCode) return callRows;
    return callRows.filter(r => r.location === selectedLocationCode);
  }, [callRows, selectedLocationCode]);

  // Build period options from call data
  const periodOptions = useMemo(() => {
    if (!locationCallRows) return [];
    const keys = new Set();
    locationCallRows.forEach(r => { const k = getCallDateKey(r, marketingPeriod); if (k) keys.add(k); });
    const sorted = [...keys].sort((a,b) => b.localeCompare(a));
    return sorted.map(k => {
      let label = k;
      if (marketingPeriod === "month") {
        const [y,m] = k.split("-");
        label = new Date(parseInt(y), parseInt(m)-1, 1).toLocaleString("default",{month:"short",year:"numeric"});
      } else if (marketingPeriod === "week") {
        const [s,e] = k.split("_");
        label = `${s} – ${e}`;
      }
      return { key: k, label };
    });
  }, [locationCallRows, marketingPeriod]);

  // Auto-select most recent period
  useEffect(() => {
    if (periodOptions.length && !selectedMarketingPeriod) {
      setSelectedMarketingPeriod(periodOptions[0].key);
    }
  }, [periodOptions]);

  // Filter calls to selected period
  const filteredCalls = useMemo(() => {
    if (!locationCallRows) return [];
    if (!selectedMarketingPeriod) return locationCallRows;
    return locationCallRows.filter(r => getCallDateKey(r, marketingPeriod) === selectedMarketingPeriod);
  }, [locationCallRows, selectedMarketingPeriod, marketingPeriod]);

  // Compute call metrics
  const metrics = useMemo(() => {
    if (!filteredCalls.length) return null;
    const total = filteredCalls.length;
    const durations = filteredCalls.map(r => parseDuration(r["Duration"]));
    const hangups = durations.filter(d => d === 0).length;
    const short = durations.filter(d => d > 0 && d < 30).length;
    const real = durations.filter(d => d >= 120).length;
    const avgDur = Math.round(durations.reduce((s,d)=>s+d,0) / total);
    const campaigns = new Set(filteredCalls.map(r => r["campaign"]).filter(Boolean));
    // prev period for indicators
    const prevIdx = periodOptions.findIndex(p => p.key === selectedMarketingPeriod);
    let prevFiltered = [];
    if (prevIdx >= 0 && prevIdx < periodOptions.length - 1) {
      const prevKey = periodOptions[prevIdx+1].key;
      prevFiltered = locationCallRows.filter(r => getCallDateKey(r, marketingPeriod) === prevKey);
    }
    const prevTotal = prevFiltered.length;
    const prevReal = prevFiltered.filter(r => parseDuration(r["Duration"]) >= 120).length;
    const prevAvgDur = prevFiltered.length
      ? Math.round(prevFiltered.map(r=>parseDuration(r["Duration"])).reduce((s,d)=>s+d,0) / prevFiltered.length)
      : 0;
    const prevCampaigns = new Set(prevFiltered.map(r => r["campaign"]).filter(Boolean));
    return { total, hangups, short, real, avgDur, campaigns: campaigns.size,
      prevTotal, prevReal, prevAvgDur, prevCampaigns: prevCampaigns.size };
  }, [filteredCalls, locationCallRows, selectedMarketingPeriod, marketingPeriod, periodOptions]);

  // Campaign breakdown
  // normalizeCampaign and normalizeSource are defined at module level

  const campaignData = useMemo(() => {
    if (!filteredCalls.length) return [];
    const map = {};
    filteredCalls.forEach(r => {
      const camp = normalizeCampaign(r["campaign"]);
      const dur = parseDuration(r["Duration"]);
      if (!map[camp]) map[camp] = { name: camp, total: 0, real: 0, short: 0, hangup: 0, totalDur: 0 };
      map[camp].total++;
      map[camp].totalDur += dur;
      if (dur === 0) map[camp].hangup++;
      else if (dur < 30) map[camp].short++;
      else if (dur >= 120) map[camp].real++;
    });
    // Sort: named campaigns first (not "Other"), then Other last
    return Object.values(map).sort((a,b) => {
      if (a.name === "Other") return 1;
      if (b.name === "Other") return -1;
      return b.total - a.total;
    });
  }, [filteredCalls]);

  // Source breakdown
  const sourceData = useMemo(() => {
    if (!filteredCalls.length) return [];
    const map = {};
    filteredCalls.forEach(r => {
      const src = normalizeSource(r["source"]);
      if (!map[src]) map[src] = 0;
      map[src]++;
    });
    // Sort: named sources first, Other last
    return Object.entries(map).sort((a,b) => {
      if (a[0] === "Other") return 1;
      if (b[0] === "Other") return -1;
      return b[1] - a[1];
    });
  }, [filteredCalls]);

  const Widget = ({ label, value, prev, prevValue, currentNumeric, color=_tenantHeaderBg, tooltip="" }) => {
    const [show, setShow] = useState(false);
    let dir = null, pct = null;
    const compareVal = currentNumeric != null ? currentNumeric : value;
    if (prev != null && prevValue != null && prevValue !== 0) {
      const diff = compareVal - prevValue;
      pct = Math.abs(Math.round(diff/prevValue*100));
      dir = diff > 0 ? "up" : diff < 0 ? "down" : null;
    }
    return (
      <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, padding:"16px 20px", flex:1, minWidth:160, position:"relative" }}
        onMouseEnter={()=>tooltip&&setShow(true)} onMouseLeave={()=>setShow(false)}>
        <div style={{ fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT, marginBottom:6 }}>{label}</div>
        <div style={{ fontSize:26, fontWeight:800, color:color, fontFamily:FONT, lineHeight:1 }}>{value}</div>
        {dir && (
          <div style={{ fontSize:11, fontFamily:FONT, marginTop:4, color: dir==="up"?GREEN:RED, fontWeight:600 }}>
            {dir==="up"?"↑":"↓"} {pct}% vs last {marketingPeriod}
          </div>
        )}
        {tooltip && show && (
          <div style={{ position:"absolute", bottom:"calc(100% + 6px)", left:0, zIndex:999, background:_tenantHeaderBg, color:WHITE, borderRadius:8, padding:"10px 14px", fontSize:11, fontFamily:FONT, lineHeight:1.6, minWidth:220, maxWidth:300, boxShadow:"0 8px 24px rgba(0,0,0,0.25)", pointerEvents:"none" }}>
            {tooltip}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

      {/* Sticky filter + widgets */}
      <div data-sticky="marketing" style={{ position:"sticky", top:60, zIndex:90, background:GRAY_BG, paddingBottom:8, marginBottom:-8 }}>

        {/* Filter bar */}
        <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, padding:"14px 18px", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap", marginBottom:12 }}>
          <span style={{ fontSize:12, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT }}>Filter by</span>
          <div style={{ display:"flex", gap:0, border:`1px solid ${GRAY_B}`, borderRadius:6, overflow:"hidden" }}>
            {[["month","Month"],["week","Week"],["year","Year"]].map(([m,l])=>(
              <button key={m} onClick={()=>{ setMarketingPeriod(m); setSelectedMarketingPeriod(null); }}
                style={{ padding:"6px 14px", border:"none", borderRight:`1px solid ${GRAY_B}`, cursor:"pointer", fontSize:12, fontWeight:600, fontFamily:FONT,
                  background:marketingPeriod===m?_tenantHeaderBg:WHITE, color:marketingPeriod===m?WHITE:GRAY_MID }}>{l}</button>
            ))}
          </div>
          {locationCallRows && periodOptions.length > 0 && (
            <select value={selectedMarketingPeriod||""} onChange={e=>setSelectedMarketingPeriod(e.target.value)}
              style={{ fontSize:12, padding:"6px 10px", borderRadius:6, border:`1px solid ${GRAY_B}`, fontFamily:FONT, background:WHITE, color:_tenantHeaderBg, cursor:"pointer" }}>
              <option value="">All {marketingPeriod === "month" ? "Months" : marketingPeriod === "week" ? "Weeks" : "Years"}</option>
              {periodOptions.map(p=><option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          )}
          {locationCallRows && (
            <span style={{ marginLeft:"auto", fontSize:12, color:GREEN, fontWeight:600, fontFamily:FONT }}>
              ✓ {locationCallRows.length.toLocaleString()} calls{selectedLocationCode ? " (filtered)" : " loaded"}
            </span>
          )}
        </div>

        {/* Widgets */}
        {metrics && (
          <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
            <Widget label="Total Calls" value={metrics.total.toLocaleString()} prev={true} prevValue={metrics.prevTotal}
              tooltip="All calls tracked in the selected period, including hangups and short calls." />
            <Widget label="Qualified Calls (2min+)" value={`${metrics.real.toLocaleString()} (${Math.round(metrics.real/metrics.total*100)}%)`} prev={true} prevValue={metrics.prevTotal > 0 ? Math.round(metrics.prevReal/metrics.prevTotal*100) : null} currentNumeric={Math.round(metrics.real/metrics.total*100)} color={GREEN}
              tooltip="Calls lasting 2 minutes or more. Count + qualification rate shown. Indicator tracks rate change vs prior period." />
            <Widget label="Avg Call Duration" value={fmtDuration(metrics.avgDur)} prev={true} prevValue={metrics.prevAvgDur}
              tooltip="Average duration of all calls including hangups. Filter to real calls only for a more meaningful average." />
            <Widget label="Campaigns Active" value={metrics.campaigns} prev={true} prevValue={metrics.prevCampaigns} color={"#7C3AED"}
              tooltip="Number of distinct Google Ads campaigns that generated calls in this period." />
          </div>
        )}
      </div>

      {/* No data state */}
      {!locationCallRows && (
        <div style={{ background:WHITE, border:`2px dashed ${GRAY_B}`, borderRadius:12, padding:60, textAlign:"center" }}>
          <div style={{ fontSize:36, marginBottom:12 }}>📞</div>
          <div style={{ fontSize:17, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT, marginBottom:8 }}>No call data loaded</div>
          <div style={{ fontSize:13, color:GRAY_MID, fontFamily:FONT, marginBottom:24 }}>Upload your Google Ads call tracking CSV to begin the marketing ROI analysis</div>
          <button onClick={()=>{ const f = document.querySelector("input[data-universal]"); if(f) f.click(); }} style={{ background:_tenantHeaderBg, color:WHITE, border:"none", borderRadius:8, padding:"10px 24px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>
            ↑ Upload Google Calls CSV
          </button>
          {callLoading && <div style={{ marginTop:12, fontSize:12, color:_tenantHeaderBg, fontFamily:FONT }}>⏳ Reading file…</div>}
        </div>
      )}

      {/* Call Quality section */}
      {locationCallRows && filteredCalls.length > 0 && (() => {
        const total = filteredCalls.length;
        const buckets = [
          { label:"Zero Duration (Hangups)", count: filteredCalls.filter(r=>parseDuration(r["Duration"])===0).length, color:"#EF4444", desc:"Caller hung up before anyone answered" },
          { label:"Under 30 Seconds", count: filteredCalls.filter(r=>{ const d=parseDuration(r["Duration"]); return d>0&&d<30; }).length, color:"#F97316", desc:"Wrong number or immediate hangup" },
          { label:"30s – 2 Minutes", count: filteredCalls.filter(r=>{ const d=parseDuration(r["Duration"]); return d>=30&&d<120; }).length, color:"#EAB308", desc:"Gray zone — could be legitimate" },
          { label:"2 Minutes or More", count: filteredCalls.filter(r=>parseDuration(r["Duration"])>=120).length, color:GREEN, desc:"Real conversations" },
        ];
        return (
          <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
            <div style={{ padding:"14px 20px", background:_tenantHeaderBg, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>Call Quality Breakdown</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", fontFamily:FONT, marginTop:2 }}>Not all calls are equal — duration filters out noise</div>
              </div>

            </div>
            <div style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:14 }}>
              {buckets.map(b => (
                <div key={b.label}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <span style={{ fontSize:12, fontWeight:600, color:_tenantHeaderBg, fontFamily:FONT }}>{b.label}</span>
                    <span style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT }}>{b.count.toLocaleString()} ({Math.round(b.count/total*100)}%) — {b.desc}</span>
                  </div>
                  <div style={{ height:10, background:GRAY_B, borderRadius:5, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${b.count/total*100}%`, background:b.color, borderRadius:5, transition:"width 0.4s" }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Campaign Performance section */}
      {locationCallRows && campaignData.length > 0 && (
        <div data-pdf="campaign-table" style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
          <div style={{ padding:"14px 20px", background:_tenantHeaderBg }}>
            <div style={{ fontSize:13, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>Campaign Performance</div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", fontFamily:FONT, marginTop:2 }}>Calls by campaign — qualified calls (2min+) are the only reliable signal</div>
          </div>
          <div style={{ borderTop:`1px solid ${GRAY_B}` }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 90px 90px 90px 90px 100px", padding:"8px 20px", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}` }}>
              {["Campaign","Total","Qualified","Short","Hangup","Avg Duration"].map((h,i)=>(
                <span key={i} style={{ fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT, textAlign:i>0?"center":"left" }}>{h}</span>
              ))}
            </div>
            {campaignData.map((c,i) => {
              const maxTotal = campaignData[0].total;
              return (
                <div key={c.name} style={{ display:"grid", gridTemplateColumns:"1fr 90px 90px 90px 90px 100px", padding:"10px 20px", borderBottom:`1px solid ${GRAY_B}`, background:i%2===0?WHITE:GRAY_BG }}>
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT }}>{c.name}</div>
                    <div style={{ marginTop:4, height:4, background:GRAY_B, borderRadius:2, maxWidth:200, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${c.total/maxTotal*100}%`, background:_tenantHeaderBg, borderRadius:2 }} />
                    </div>
                  </div>
                  <span style={{ fontSize:12, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{c.total}</span>
                  <span style={{ fontSize:12, fontWeight:700, color:GREEN, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{c.real}</span>
                  <span style={{ fontSize:12, color:AMBER, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{c.short}</span>
                  <span style={{ fontSize:12, color:RED, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{c.hangup}</span>
                  <span style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{fmtDuration(Math.round(c.totalDur/c.total))}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Source breakdown */}
      {locationCallRows && sourceData.length > 0 && (
        <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
          <div style={{ padding:"14px 20px", background:_tenantHeaderBg }}>
            <div style={{ fontSize:13, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>Traffic Source</div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", fontFamily:FONT, marginTop:2 }}>Where callers came from before dialing</div>
          </div>
          <div style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:12 }}>
            {sourceData.map(([src, cnt]) => (
              <div key={src}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                  <span style={{ fontSize:12, fontWeight:600, color:_tenantHeaderBg, fontFamily:FONT }}>{src}</span>
                  <span style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT }}>{cnt.toLocaleString()} ({Math.round(cnt/filteredCalls.length*100)}%)</span>
                </div>
                <div style={{ height:8, background:GRAY_B, borderRadius:4, overflow:"hidden" }}>
                  <div style={{ height:"100%", width:`${cnt/filteredCalls.length*100}%`, background:"#2563EB", borderRadius:4 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ProPet upload prompt */}
      {locationCallRows && !propetRows && (
        <div style={{ background:WHITE, border:`2px dashed ${GRAY_B}`, borderRadius:10, padding:"40px 24px", textAlign:"center" }}>
          <div style={{ fontSize:28, marginBottom:10 }}>🔗</div>
          <div style={{ fontSize:15, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT, marginBottom:8 }}>Upload Customer List to Unlock Match Analysis</div>
          <div style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT, lineHeight:1.8, maxWidth:520, margin:"0 auto 20px" }}>
            Match every call against your customer database to identify existing customers clicking your ads
            and calculate your true cost per new customer acquisition. Upload your full customer list
            (.xlsx or .csv) using the <strong>New File</strong> button above.
          </div>
          <button onClick={()=>{ const f = document.querySelector("input[data-universal]"); if(f) f.click(); }}
            style={{ background:_tenantHeaderBg, color:WHITE, border:"none", borderRadius:8, padding:"10px 24px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:FONT }}>
            ↑ Upload Customers
          </button>
        </div>
      )}

      {/* Match Analysis — only when both files loaded */}
      {locationCallRows && propetRows && (() => {
        // ── Build phone → ProPet lookup ───────────────────────────────────────
        const phoneMap = {};
        propetRows.forEach(r => {
          ["phone_1","phone_2","phone_3"].forEach(f => {
            const p = String(r[f]||"").replace(/\D/g,"").slice(-10);
            if (p.length===10 && !phoneMap[p]) phoneMap[p] = r;
          });
        });

        // ── Build PP-ID → transactions lookup [(dateStr, amount)] ─────────────
        // Needed for: confirmed conversion check, 30/60/90d revenue, CLTV
        const ppidTxns = {}; // propet_id (numeric string) → [{date: Date, amt: number}]
        if (rows) {
          rows.forEach(r => {
            const raw  = String(r["PP-ID"]||"").trim();
            // Primary: normalize PP-ID column (strip PP- prefix)
            let ppid = raw.startsWith("PP-") ? raw.slice(3) : raw;
            // Fallback: if PP-ID column is blank or zero, extract numeric ID from name fields
            // QB sync bug (Nov 2025+) appends ID to Customer/Consolidated Name e.g. "Varma, Sarina 2408637"
            // Note: QB stores missing PP-ID as numeric 0, SheetJS converts to string "0" — treat as blank
            if (!ppid || ppid === "0" || !/^\d+$/.test(ppid)) {
              const nameFields = [
                String(r["Consolidated Name"]||""),
                String(r["Customer"]||""),
                String(r["First Name"]||""),
              ];
              for (const field of nameFields) {
                const m = field.match(/\b(\d{6,8})\b/);
                if (m) { ppid = m[1]; break; }
              }
            }
            const amt  = parseFloat(String(r.Amount||"").replace(/[^0-9.-]/g,""));
            const dt   = String(r.Date||"").slice(0,10);
            if (ppid && /^\d+$/.test(ppid) && amt > 0 && dt.match(/^\d{4}-\d{2}-\d{2}$/)) {
              const d = new Date(parseInt(dt.slice(0,4)), parseInt(dt.slice(5,7))-1, parseInt(dt.slice(8,10)));
              if (!ppidTxns[ppid]) ppidTxns[ppid] = [];
              ppidTxns[ppid].push({ date: d, amt });
            }
          });
        }

        // ── Match filtered calls → classify each call ─────────────────────────
        // TIME-BASED: only calls within the selected period (filteredCalls already handles this)
        // New Customer = ProPet created_at >= call date AND at least one QB txn >= call date
        const matchResults = filteredCalls.map(call => {
          const phone = String(call["caller_phone"] || call["Customer #"] || call["Caller ID"]||"").replace(/\D/g,"").slice(-10);
          const dur   = parseDuration(call["Duration"]);
          const camp  = normalizeCampaign(call["campaign"]);
          const pp    = phone.length===10 ? phoneMap[phone] : null;
          let status  = "untracked";
          let cltv = 0, rev30 = 0, rev60 = 0, rev90 = 0;

          if (pp) {
            const callDt = String(call.Date||"").slice(0,10);
            const creatDt = String(pp.created_at||"").slice(0,10);
            let callDate = null, created = null;
            if (callDt.match(/^\d{4}-\d{2}-\d{2}$/)) {
              callDate = new Date(parseInt(callDt.slice(0,4)), parseInt(callDt.slice(5,7))-1, parseInt(callDt.slice(8,10)));
            }
            if (creatDt.match(/^\d{4}-\d{2}-\d{2}$/)) {
              created = new Date(parseInt(creatDt.slice(0,4)), parseInt(creatDt.slice(5,7))-1, parseInt(creatDt.slice(8,10)));
            }

            if (callDate && created) {
              if (created < callDate) {
                status = "existing";
              } else {
                // Potentially new — must have at least one QB txn on/after call date
                // ppidTxns keyed by numeric ID (no PP- prefix) to handle both QB formats
                const ppKey = String(pp.id);
                const txns  = ppidTxns[ppKey] || [];
                const txnsAfterCall = txns.filter(t => t.date >= callDate);
                if (txnsAfterCall.length > 0) {
                  status = "new";
                  // COHORT-BASED: CLTV = ALL time revenue (not just after call)
                  cltv = txns.reduce((s,t) => s+t.amt, 0);
                  // TIME-BASED windows from individual call date
                  rev30 = txnsAfterCall.filter(t => (t.date-callDate)/86400000 <= 30).reduce((s,t)=>s+t.amt,0);
                  rev60 = txnsAfterCall.filter(t => (t.date-callDate)/86400000 <= 60).reduce((s,t)=>s+t.amt,0);
                  rev90 = txnsAfterCall.filter(t => (t.date-callDate)/86400000 <= 90).reduce((s,t)=>s+t.amt,0);
                } else {
                  // Tertiary fallback: check ProPet total_value > 0
                  // Confirms conversion when QB sync is broken — no dollar amounts used from ProPet
                  const propetValue = parseFloat(String(pp.total_value||"").replace(/[^0-9.-]/g,"")) || 0;
                  if (propetValue > 0) {
                    status = "new"; // confirmed converted, revenue pending QB sync
                    cltv = 0; // QB revenue not available — do not use ProPet total_value for calculations
                  } else {
                    status = "untracked"; // in ProPet but no confirming revenue anywhere
                  }
                }
              }
            }
          }
          return { camp, dur, qualified: dur>=120, status, cltv, rev30, rev60, rev90, ppid: pp ? String(pp.id) : null };
        });

        // ── Deduplicate new customers — if same person called multiple times,
        //    count them once (first call wins) for CLTV/revenue metrics
        const seenNewPpids = new Set();
        const matchResultsDeduped = matchResults.map(r => {
          if (r.status === "new" && r.ppid) {
            if (seenNewPpids.has(r.ppid)) {
              // Subsequent call from same new customer — count as call but not as new acquisition
              return { ...r, status: "existing_repeat", cltv:0, rev30:0, rev60:0, rev90:0 };
            }
            seenNewPpids.add(r.ppid);
          }
          return r;
        });

        // ── TIME-BASED aggregates (from filtered period) ──────────────────────
        const total      = matchResults.length; // total calls unchanged
        const existing   = matchResults.filter(r=>r.status==="existing"||r.status==="existing_repeat").length;
        const newCusts   = matchResultsDeduped.filter(r=>r.status==="new");
        const nNew       = newCusts.length;
        const untracked  = matchResults.filter(r=>r.status==="untracked").length;
        const qualified  = matchResults.filter(r=>r.qualified).length;
        const qualExist  = matchResults.filter(r=>r.qualified&&(r.status==="existing"||r.status==="existing_repeat")).length;
        const qualNew    = matchResultsDeduped.filter(r=>r.qualified&&r.status==="new").length;

        // ── COHORT-BASED aggregates (acquired in period, revenue all-time) ────
        const totalCLTV  = newCusts.reduce((s,r)=>s+r.cltv, 0);
        const avgCLTV    = nNew ? totalCLTV/nNew : 0;
        const total30    = newCusts.reduce((s,r)=>s+r.rev30, 0);
        const total60    = newCusts.reduce((s,r)=>s+r.rev60, 0);
        const total90    = newCusts.reduce((s,r)=>s+r.rev90, 0);
        const avg30      = nNew ? total30/nNew : 0;

        // ── Calculate total spend for the selected period ─────────────────────
        // spend = monthlySpend × number of months in the selected period
        // This ensures CAC/CQC/ROI reflect the true spend for the period, not just 1 month
        const periodMonths = (() => {
          if (!selectedMarketingPeriod) {
            // "All" — count distinct months across all calls
            const months = new Set(locationCallRows.map(r => String(r.Date||"").slice(0,7)).filter(Boolean));
            return months.size || 1;
          }
          if (marketingPeriod === "month") return 1;
          if (marketingPeriod === "year")  return 12;
          if (marketingPeriod === "week")  return 1/4.33; // ~1 week = 0.23 months
          return 1;
        })();
        const spend      = monthlySpend * periodMonths;
        const roiLife    = spend > 0 ? (totalCLTV  - spend)/spend*100 : 0;
        const roi90      = spend > 0 ? (total90    - spend)/spend*100 : 0;
        const roi60      = spend > 0 ? (total60    - spend)/spend*100 : 0;
        const roi30      = spend > 0 ? (total30    - spend)/spend*100 : 0;
        const cac        = nNew  > 0 ? spend/nNew : 0;
        const cqc        = qualified > 0 ? spend/qualified : 0;

        // ── Per-campaign breakdown ────────────────────────────────────────────
        const campMap = {};
        matchResultsDeduped.forEach(r => {
          if (!campMap[r.camp]) campMap[r.camp] = { total:0,qualified:0,qualExist:0,qualNew:0,existing:0,nNew:0,untracked:0,cltv:[],rev30:[],rev60:[],rev90:[] };
          const c = campMap[r.camp];
          c.total++;
          if (r.qualified) {
            c.qualified++;
            if (r.status==="existing"||r.status==="existing_repeat") c.qualExist++;
            if (r.status==="new")      c.qualNew++;
          }
          if (r.status==="existing"||r.status==="existing_repeat") c.existing++;
          if (r.status==="new")    { c.nNew++; c.cltv.push(r.cltv); c.rev30.push(r.rev30); c.rev60.push(r.rev60); c.rev90.push(r.rev90); }
          if (r.status==="untracked") c.untracked++;
        });
        const campRows = Object.entries(campMap)
          .sort((a,b) => a[0]==="Other"?1:b[0]==="Other"?-1:b[1].total-a[1].total);

        const fmt$   = n => n > 0 ? `$${Math.round(n).toLocaleString()}` : "—";
        const fmtPct = (n,d) => d > 0 ? `${Math.round(n/d*100)}%` : "—";
        const fmtN   = (n,d) => `${n.toLocaleString()} (${fmtPct(n,d)})`;
        const fmtROI = r => `${r >= 0 ? "+" : ""}${Math.round(r)}%`;
        const roiColor = r => r >= 0 ? GREEN : RED;
        const avg    = arr => arr.length ? arr.reduce((s,v)=>s+v,0)/arr.length : 0;

        // ── Top widgets ───────────────────────────────────────────────────────
        const topWidgets = [
          { label:"Existing Customers", value:existing.toLocaleString(),
            sub:`${Math.round(existing/total*100)}% of calls`, color:AMBER,
            tip:"Confirmed existing customers — ProPet record created before the call date. These calls should not count as new lead acquisitions." },
          { label:"New Customers", value:nNew.toLocaleString(),
            sub:`${Math.round(nNew/total*100)}% of calls`, color:GREEN,
            tip:"Callers added to the booking system on/after the call date WITH at least one transaction on/after the call. True confirmed acquisitions." },
          { label:"Conv Rate", value:fmtPct(nNew,total),
            sub:`${fmtPct(nNew,qualified)} of qualified`, color:"#7C3AED",
            tip:"Top: New Customers ÷ Total Calls. Bottom: New Customers ÷ Qualified Calls (2min+). Based on selected period only." },
          { label:"CAC", value:fmt$(cac),
            sub:`Spend ÷ New Customers`, color:_tenantHeaderBg,
            tip:`Customer Acquisition Cost = $${Math.round(spend).toLocaleString()} period spend (${Math.round(periodMonths * 10)/10} months × $${monthlySpend.toLocaleString()}/mo) ÷ ${nNew} new customers.` },
          { label:"Cost/Qualified Call", value:fmt$(cqc),
            sub:`Spend ÷ Qualified Calls`, color:GRAY_MID,
            tip:`Cost Per Qualified Call = $${Math.round(spend).toLocaleString()} period spend ÷ ${qualified} qualified calls in selected period.` },
          { label:"Avg CLTV", value:fmt$(avgCLTV),
            sub:`${nNew} customers matched`, color:_tenantHeaderBg,
            tip:"Average total QB lifetime revenue of new customers acquired in this period. Uses ALL their revenue — not just within the period." },
        ];

        // ── LTV:CAC ratio ─────────────────────────────────────────────────────
        const ltvCac     = cac > 0 ? avgCLTV / cac : 0;
        const ltvCacColor = ltvCac >= 3 ? GREEN : ltvCac >= 2 ? AMBER : RED;
        const ltvCacLabel = ltvCac >= 3 ? "Strong ≥3:1" : ltvCac >= 2 ? "Acceptable ≥2:1" : ltvCac > 0 ? "Risky <2:1" : "—";

        const roiWidgets = [
          { label:"30-Day ROI", value:fmtROI(roi30), sub:`$${Math.round(total30).toLocaleString()} rev · $${Math.round(spend).toLocaleString()} spend`, color:roiColor(roi30),
            tip:`Revenue new customers generated within 30 days of their call date minus full spend, divided by full spend. 30d Rev: $${Math.round(total30).toLocaleString()}` },
          { label:"60-Day ROI", value:fmtROI(roi60), sub:`$${Math.round(total60).toLocaleString()} rev · $${Math.round(spend).toLocaleString()} spend`, color:roiColor(roi60),
            tip:`Revenue new customers generated within 60 days of their call date. 60d Rev: $${Math.round(total60).toLocaleString()}` },
          { label:"90-Day ROI", value:fmtROI(roi90), sub:`$${Math.round(total90).toLocaleString()} rev · $${Math.round(spend).toLocaleString()} spend`, color:roiColor(roi90),
            tip:`Revenue new customers generated within 90 days of their call date. 90d Rev: $${Math.round(total90).toLocaleString()}` },
          { label:"Lifetime ROI", value:fmtROI(roiLife), sub:`$${Math.round(totalCLTV).toLocaleString()} lifetime rev`, color:roiColor(roiLife),
            tip:`Total lifetime QB revenue of new customers acquired in period vs full spend. CLTV uses all revenue ever, not just the period window.` },
          { label:"30-Day Rev/Customer", value:fmt$(avg30), sub:`avg first 30 days`, color:_tenantHeaderBg,
            tip:"Average QB revenue per new customer within 30 days of their call. Shows payback speed." },
          { label:"LTV : CAC", value:ltvCac > 0 ? `${(Math.round(ltvCac*10)/10).toFixed(1)}:1` : "—", sub:ltvCacLabel, color:ltvCacColor,
            tip:`Avg CLTV ($${Math.round(avgCLTV).toLocaleString()}) ÷ CAC ($${Math.round(cac).toLocaleString()}). For every $1 spent acquiring a customer, we generate $${(Math.round(ltvCac*100)/100).toFixed(2)} in lifetime revenue. Benchmarks: 3:1+ = Strong & scalable · 2:1 = Acceptable · <2:1 = Risky, low margin.` },
        ];

        return (
          <>
            {/* Layer 2 Warning — ProPet-only customers with no QB match */}
            {customerMaster && customerMaster.layer2.length > 0 && (() => {
              const l2 = customerMaster.layer2
                .filter(c => {
                  // Only show if their ProPet created date is within the selected period
                  if (!c.createdStr) return false;
                  if (!selectedMarketingPeriod) return true;
                  if (marketingPeriod === "month") return c.createdStr.slice(0,7) === selectedMarketingPeriod;
                  if (marketingPeriod === "year")  return c.createdStr.slice(0,4) === selectedMarketingPeriod;
                  return true;
                });
              if (!l2.length) return null;
              return (
                <div style={{ background:"#fffbeb", border:"1px solid #fde047", borderRadius:10, padding:"14px 20px" }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ fontSize:16 }}>⚠️</span>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:"#713f12", fontFamily:FONT }}>
                          {l2.length} customer{l2.length > 1 ? "s" : ""} confirmed in booking system but missing from financial file
                        </div>
                        <div style={{ fontSize:11, color:"#92400e", fontFamily:FONT, marginTop:2 }}>
                          These customers have a total value &gt; 0 but no matching transaction in the financial file. Counted as new customers but CLTV shows $0. Verify these invoices exist in your financial file.
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize:11, color:"#92400e", fontFamily:FONT, fontWeight:700, whiteSpace:"nowrap", marginLeft:16 }}>
                      Ideally this list should be empty
                    </div>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 120px 100px 140px 150px", padding:"6px 12px", background:"#fef9c3", borderRadius:6, marginBottom:4 }}>
                    {["Name","Customer ID","Created","Total Value","Campaign"].map(h => (
                      <span key={h} style={{ fontSize:10, fontWeight:700, color:"#713f12", textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT }}>{h}</span>
                    ))}
                  </div>
                  {l2.map(c => (
                    <div key={c.ppid} style={{ display:"grid", gridTemplateColumns:"1fr 120px 100px 140px 150px", padding:"6px 12px", borderBottom:"1px solid #fde047" }}>
                      <span style={{ fontSize:12, fontWeight:600, color:"#713f12", fontFamily:FONT }}>{c.name}</span>
                      <span style={{ fontSize:11, color:"#92400e", fontFamily:FONT }}>{c.ppid}</span>
                      <span style={{ fontSize:11, color:"#92400e", fontFamily:FONT }}>{c.createdStr}</span>
                      <span style={{ fontSize:11, color:"#92400e", fontFamily:FONT }}>${c.propetValue.toLocaleString("en-US", {minimumFractionDigits:2, maximumFractionDigits:2})}</span>
                      <span style={{ fontSize:11, color:"#92400e", fontFamily:FONT }}>{c.firstCampaign || "—"}</span>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Spend control */}
            <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, padding:"14px 20px", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
              <span style={{ fontSize:12, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT }}>Monthly Ad Spend</span>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontSize:13, color:_tenantHeaderBg, fontFamily:FONT }}>$</span>
                <input type="number" value={monthlySpend} onChange={e=>setMonthlySpend(Math.max(0,parseInt(e.target.value)||0))}
                  style={{ width:100, fontSize:13, padding:"5px 8px", borderRadius:6, border:`1px solid ${GRAY_B}`, fontFamily:FONT, color:_tenantHeaderBg, fontWeight:600 }} />
              </div>
              <span style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, fontStyle:"italic" }}>
                Monthly rate · total spend for period: <strong style={{color:_tenantHeaderBg}}>${Math.round(spend).toLocaleString()}</strong> ({Math.round(periodMonths * 10)/10} {marketingPeriod === "week" ? "weeks" : "months"})
              </span>
              <div style={{ marginLeft:"auto", fontSize:11, color:GRAY_MID, fontFamily:FONT }}>
                {propetRows.length.toLocaleString()} customers · {Object.keys(phoneMap).length.toLocaleString()} phones · {rows ? Object.keys(ppidTxns).length.toLocaleString() + " accounts matched" : "no financial data — upload financial file for CLTV"}
              </div>
            </div>

            {/* Acquisition widgets */}
            <div data-pdf="acquisition-table" style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
              <div style={{ padding:"12px 20px", background:_tenantHeaderBg, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ fontSize:13, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>Acquisition Metrics</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", fontFamily:FONT }}>Time-based · selected period only</div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)" }}>
                {topWidgets.map((w,i) => (
                  <div key={w.label} style={{ padding:"16px 16px", borderRight:i<5?`1px solid ${GRAY_B}`:"none", borderBottom:`1px solid ${GRAY_B}`, position:"relative", cursor:w.tip?"help":"default" }}
                    onMouseEnter={e=>{ const r=e.currentTarget.getBoundingClientRect(); setHoveredCard(i); setTooltipPos({x:r.left, y:r.bottom, above: r.bottom > window.innerHeight*0.6}); }} onMouseLeave={()=>setHoveredCard(null)}>
                    <div style={{ fontSize:10, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT, marginBottom:5 }}>{w.label}</div>
                    <div style={{ fontSize:20, fontWeight:800, color:w.color, fontFamily:FONT, lineHeight:1 }}>{w.value}</div>
                    {w.sub && <div style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, marginTop:4 }}>{w.sub}</div>}
                    {w.tip && hoveredCard===i && (
                      <div style={{ position:"fixed", top: tooltipPos.above ? tooltipPos.y - 120 : tooltipPos.y + 6, left: Math.min(tooltipPos.x, window.innerWidth - 280), zIndex:9999, background:_tenantHeaderBg, color:WHITE, borderRadius:8, padding:"10px 14px", fontSize:11, fontFamily:FONT, lineHeight:1.6, width:260, boxShadow:"0 8px 24px rgba(0,0,0,0.25)", pointerEvents:"none" }}>{w.tip}</div>
                    )}
                  </div>
                ))}
              </div>
              {/* Qualified sub-breakdown */}
              <div style={{ padding:"10px 20px", background:GRAY_BG, fontSize:12, fontFamily:FONT, color:GRAY_MID }}>
                <strong style={{ color:_tenantHeaderBg }}>{total.toLocaleString()}</strong> total calls ·
                <strong style={{ color:_tenantHeaderBg }}> {qualified.toLocaleString()}</strong> qualified (2min+) ·
                <span style={{ color:AMBER }}> {matchResults.filter(r=>r.qualified&&r.status==="existing").length} existing</span> ·
                <span style={{ color:GREEN }}> {matchResults.filter(r=>r.qualified&&r.status==="new").length} new</span> ·
                <span style={{ color:GRAY_MID }}> {matchResults.filter(r=>r.qualified&&r.status==="untracked").length} untracked</span>
              </div>
            </div>

            {/* ROI widgets */}
            <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
              <div style={{ padding:"12px 20px", background:_tenantHeaderBg, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={{ fontSize:13, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>ROI & Revenue Recovery</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", fontFamily:FONT }}>Cohort-based · customers acquired in period · full spend · revenue window varies</div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)" }}>
                {roiWidgets.map((w,i) => (
                  <div key={w.label} style={{ padding:"16px 16px", borderRight:i<5?`1px solid ${GRAY_B}`:"none", position:"relative", cursor:"help" }}
                    onMouseEnter={e=>{ const r=e.currentTarget.getBoundingClientRect(); setHoveredCard(100+i); setTooltipPos({x:r.left, y:r.bottom, above: r.bottom > window.innerHeight*0.6}); }} onMouseLeave={()=>setHoveredCard(null)}>
                    <div style={{ fontSize:10, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT, marginBottom:5 }}>{w.label}</div>
                    <div style={{ fontSize:20, fontWeight:800, color:w.color, fontFamily:FONT, lineHeight:1 }}>{w.value}</div>
                    {w.sub && <div style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, marginTop:4 }}>{w.sub}</div>}
                    {hoveredCard===100+i && (
                      <div style={{ position:"fixed", top: tooltipPos.above ? tooltipPos.y - 120 : tooltipPos.y + 6, left: Math.min(tooltipPos.x, window.innerWidth - 280), zIndex:9999, background:_tenantHeaderBg, color:WHITE, borderRadius:8, padding:"10px 14px", fontSize:11, fontFamily:FONT, lineHeight:1.6, width:260, boxShadow:"0 8px 24px rgba(0,0,0,0.25)", pointerEvents:"none" }}>{w.tip}</div>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ padding:"10px 20px", background:GRAY_BG, fontSize:11, color:GRAY_MID, fontFamily:FONT, fontStyle:"italic" }}>
                Revenue windows (30/60/90d) measured from each customer's individual call date. Lifetime uses total QB revenue ever. Full spend (${Math.round(spend).toLocaleString()}) used in all ROI calculations regardless of window.
              </div>
            </div>

            {/* ROI Chart — between ROI widgets and campaign breakdown */}
            {(() => {

              // Build per-campaign ROI data from campMap
              const chartData = campRows
                .map(([camp, c]) => {
                  // Allocate spend proportionally by new customers acquired
                  // Each campaign bears its fair share of total spend, not the full amount
                  const campSpend = nNew > 0 ? spend * (c.nNew / nNew) : 0;
                  const t30  = c.rev30.reduce((s,v)=>s+v,0);
                  const t60  = c.rev60.reduce((s,v)=>s+v,0);
                  const t90  = c.rev90.reduce((s,v)=>s+v,0);
                  const tLtv = c.cltv.reduce((s,v)=>s+v,0);
                  const roi  = (r,s) => s > 0 ? Math.round((r-s)/s*100) : -100;
                  return {
                    camp,
                    nNew:    c.nNew,
                    cac:     c.nNew > 0 ? campSpend/c.nNew : 0,
                    t30, t60, t90, tLtv,
                    roi30:   roi(t30,  campSpend),
                    roi60:   roi(t60,  campSpend),
                    roi90:   roi(t90,  campSpend),
                    roiLife: roi(tLtv, campSpend),
                  };
                })
                .filter(d => d.nNew > 0) // only campaigns with new customers
                .sort((a,b) => b.roiLife - a.roiLife); // sort by lifetime ROI desc

              if (!chartData.length) return (
                <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, padding:"40px 24px", textAlign:"center", color:GRAY_MID, fontFamily:FONT, fontSize:13 }}>
                  No campaigns with confirmed new customers in this period
                </div>
              );

              const BAR_COLORS = ["#bfdbfe","#60a5fa","#2563eb","#1e3a8a"]; // light→dark blue
              const labels = ["30-Day","60-Day","90-Day","Lifetime"];
              const roiKeys = ["roi30","roi60","roi90","roiLife"];
              const revKeys = ["t30","t60","t90","tLtv"];
              const fmt$k   = n => n >= 1000 ? `$${(n/1000).toFixed(1)}k` : `$${Math.round(n)}`;

              // Chart dimensions
              const W = 900, H = 340;
              const PAD = { top:40, right:20, bottom:80, left:60 };
              const cW = W - PAD.left - PAD.right;
              const cH = H - PAD.top - PAD.bottom;
              const nCamps = chartData.length;
              const groupW = cW / nCamps;
              const barW   = Math.min(18, (groupW - 16) / 4);
              const gap    = 3;

              // Y axis range
              const allVals = chartMode === "roi"
                ? chartData.flatMap(d => [d.roi30, d.roi60, d.roi90, d.roiLife])
                : chartMode === "revenue"
                ? chartData.flatMap(d => [d.t30, d.t60, d.t90, d.tLtv])
                : chartData.map(d => d.nNew);

              const yMin = Math.min(0, ...allVals);
              const yMax = Math.max(0, ...allVals);
              const yRange = yMax - yMin || 1;
              const yPad   = yRange * 0.15;
              const yLo    = yMin - yPad;
              const yHi    = yMax + yPad;
              const ySpan  = yHi - yLo;

              const getY  = v => PAD.top + cH - ((v - yLo) / ySpan) * cH;
              const zero  = getY(0);

              // Y ticks
              const nTicks = 6;
              const tickStep = ySpan / nTicks;
              const ticks = Array.from({length: nTicks+1}, (_,i) => yLo + i * tickStep);

              const fmtTick = v => chartMode === "roi" ? `${Math.round(v)}%`
                : chartMode === "revenue" ? fmt$k(v)
                : Math.round(v).toString();

              // In ROI mode: use shaded colors per timeframe so bars are always distinguishable
              // even when all negative. Color = red/amber/green based on value, shade = timeframe index
              const BAR_SHADES = {
                negative: ["#fca5a5","#f87171","#ef4444","#b91c1c"], // light→dark red
                low:      ["#fde68a","#fbbf24","#f59e0b","#b45309"], // light→dark amber
                positive: ["#bbf7d0","#4ade80","#16a34a","#14532d"], // light→dark green
              };
              const barColor = (val, bi) => {
                if (chartMode !== "roi") return BAR_COLORS[bi];
                const shade = val < 0 ? "negative" : val < 50 ? "low" : "positive";
                return BAR_SHADES[shade][bi];
              };

              // Tooltip state lives at component level (chartTip/setChartTip)

              return (
                <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
                  {/* Header */}
                  <div style={{ padding:"14px 20px", background:_tenantHeaderBg, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>ROI by Campaign</div>
                      <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", fontFamily:FONT, marginTop:2 }}>Campaigns with confirmed new customers · sorted by Lifetime ROI · full period spend as denominator</div>
                    </div>
                    {/* Toggle */}
                    <div style={{ display:"flex", gap:2, background:"rgba(255,255,255,0.1)", borderRadius:6, padding:2 }}>
                      {[["roi","ROI %"],["revenue","Revenue $"],["customers","New Custs"]].map(([m,l])=>(
                        <button key={m} onClick={()=>setChartMode(m)}
                          style={{ padding:"5px 12px", borderRadius:4, border:"none", cursor:"pointer", fontSize:11, fontWeight:600, fontFamily:FONT, background:chartMode===m?WHITE:"transparent", color:chartMode===m?_tenantHeaderBg:"rgba(255,255,255,0.75)" }}>{l}</button>
                      ))}
                    </div>
                  </div>

                  {/* Legend */}
                  <div style={{ display:"flex", gap:20, padding:"10px 20px", borderBottom:`1px solid ${GRAY_B}`, background:GRAY_BG, flexWrap:"wrap", alignItems:"center" }}>
                    {labels.map((l,i) => (
                      <div key={l} style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <div style={{ width:14, height:14, borderRadius:3, background:chartMode==="roi" ? BAR_SHADES.negative[i] : BAR_COLORS[i] }} />
                        <span style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT }}>{l}</span>
                      </div>
                    ))}
                    {chartMode === "roi" && (
                      <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:16, fontSize:11, fontFamily:FONT }}>
                        <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                          <div style={{ width:28, height:10, borderRadius:2, background:`linear-gradient(to right, ${BAR_SHADES.negative[0]}, ${BAR_SHADES.negative[3]})` }} />
                          <span style={{ color:"#ef4444", fontWeight:600 }}>Negative</span>
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                          <div style={{ width:28, height:10, borderRadius:2, background:`linear-gradient(to right, ${BAR_SHADES.low[0]}, ${BAR_SHADES.low[3]})` }} />
                          <span style={{ color:"#f59e0b", fontWeight:600 }}>Low (&lt;50%)</span>
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                          <div style={{ width:28, height:10, borderRadius:2, background:`linear-gradient(to right, ${BAR_SHADES.positive[0]}, ${BAR_SHADES.positive[3]})` }} />
                          <span style={{ color:"#16a34a", fontWeight:600 }}>Strong (≥50%)</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* SVG Chart */}
                  <div style={{ padding:"16px 20px", position:"relative" }}>
                    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow:"visible", fontFamily:FONT }}>

                      {/* Y gridlines + labels */}
                      {ticks.map((t,i) => {
                        const y = getY(t);
                        const isZero = Math.abs(t) < tickStep * 0.1;
                        return (
                          <g key={i}>
                            <line x1={PAD.left} x2={PAD.left+cW} y1={y} y2={y}
                              stroke={isZero ? "#94a3b8" : "#e2e8f0"}
                              strokeWidth={isZero ? 1.5 : 1}
                              strokeDasharray={isZero ? "none" : "4,3"} />
                            <text x={PAD.left-8} y={y+4} textAnchor="end" fontSize={10} fill="#94a3b8">
                              {fmtTick(t)}
                            </text>
                          </g>
                        );
                      })}

                      {/* Zero reference line label */}
                      {chartMode === "roi" && (
                        <text x={PAD.left+4} y={zero-4} fontSize={9} fill="#94a3b8" fontWeight={600}>Break-even</text>
                      )}

                      {/* Bars per campaign */}
                      {chartData.map((d, ci) => {
                        const groupX = PAD.left + ci * groupW + groupW/2;
                        const vals = chartMode === "roi" ? roiKeys.map(k=>d[k])
                          : chartMode === "revenue" ? revKeys.map(k=>d[k])
                          : [d.nNew, d.nNew, d.nNew, d.nNew];
                        const totalBarW = 4 * barW + 3 * gap;
                        const startX = groupX - totalBarW/2;

                        return (
                          <g key={d.camp}>
                            {vals.map((val, bi) => {
                              const x  = startX + bi * (barW + gap);
                              const y0 = zero;
                              const y1 = getY(val);
                              const bH = Math.abs(y1 - y0);
                              const bY = val >= 0 ? y1 : y0;
                              const col = chartMode === "roi" ? barColor(val, bi) : BAR_COLORS[bi];

                              return (
                                <g key={bi}>
                                  <rect x={x} y={bY} width={barW} height={Math.max(bH,2)}
                                    fill={col} rx={2}
                                    style={{ cursor:"pointer" }}
                                    onMouseEnter={e => setChartTip({ d, bi, x: e.clientX, y: e.clientY })}
                                    onMouseLeave={() => setChartTip(null)}
                                  />
                                  {/* Value label on bar */}
                                  {bH > 18 && (
                                    <text x={x+barW/2} y={val>=0?bY-3:bY+bH+10} textAnchor="middle" fontSize={8} fill={val>=0?"#14532d":"#7f1d1d"} fontWeight={700}>
                                      {chartMode==="roi" ? `${val}%` : chartMode==="revenue" ? fmt$k(val) : val}
                                    </text>
                                  )}
                                </g>
                              );
                            })}

                            {/* Campaign label */}
                            <text x={groupX} y={PAD.top+cH+16} textAnchor="middle" fontSize={10} fill={_tenantHeaderBg} fontWeight={600}>
                              {d.camp.length > 14 ? d.camp.slice(0,13)+"…" : d.camp}
                            </text>
                            <text x={groupX} y={PAD.top+cH+28} textAnchor="middle" fontSize={9} fill="#94a3b8">
                              {d.nNew} new · CAC ${Math.round(d.cac).toLocaleString()}
                            </text>
                          </g>
                        );
                      })}

                    </svg>

                    {/* Tooltip */}
                    {chartTip && (() => {
                      const { d, bi } = chartTip;
                      const timeLabels = ["30-Day","60-Day","90-Day","Lifetime"];
                      const revs = [d.t30, d.t60, d.t90, d.tLtv];
                      const rois = [d.roi30, d.roi60, d.roi90, d.roiLife];
                      return (
                        <div style={{ position:"fixed", top: chartTip.y - 10, left: Math.min(chartTip.x + 12, window.innerWidth - 280),
                          zIndex:9999, background:_tenantHeaderBg, color:WHITE, borderRadius:10, padding:"14px 16px",
                          fontSize:11, fontFamily:FONT, lineHeight:1.8, width:260,
                          boxShadow:"0 8px 32px rgba(0,0,0,0.35)", pointerEvents:"none" }}>
                          <div style={{ fontWeight:800, fontSize:13, color:"#93c5fd", marginBottom:8 }}>{d.camp}</div>
                          <div style={{ color:"rgba(255,255,255,0.6)", marginBottom:8, paddingBottom:8, borderBottom:"1px solid rgba(255,255,255,0.15)" }}>
                            <span>{d.nNew} new customers</span><span style={{ margin:"0 8px" }}>·</span>
                            <span>CAC ${Math.round(d.cac).toLocaleString()}</span><span style={{ margin:"0 8px" }}>·</span>
                            <span>Spend ${Math.round(spend).toLocaleString()}</span>
                          </div>
                          {timeLabels.map((l,i) => (
                            <div key={l} style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                              background: i===bi ? "rgba(255,255,255,0.1)" : "transparent",
                              margin:"0 -4px", padding:"2px 4px", borderRadius:4 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                <div style={{ width:8, height:8, borderRadius:2, background:BAR_COLORS[i] }} />
                                <span style={{ color: i===bi ? WHITE : "rgba(255,255,255,0.7)" }}>{l} ROI</span>
                              </div>
                              <div style={{ textAlign:"right" }}>
                                <span style={{ fontWeight:700, color: rois[i]>=0?"#86efac":"#fca5a5" }}>{rois[i]}%</span>
                                <span style={{ color:"rgba(255,255,255,0.5)", marginLeft:8, fontSize:10 }}>${Math.round(revs[i]).toLocaleString()} rev</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })()}

            {/* Campaign Breakdown table */}
            <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
              <div style={{ padding:"14px 20px", background:_tenantHeaderBg }}>
                <div style={{ fontSize:13, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>Campaign Breakdown</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", fontFamily:FONT, marginTop:2 }}>Per-campaign view of call quality, existing vs new, and conversion performance</div>
              </div>
              {/* Header */}
              <div style={{ display:"grid", gridTemplateColumns:"1.2fr 80px 85px 100px 100px 90px 90px 80px 100px 90px", padding:"8px 20px", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}` }}>
                {[
                  ["Campaign","left"],
                  ["Total Calls","center"],
                  ["Existing","center"],
                  ["Qual · Existing","center"],
                  ["Qual · New","center"],
                  ["New Customers","center"],
                  ["Non-Conv","center"],
                  ["Conv Rate","center"],
                  ["Avg CLTV","center"],
                  ["LTV:CAC","center"],
                ].map(([h,a])=>(
                  <span key={h} style={{ fontSize:10, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT, textAlign:a }}>{h}</span>
                ))}
              </div>
              {/* Rows */}
              {campRows.map(([camp, c], i) => {
                const existColor   = c.total>0 ? (c.existing/c.total>=0.7?RED:c.existing/c.total>=0.5?AMBER:GREEN) : GRAY_MID;
                const campAvgCLTV  = avg(c.cltv);
                return (
                  <div key={camp} style={{ display:"grid", gridTemplateColumns:"1.2fr 80px 85px 100px 100px 90px 90px 80px 100px 90px", padding:"10px 20px", borderBottom:`1px solid ${GRAY_B}`, background:i%2===0?WHITE:GRAY_BG }}>
                    <span style={{ fontSize:12, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT, alignSelf:"center" }}>{camp}</span>
                    <span style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{fmtN(c.total,total)}</span>
                    <span style={{ fontSize:12, fontWeight:700, color:existColor, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{fmtN(c.existing,c.total)}</span>
                    <span style={{ fontSize:12, color:AMBER, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{fmtN(c.qualExist,c.qualified)}</span>
                    <span style={{ fontSize:12, color:GREEN, fontWeight:600, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{fmtN(c.qualNew,c.qualified)}</span>
                    <span style={{ fontSize:12, fontWeight:700, color:GREEN, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{fmtN(c.nNew,c.total)}</span>
                    <span style={{ fontSize:12, color:RED, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{fmtN(c.untracked,c.total)}</span>
                    <div style={{ textAlign:"center", alignSelf:"center" }}>
                      <div style={{ fontSize:12, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT }}>{fmtPct(c.nNew,c.total)}</div>
                      <div style={{ fontSize:10, color:GRAY_MID, fontFamily:FONT }}>q: {fmtPct(c.nNew,c.qualNew)}</div>
                    </div>
                    <span style={{ fontSize:12, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{fmt$(campAvgCLTV)}</span>
                    {(() => {
                      const campCac = c.nNew > 0 ? (spend * c.nNew / nNew) / c.nNew : 0;
                      const ratio   = campCac > 0 && campAvgCLTV > 0 ? campAvgCLTV / campCac : 0;
                      const ratioColor = ratio >= 3 ? GREEN : ratio >= 2 ? AMBER : ratio > 0 ? RED : GRAY_MID;
                      return <span style={{ fontSize:12, fontWeight:700, color:ratioColor, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{ratio > 0 ? `${(Math.round(ratio*10)/10).toFixed(1)}:1` : "—"}</span>;
                    })()}
                  </div>
                );
              })}
              {/* Total row */}
              <div style={{ display:"grid", gridTemplateColumns:"1.2fr 80px 85px 100px 100px 90px 90px 80px 100px 90px", padding:"10px 20px", borderTop:`2px solid ${_tenantHeaderBg}`, background:GRAY_BG }}>
                <span style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT, alignSelf:"center" }}>TOTAL</span>
                <span style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{total.toLocaleString()}</span>
                <span style={{ fontSize:12, fontWeight:800, color:AMBER, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{fmtN(existing,total)}</span>
                <span style={{ fontSize:12, fontWeight:800, color:AMBER, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{fmtN(qualExist,qualified)}</span>
                <span style={{ fontSize:12, fontWeight:800, color:GREEN, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{fmtN(qualNew,qualified)}</span>
                <span style={{ fontSize:12, fontWeight:800, color:GREEN, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{fmtN(nNew,total)}</span>
                <span style={{ fontSize:12, fontWeight:800, color:RED, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{fmtN(untracked,total)}</span>
                <div style={{ textAlign:"center", alignSelf:"center" }}>
                  <div style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT }}>{fmtPct(nNew,total)}</div>
                  <div style={{ fontSize:10, color:GRAY_MID, fontFamily:FONT }}>q: {fmtPct(nNew,qualNew)}</div>
                </div>
                <span style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{fmt$(avgCLTV)}</span>
                {(() => {
                  const ratioColor = ltvCac >= 3 ? GREEN : ltvCac >= 2 ? AMBER : ltvCac > 0 ? RED : GRAY_MID;
                  return <span style={{ fontSize:12, fontWeight:800, color:ratioColor, fontFamily:FONT, textAlign:"center", alignSelf:"center" }}>{ltvCac > 0 ? `${(Math.round(ltvCac*10)/10).toFixed(1)}:1` : "—"}</span>;
                })()}
              </div>
              <div style={{ padding:"10px 20px", fontSize:11, color:GRAY_MID, fontFamily:FONT, fontStyle:"italic", background:WHITE }}>
                Qual·Existing / Qual·New = qualified calls (2min+) split by customer status · New Customers = confirmed QB txn on/after call · Non-Conv = no ProPet record · Conv Rate: total (q: vs qual-new only) · CLTV = lifetime QB revenue
              </div>
            </div>
          </>
        );
      })()}

    </div>
  );
}


// ─── SCORECARD SUMMARY ────────────────────────────────────────────────────────

// ─── AI INSIGHT CARD COMPONENT ────────────────────────────────────────────────
function AIInsightCard({ icon, section, color, metrics, prompt, fallback }) {
  const [insight, setInsight]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const hasFetched              = useRef(false);

  useEffect(() => {
    if (!metrics || hasFetched.current) return;
    hasFetched.current = true;
    setLoading(true);

    fetch(`${API_BASE}/api/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-opus-4-5-20251101",
        max_tokens: 600,
        system: `You are a concise business analyst for a pet resort. Respond with exactly 3 JSON fields:
{
  "title": "one punchy sentence headline (max 12 words)",
  "metric": "the single most important number or stat (max 15 words)",
  "interpretation": "2-3 sentences of plain-english insight. Be specific, use the numbers provided. Focus on what the owner should act on.",
  "watch": "one sentence — the most important risk or opportunity to watch (max 20 words)"
}
Return ONLY valid JSON. No markdown, no preamble.`,
        messages: [{ role: "user", content: prompt }],
      }),
    })
    .then(r => r.json())
    .then(data => {
      const text = data.content?.map(b => b.text || "").join("").trim();
      const parsed = JSON.parse(text.replace(/```json|```/g, "").trim());
      setInsight(parsed);
      setLoading(false);
    })
    .catch(err => {
      console.error("AI insight error:", err);
      setInsight(fallback);
      setError(true);
      setLoading(false);
    });
  }, [metrics]);

  const display = insight || fallback;

  return (
    <div data-ai-loading={loading ? "true" : "false"} data-pdf="insight-card"
      style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:12, padding:"22px 24px", display:"flex", flexDirection:"column", gap:12 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:20 }}>{icon}</span>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color, textTransform:"uppercase", letterSpacing:"0.1em", fontFamily:FONT }}>{section}</div>
            <div style={{ fontSize:14, fontWeight:800, color:NAVY, fontFamily:FONT }}>
              {loading ? "Analyzing your data…" : (display?.title || "—")}
            </div>
          </div>
        </div>
        {loading && (
          <div style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, fontStyle:"italic", display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ display:"inline-block", width:8, height:8, borderRadius:"50%", background:color, animation:"pulse 1s infinite" }} />
            AI analyzing…
          </div>
        )}
        {error && <span style={{ fontSize:10, color:GRAY_MID, fontFamily:FONT }}>⚠ using fallback</span>}
      </div>
      {display?.metric && (
        <div style={{ background:GRAY_BG, borderRadius:8, padding:"10px 14px", fontSize:13, fontWeight:700, color, fontFamily:FONT }}>
          {display.metric}
        </div>
      )}
      {display?.interpretation && (
        <div style={{ fontSize:12, color:"#374151", fontFamily:FONT, lineHeight:1.7 }}>
          {loading ? <span style={{ color:GRAY_MID, fontStyle:"italic" }}>Generating insight…</span> : display.interpretation}
        </div>
      )}
      {display?.watch && (
        <div style={{ background:"#fef9c3", border:"1px solid #fde047", borderRadius:8, padding:"10px 14px" }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#713f12", fontFamily:FONT, marginBottom:3 }}>⚠ What to watch</div>
          <div style={{ fontSize:12, color:"#713f12", fontFamily:FONT, lineHeight:1.6 }}>
            {loading ? "—" : display.watch}
          </div>
        </div>
      )}
    </div>
  );
}

function ScorecardSummary({ rows, callRows, propetRows, monthlySpend, customerMaster, bookingRows, authClient }) {

  // ── Compute live metrics for insights ──────────────────────────────────────
  const insights = useMemo(() => {
    if (!rows && !callRows) return null;

    // ── Marketing metrics (2025 default) ──────────────────────────────────
    let mktData = null;
    if (callRows && propetRows && rows) {
      const phoneMap = {};
      propetRows.forEach(r => {
        ["phone_1","phone_2","phone_3"].forEach(f => {
          const p = String(r[f]||"").replace(/\D/g,"").slice(-10);
          if (p.length===10 && !phoneMap[p]) phoneMap[p] = r;
        });
      });
      const ppidTxns = {};
      rows.forEach(r => {
        const raw  = String(r["PP-ID"]||"").trim();
        let ppid = raw.startsWith("PP-") ? raw.slice(3) : raw;
        // Fallback: extract PP-ID from name fields when QB sync broke (Nov 2025+)
        // "0" means QB stored numeric zero — treat same as blank
        if (!ppid || ppid === "0" || !/^\d+$/.test(ppid)) {
          const nameFields = [String(r["Consolidated Name"]||""), String(r["Customer"]||""), String(r["First Name"]||"")];
          for (const field of nameFields) {
            const m = field.match(/\b(\d{6,8})\b/);
            if (m) { ppid = m[1]; break; }
          }
        }
        const amt  = parseFloat(String(r.Amount||"").replace(/[^0-9.-]/g,""));
        const dt   = String(r.Date||"").slice(0,10);
        if (ppid && /^\d+$/.test(ppid) && amt > 0 && dt.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const d = new Date(parseInt(dt.slice(0,4)), parseInt(dt.slice(5,7))-1, parseInt(dt.slice(8,10)));
          if (!ppidTxns[ppid]) ppidTxns[ppid] = [];
          ppidTxns[ppid].push({ date: d, amt });
        }
      });
      // Filter to 2025
      const calls2025 = callRows.filter(r => String(r.Date||"").startsWith("2025"));
      const spend2025 = monthlySpend * 12;
      let total=0, existing=0, nNew=0, qualified=0, totalCLTV=0, total90=0;
      const seenNew = new Set();
      const campNewMap = {};
      calls2025.forEach(call => {
        const phone = String(call["caller_phone"] || call["Customer #"] || call["Caller ID"]||"").replace(/\D/g,"").slice(-10);
        if (!phone || phone.length!==10) return;
        const pp = phoneMap[phone];
        const dur = parseDuration(call["Duration"]);
        const camp = normalizeCampaign(call["campaign"]);
        total++;
        if (dur >= 120) qualified++;
        if (!pp) return;
        const callDt = String(call.Date||"").slice(0,10);
        const crDt   = String(pp.created_at||"").slice(0,10);
        if (!callDt.match(/^\d{4}-\d{2}-\d{2}$/) || !crDt.match(/^\d{4}-\d{2}-\d{2}$/)) return;
        const callDate = new Date(parseInt(callDt.slice(0,4)), parseInt(callDt.slice(5,7))-1, parseInt(callDt.slice(8,10)));
        const created  = new Date(parseInt(crDt.slice(0,4)),  parseInt(crDt.slice(5,7))-1,  parseInt(crDt.slice(8,10)));
        if (created < callDate) { existing++; return; }
        const ppid = String(pp.id);
        const txns = ppidTxns[ppid] || [];
        const txnsAfter = txns.filter(t => t.date >= callDate);
        if (!txnsAfter.length) return;
        if (seenNew.has(ppid)) return;
        seenNew.add(ppid);
        nNew++;
        const cltv = txns.reduce((s,t)=>s+t.amt,0);
        const r90  = txnsAfter.filter(t=>(t.date-callDate)/86400000<=90).reduce((s,t)=>s+t.amt,0);
        totalCLTV += cltv;
        total90   += r90;
        if (!campNewMap[camp]) campNewMap[camp] = { nNew:0, cltv:0 };
        campNewMap[camp].nNew++;
        campNewMap[camp].cltv += cltv;
      });
      const avgCLTV  = nNew ? totalCLTV/nNew : 0;
      const cac      = nNew ? spend2025/nNew : 0;
      const ltvCac   = cac > 0 ? avgCLTV/cac : 0;
      const roi90    = spend2025 > 0 ? (total90-spend2025)/spend2025*100 : -100;
      const roiLife  = spend2025 > 0 ? (totalCLTV-spend2025)/spend2025*100 : -100;
      const existPct = total > 0 ? Math.round(existing/total*100) : 0;
      const convRate = total > 0 ? (nNew/total*100).toFixed(1) : 0;
      // Best campaign by lifetime ROI
      const bestCamp = Object.entries(campNewMap)
        .filter(([k])=>k!=="Other")
        .map(([k,v]) => ({ name:k, roi: v.nNew>0 ? (v.cltv - spend2025*(v.nNew/nNew))/( spend2025*(v.nNew/nNew))*100 : -100 }))
        .sort((a,b)=>b.roi-a.roi)[0];
      const worstCamp = Object.entries(campNewMap)
        .map(([k,v]) => ({ name:k, existPct: existing > 0 ? 0 : 0, roi: v.nNew>0 ? (v.cltv - spend2025*(v.nNew/nNew))/(spend2025*(v.nNew/nNew))*100 : -100 }))
        .sort((a,b)=>a.roi-b.roi)[0];
      mktData = { total, existing, existPct, nNew, qualified, avgCLTV, cac, ltvCac, roi90, roiLife, convRate, spend:spend2025, bestCamp, worstCamp };
    }

    // ── Customer metrics (2021–2025 default) ──────────────────────────────
    let custData = null;
    if (rows) {
      const profiles = buildCustomerProfiles(rows).filter(p => p.firstYear >= 2021 && p.firstYear <= 2025);
      const n = profiles.length;
      const avgCLTV = n ? profiles.reduce((s,p)=>s+p.totalRev,0)/n : 0;
      const multiSvc = n ? profiles.filter(p=>p.nServices > 1).length/n : 0;
      const training = profiles.filter(p=>p.firstService==="Dog Training");
      const boarding = profiles.filter(p=>p.firstService==="Boarding");
      const trainingCLTV = training.length ? training.reduce((s,p)=>s+p.totalRev,0)/training.length : 0;
      const boardingCLTV = boarding.length ? boarding.reduce((s,p)=>s+p.totalRev,0)/boarding.length : 0;
      custData = { n, avgCLTV, multiSvc, trainingCLTV, boardingCLTV, trainingN: training.length };
    }

    return { mktData, custData };
  }, [rows, callRows, propetRows, monthlySpend]);

  const fmt$ = n => `$${Math.round(n).toLocaleString()}`;
  const fmtPct = n => `${Math.round(n)}%`;

  // ── Insight card component ─────────────────────────────────────────────────
  const InsightCard = ({ icon, title, section, metric, interpretation, benchmarks, watch, color=scorecardHeaderBg }) => (
    <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:12, padding:"22px 24px", display:"flex", flexDirection:"column", gap:12 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontSize:20 }}>{icon}</span>
        <div>
          <div style={{ fontSize:10, fontWeight:700, color:color, textTransform:"uppercase", letterSpacing:"0.1em", fontFamily:FONT }}>{section}</div>
          <div style={{ fontSize:14, fontWeight:800, color:scorecardHeaderBg, fontFamily:FONT }}>{title}</div>
        </div>
      </div>
      <div style={{ background:GRAY_BG, borderRadius:8, padding:"10px 14px", fontSize:13, fontWeight:700, color:color, fontFamily:FONT }}>
        {metric}
      </div>
      <div style={{ fontSize:12, color:"#374151", fontFamily:FONT, lineHeight:1.7 }}>{interpretation}</div>
      {benchmarks && (
        <div style={{ borderTop:`1px solid ${GRAY_B}`, paddingTop:10 }}>
          <div style={{ fontSize:10, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:FONT, marginBottom:6 }}>Benchmarks</div>
          <div style={{ fontSize:12, color:"#374151", fontFamily:FONT, lineHeight:1.8 }}>{benchmarks}</div>
        </div>
      )}
      {watch && (
        <div style={{ background:"#fef9c3", border:"1px solid #fde047", borderRadius:8, padding:"10px 14px" }}>
          <div style={{ fontSize:11, fontWeight:700, color:"#713f12", fontFamily:FONT, marginBottom:3 }}>⚠ What to watch</div>
          <div style={{ fontSize:12, color:"#713f12", fontFamily:FONT, lineHeight:1.6 }}>{watch}</div>
        </div>
      )}
    </div>
  );

  const m = insights?.mktData;
  const c = insights?.custData;

  // ── Metric definitions table ───────────────────────────────────────────────
  const metricDefs = [
    { metric:"Total Calls", formula:"Count of all Google Ads tracked calls", purpose:"Baseline call volume — includes hangups, wrong numbers, and real conversations." },
    { metric:"Qualified Calls (2min+)", formula:"Calls where Duration ≥ 120 seconds", purpose:"Proxy for real conversations. Filters out noise. A customer can still convert from a short call." },
    { metric:"Qualification Rate", formula:"Qualified Calls ÷ Total Calls", purpose:"Measures lead quality, not just volume. Low rate = high noise in your call tracking." },
    { metric:"Existing Customers", formula:"Phone matches booking system AND created_at < call date", purpose:"Confirmed existing customers clicking your ads. Should NOT count as new acquisitions." },
    { metric:"New Customers", formula:"Booking system created_at ≥ call date AND at least one paid invoice ≥ call date", purpose:"True confirmed acquisitions. Requires both a client record and at least one paid invoice." },
    { metric:"Non-Conversion", formula:"No client record found for caller's phone", purpose:"Called but never entered the booking system. True non-conversions." },
    { metric:"Conversion Rate", formula:"New Customers ÷ Total Calls (also shown vs Qualified Calls)", purpose:"How efficiently calls turn into paying customers." },
    { metric:"CAC", formula:"Period Spend ÷ New Customers", purpose:"True cost per new customer. Period spend = monthly rate × months in selected period." },
    { metric:"Cost Per Qualified Call (CQC)", formula:"Period Spend ÷ Qualified Calls", purpose:"Efficiency of generating real conversations. Lower is better." },
    { metric:"Avg CLTV", formula:"Total lifetime revenue ÷ New Customers", purpose:"Average revenue per acquired customer across all time — not just the selected period." },
    { metric:"30/60/90-Day ROI", formula:"(Revenue within X days of call − Period Spend) ÷ Period Spend", purpose:"Shows payback speed. Revenue window is per customer from their individual call date. Full period spend used as denominator." },
    { metric:"Lifetime ROI", formula:"(Total CLTV of new customers − Period Spend) ÷ Period Spend", purpose:"Long-term return on acquired customers. Uses all revenue ever generated, not just within the period." },
    { metric:"LTV:CAC", formula:"Avg CLTV ÷ CAC", purpose:"For every $1 spent acquiring a customer, how many dollars of lifetime revenue are generated. 3:1+ = strong. <2:1 = risky." },
    { metric:"30-Day Rev/Customer", formula:"Sum of 30-day revenue ÷ New Customers", purpose:"Average first-month revenue per new customer. Indicates payback speed and initial booking value." },
    { metric:"Match Rate", formula:"(Existing + New) ÷ Total Calls", purpose:"% of calls matched to a client record. Higher = better data coverage." },
  ];

  const scorecardHeaderBg = authClient?.branding?.headerBg || authClient?.branding?.primaryColor || NAVY;
  const isPK = authClient?.clientId === "petkennect";
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:28 }}>

      {/* Page header */}
      <div style={{ background:scorecardHeaderBg, borderRadius:12, padding:"28px 32px", color:WHITE }}>
        <div style={{ fontSize:22, fontWeight:800, fontFamily:FONT, letterSpacing:"0.04em", marginBottom:6 }}>Scorecard Summary</div>
        <div style={{ fontSize:13, color:"rgba(255,255,255,0.65)", fontFamily:FONT, lineHeight:1.6 }}>
          Dynamic insights based on live dashboard data · Marketing defaults to 2025 · Customer KPIs default to 2021–2025 cohorts
        </div>
        <div style={{ marginTop:12, display:"flex", gap:24, flexWrap:"wrap" }}>
          {[
            { label:"Financial", val:"YTD", color:"#60a5fa" },
            { label:"Customer KPIs", val:"2021–2025 Cohorts", color:"#86efac" },
            { label:"Marketing", val:"2025 Full Year", color:"#c4b5fd" },
          ].map(d => (
            <div key={d.label} style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:8, height:8, borderRadius:2, background:d.color }} />
              <span style={{ fontSize:12, color:"rgba(255,255,255,0.6)", fontFamily:FONT }}>{d.label}:</span>
              <span style={{ fontSize:12, fontWeight:700, color:WHITE, fontFamily:FONT }}>{d.val}</span>
            </div>
          ))}
        </div>
      </div>

      {/* No data state */}
      {!insights?.mktData && !insights?.custData && (
        <div style={{ background:WHITE, border:`2px dashed ${GRAY_B}`, borderRadius:12, padding:60, textAlign:"center" }}>
          <div style={{ fontSize:32, marginBottom:12 }}>📊</div>
          <div style={{ fontSize:15, fontWeight:700, color:scorecardHeaderBg, fontFamily:FONT, marginBottom:8 }}>Load your data to generate insights</div>
          <div style={{ fontSize:13, color:GRAY_MID, fontFamily:FONT }}>Upload your Financial File, Bookings Data, and Customer export to populate all five insight sections.</div>
        </div>
      )}

      {/* ── INSIGHTS ─────────────────────────────────────────────────────── */}
      {(m || c) && (
        <>
          <div style={{ fontSize:13, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.1em", fontFamily:FONT }}>
            💡 Business Insights
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

            <div data-pdf="summary-content" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            {/* 1. Acquisition */}
            {m && (
              <InsightCard
                icon="📣" section="1 · Acquisition" color="#7C3AED"
                title={`${m.existPct}% of tracked calls are existing customers`}
                metric={`${m.existing.toLocaleString()} existing · ${m.nNew} new · ${m.total.toLocaleString()} total calls (2025)`}
                interpretation={`Of every 100 calls Google Ads tracked in 2025, ${m.existPct} were from people already in your system. You are paying to advertise to your own customers. Only ${(100-m.existPct)}% of tracked calls were potentially new — and of those, just ${m.convRate}% confirmed into paying customers.`}
                benchmarks={<><span style={{color:GREEN, fontWeight:700}}>✓ &lt;30% existing</span> — healthy mix · <span style={{color:AMBER, fontWeight:700}}>30–50%</span> — investigate · <span style={{color:RED, fontWeight:700}}>&gt;50%</span> — significant waste</>}
                watch={`At ${m.existPct}% existing, over half your ad spend is retargeting loyal customers who would have called anyway. Performance Max campaigns are the worst offenders — reallocate budget to campaigns with lower existing-customer rates.`}
              />
            )}

            {/* 2. Customer Quality */}
            {c && (
              <InsightCard
                icon="🐾" section="2 · Customer Quality" color="#059669"
                title={`Avg customer lifetime value is ${fmt$(c.avgCLTV)}`}
                metric={`${c.n.toLocaleString()} customers · 2021–2025 cohorts · ${fmtPct(c.multiSvc*100)} use multiple services`}
                interpretation={`The average Waggles customer generates ${fmt$(c.avgCLTV)} in lifetime revenue. Dog Training customers are your most valuable at ${fmt$(c.trainingCLTV)} avg CLTV — ${Math.round((c.trainingCLTV/c.avgCLTV-1)*100)}% above average. They invest in their dog's wellbeing and use more services over time. ${fmtPct(c.multiSvc*100)} of customers cross into multiple service lines, which is a strong indicator of retention health.`}
                benchmarks={<><span style={{color:GREEN, fontWeight:700}}>≥70% multi-service</span> — excellent retention · <span style={{color:AMBER, fontWeight:700}}>50–70%</span> — room to grow · <span style={{color:RED, fontWeight:700}}>&lt;50%</span> — single-service risk</>}
                watch={`Training-first customers (${c.trainingN} total) have the highest CLTV. Marketing strategy should prioritize acquiring training customers, not just boarding volume.`}
              />
            )}

            {/* 3. Payback & Efficiency */}
            {m && (
              <InsightCard
                icon="⏱" section="3 · Payback & Efficiency" color="#0891B2"
                title={`CAC is ${fmt$(m.cac)} — ${m.ltvCac >= 3 ? "strong" : m.ltvCac >= 2 ? "acceptable" : "risky"} LTV:CAC of ${(Math.round(m.ltvCac*10)/10).toFixed(1)}:1`}
                metric={`CAC ${fmt$(m.cac)} · Avg CLTV ${fmt$(m.avgCLTV)} · 90-Day ROI ${Math.round(m.roi90)}% · Lifetime ROI ${Math.round(m.roiLife)}%`}
                interpretation={`It costs ${fmt$(m.cac)} to acquire each new customer. For every $1 spent, you generate ${(Math.round(m.ltvCac*100)/100).toFixed(2)} in lifetime revenue. The 90-day ROI is ${Math.round(m.roi90) >= 0 ? "+" : ""}${Math.round(m.roi90)}%, meaning it takes ${Math.round(m.roi90) >= 0 ? "less than 90 days" : "more than 90 days"} to recover your acquisition cost. Lifetime ROI is ${Math.round(m.roiLife) >= 0 ? "+" : ""}${Math.round(m.roiLife)}% — these customers are ${Math.round(m.roiLife) >= 0 ? "profitable over time" : "not yet recovering spend"}.`}
                benchmarks={<><span style={{color:GREEN, fontWeight:700}}>LTV:CAC 3:1+</span> — scalable · <span style={{color:AMBER, fontWeight:700}}>2:1</span> — acceptable · <span style={{color:RED, fontWeight:700}}>&lt;2:1</span> — risky, low margin</>}
                watch={`Many new customers call months before their first visit (seasonal business). 30-day ROI will appear negative — this is expected. Focus on 90-day and lifetime ROI as the true performance indicators.`}
              />
            )}

            {/* 4. Campaign Performance */}
            {m && m.bestCamp && (
              <InsightCard
                icon="🎯" section="4 · Campaign Performance" color="#D97706"
                title={`${m.bestCamp.name} is your only profitable campaign`}
                metric={`Best: ${m.bestCamp.name} (+${Math.round(m.bestCamp.roi)}% lifetime ROI) · Worst: ${m.worstCamp?.name} (${Math.round(m.worstCamp?.roi || -100)}% lifetime ROI)`}
                interpretation={`Dog Training is the only campaign generating positive lifetime ROI. Performance Max and broad Boarding display campaigns have the highest percentage of existing customers (70–84%), meaning you are largely paying Google to show ads to people who already use Waggles. The agency's reported lead volume is inflated by repeat callers and existing clients. True new customer acquisition is a fraction of what is being reported.`}
                benchmarks={<><span style={{color:GREEN, fontWeight:700}}>Positive lifetime ROI</span> — scale this · <span style={{color:AMBER, fontWeight:700}}>Negative but declining</span> — monitor · <span style={{color:RED, fontWeight:700}}>Negative and flat</span> — cut or restructure</>}
                watch={`Demand an existing-customer-adjusted report from your agency. Any campaign showing 70%+ existing callers is not acquiring new customers — it is retargeting your own base at your expense.`}
              />
            )}

            {/* 5. Financial Impact — marketing spend analysis */}
            {m && (
              <div style={{ background:WHITE, border:`1px solid #fecaca`, borderRadius:12, padding:"20px 24px", display:"flex", flexDirection:"column", gap:16 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:20 }}>💰</span>
                  <div style={{ fontSize:14, fontWeight:800, color:scorecardHeaderBg, fontFamily:FONT, letterSpacing:"0.04em" }}>5 · Financial Impact</div>
                </div>
                <div style={{ fontSize:18, fontWeight:800, color:"#DC2626", fontFamily:FONT }}>{fmt$(m.spend)} spent in 2025 — here is what it actually bought</div>
                <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"10px 14px",
                  fontSize:13, fontWeight:700, color:"#DC2626", fontFamily:FONT }}>
                  {m.nNew} confirmed new customers · {fmt$(Math.round(m.avgCLTV * m.nNew))} lifetime revenue · {fmt$(m.cac)} per customer
                </div>
                <div style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT, lineHeight:1.7 }}>
                  {`In 2025, ${authClient?.clientName || "the resort"} spent ${fmt$(m.spend)} on Google Ads and acquired ${m.nNew} confirmed new customers with an average lifetime value of ${fmt$(m.avgCLTV)}. That is ${fmt$(Math.round(m.avgCLTV * m.nNew))} in projected lifetime revenue from this year's marketing spend. The remaining ${m.existing.toLocaleString()} matched calls were existing customers — that portion of the budget delivered no new revenue.`}
                </div>
                <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:"10px 14px" }}>
                  <div style={{ fontSize:11, fontWeight:700, color:"#92400e", fontFamily:FONT, marginBottom:4 }}>⚠ What to watch</div>
                  <div style={{ fontSize:12, color:"#78350f", fontFamily:FONT, lineHeight:1.6 }}>
                    {`If the agency is reporting ${m.total.toLocaleString()} total calls as leads, the true conversion rate is ${m.convRate}% — not the engagement rate being presented. Ask for cost-per-new-customer, not cost-per-call.`}
                  </div>
                </div>
              </div>
            )}

            {/* 6. Financials AI Insight */}
            {rows && (() => {
              // Compute QB financial metrics to pass to AI
              const allRev = rows.filter(r => r.Exclude_From_Revenue !== "Yes" && r.Is_Refund !== "Yes");
              const byBucket = {};
              allRev.forEach(r => {
                const b = r.FinalMainBucket || "Other";
                if (!byBucket[b]) byBucket[b] = 0;
                byBucket[b] += parseFloat(r.Amount) || 0;
              });
              const totalRev = Object.values(byBucket).reduce((s,v)=>s+v,0);
              const topBuckets = Object.entries(byBucket).sort((a,b)=>b[1]-a[1]).slice(0,4)
                .map(([k,v])=>`${k}: $${Math.round(v).toLocaleString()} (${Math.round(v/totalRev*100)}%)`).join(", ");
              // YoY - compare 2025 vs 2024
              const rev2025 = allRev.filter(r=>r.Date?.startsWith("2025")).reduce((s,r)=>s+(parseFloat(r.Amount)||0),0);
              const rev2024 = allRev.filter(r=>r.Date?.startsWith("2024")).reduce((s,r)=>s+(parseFloat(r.Amount)||0),0);
              const yoy = rev2024>0 ? ((rev2025-rev2024)/rev2024*100).toFixed(1) : null;
              const avgMonthly2025 = rev2025 > 0 ? (rev2025/12).toFixed(0) : null;
              const refundTotal = rows.filter(r=>r.Is_Refund==="Yes").reduce((s,r)=>s+(parseFloat(r.Amount)||0),0);
              const discountTotal = rows.filter(r=>r.FinalMainBucket==="Discounts").reduce((s,r)=>s+(parseFloat(r.Amount)||0),0);

              const metrics = { totalRev, topBuckets, yoy, avgMonthly2025, refundTotal, discountTotal };
              const prompt = `${authClient?.clientName || "Pet Resort"} financial data:
- Total revenue (all time in file): $${Math.round(totalRev).toLocaleString()}
- Revenue by service: ${topBuckets}
- 2025 full year revenue: $${Math.round(rev2025).toLocaleString()}
- 2024 full year revenue: $${Math.round(rev2024).toLocaleString()}
- Year-over-year growth 2024→2025: ${yoy}%
- Average monthly revenue 2025: $${parseInt(avgMonthly2025||0).toLocaleString()}
- Total refunds: $${Math.round(Math.abs(refundTotal)).toLocaleString()}
- Total discounts: $${Math.round(Math.abs(discountTotal)).toLocaleString()}
Provide a financial performance insight for the resort owner.`;

              const fallback = {
                title: `${yoy ? (yoy>0?"+":"")+yoy+"% revenue growth 2024→2025" : "Financial performance summary"}`,
                metric: `$${Math.round(rev2025).toLocaleString()} in 2025 revenue · ${topBuckets.split(",")[0]}`,
                interpretation: `Revenue grew ${yoy}% year over year, with ${topBuckets.split(",")[0].split(":")[0]} as the top service. Average monthly revenue in 2025 was $${parseInt(avgMonthly2025||0).toLocaleString()}.`,
                watch: `Refunds ($${Math.round(Math.abs(refundTotal)).toLocaleString()}) and discounts ($${Math.round(Math.abs(discountTotal)).toLocaleString()}) represent margin leakage worth reviewing.`
              };

              return (
                <AIInsightCard
                  key="financials-ai"
                  icon="📊" section="6 · Financials Insight" color="#2563eb"
                  metrics={metrics} prompt={prompt} fallback={fallback}
                />
              );
            })()}

            {/* 7. Bookings AI Insight */}
            {bookingRows && bookingRows.length > 0 && (() => {
              const past   = bookingRows.filter(r => !r.is_future);
              const future = bookingRows.filter(r => r.is_future);
              const futureRev = future.reduce((s,r)=>s+(r.booking_value||0),0);
              const past2025  = past.filter(r=>r.start_date?.startsWith("2025"));
              const past2024  = past.filter(r=>r.start_date?.startsWith("2024"));
              const rev2025   = past2025.reduce((s,r)=>s+(r.booking_value||0),0);
              const rev2024   = past2024.reduce((s,r)=>s+(r.booking_value||0),0);
              const yoy       = rev2024>0 ? ((rev2025-rev2024)/rev2024*100).toFixed(1) : null;

              // Service mix
              const byType = {};
              past2025.forEach(r => { byType[r.order_type]=(byType[r.order_type]||0)+r.booking_value; });
              const totalType = Object.values(byType).reduce((s,v)=>s+v,0);
              const mix = Object.entries(byType).sort((a,b)=>b[1]-a[1]).slice(0,3)
                .map(([k,v])=>`${k}: ${Math.round(v/totalType*100)}%`).join(", ");

              // RPN estimate - total boarding rev / total boarding nights
              const boardingRows = past2025.filter(r=>r.order_type==="boarding");
              const byRes = {};
              boardingRows.forEach(r=>{ if(!byRes[r.reservation_string])byRes[r.reservation_string]=[]; byRes[r.reservation_string].push(r); });
              let totalBoardRev=0, totalNights=0;
              Object.values(byRes).forEach(rows=>{
                const lodging = rows.find(r=>(r.rate||"").toLowerCase().includes("lodging"));
                if(lodging?.start_date&&lodging?.end_date){
                  const n=Math.max(Math.round((new Date(lodging.end_date+"T00:00:00")-new Date(lodging.start_date+"T00:00:00"))/86400000),1);
                  totalBoardRev+=rows.reduce((s,r)=>s+r.booking_value,0);
                  totalNights+=n;
                }
              });
              const rpn = totalNights>0 ? (totalBoardRev/totalNights).toFixed(2) : null;

              const metrics = { futureRev, future: future.length, rev2025, rev2024, yoy, mix, rpn };
              const prompt = `${authClient?.clientName || "Pet Resort"} bookings data:
- Confirmed future pipeline: $${Math.round(futureRev).toLocaleString()} (${future.length} bookings)
- 2025 booking revenue: $${Math.round(rev2025).toLocaleString()}
- 2024 booking revenue: $${Math.round(rev2024).toLocaleString()}
- Year-over-year growth: ${yoy}%
- 2025 service mix (by revenue): ${mix}
- Average Revenue Per Night (boarding 2025): $${rpn||"N/A"}
- Boarding is the largest future pipeline service
Provide a bookings and occupancy insight for the resort owner. Focus on pipeline health, RPN performance, and seasonal patterns.`;

              const fallback = {
                title: `$${Math.round(futureRev).toLocaleString()} confirmed pipeline with ${yoy?yoy+"%":""} YoY growth`,
                metric: `${future.length} future bookings · $${Math.round(futureRev).toLocaleString()} pipeline · RPN $${rpn||"—"}`,
                interpretation: `Bookings revenue grew ${yoy}% year over year. The confirmed pipeline of $${Math.round(futureRev).toLocaleString()} provides near-term revenue visibility. Service mix in 2025: ${mix}.`,
                watch: `Revenue Per Night of $${rpn||"—"} should be tracked monthly — small RPN improvements compound significantly across hundreds of boarding stays.`
              };

              return (
                <AIInsightCard
                  key="bookings-ai"
                  icon="📅" section="7 · Bookings Insight" color="#059669"
                  metrics={metrics} prompt={prompt} fallback={fallback}
                />
              );
            })()}

            </div>{/* end summary-content */}
            {/* 8. Executive Summary */}
            {(m || c) && (
              <div data-pdf="exec-summary" style={{ background:scorecardHeaderBg, border:`1px solid ${scorecardHeaderBg}`, borderRadius:12, padding:"22px 24px", display:"flex", flexDirection:"column", gap:16, color:WHITE }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:20 }}>📋</span>
                  <div style={{ fontSize:14, fontWeight:800, color:WHITE, fontFamily:FONT, letterSpacing:"0.04em" }}>Executive Summary</div>
                </div>

                {/* Key Findings */}
                <div>
                  <div style={{ fontSize:10, fontWeight:700, color:WHITE, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:FONT, marginBottom:8 }}>Key Findings</div>
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {[
                      m && `${m.existPct}% of tracked calls were existing customers — meaning the majority of ad spend is retargeting your own client base, not growing it.`,
                      m && m.bestCamp && `${m.bestCamp.name} is the only campaign delivering positive lifetime ROI (+${Math.round(m.bestCamp.roi)}%). The rest are generating calls, not customers — and the agency's numbers don't show that distinction.`,
                      c && m && `Dog Training customers generate ${fmt$(c.trainingCLTV)} in lifetime revenue — ${Math.round((c.trainingCLTV/c.avgCLTV-1)*100)}% above average — making them your highest-value segment and your most under-invested acquisition target.`,
                    ].filter(Boolean).map((finding, i) => (
                      <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                        <div style={{ width:6, height:6, borderRadius:"50%", background:"#60a5fa", marginTop:5, flexShrink:0 }} />
                        <span style={{ fontSize:12, color:"rgba(255,255,255,0.85)", fontFamily:FONT, lineHeight:1.7 }}>{finding}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* What This Means */}
                <div style={{ background:"rgba(255,255,255,0.08)", borderRadius:8, padding:"12px 14px" }}>
                  <div style={{ fontSize:10, fontWeight:700, color:WHITE, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:FONT, marginBottom:6 }}>What This Means</div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.85)", fontFamily:FONT, lineHeight:1.7 }}>
                    {m && c
                      ? `${fmt$(m.spend)} in annual ad spend is producing ${m.nNew} confirmed new customers — at ${fmt$(m.cac)} each — while ${m.existPct}% of reported leads were already customers. The strategy needs to be reoriented around acquisition, not activity.`
                      : m
                      ? `Reported lead volume significantly overstates true acquisition. Confirmed new customers represent a fraction of tracked calls — and only one campaign is producing them profitably.`
                      : `Customer quality and lifetime value are strong. The opportunity is to acquire more customers like the ones already driving revenue.`
                    }
                  </div>
                </div>

                {/* Top Actions */}
                <div>
                  <div style={{ fontSize:10, fontWeight:700, color:WHITE, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:FONT, marginBottom:8 }}>Top Actions</div>
                  <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                    {[
                      m && m.bestCamp && `Double down on ${m.bestCamp.name}. It is the only campaign with proven positive ROI — reallocate budget from underperforming campaigns before increasing total spend.`,
                      m && `Require the agency to report cost-per-new-customer, not cost-per-call. ${m.existPct}% of their "leads" are your existing clients — that number should not appear in any acquisition metric.`,
                      m && c && `Prioritize Dog Training acquisition. At ${fmt$(c.trainingCLTV)} avg CLTV vs ${fmt$(c.avgCLTV)} overall, the higher CAC is justified — and the LTV:CAC math makes it the most scalable segment in the business.`,
                    ].filter(Boolean).map((action, i) => (
                      <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                        <div style={{ width:18, height:18, borderRadius:4, background:"#2563eb", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:10, fontWeight:800, color:WHITE, fontFamily:FONT, marginTop:1 }}>{i+1}</div>
                        <span style={{ fontSize:12, color:"rgba(255,255,255,0.85)", fontFamily:FONT, lineHeight:1.7 }}>{action}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

          </div>
        </>
      )}

      {/* ── METRIC DEFINITIONS ──────────────────────────────────────────── */}
      <div style={{ fontSize:13, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.1em", fontFamily:FONT, marginTop:8 }}>
        📐 Metric Definitions & Assumptions
      </div>
      <div data-pdf="metric-definitions" style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:12, overflow:"hidden" }}>
        <div style={{ padding:"14px 20px", background:scorecardHeaderBg }}>
          <div style={{ fontSize:13, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>Marketing ROI — Confirmed Calculations</div>
          <div style={{ fontSize:11, color:"rgba(255,255,255,0.55)", fontFamily:FONT, marginTop:2 }}>All definitions confirmed and approved. Source: ClaudeConfirmation.xlsx + ClaudeNewMetrics.xlsx</div>
        </div>
        {/* Header */}
        <div style={{ display:"grid", gridTemplateColumns:"180px 1fr 1fr", padding:"8px 20px", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}` }}>
          {["Metric","Formula","Purpose & Assumptions"].map(h => (
            <span key={h} style={{ fontSize:10, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT }}>{h}</span>
          ))}
        </div>
        {metricDefs.map((d, i) => (
          <div key={d.metric} style={{ display:"grid", gridTemplateColumns:"180px 1fr 1fr", padding:"12px 20px", borderBottom:`1px solid ${GRAY_B}`, background:i%2===0?WHITE:GRAY_BG, alignItems:"start" }}>
            <span style={{ fontSize:12, fontWeight:700, color:scorecardHeaderBg, fontFamily:FONT, paddingRight:12 }}>{d.metric}</span>
            <span style={{ fontSize:11, color:"#374151", fontFamily:FONT, fontStyle:"italic", paddingRight:16, lineHeight:1.6 }}>{d.formula}</span>
            <span style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, lineHeight:1.6 }}>{d.purpose}</span>
          </div>
        ))}
        {/* Footer */}
        <div style={{ padding:"12px 20px", background:GRAY_BG, fontSize:11, color:GRAY_MID, fontFamily:FONT, fontStyle:"italic", lineHeight:1.8 }}>
          Data sources: Financial file export (revenue) · Customer export (phone matching) · Google Ads call tracking CSV (campaign attribution) ·
          Match method: phone number normalization (last 10 digits) · Period spend = $monthly rate × months in filter · All metrics exclude existing customers from acquisition counts.
        </div>
      </div>

      {/* ── PLAIN-ENGLISH ADDENDUM ────────────────────────────────────── */}
      <div style={{ fontSize:13, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.1em", fontFamily:FONT, marginTop:8 }}>
        📖 How We Define a Customer
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

        {/* Q1 */}
        <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:12, padding:"22px 28px" }}>
          <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
            <div style={{ width:36, height:36, borderRadius:"50%", background:scorecardHeaderBg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <span style={{ fontSize:16, color:WHITE, fontWeight:800, fontFamily:FONT }}>1</span>
            </div>
            <div>
              <div style={{ fontSize:14, fontWeight:800, color:scorecardHeaderBg, fontFamily:FONT, marginBottom:6 }}>What makes someone a "new customer"?</div>
              <div style={{ fontSize:13, color:"#374151", fontFamily:FONT, lineHeight:1.8, marginBottom:10 }}>
                A new customer is someone who called the tracked Google Ads number, was added to the booking system on or after that call, and then generated at least one paid invoice. All three conditions must be true.
              </div>
              <div style={{ background:GRAY_BG, borderRadius:8, padding:"10px 16px", fontSize:12, color:GRAY_MID, fontFamily:FONT, lineHeight:1.7 }}>
                <span style={{ fontWeight:700, color:scorecardHeaderBg }}>Why all three?</span> A call alone means someone was interested. A client record in your booking system means they took the next step. An invoice means they actually paid. We only count customers who completed all three — anything less is an inquiry, not an acquisition.
              </div>
            </div>
          </div>
        </div>

        {/* Q2 */}
        <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:12, padding:"22px 28px" }}>
          <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
            <div style={{ width:36, height:36, borderRadius:"50%", background:scorecardHeaderBg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <span style={{ fontSize:16, color:WHITE, fontWeight:800, fontFamily:FONT }}>2</span>
            </div>
            <div>
              <div style={{ fontSize:14, fontWeight:800, color:scorecardHeaderBg, fontFamily:FONT, marginBottom:6 }}>When does the revenue clock start?</div>
              <div style={{ fontSize:13, color:"#374151", fontFamily:FONT, lineHeight:1.8, marginBottom:10 }}>
                Revenue windows (30, 60, 90 days) are measured from the date the customer was created in the booking system — not their call date. You cannot book a service without a client record, so this is the earliest possible moment a customer relationship begins.
              </div>
              <div style={{ background:GRAY_BG, borderRadius:8, padding:"10px 16px", fontSize:12, color:GRAY_MID, fontFamily:FONT, lineHeight:1.7 }}>
                <span style={{ fontWeight:700, color:scorecardHeaderBg }}>Example:</span> A customer calls on November 10, gets added to the booking system on November 10, and pays their first invoice on December 5. Their 30-day revenue window runs November 10 – December 10. The December 5 invoice counts. This approach is consistent regardless of how many calls preceded the booking.
              </div>
            </div>
          </div>
        </div>

        {/* Q3 */}
        <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:12, padding:"22px 28px" }}>
          <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
            <div style={{ width:36, height:36, borderRadius:"50%", background:scorecardHeaderBg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <span style={{ fontSize:16, color:WHITE, fontWeight:800, fontFamily:FONT }}>3</span>
            </div>
            <div>
              <div style={{ fontSize:14, fontWeight:800, color:scorecardHeaderBg, fontFamily:FONT, marginBottom:6 }}>What if someone called multiple times before booking?</div>
              <div style={{ fontSize:13, color:"#374151", fontFamily:FONT, lineHeight:1.8, marginBottom:10 }}>
                We track every call. If a customer called three times over two months before finally booking, all three calls appear in the data. However, acquisition credit is given once — to the first call. Subsequent calls from the same person are counted as activity, not as separate leads or new customers.
              </div>
              <div style={{ background:GRAY_BG, borderRadius:8, padding:"10px 16px", fontSize:12, color:GRAY_MID, fontFamily:FONT, lineHeight:1.7 }}>
                <span style={{ fontWeight:700, color:scorecardHeaderBg }}>Why this matters:</span> An agency reporting "total calls" will count each of those three calls as a separate lead. We do not. One person = one customer, regardless of how many times they called before deciding. This is why our new customer counts will always be lower than what an agency reports — and why ours are more accurate.
              </div>
            </div>
          </div>
        </div>

        {/* Q4 */}
        <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:12, padding:"22px 28px" }}>
          <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
            <div style={{ width:36, height:36, borderRadius:"50%", background:scorecardHeaderBg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <span style={{ fontSize:16, color:WHITE, fontWeight:800, fontFamily:FONT }}>4</span>
            </div>
            <div>
              <div style={{ fontSize:14, fontWeight:800, color:scorecardHeaderBg, fontFamily:FONT, marginBottom:6 }}>What about existing customers who call in?</div>
              <div style={{ fontSize:13, color:"#374151", fontFamily:FONT, lineHeight:1.8, marginBottom:10 }}>
                If a caller already had a client record in the booking system before the call date, they are classified as an existing customer and excluded from all acquisition metrics. Their call is counted in total call volume but does not contribute to new customer counts, CAC, ROI, or any conversion rate. They are loyal customers — not marketing results.
              </div>
              <div style={{ background:GRAY_BG, borderRadius:8, padding:"10px 16px", fontSize:12, color:GRAY_MID, fontFamily:FONT, lineHeight:1.7 }}>
                <span style={{ fontWeight:700, color:RED }}>The uncomfortable truth:</span> In 2025, over half of all tracked Google Ads calls were from existing customers. This means a significant portion of the marketing budget is being spent reaching people who already use your company — people who likely would have called regardless of whether an ad was shown to them.
              </div>
            </div>
          </div>
        </div>

        {/* Q5 — Data limitations */}
        <div style={{ background:"#f0f9ff", border:`1px solid #bae6fd`, borderRadius:12, padding:"22px 28px" }}>
          <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
            <div style={{ width:36, height:36, borderRadius:"50%", background:"#0284c7", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              <span style={{ fontSize:16, color:WHITE, fontWeight:800, fontFamily:FONT }}>!</span>
            </div>
            <div>
              <div style={{ fontSize:14, fontWeight:800, color:"#0284c7", fontFamily:FONT, marginBottom:6 }}>Known data limitations</div>
              <div style={{ fontSize:13, color:"#374151", fontFamily:FONT, lineHeight:1.8 }}>
                <div style={{ marginBottom:8 }}><span style={{ fontWeight:700, color:scorecardHeaderBg }}>Phone matching only:</span> Google Analytics calls are matched by phone number to new customers. Customers who called from a different number than what is on file will not match and will appear as non-conversions, even if they did book. This may undercount true conversions slightly.</div>
                {!isPK && <div style={{ marginBottom:8 }}><span style={{ fontWeight:700, color:scorecardHeaderBg }}>QB sync gap (Nov 2025+):</span> A known issue in the QuickBooks/ProPet integration caused PP-IDs to stop writing correctly to QB for newer customers. This dashboard recovers those records automatically using name-field extraction, but any customers created after the export date will not yet appear in revenue figures.</div>}
                <div><span style={{ fontWeight:700, color:scorecardHeaderBg }}>Tracked calls only:</span> This analysis only covers calls routed through Google Ads call tracking. Customers who found your company through organic search, referral, or direct contact and called a non-tracked number are not included in marketing attribution — but they are included in Customer KPI and Financial data.</div>
              </div>
            </div>
          </div>
        </div>

      </div>

    </div>
  );
}


// ─── BOOKINGS TAB ─────────────────────────────────────────────────────────────
function BookingsTab({ bookingRows, precomputedMetrics, isPKTenantUser }) {
  const [periodMode, setPeriodMode] = useState("month"); // month | year
  const [viewMode, setViewMode] = useState("future");   // overview | future | trend — default pipeline

  const today = new Date().toISOString().slice(0,10);

  const ORDER_COLORS = {
    boarding:   "#1B3A6B",
    daycare:    "#60A5FA",
    grooming:   "#A78BFA",
    training:   "#34D399",
    standalone: "#94A3B8",
  };
  const ORDER_LABELS = {
    boarding: "Boarding", daycare: "Daycare",
    grooming: "Grooming", training: "Training"
  };

  const [filterYear, setFilterYear] = useState("all");
  const [selectedBookingPeriod, setSelectedBookingPeriod] = useState(null);

  // Available years from data
  const availableYears = useMemo(() => {
    const yrs = new Set((bookingRows||[]).map(r => r.start_date?.slice(0,4)).filter(Boolean));
    return ["all", ...([...yrs].sort().reverse())];
  }, [bookingRows]);

  // Available periods for the dropdown (month or week keys, descending)
  const availablePeriods = useMemo(() => {
    const keys = new Set();
    (bookingRows||[]).filter(r => !r.is_future).forEach(r => {
      if (!r.start_date) return;
      if (periodMode === "month") {
        keys.add(r.start_date.slice(0,7));
      } else if (periodMode === "week") {
        const dt = new Date(r.start_date+"T00:00:00");
        const we = new Date(dt); we.setDate(dt.getDate()+(6-dt.getDay()+7)%7);
        keys.add(we.toISOString().slice(0,10));
      } else {
        keys.add(r.start_date.slice(0,4));
      }
    });
    return [...keys].sort().reverse();
  }, [bookingRows, periodMode]);

  // Auto-select most recent period when mode changes
  const handlePeriodMode = (mode) => {
    setPeriodMode(mode);
    // Will auto-select via effect below
  };

  // Auto-select most recent period when availablePeriods changes
  useEffect(() => {
    if (availablePeriods.length > 0) {
      setSelectedBookingPeriod(availablePeriods[0]);
    }
  }, [availablePeriods]);

  // Format period key for display
  const fmtPeriodLabel = (k) => {
    if (!k) return "";
    if (k.length === 4) return k;
    if (k.length === 7) {
      const [y,m] = k.split("-");
      return `${["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(m)]} ${y}`;
    }
    if (k.length === 10) {
      const d = new Date(k+"T00:00:00");
      return `Wk ${d.getMonth()+1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
    }
    return k;
  };

  const past   = useMemo(() => {
    const all = (bookingRows || []).filter(r => !r.is_future);
    if (!selectedBookingPeriod) return all;
    return all.filter(r => {
      if (!r.start_date) return false;
      if (periodMode === "month") return r.start_date.slice(0,7) === selectedBookingPeriod;
      if (periodMode === "year")  return r.start_date.slice(0,4) === selectedBookingPeriod;
      if (periodMode === "week") {
        const dt = new Date(r.start_date+"T00:00:00");
        const we = new Date(dt); we.setDate(dt.getDate()+(6-dt.getDay()+7)%7);
        return we.toISOString().slice(0,10) === selectedBookingPeriod;
      }
      return true;
    });
  }, [bookingRows, selectedBookingPeriod, periodMode]);

  const future = (bookingRows || []).filter(r => r.is_future);

  // Pre-compute boarding night occupancy once — used by KPI widgets
  const boardingOccupancy = useMemo(() => {
    const nightDogs = {};
    const bRes = {};
    (bookingRows||[]).filter(r => !r.is_future && r.order_type==="boarding").forEach(r => {
      const rs = r.reservation_string;
      if (rs) { if (!bRes[rs]) bRes[rs]=[]; bRes[rs].push(r); }
    });
    Object.values(bRes).forEach(rows => {
      const lodging = rows.find(r => (r.rate||"").toLowerCase().includes("lodging"));
      if (!lodging?.start_date || !lodging?.end_date) return;
      const s = new Date(lodging.start_date+"T00:00:00");
      const n = Math.max(Math.round((new Date(lodging.end_date+"T00:00:00")-s)/86400000),1);
      for (let i=0; i<n; i++) {
        const d = new Date(s); d.setDate(s.getDate()+i);
        const k = d.toISOString().slice(0,10);
        nightDogs[k] = (nightDogs[k]||0)+1;
      }
    });
    const entries = Object.entries(nightDogs);
    const all   = entries.reduce((s,[,v])=>s+v,0)/Math.max(entries.length,1);
    const wknd  = entries.filter(([d])=>[5,6,0].includes(new Date(d+"T00:00:00").getDay()));
    const wkdy  = entries.filter(([d])=>[1,2,3,4].includes(new Date(d+"T00:00:00").getDay()));
    return {
      avg:     all,
      wkndAvg: wknd.length ? wknd.reduce((s,[,v])=>s+v,0)/wknd.length : 0,
      wkdyAvg: wkdy.length ? wkdy.reduce((s,[,v])=>s+v,0)/wkdy.length : 0,
    };
  }, [bookingRows]);

  // ── Summary stats ──────────────────────────────────────────────────────────
  const totalPastRev   = past.reduce((s,r)  => s+r.booking_value, 0);
  const totalFutureRev = future.reduce((s,r) => s+r.booking_value, 0);
  const totalBookings  = past.length;
  const avgBookingVal  = totalBookings ? totalPastRev/totalBookings : 0;

  // YTD (2026)
  const ytd     = past.filter(r => r.start_date?.startsWith("2026"));
  const ytdRev  = ytd.reduce((s,r) => s+r.booking_value, 0);
  const ytdBks  = ytd.length;

  // Prior year same period (2025 Jan-Apr)
  const priorYTD    = past.filter(r => r.start_date >= "2025-01-01" && r.start_date <= "2025-04-09");
  const priorYTDRev = priorYTD.reduce((s,r) => s+r.booking_value, 0);
  const ytdGrowth   = priorYTDRev > 0 ? (ytdRev-priorYTDRev)/priorYTDRev*100 : 0;

  // Full year comparison
  const rev2025 = past.filter(r => r.start_date?.startsWith("2025")).reduce((s,r) => s+r.booking_value, 0);
  const rev2024 = past.filter(r => r.start_date?.startsWith("2024")).reduce((s,r) => s+r.booking_value, 0);
  const rev2023 = past.filter(r => r.start_date?.startsWith("2023")).reduce((s,r) => s+r.booking_value, 0);
  const yoy2025 = rev2024 > 0 ? (rev2025-rev2024)/rev2024*100 : 0;

  // ── Revenue by period ──────────────────────────────────────────────────────
  const periodData = useMemo(() => {
    const map = {};
    past.forEach(r => {
      const key = periodMode === "year" ? r.start_date?.slice(0,4) : r.start_date?.slice(0,7);
      if (!key) return;
      if (!map[key]) map[key] = { total:0, boarding:0, daycare:0, grooming:0, training:0, count:0 };
      const effectiveType = r.order_type === "standalone" && r.rate?.toLowerCase().includes("train")
        ? "training" : r.order_type;
      if (effectiveType === "standalone" && (r.booking_value||0) === 0) { map[key].count++; return; }
      if (effectiveType === "standalone") return;
      map[key].total += r.booking_value;
      map[key][effectiveType] = (map[key][effectiveType] || 0) + r.booking_value;
      map[key].count++;
    });
    // Descending sort — most recent first
    return Object.entries(map).sort((a,b) => b[0].localeCompare(a[0]));
  }, [past, periodMode]);

  // ── Future by month ────────────────────────────────────────────────────────
  const futureByMonth = useMemo(() => {
    const map = {};
    future.forEach(r => {
      const key = r.start_date?.slice(0,7);
      if (!key) return;
      if (!map[key]) map[key] = { total:0, boarding:0, daycare:0, grooming:0, training:0, standalone:0, count:0 };
      map[key].total += r.booking_value;
      map[key][r.order_type] = (map[key][r.order_type] || 0) + r.booking_value;
      map[key].count++;
    });
    return Object.entries(map).sort((a,b) => a[0].localeCompare(b[0]));
  }, [future]);

  // ── Top customers (future) ─────────────────────────────────────────────────
  const topFutureCustomers = useMemo(() => {
    // Group future rows by customer, then by reservation, then dedup each reservation
    const byCustomer = {};
    future.forEach(r => {
      const key = r.customer_id || r.customer_name;
      if (!byCustomer[key]) byCustomer[key] = { name: r.customer_name, id: r.customer_id, locationCode: r.locationCode || "", reservations: {}, services: new Set() };
      const rs = r.reservation_string || r.customer_id;
      if (!byCustomer[key].reservations[rs]) byCustomer[key].reservations[rs] = [];
      byCustomer[key].reservations[rs].push(r);
      byCustomer[key].services.add(r.order_type);
    });
    return Object.values(byCustomer).map(c => {
      // Dedup each reservation then sum
      const total = Object.values(c.reservations).reduce((sum, res_rows) => {
        const byRate = {};
        res_rows.forEach(r => {
          const rt = r.rate || "";
          if (!byRate[rt]) byRate[rt] = [];
          byRate[rt].push(r.booking_value || 0);
        });
        return sum + Object.values(byRate).reduce((s, vals) => {
          const unique = [...new Set(vals.map(v => Math.round(v*100)/100))];
          return s + (unique.length === 1 ? unique[0] : vals.reduce((a,b)=>a+b,0));
        }, 0);
      }, 0);
      return { name: c.name, id: c.id, total, bookings: Object.keys(c.reservations).length, services: c.services };
    }).sort((a,b) => b.total-a.total).slice(0,10);
  }, [future]);

  const fmt$ = n => `$${Math.round(n).toLocaleString()}`;
  const fmtK = n => n >= 1000 ? `$${(n/1000).toFixed(0)}k` : fmt$(n);
  const fmtPct = n => `${n >= 0 ? "+" : ""}${Math.round(n)}%`;
  const fmtMo = k => {
    if (!k) return "";
    if (k.length === 4) return k;
    const [y,m] = k.split("-");
    const mo = ["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(m)];
    return `${mo} ${y}`;
  };

  // Chart max for scaling
  const chartMax = Math.max(...periodData.map(([,d]) => d.total), 1);
  const futureChartMax = Math.max(...futureByMonth.map(([,d]) => d.total), 1);

  if (!bookingRows || bookingRows.length === 0) {
    return (
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:80, gap:16 }}>
        <div style={{ fontSize:48 }}>📅</div>
        <div style={{ fontSize:16, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT }}>No booking data loaded</div>
        <div style={{ fontSize:13, color:GRAY_MID, fontFamily:FONT }}>Upload your Bookings Data using the file import button above</div>
      </div>
    );
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

      {/* Header */}
      {/* ── Sticky filter bar — identical to Financial tab ─────────── */}
      <div data-pdf="bookings-kpis" style={{ position:"sticky", top:60, zIndex:90, background:GRAY_BG, paddingBottom:8, marginBottom:-8 }}>
        <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, padding:"14px 18px",
          display:"flex", alignItems:"center", gap:16, flexWrap:"wrap", marginBottom:0 }}>
          <span style={{ fontSize:12, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT }}>Filter by</span>
          <div style={{ display:"flex", gap:0, border:`1px solid ${GRAY_B}`, borderRadius:6, overflow:"hidden" }}>
            {[["month","Month"],["week","Week"],["year","Year"]].map(([m,l])=>(
              <button key={m} onClick={()=>setPeriodMode(m)}
                style={{ padding:"6px 14px", border:"none", borderRight:`1px solid ${GRAY_B}`, cursor:"pointer",
                  fontSize:12, fontWeight:600, fontFamily:FONT,
                  background:periodMode===m?_tenantHeaderBg:WHITE,
                  color:periodMode===m?WHITE:GRAY_MID, transition:"all 0.1s" }}>{l}</button>
            ))}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT }}>Period:</span>
            <select value={selectedBookingPeriod||""} onChange={e=>setSelectedBookingPeriod(e.target.value)}
              style={{ fontSize:13, fontWeight:600, color:_tenantHeaderBg, padding:"5px 10px", borderRadius:6,
                border:`1px solid ${GRAY_B}`, fontFamily:FONT, cursor:"pointer", background:WHITE }}>
              {availablePeriods.map(p => <option key={p} value={p}>{fmtPeriodLabel(p)}</option>)}
            </select>
          </div>
          <div style={{ marginLeft:"auto", fontSize:12, color:GRAY_MID, fontFamily:FONT }}>
            {past.length.toLocaleString()} bookings · {fmtPeriodLabel(selectedBookingPeriod||"")}
          </div>
        </div>

        {/* KPI widgets + Customer Intros — sticky with filter bar */}
      {(() => {
        // Compute filtered metrics
        const filtPast = past; // already filtered by filterYear
        const filtRev  = filtPast.reduce((s,r)=>s+r.booking_value,0);
        const filtBks  = filtPast.length;

        const str = s => String(s||""); // define first — used throughout

        // Prior year same period for comparison
        const currYr   = filterYear === "all" ? "2025" : filterYear;
        const priorYr  = String(parseInt(currYr)-1);
        const priorRev = (bookingRows||[]).filter(r => !r.is_future && r.start_date?.startsWith(priorYr))
                          .reduce((s,r)=>s+r.booking_value,0);
        const yoyGrowth = priorRev > 0 ? (filtRev-priorRev)/priorRev*100 : 0;

        // Avg daycare dogs (weekday M-F) from occupancy — use pre-built maps from KennelOpsTable
        // Approximate from reservation counts for widget
        const dcDays = new Set(filtPast.filter(r=>r.order_type==="daycare"&&!["employee dog"].includes(str(r.rate||"").toLowerCase())).map(r=>r.start_date).filter(Boolean));
        const dcAvg  = dcDays.size > 0
          ? filtPast.filter(r=>r.order_type==="daycare" && str(r.rate||"").toLowerCase()!=="employee dog").length / dcDays.size
          : 0;

        // Use pre-computed boarding occupancy
        const bAvg    = boardingOccupancy.avg;
        const bWkndAvg= boardingOccupancy.wkndAvg;
        const bWkdyAvg= boardingOccupancy.wkdyAvg;

        const avgBkgVal = filtBks > 0 ? filtRev/filtBks : 0;

        const widgets = [
          { label:`Revenue — ${fmtPeriodLabel(selectedBookingPeriod||"")||"All"}`,
            value:fmt$(filtRev), sub:`${filtBks.toLocaleString()} bookings`,
            color:_tenantHeaderBg, note:`${fmtPct(yoyGrowth)} vs ${priorYr}`,
            noteColor: yoyGrowth>=0?GREEN:RED },
          { label:"Future Reservations",
            value:fmt$(totalFutureRev), sub:`${future.length} confirmed bookings`,
            color:GREEN, note:"confirmed pipeline" },
          { label:`Avg ${label("Daycare")} Dogs (M–F)`,
            value:Math.round(dcAvg).toString(), sub:"paying dogs, weekdays only",
            color:"#60A5FA" },
          { label:`Avg ${label("Boarding")} Dogs`,
            value:Math.round(bWkndAvg).toString(), sub:"Fri–Sun avg dogs/night",
            color:_tenantHeaderBg, extra:[
              { label:"Mon–Thu", val:Math.round(bWkdyAvg) },
            ]},
          { label:"Avg Booking Value",
            value:fmt$(avgBkgVal), sub:"avg total revenue per booking line",
            color:_tenantHeaderBg },
        ];
        return (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:14 }}>
            {widgets.map((w,i) => (
              <div key={i} style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, padding:"16px 18px" }}>
                <div style={{ fontSize:10, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT, marginBottom:6 }}>{w.label}</div>
                <div style={{ fontSize:22, fontWeight:800, color:w.color, fontFamily:FONT, lineHeight:1 }}>{w.value}</div>
                <div style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, marginTop:4 }}>{w.sub}</div>
                {w.note && <div style={{ fontSize:11, fontWeight:700, color:w.noteColor||GRAY_MID, fontFamily:FONT, marginTop:4 }}>{w.note}</div>}
                {w.extra && w.extra.map((e,j) => (
                  <div key={j} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:4 }}>
                    <span style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT }}>{e.label}</span>
                    <span style={{ fontSize:14, fontWeight:700, color:w.color, fontFamily:FONT }}>{e.val}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        );
      })()}



      {/* Customer Intros / New Customer Visits widget */}
      {(() => {
        const allPast = (bookingRows||[]).filter(r => !r.is_future);
        const intros = isPKTenantUser
          // PK: first-time daycamp visits + training evaluations (flagged)
          ? allPast.filter(r =>
              (r.order_type==="daycare" && r.is_evaluation) ||
              (r.order_type==="training" && (r.rate||"").toLowerCase().includes("eval"))
            )
          // Waggles: meet & greet / tour / standalone
          : allPast.filter(r =>
              r.order_type==="standalone" ||
              (r.rate||"").toLowerCase().includes("meet") ||
              (r.rate||"").toLowerCase().includes("tour")
            );
        const introsByMonth = {};
        intros.forEach(r => {
          const mo = r.start_date?.slice(0,7);
          if (mo) introsByMonth[mo] = (introsByMonth[mo]||0) + 1;
        });
        const now = new Date();
        const thisMonth = now.toISOString().slice(0,7);
        const lastDate  = new Date(now.getFullYear(), now.getMonth()-1, 1);
        const lastMonth = lastDate.toISOString().slice(0,7);
        const thisCount = introsByMonth[thisMonth] || 0;
        const lastCount = introsByMonth[lastMonth] || 0;
        const ytdCount  = intros.filter(r => r.start_date?.startsWith(now.getFullYear().toString())).length;
        const trend     = thisCount > lastCount ? `▲ ${thisCount-lastCount} vs last mo` : thisCount < lastCount ? `▼ ${lastCount-thisCount} vs last mo` : "= same as last mo";
        const trendColor= thisCount > lastCount ? GREEN : thisCount < lastCount ? RED : GRAY_MID;
        return (
          <div style={{ background:WHITE, border:`1px solid #c7d2fe`, borderRadius:10, padding:"16px 20px", display:"grid", gridTemplateColumns:"1.5fr 1fr 1fr 1fr", gap:16, alignItems:"center" }}>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:"#6366f1", fontFamily:FONT, marginBottom:4 }}>
                📋 {isPKTenantUser ? "New Customer Visits" : "Customer Intros"}
              </div>
              <div style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, lineHeight:1.6 }}>
                {isPKTenantUser
                  ? "First-time Day Camp visits and Training evaluations. Tracked by count — no revenue."
                  : "Meet & Greets, Tours, and first-visit appointments. Tracked by count — no revenue."}
              </div>
            </div>
            {[
              { label:"This Month",   val:thisCount, note:trend,    noteColor:trendColor },
              { label:"Last Month",   val:lastCount, note:null },
              { label:`${now.getFullYear()} YTD`, val:ytdCount, note:`${intros.length} all time`, noteColor:GRAY_MID },
            ].map((w,i) => (
              <div key={i} style={{ borderLeft:`1px solid ${GRAY_B}`, paddingLeft:16 }}>
                <div style={{ fontSize:10, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT, marginBottom:4 }}>{w.label}</div>
                <div style={{ fontSize:24, fontWeight:800, color:"#6366f1", fontFamily:FONT }}>{w.val}</div>
                {w.note && <div style={{ fontSize:11, color:w.noteColor||GRAY_MID, fontFamily:FONT, marginTop:2 }}>{w.note}</div>}
              </div>
            ))}
          </div>
        );
      })()}

      </div>{/* end sticky container */}

      {/* ── KENNEL OPERATIONS TABLE — always visible above charts ────────── */}
      <div data-pdf="kennel-ops">
      <div style={{ fontSize:13, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.1em", fontFamily:FONT }}>
        📋 Kennel Operations Detail
      </div>
      <KennelOpsTable bookingRows={bookingRows} precomputedMetrics={precomputedMetrics} />
      </div>

      {/* ── OVERVIEW: Past Revenue ─────────────────────────────────────────── */}
      {viewMode === "overview" && (
        <>
          <div data-pdf="bookings-chart" style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
            <div style={{ padding:"12px 20px", background:_tenantHeaderBg, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ fontSize:13, fontWeight:700, color:WHITE, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT }}>Revenue by {periodMode === "year" ? "Year" : periodMode === "week" ? "Week" : "Month"}</span>
              <div style={{ display:"flex", gap:2, background:"rgba(255,255,255,0.15)", borderRadius:6, padding:2 }}>
                {[["overview","Revenue"],["future","Pipeline"],["trend","Trend"]].map(([m,l])=>(
                  <button key={m} onClick={()=>setViewMode(m)} data-viewmode={m}
                    style={{ padding:"4px 12px", borderRadius:4, border:"none", cursor:"pointer", fontSize:11, fontWeight:600,
                      fontFamily:FONT, background:viewMode===m?WHITE:"transparent",
                      color:viewMode===m?_tenantHeaderBg:"rgba(255,255,255,0.8)" }}>{l}</button>
                ))}
              </div>
              <div style={{ display:"flex", gap:2, background:"rgba(255,255,255,0.1)", borderRadius:6, padding:2 }}>
                {[["month","Month"],["year","Year"]].map(([m,l]) => (
                  <button key={m} onClick={()=>setPeriodMode(m)}
                    style={{ padding:"4px 12px", borderRadius:4, border:"none", cursor:"pointer", fontSize:11, fontWeight:600,
                      fontFamily:FONT, background:periodMode===m?WHITE:"transparent", color:periodMode===m?_tenantHeaderBg:"rgba(255,255,255,0.75)" }}>{l}</button>
                ))}
              </div>
            </div>
            {/* Legend */}
            <div style={{ display:"flex", gap:16, padding:"10px 20px", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}`, flexWrap:"wrap" }}>
              {Object.entries(ORDER_LABELS).map(([k,l]) => (
                <div key={k} style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <div style={{ width:12, height:12, borderRadius:2, background:ORDER_COLORS[k] }} />
                  <span style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT }}>{l}</span>
                </div>
              ))}
            </div>
            {/* Chart */}
            <div style={{ padding:"20px", overflowX:"auto" }}>
              {/* Clustered column chart — one bar per service per period */}
              {(() => {
                const chartPeriods = [...periodData].reverse(); // chart L→R ascending
                const SERVICES = ["boarding","daycare","grooming","training"];
                // Per-service max so small services (grooming/training) are visible
                const serviceMins = {};
                const serviceMaxes = {};
                SERVICES.forEach(t => {
                  const vals = periodData.map(([,d]) => d[t]||0).filter(v=>v>0);
                  serviceMaxes[t] = Math.max(...vals, 1);
                });
                // Shared max within similar-scale services: boarding+daycare share one scale, grooming+training share another
                const bigMax   = Math.max(serviceMaxes.boarding, serviceMaxes.daycare, 1);
                const smallMax = Math.max(serviceMaxes.grooming, serviceMaxes.training, 1);
                const getMax = (t) => ["boarding","daycare"].includes(t) ? bigMax : smallMax;
                const barW = periodMode==="year" ? 18 : periodMode==="month" ? 8 : 5;
                const gap  = periodMode==="year" ? 4  : periodMode==="month" ? 2 : 1;
                const grpGap = periodMode==="year" ? 24 : periodMode==="month" ? 12 : 6;
                const grpW = SERVICES.length*(barW+gap) + grpGap;
                return (
                  <>
                  <div style={{ overflowX:"auto" }}>
                    <div style={{ display:"flex", alignItems:"flex-end", gap:grpGap, minWidth: periodMode==="year" ? "auto" : chartPeriods.length*grpW, height:220, paddingBottom:30 }}>
                      {chartPeriods.map(([period, d]) => {
                        const isCurrentYr = period==="2026" || period?.startsWith("2026");
                        return (
                          <div key={period} style={{ display:"flex", flexDirection:"column", alignItems:"center", flex: periodMode==="year" ? 1 : "none" }}>
                            <div style={{ display:"flex", alignItems:"flex-end", gap, height:180 }}>
                              {SERVICES.map(type => {
                                const val = d[type] || 0;
                                const h   = (val/getMax(type))*175;
                                return (
                                  <div key={type} style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-end", height:180, width:barW }}>
                                    {val > 0 && h > 14 && <div style={{ fontSize:7, color:ORDER_COLORS[type], fontFamily:FONT, fontWeight:700, whiteSpace:"nowrap", marginBottom:1, transform:"rotate(-90deg)", transformOrigin:"center" }}>{fmtK(val)}</div>}
                                    <div style={{ width:barW, height:Math.max(h,2), background:ORDER_COLORS[type], opacity:isCurrentYr?0.75:1, borderRadius:"2px 2px 0 0" }} />
                                  </div>
                                );
                              })}
                            </div>
                            <div style={{ fontSize:9, color:GRAY_MID, fontFamily:FONT, textAlign:"center", whiteSpace:"nowrap",
                              transform: periodMode==="month" ? "rotate(-45deg)" : "none", transformOrigin:"top center",
                              marginTop: periodMode==="month" ? 8 : 4 }}>
                              {fmtMo(period)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{ fontSize:10, color:GRAY_MID, fontFamily:FONT, fontStyle:"italic", marginTop:4 }}>
                    Chart uses two Y-axis scales: Boarding &amp; Daycare share one scale · Grooming &amp; Training share a separate scale for visibility
                  </div>
                  </>
                );
              })()}
            </div>
            {/* Table footer */}
            <div style={{ borderTop:`1px solid ${GRAY_B}` }}>
              <div style={{ display:"grid", gridTemplateColumns:"120px 1fr 120px 120px 120px 120px 120px", padding:"8px 20px", background:GRAY_BG }}>
                {["Period","","Boarding","Daycare","Grooming","Training","Total"].map(h => (
                  <span key={h} style={{ fontSize:10, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT, textAlign:"right" }}>{h}</span>
                ))}
              </div>
              {(filterYear !== "all" ? periodData : periodData.slice(0, 24)).map(([period, d]) => (
                <div key={period} style={{ display:"grid", gridTemplateColumns:"120px 1fr 120px 120px 120px 120px 120px", padding:"8px 20px", borderBottom:`1px solid ${GRAY_B}` }}>
                  <span style={{ fontSize:12, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT }}>{fmtMo(period)}</span>
                  <span />
                  {["boarding","daycare","grooming","training"].map(t => (
                    <span key={t} style={{ fontSize:11, color:d[t]?_tenantHeaderBg:GRAY_MID, fontFamily:FONT, textAlign:"right" }}>{d[t]?fmt$(d[t]):"—"}</span>
                  ))}
                  <span style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"right" }}>{fmt$(d.total)}</span>
                </div>
              ))}
              {/* Total row */}
              <div style={{ display:"grid", gridTemplateColumns:"120px 1fr 120px 120px 120px 120px 120px", padding:"10px 20px", borderTop:`2px solid ${_tenantHeaderBg}`, background:GRAY_BG }}>
                <span style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT }}>TOTAL</span>
                <span />
                {["boarding","daycare","grooming","training"].map(t => (
                  <span key={t} style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"right" }}>
                    {fmt$(past.filter(r=>r.order_type===t).reduce((s,r)=>s+r.booking_value,0))}
                  </span>
                ))}
                <span style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"right" }}>{fmt$(totalPastRev)}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── FUTURE PIPELINE ────────────────────────────────────────────────── */}
      {viewMode === "future" && (
        <>
          {/* View header with toggle */}
          <div data-pdf="pipeline-header" style={{ background:_tenantHeaderBg, borderRadius:10, padding:"12px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <span style={{ fontSize:13, fontWeight:700, color:WHITE, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT }}>Revenue — Pipeline</span>
            <div style={{ display:"flex", gap:2, background:"rgba(255,255,255,0.15)", borderRadius:6, padding:2 }}>
              {[["overview","Revenue"],["future","Pipeline"],["trend","Trend"]].map(([m,l])=>(
                <button key={m} onClick={()=>setViewMode(m)}
                  style={{ padding:"4px 12px", borderRadius:4, border:"none", cursor:"pointer", fontSize:11, fontWeight:600,
                    fontFamily:FONT, background:viewMode===m?WHITE:"transparent",
                    color:viewMode===m?_tenantHeaderBg:"rgba(255,255,255,0.8)" }}>{l}</button>
              ))}
            </div>
          </div>
          {/* Future KPIs */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 }}>
            {["boarding","daycare","grooming","training"].map(type => {
              const typeRows = future.filter(r => r.order_type === type);
              const rev = typeRows.reduce((s,r) => s+r.booking_value, 0);
              return (
                <div key={type} style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, padding:"16px 18px", borderLeft:`4px solid ${ORDER_COLORS[type]}` }}>
                  <div style={{ fontSize:10, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT, marginBottom:6 }}>{ORDER_LABELS[type]}</div>
                  <div style={{ fontSize:20, fontWeight:800, color:ORDER_COLORS[type], fontFamily:FONT }}>{fmt$(rev)}</div>
                  <div style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, marginTop:4 }}>{typeRows.length} bookings confirmed</div>
                </div>
              );
            })}
          </div>

          {/* Future timeline chart */}
          <div data-pdf="pipeline-chart" style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
            <div style={{ padding:"12px 20px", background:_tenantHeaderBg }}>
              <span style={{ fontSize:13, fontWeight:700, color:WHITE, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT }}>
                Confirmed Pipeline by Month — {fmt$(totalFutureRev)} total
              </span>
            </div>
            <div style={{ padding:"20px", overflowX:"auto" }}>
              <div style={{ display:"flex", alignItems:"flex-end", gap:8, minWidth:futureByMonth.length*80, height:200 }}>
                {futureByMonth.map(([mo, d]) => {
                  const barH = (d.total/futureChartMax)*170;
                  return (
                    <div key={mo} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, flex:1, minWidth:70 }}>
                      <div style={{ fontSize:10, color:_tenantHeaderBg, fontFamily:FONT, fontWeight:700 }}>{fmtK(d.total)}</div>
                      <div style={{ width:"80%", display:"flex", flexDirection:"column-reverse", height:170 }}>
                        {["boarding","daycare","grooming","training","standalone"].map(type => {
                          const val = d[type] || 0;
                          if (!val) return null;
                          const h = (val/futureChartMax)*170;
                          return <div key={type} style={{ width:"100%", height:h, background:ORDER_COLORS[type] }} />;
                        })}
                      </div>
                      <div style={{ fontSize:10, color:GRAY_MID, fontFamily:FONT, textAlign:"center" }}>{fmtMo(mo)}</div>
                      <div style={{ fontSize:9, color:GRAY_MID, fontFamily:FONT }}>{d.count} bkgs</div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Future table */}
            <div style={{ borderTop:`1px solid ${GRAY_B}` }}>
              <div style={{ display:"grid", gridTemplateColumns:"120px 1fr 130px 130px 130px 100px", padding:"8px 20px", background:GRAY_BG }}>
                {["Month","","Boarding","Daycare","Grooming+","Bookings"].map(h => (
                  <span key={h} style={{ fontSize:10, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT, textAlign:"right" }}>{h}</span>
                ))}
              </div>
              {futureByMonth.map(([mo, d]) => (
                <div key={mo} style={{ display:"grid", gridTemplateColumns:"120px 1fr 130px 130px 130px 100px", padding:"8px 20px", borderBottom:`1px solid ${GRAY_B}` }}>
                  <span style={{ fontSize:12, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT }}>{fmtMo(mo)}</span>
                  <span />
                  <span style={{ fontSize:11, color:d.boarding?_tenantHeaderBg:GRAY_MID, fontFamily:FONT, textAlign:"right" }}>{d.boarding?fmt$(d.boarding):"—"}</span>
                  <span style={{ fontSize:11, color:d.daycare?_tenantHeaderBg:GRAY_MID, fontFamily:FONT, textAlign:"right" }}>{d.daycare?fmt$(d.daycare):"—"}</span>
                  <span style={{ fontSize:11, color:(d.grooming||d.training)?_tenantHeaderBg:GRAY_MID, fontFamily:FONT, textAlign:"right" }}>{fmt$((d.grooming||0)+(d.training||0)+(d.standalone||0))}</span>
                  <span style={{ fontSize:11, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"right" }}>{d.count}</span>
                </div>
              ))}
              <div style={{ display:"grid", gridTemplateColumns:"120px 1fr 130px 130px 130px 100px", padding:"10px 20px", borderTop:`2px solid ${_tenantHeaderBg}`, background:GRAY_BG }}>
                <span style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT }}>TOTAL</span>
                <span />
                <span style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"right" }}>{fmt$(future.filter(r=>r.order_type==="boarding").reduce((s,r)=>s+r.booking_value,0))}</span>
                <span style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"right" }}>{fmt$(future.filter(r=>r.order_type==="daycare").reduce((s,r)=>s+r.booking_value,0))}</span>
                <span style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"right" }}>{fmt$(future.filter(r=>["grooming","training","standalone"].includes(r.order_type)).reduce((s,r)=>s+r.booking_value,0))}</span>
                <span style={{ fontSize:12, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"right" }}>{future.length}</span>
              </div>
            </div>
          </div>

          {/* Top customers with future bookings */}
          <div data-pdf="top-customers" style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
            <div style={{ padding:"12px 20px", background:_tenantHeaderBg }}>
              <span style={{ fontSize:13, fontWeight:700, color:WHITE, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT }}>Top 10 Customers — Confirmed Future Bookings</span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 120px 100px 120px 120px", padding:"8px 20px", background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}` }}>
              {["Customer", isPKTenantUser ? "Location" : "PP-ID", "Bookings","Services","Value"].map(h => (
                <span key={h} style={{ fontSize:10, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FONT, textAlign:h==="Customer"?"left":"right" }}>{h}</span>
              ))}
            </div>
            {topFutureCustomers.map((c,i) => (
              <div key={i} style={{ display:"grid", gridTemplateColumns:"1fr 120px 100px 120px 120px", padding:"10px 20px", borderBottom:`1px solid ${GRAY_B}`, background:i%2===0?WHITE:GRAY_BG }}>
                <span style={{ fontSize:12, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT }}>{c.name}</span>
                {isPKTenantUser
                  ? <span style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT }}>{c.locationCode || "—"}</span>
                  : <span style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, textAlign:"right" }}>PP-{c.id}</span>}
                <span style={{ fontSize:11, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"right" }}>{c.bookings}</span>
                <span style={{ fontSize:11, color:_tenantHeaderBg, fontFamily:FONT, textAlign:"right" }}>{[...c.services].map(s=>ORDER_LABELS[s]||s).join(", ")}</span>
                <span style={{ fontSize:12, fontWeight:700, color:GREEN, fontFamily:FONT, textAlign:"right" }}>{fmt$(c.total)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── YEAR OVER YEAR ─────────────────────────────────────────────────── */}
      {viewMode === "trend" && (
        <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden" }}>
          <div style={{ padding:"12px 20px", background:_tenantHeaderBg, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <span style={{ fontSize:13, fontWeight:700, color:WHITE, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT }}>Revenue — Trend (Year over Year)</span>
            <div style={{ display:"flex", gap:2, background:"rgba(255,255,255,0.15)", borderRadius:6, padding:2 }}>
              {[["overview","Revenue"],["future","Pipeline"],["trend","Trend"]].map(([m,l])=>(
                <button key={m} onClick={()=>setViewMode(m)}
                  style={{ padding:"4px 12px", borderRadius:4, border:"none", cursor:"pointer", fontSize:11, fontWeight:600,
                    fontFamily:FONT, background:viewMode===m?WHITE:"transparent",
                    color:viewMode===m?_tenantHeaderBg:"rgba(255,255,255,0.8)" }}>{l}</button>
              ))}
            </div>
          </div>
          {[
            { year:"2020", rev:past.filter(r=>r.start_date?.startsWith("2020")).reduce((s,r)=>s+r.booking_value,0), note:"COVID year — partial data" },
            { year:"2022", rev:past.filter(r=>r.start_date?.startsWith("2022")).reduce((s,r)=>s+r.booking_value,0), note:"Post-COVID recovery" },
            { year:"2023", rev:past.filter(r=>r.start_date?.startsWith("2023")).reduce((s,r)=>s+r.booking_value,0), note:"" },
            { year:"2024", rev:past.filter(r=>r.start_date?.startsWith("2024")).reduce((s,r)=>s+r.booking_value,0), note:"" },
            { year:"2025", rev:past.filter(r=>r.start_date?.startsWith("2025")).reduce((s,r)=>s+r.booking_value,0), note:"Full year" },
            { year:"2026", rev:ytdRev + totalFutureRev, note:"YTD + confirmed pipeline", isForecast: true },
          ].map((row, i, arr) => {
            const prev = arr[i-1];
            const growth = prev && prev.rev > 0 ? (row.rev-prev.rev)/prev.rev*100 : null;
            const maxRev = Math.max(...arr.map(r=>r.rev));
            const barWidth = `${(row.rev/maxRev)*100}%`;
            return (
              <div key={row.year} style={{ padding:"14px 20px", borderBottom:`1px solid ${GRAY_B}`, background:row.isForecast?"#f0fdf4":i%2===0?WHITE:GRAY_BG }}>
                <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:8 }}>
                  <div style={{ width:50, fontSize:14, fontWeight:800, color:_tenantHeaderBg, fontFamily:FONT }}>{row.year}</div>
                  <div style={{ flex:1, background:GRAY_B, borderRadius:4, height:24, overflow:"hidden" }}>
                    <div style={{ width:barWidth, height:"100%", background:row.isForecast?"#86efac":_tenantHeaderBg, borderRadius:4, display:"flex", alignItems:"center", paddingLeft:8, transition:"width 0.5s" }}>
                      <span style={{ fontSize:11, fontWeight:700, color:WHITE, fontFamily:FONT, whiteSpace:"nowrap" }}>{fmt$(row.rev)}</span>
                    </div>
                  </div>
                  <div style={{ width:80, textAlign:"right" }}>
                    {growth !== null && (
                      <span style={{ fontSize:12, fontWeight:700, color:growth>=0?GREEN:RED, fontFamily:FONT }}>{fmtPct(growth)}</span>
                    )}
                  </div>
                  <div style={{ width:180, fontSize:11, color:GRAY_MID, fontFamily:FONT, fontStyle:"italic" }}>
                    {row.note}{row.isForecast && <span style={{ color:GREEN, fontStyle:"normal", fontWeight:700 }}> ← projected</span>}
                  </div>
                </div>
                {/* Service breakdown bars */}
                <div style={{ display:"flex", gap:2, marginLeft:66, height:6, borderRadius:3, overflow:"hidden" }}>
                  {["boarding","daycare","grooming","training","standalone"].map(type => {
                    const typeRev = row.isForecast
                      ? [...past,...future].filter(r=>(r.start_date?.startsWith(row.year))&&r.order_type===type).reduce((s,r)=>s+r.booking_value,0)
                      : past.filter(r=>r.start_date?.startsWith(row.year)&&r.order_type===type).reduce((s,r)=>s+r.booking_value,0);
                    const pct = row.rev > 0 ? typeRev/row.rev*100 : 0;
                    return pct > 0 ? <div key={type} style={{ width:`${pct}%`, background:ORDER_COLORS[type], height:"100%" }} /> : null;
                  })}
                </div>
              </div>
            );
          })}
          <div style={{ padding:"10px 20px", fontSize:11, color:GRAY_MID, fontFamily:FONT, fontStyle:"italic", background:GRAY_BG }}>
            2021 data not available · 2026 = YTD actuals ({fmt$(ytdRev)}) + confirmed future pipeline ({fmt$(totalFutureRev)}) · Green bar = projected
          </div>
        </div>
      )}


    </div>
  );
}


// ─── KENNEL OPERATIONS TABLE ──────────────────────────────────────────────────
function KennelOpsTable({ bookingRows, precomputedMetrics }) {
  const [periodMode, setPeriodMode] = useState("week");
  const scrollRef = useRef();
  const today = new Date().toISOString().slice(0,10);

  const satKey = (sd) => {
    if (!sd) return "";
    const dt = new Date(sd+"T00:00:00");
    if (isNaN(dt.getTime())) return ""; // guard against invalid dates
    const we = new Date(dt); we.setDate(dt.getDate()+(6-dt.getDay()+7)%7);
    return we.toISOString().slice(0,10);
  };

  // ── ONE-TIME pre-index: runs once on upload, never again ─────────────────
  const preIndex = useMemo(() => {
    if (!bookingRows || !bookingRows.length) return null;

    const isPKData = bookingRows.some(r => r.service_tab); // PK rows have service_tab field

    // Row index by period key for all 3 modes
    const byMonth = {}, byWeek = {}, byYear = {};
    const nightDogs = {}, dayDogs = {}, empDogs = {};

    // Reservation grouping for RPN, avg nights, bath%
    const byRes = {};

    bookingRows.forEach(r => {
      if (r.is_future) return;
      const sd = r.start_date || "";
      if (!sd) return;
      // Skip rows with invalid dates
      const sdTest = new Date(sd + "T00:00:00");
      if (isNaN(sdTest.getTime())) return;

      const mo = sd.slice(0,7);
      const yr = sd.slice(0,4);
      const wk = satKey(sd);

      if (!byMonth[mo]) byMonth[mo] = [];
      if (!byWeek[wk])  byWeek[wk]  = [];
      if (!byYear[yr])  byYear[yr]  = [];
      byMonth[mo].push(r);
      byWeek[wk].push(r);
      byYear[yr].push(r);

      const otype = r.order_type || "";
      const rate  = (r.rate || "").toLowerCase();
      const rs    = r.reservation_string;

      // Boarding nights expansion
      if (otype === "boarding" && r.end_date) {
        // PK: all boarding rows have start/end dates and nights pre-calculated
        // Waggles ProPet: only lodging rows have end_date
        const isLodgingRow = isPKData ? true : rate.includes("lodging");
        if (isLodgingRow) {
          const sd0 = new Date(sd+"T00:00:00");
          const ed0 = new Date(r.end_date+"T00:00:00");
          const n = Math.max(Math.round((ed0-sd0)/86400000), 1);
          // Only expand if reasonable stay length (avoid bad data)
          if (n <= 365) {
            for (let i=0; i<n; i++) {
              const d = new Date(sd0); d.setDate(sd0.getDate()+i);
              const k = d.toISOString().slice(0,10);
              nightDogs[k] = (nightDogs[k]||0) + 1;
            }
          }
        }
      }

      // Daycare occupancy — PK: Boarder type counts toward occupancy
      if (otype === "daycare") {
        if (isPKData) {
          // PK: Boarder = boarding dog in daycare, counts for occupancy
          if (r.is_boarder) empDogs[sd] = (empDogs[sd]||0) + 1; // reuse empDogs slot for boarder dogs
          else dayDogs[sd] = (dayDogs[sd]||0) + 1;
        } else {
          if (rate.includes("employee dog")) empDogs[sd] = (empDogs[sd]||0) + 1;
          else dayDogs[sd] = (dayDogs[sd]||0) + 1;
        }
      }

      // Reservation grouping
      if (rs) {
        if (!byRes[rs]) byRes[rs] = [];
        byRes[rs].push(r);
      }
    });

    // For PK: also link grooming reservations to boarding via corresp_id
    // This lets us detect baths on boarding stays
    if (isPKData) {
      bookingRows.forEach(r => {
        if (r.is_future) return;
        if (r.order_type === "grooming" && r.corresp_id && r.from_schedule === "Boarding") {
          const boardingRes = r.corresp_id;
          if (!byRes[boardingRes]) byRes[boardingRes] = [];
          // Add grooming row to the boarding reservation group for bath detection
          if (!byRes[boardingRes].find(x => x.reservation_string === r.reservation_string)) {
            byRes[boardingRes].push({ ...r, _linkedGrooming: true });
          }
        }
      });
    }

    // Pre-compute reservation-level stats (nights, RPN, bath%)
    const resStats = {}; // rs -> { nights, rev, hasBath, category }
    Object.entries(byRes).forEach(([rs, rows]) => {
      const otypes  = new Set(rows.map(r => r.order_type));
      const rates   = rows.map(r => (r.rate||"").toLowerCase());
      const hasLodging = isPKData
        ? otypes.has("boarding")
        : otypes.has("boarding") || rates.some(r => r.includes("lodging"));
      // PK: bath detected via linked grooming rows (from_schedule=Boarding)
      // Waggles: bath detected via rate field containing "bath"
      const hasBath = isPKData
        ? rows.some(r => r.order_type === "grooming" && r.from_schedule === "Boarding")
        : rates.some(r => r.includes("bath"));
      const hasDaycare = otypes.has("daycare") && !hasLodging;
      const primary    = hasLodging
        ? (isPKData
            ? rows.find(r => r.order_type === "boarding") || rows[0]
            : rows.find(r => (r.rate||"").toLowerCase().includes("lodging")) || rows[0])
        : rows[0];
      let nights = 0;
      if (hasLodging && primary?.start_date && primary?.end_date) {
        const s = new Date(primary.start_date+"T00:00:00");
        const e = new Date(primary.end_date+"T00:00:00");
        nights = Math.max(Math.round((e-s)/86400000), 1);
      }
      // Deduplicate ProPet's one-row-per-night pattern:
      // Activity packages appear once per night with qty=total_nights, val=total_value
      // Group by rate — if all rows have same value, count only once
      const dedupRevenue = (resRows) => {
        const byRate = {};
        resRows.filter(r => !r._linkedGrooming).forEach(r => {
          const rt = r.rate || "";
          if (!byRate[rt]) byRate[rt] = [];
          byRate[rt].push(r.booking_value || 0);
        });
        return Object.values(byRate).reduce((total, vals) => {
          const unique = [...new Set(vals.map(v => Math.round(v*100)/100))];
          return total + (unique.length === 1 ? unique[0] : vals.reduce((s,v)=>s+v,0));
        }, 0);
      };

      const TRAINING_RATE_TERMS = ['training refresher','stay and train','follow up lesson','go home lesson','private training lesson','training evaluation'];
      const isTrainingRate = rt => TRAINING_RATE_TERMS.some(t => (rt||"").toLowerCase().includes(t));
      const rev    = dedupRevenue(rows);
      const rpnRev = dedupRevenue(rows.filter(r => !isTrainingRate(r.rate)));
      const cat = hasLodging ? "boarding" : hasDaycare ? "daycare" : "other";
      resStats[rs] = { nights, rev, rpnRev, hasBath, cat, start: primary?.start_date || "" };
    });

    return { byMonth, byWeek, byYear, nightDogs, dayDogs, empDogs, resStats, isPKData };
  }, [bookingRows]);

  // ── Compute metrics per period — uses precomputed if available ──────────
  const { periods, metrics } = useMemo(() => {
    // Fast path: use pre-computed metrics from Excel sheet
    if (precomputedMetrics && precomputedMetrics[periodMode]) {
      const modeData = precomputedMetrics[periodMode];
      const periods = Object.keys(modeData).sort().reverse();
      return { periods, metrics: modeData };
    }
    // Slow path: compute from raw data (fallback when no pre-computed sheet)
    if (!preIndex) return { periods:[], metrics:{} };
    const { byMonth, byWeek, byYear, nightDogs, dayDogs, empDogs, resStats, isPKData } = preIndex;

    const rowMap = periodMode==="week" ? byWeek : periodMode==="month" ? byMonth : byYear;
    const nightMap = periodMode==="week"
      ? Object.fromEntries(Object.entries(nightDogs).map(([d,v]) => [satKey(d), (rowMap[satKey(d)]||0)+v]).map(([k])=>[k, Object.entries(nightDogs).filter(([d])=>satKey(d)===k).reduce((s,[,v])=>s+v,0)]))
      : nightDogs;

    const periods = Object.keys(rowMap).sort().reverse();
    const metrics = {};

    // Pre-group night dates by period key for occupancy
    const nightsByPeriod = {};
    Object.entries(nightDogs).forEach(([d, ct]) => {
      const k = periodMode==="week" ? satKey(d) : periodMode==="month" ? d.slice(0,7) : d.slice(0,4);
      if (!nightsByPeriod[k]) nightsByPeriod[k] = [];
      nightsByPeriod[k].push({ d, ct });
    });
    const daysByPeriod = {};
    Object.entries(dayDogs).forEach(([d, ct]) => {
      const k = periodMode==="week" ? satKey(d) : periodMode==="month" ? d.slice(0,7) : d.slice(0,4);
      if (!daysByPeriod[k]) daysByPeriod[k] = [];
      daysByPeriod[k].push({ d, ct });
    });
    const empsByPeriod = {};
    Object.entries(empDogs).forEach(([d, ct]) => {
      const k = periodMode==="week" ? satKey(d) : periodMode==="month" ? d.slice(0,7) : d.slice(0,4);
      if (!empsByPeriod[k]) empsByPeriod[k] = [];
      empsByPeriod[k].push({ d, ct });
    });

    periods.forEach(p => {
      const rows = rowMap[p] || [];

      // Daycare
      const wkdyDays  = (daysByPeriod[p]||[]).filter(({d}) => [1,2,3,4,5].includes(new Date(d+"T00:00:00").getDay()));
      const wkdyEmps  = (empsByPeriod[p]||[]).filter(({d}) => [1,2,3,4,5].includes(new Date(d+"T00:00:00").getDay()));
      const dcAvgDogs = wkdyDays.length ? wkdyDays.reduce((s,{ct})=>s+ct,0)/wkdyDays.length : 0;
      const dcEmpDogs = wkdyEmps.length ? wkdyEmps.reduce((s,{ct})=>s+ct,0)/wkdyEmps.length : 0;
      const dcEvals   = isPKData
        ? rows.filter(r => r.order_type==="daycare" && r.is_evaluation).length
        : rows.filter(r => (r.rate||"").toLowerCase().includes("eval") && r.order_type==="daycare").length;
      const dcBaths   = isPKData
        ? rows.filter(r => r.order_type==="grooming" && (r.from_schedule||"").toLowerCase().includes("daycamp")).length
        : rows.filter(r => r.order_type==="daycare" && (r.rate||"").toLowerCase().includes("bath")).length;
      const dcRev     = rows.filter(r => r.order_type==="daycare" && !r.is_boarder).reduce((s,r)=>s+(r.booking_value||0),0);

      // Boarding occupancy
      const nightDates = nightsByPeriod[p] || [];
      const avgBoarding = nightDates.length ? nightDates.reduce((s,{ct})=>s+ct,0)/nightDates.length : 0;
      const wkndNights  = nightDates.filter(({d}) => [5,6,0].includes(new Date(d+"T00:00:00").getDay()));
      const wkdyNights  = nightDates.filter(({d}) => [1,2,3,4].includes(new Date(d+"T00:00:00").getDay()));
      const avgWknd = wkndNights.length ? wkndNights.reduce((s,{ct})=>s+ct,0)/wkndNights.length : 0;
      const avgWkdy = wkdyNights.length ? wkdyNights.reduce((s,{ct})=>s+ct,0)/wkdyNights.length : 0;

      // Boarding reservations in period — from resStats
      const bRes = Object.values(resStats).filter(r => r.cat==="boarding" && r.start &&
        (periodMode==="week" ? satKey(r.start) : periodMode==="month" ? r.start.slice(0,7) : r.start.slice(0,4)) === p);
      const bRev     = bRes.reduce((s,r)=>s+r.rev, 0);      // total boarding revenue (all items)
      const bRPNrev  = bRes.reduce((s,r)=>s+r.rpnRev, 0);   // RPN revenue (excl. training programs)
      const bNights  = bRes.reduce((s,r)=>s+r.nights, 0);
      const avgRPN   = bNights > 0 ? bRPNrev/bNights : 0;   // RPN uses rpnRev not total rev
      const bBaths   = bRes.filter(r=>r.hasBath).length;
      const bGroomPct= bRes.length > 0 ? bBaths/bRes.length*100 : 0;
      const bStays   = bRes.filter(r=>r.nights>0);
      const bAvgNights = bStays.length ? bStays.reduce((s,r)=>s+r.nights,0)/bStays.length : 0;
      const bRevTotal = rows.filter(r=>r.order_type==="boarding").reduce((s,r)=>s+(r.booking_value||0),0);

      // Training — use order_type for PK, rate keywords for Waggles
      const isRate = (r, ...terms) => terms.some(t => (r.rate||"").toLowerCase().includes(t));
      const trRows = isPKData
        ? rows.filter(r => r.order_type === "training")
        : rows.filter(r => isRate(r,"train","lesson","preschool","refresher","go home","evaluation","stay and train","private training"));
      const trEvals     = isPKData
        ? rows.filter(r => r.order_type==="training" && r.is_evaluation).length
        : trRows.filter(r => isRate(r,"evaluation")).length;
      const trDayTrain  = isPKData
        ? trRows.filter(r => (r.rate||"").toLowerCase().includes("day train")).length
        : trRows.filter(r => isRate(r,"day train","adult day train") && !isRate(r,"puppy day train")).length;
      const trPuppy     = isPKData
        ? trRows.filter(r => { const rt=(r.rate||"").toLowerCase(); return rt.includes("puppy") || rt.includes("preschool"); }).length
        : trRows.filter(r => isRate(r,"preschool","puppy day train")).length;
      const trRefresher = trRows.filter(r => isRate(r,"refresher")).length;
      const trLessons   = isPKData
        ? trRows.filter(r => { const rt=(r.rate||"").toLowerCase(); return rt.includes("lesson") || rt.includes("private"); }).length
        : trRows.filter(r => isRate(r,"lesson","go home","stay and train","private training")).length;
      const trRevenue   = trRows.reduce((s,r)=>s+(r.booking_value||0),0);

      // Grooming — use order_type for PK, rate keywords for Waggles
      const grRows     = isPKData
        ? rows.filter(r => r.order_type === "grooming")
        : rows.filter(r => (r.rate||"").toLowerCase().includes("deluxe bath") || (r.rate||"").toLowerCase().includes("ear cleaning") || (r.rate||"").toLowerCase().includes("nail file"));
      const grBaths    = isPKData
        ? grRows.filter(r => { const rt=(r.rate||"").toLowerCase(); return rt.includes("bath") || rt.includes("groom"); }).length
        : rows.filter(r => (r.rate||"").toLowerCase().includes("deluxe bath")).length;
      const grEarsNails= isPKData
        ? grRows.filter(r => { const rt=(r.rate||"").toLowerCase(); return rt.includes("nail") || rt.includes("ear"); }).length
        : rows.filter(r => { const rt=(r.rate||"").toLowerCase(); return rt.includes("ear cleaning")||rt.includes("nail file"); }).length;
      const grRevenue  = grRows.reduce((s,r)=>s+(r.booking_value||0),0);

      metrics[p] = {
        dcAvgDogs, dcEmpDogs, dcBaths, dcEvals, dcRevenue: dcRev,
        bAvgDogs: avgBoarding, bWeekendAvg: avgWknd, bWeekdayAvg: avgWkdy,
        bAvgNights: bAvgNights, bBaths, bGroomPct, bRevenue: bRevTotal, bNights, avgRPN,
        trEvals, trDayTrain, trPuppy, trRefresher, trLessons, trRevenue,
        grBaths, grEarsNails,
        grRevenue,
      };
    });

    return { periods, metrics };
  }, [preIndex, periodMode]);

  const fmtMo = k => {
    if (!k) return "";
    if (k.length===4) return k;
    if (k.length===10) {
      const d = new Date(k+"T00:00:00");
      return `${d.getMonth()+1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
    }
    const [y,m] = k.split("-");
    return `${["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(m)]} ${y}`;
  };

  const fmtN   = n => n>0 ? Math.round(n).toString() : "—";
  const fmtI   = n => n>0 ? Math.round(n).toString() : "—";
  const fmt$   = n => n>0 ? `$${Math.round(n).toLocaleString()}` : "—";
  const fmt$D  = n => n>0 ? `$${n.toFixed(2)}` : "—";
  const fmtPct2= n => n>0 ? `${Math.round(n)}%` : "—";

  const COL_W   = periodMode==="week" ? 90 : periodMode==="month" ? 85 : 70;
  const LABEL_W = 220;
  const ROW_H   = 36;

  const SECTION_DAYCARE = [
    { key:"dcAvgDogs", label:"Avg Daycare Dogs (M–F)", fmt:fmtN,  sub:"paying/day" },
    { key:"dcEmpDogs", label:"Employee Dogs (M–F)",    fmt:fmtN,  sub:"staff/day" },
    { key:"dcBaths",   label:label("Daycare") + " Baths",  fmt:fmtI,  sub:"count" },
    { key:"dcEvals",   label:"Evaluations",            fmt:fmtI,  sub:"count" },
    { key:"dcRevenue", label:"Daycare Revenue",        fmt:fmt$,  sub:"", bold:true },
  ];
  const SECTION_BOARDING = [
    { key:"bAvgDogs",    label:"Avg Boarding Dogs",   fmt:fmtN,   sub:"dogs/night" },
    { key:"bWeekendAvg", label:"Weekend Avg Fri–Sun", fmt:fmtN,   sub:"dogs/night" },
    { key:"bWeekdayAvg", label:"Weekday Avg M–Thu",   fmt:fmtN,   sub:"dogs/night" },
    { key:"bAvgNights",  label:"Avg Nights/Stay",     fmt:fmtN,   sub:"nights" },
    { key:"bBaths",      label:"Boarding Baths",      fmt:fmtI,   sub:"count" },
    { key:"bGroomPct",   label:"% With Grooming",     fmt:fmtPct2,sub:"baths/nails/etc" },
    { key:"avgRPN",      label:"RPN",                 fmt:fmt$D,  sub:"rev/night", bold:true },
    { key:"bRevenue",    label:"Boarding Revenue",    fmt:fmt$,   sub:"", bold:true },
  ];
  const SECTION_TRAINING = [
    { key:"trEvals",     label:"Training Evaluations",fmt:fmtI, sub:"count" },
    { key:"trDayTrain",  label:"Day Trains",          fmt:fmtI, sub:"count" },
    { key:"trPuppy",     label:"Puppy Preschool",     fmt:fmtI, sub:"count" },
    { key:"trRefresher", label:"Refresher Days",      fmt:fmtI, sub:"count" },
    { key:"trLessons",   label:"Lessons",             fmt:fmtI, sub:"count" },
    { key:"trRevenue",   label:"Training Revenue",    fmt:fmt$, sub:"", bold:true },
  ];
  const SECTION_GROOMING = [
    { key:"grBaths",    label:"Baths (Deluxe Bath)", fmt:fmtI, sub:"count" },
    { key:"grEarsNails",label:"Ears / Nails",        fmt:fmtI, sub:"count" },
    { key:"grRevenue",  label:"Grooming Revenue",    fmt:fmt$, sub:"", bold:true },
  ];

  const NAVY_BG_LIGHT = "rgba(27,58,107,0.06)";

  if (!bookingRows || !bookingRows.length) return null;

  return (
    <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, overflow:"hidden", marginTop:8, maxHeight:520, display:"flex", flexDirection:"column" }}>
      {/* Header */}
      <div style={{ padding:"12px 16px", background:_tenantHeaderBg, display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
        <span style={{ fontSize:12, fontWeight:700, color:WHITE, letterSpacing:"0.07em", textTransform:"uppercase", fontFamily:FONT }}>
          Kennel Operations — Occupancy & Performance
        </span>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ display:"flex", gap:2, background:"rgba(255,255,255,0.1)", borderRadius:6, padding:2 }}>
            {[["week","Week"],["month","Month"],["year","Year"]].map(([m,l])=>(
              <button key={m} onClick={()=>setPeriodMode(m)}
                style={{ padding:"4px 12px", borderRadius:4, border:"none", cursor:"pointer", fontSize:12, fontWeight:600,
                  fontFamily:FONT, background:periodMode===m?WHITE:"transparent", color:periodMode===m?_tenantHeaderBg:"rgba(255,255,255,0.7)" }}>{l}</button>
            ))}
          </div>
          <div style={{ display:"flex", gap:4 }}>
            <button onClick={()=>scrollRef.current?.scrollBy({left:-(COL_W*4),behavior:"smooth"})}
              style={{ background:"rgba(255,255,255,0.15)", border:"none", color:WHITE, borderRadius:4, padding:"4px 10px", cursor:"pointer", fontSize:14 }}>‹‹</button>
            <button onClick={()=>scrollRef.current?.scrollBy({left:COL_W*4,behavior:"smooth"})}
              style={{ background:"rgba(255,255,255,0.15)", border:"none", color:WHITE, borderRadius:4, padding:"4px 10px", cursor:"pointer", fontSize:14 }}>››</button>
          </div>
        </div>
      </div>

      {/* Single scrollable table */}
      {(() => {
        const DC_HEADER = { type:"header", label:`☀️ ${label("Daycare")}`, bg:_tenantHeaderBg, color:"#93c5fd", sep:false };
        const B_HEADER  = { type:"header", label:`🏠 ${label("Boarding")}`, bg:_tenantHeaderBg, color:"#86efac", sep:true  };
        const TR_HEADER = { type:"header", label:`🎓 ${label("Dog Training")}`, bg:_tenantHeaderBg, color:"#fde68a", sep:true  };
        const GR_HEADER = { type:"header", label:`✂️ ${label("Grooming")}`,  bg:_tenantHeaderBg, color:"#c4b5fd", sep:true  };
        const ALL_SECTIONS = [
          DC_HEADER, ...SECTION_DAYCARE.map(r=>({...r,section:"daycare"})),
          B_HEADER,  ...SECTION_BOARDING.map(r=>({...r,section:"boarding"})),
          TR_HEADER, ...SECTION_TRAINING.map(r=>({...r,section:"training"})),
          GR_HEADER, ...SECTION_GROOMING.map(r=>({...r,section:"grooming"})),
        ];
        return (
          <div ref={scrollRef} style={{ overflowX:"auto", overflowY:"auto", flex:1 }}>
            <table style={{ borderCollapse:"collapse", minWidth: periods.length*COL_W + LABEL_W }}>
              <thead>
                <tr>
                  <th style={{ position:"sticky", left:0, top:0, zIndex:25, width:LABEL_W, minWidth:LABEL_W,
                    background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}`, borderRight:`1px solid ${GRAY_B}`,
                    height:ROW_H, padding:"0 12px", textAlign:"left" }}>
                    <span style={{ fontSize:10, fontWeight:700, color:GRAY_MID, fontFamily:FONT, textTransform:"uppercase" }}>Metric</span>
                  </th>
                  {periods.map(period => (
                    <th key={period} style={{ position:"sticky", top:0, zIndex:10, minWidth:COL_W, width:COL_W,
                      background:GRAY_BG, borderBottom:`1px solid ${GRAY_B}`, borderRight:`1px solid ${GRAY_B}`,
                      height:ROW_H, padding:"0 6px", textAlign:"center" }}>
                      <span style={{ fontSize:10, fontWeight:700, color:_tenantHeaderBg, fontFamily:FONT, whiteSpace:"nowrap" }}>{fmtMo(period)}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ALL_SECTIONS.map((row, i) => {
                  if (row.type === "header") return (
                    <tr key={`h${i}`} style={{ background:row.bg }}>
                      <td style={{ position:"sticky", left:0, zIndex:20, background:row.bg,
                        height:ROW_H, borderTop:row.sep?`2px solid ${GRAY_B}`:"none",
                        borderBottom:"1px solid rgba(255,255,255,0.15)", padding:"0 12px",
                        whiteSpace:"nowrap" }}>
                        <span style={{ fontSize:12, fontWeight:800, color:row.color, fontFamily:FONT }}>{row.label}</span>
                      </td>
                      {periods.map(p => (
                        <td key={p} style={{ background:row.bg, borderTop:row.sep?`2px solid ${GRAY_B}`:"none",
                          borderBottom:"1px solid rgba(255,255,255,0.15)", height:ROW_H }} />
                      ))}
                    </tr>
                  );
                  // Hide rows where all period values are zero (e.g. Employee Dogs for PK)
                  const allZero = periods.every(p => !(metrics[p]||{})[row.key]);
                  if (allZero && row.key === "dcEmpDogs") return null;
                  return (
                    <tr key={row.key} style={{ background:row.bold?NAVY_BG_LIGHT:WHITE }}>
                      <td style={{ position:"sticky", left:0, zIndex:20, 
                        background:row.bold ? "#dde3ee" : WHITE,
                        borderBottom:`1px solid ${GRAY_B}`, borderRight:`2px solid ${GRAY_B}`,
                        height:ROW_H, padding:"0 12px", whiteSpace:"nowrap" }}>
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                          <span style={{ fontSize:12, fontWeight:row.bold?700:400, color:_tenantHeaderBg, fontFamily:FONT }}>{row.label}</span>
                          {row.sub && <span style={{ fontSize:9, color:GRAY_MID, fontFamily:FONT }}>{row.sub}</span>}
                        </div>
                      </td>
                      {periods.map(period => {
                        const m   = metrics[period] || {};
                        const raw = m[row.key] || 0;
                        const val = row.fmt(raw);
                        const isRPN  = row.key === "avgRPN";
                        const pillBg = isRPN && raw>0 ? (raw>=65?"#86efac":raw>=55?"#fde68a":"#fca5a5") : "transparent";
                        return (
                          <td key={period} style={{ borderBottom:`1px solid ${GRAY_B}`, borderRight:`1px solid ${GRAY_B}`,
                            height:ROW_H, padding:"0 10px", textAlign:"right", position:"relative", zIndex:1,
                            background:row.bold&&!isRPN ? "#dde3ee" : WHITE }}>
                            <span style={{ fontSize:12, fontWeight:row.bold?700:400,
                              color: raw>0?_tenantHeaderBg:GRAY_MID, fontFamily:FONT,
                              background:pillBg, padding:isRPN&&raw>0?"2px 8px":"0",
                              borderRadius:isRPN&&raw>0?4:0 }}>{val}</span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}

      <div style={{ padding:"8px 16px", background:GRAY_BG, fontSize:11, color:GRAY_MID, fontFamily:FONT, fontStyle:"italic", borderTop:`1px solid ${GRAY_B}`, flexShrink:0 }}>
        Avg Dogs = unique dogs per night (boarding) or per day (daycare, M–F) · RPN = total boarding revenue ÷ total nights (green ≥$65 · amber ≥$55 · red &lt;$55) · Cancellations will populate when added to bookings export
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Auth Wrapper — handles login/portal/dashboard routing
// Keeps hooks out of conditional paths
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  const [authClient, setAuthClient] = useState(null);
  const [appView, setAppView]       = useState("login");

  const handleLogin = (clientData) => { setAuthClient(clientData); setAppView("portal"); };
  const handleLogout = () => { setAuthClient(null); setAppView("login"); };
  const handleEnterFeature = (featureId) => { if (featureId === "dashboard") setAppView("dashboard"); };

  if (appView === "login")  return <LoginPage onLogin={handleLogin} />;
  if (appView === "portal") return <PortalPage client={authClient} onEnterFeature={handleEnterFeature} onLogout={handleLogout} />;

  return <Dashboard authClient={authClient} onLogout={handleLogout} onPortal={() => setAppView("portal")} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard — the main app (all hooks live here, no conditional hook calls)
// ─────────────────────────────────────────────────────────────────────────────
function Dashboard({ authClient, onLogout, onPortal }) {
  const isPKTenantUser = authClient?.clientId === "petkennect";

  // ── Tenant branding theme ─────────────────────────────────────────────────
  const branding = authClient?.branding || {};
  const T_PRIMARY   = branding.primaryColor   || "#1B3A6B";
  const T_SECONDARY = branding.secondaryColor || "#2a5298";
  const T_NAV_BG    = branding.navBg          || "#e8edf5";
  const T_LOGO      = branding.logo           || null;
  const T_NAME      = branding.name           || authClient?.clientName || "Dashboard";
  const T_HEADER    = branding.headerBg       || branding.primaryColor  || "#1B3A6B";

  // Shadow NAVY_BG and NAVY with tenant colors so all sub-elements pick up branding
  // eslint-disable-next-line no-unused-vars
  const NAVY_BG = T_NAV_BG;
  // eslint-disable-next-line no-unused-vars
  const NAVY = T_HEADER;
  const [tab, setTab] = useState("import");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadMsg, setLoadMsg] = useState("");
  const [ready, setReady] = useState(false);
  const [period, setPeriod] = useState("month");
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [cohortFrom, setCohortFrom] = useState("all");

  const [cohortTo, setCohortTo] = useState("all");
  const [callRows, setCallRows] = useState(null);
  const [callLoading, setCallLoading] = useState(false);
  const [propetRows, setPropetRows] = useState(null);
  const [monthlySpend, setMonthlySpend] = useState(authClient?.monthlyAdSpend || (isPKTenantUser ? 1500 : 6185));
  const [marketingPeriod, setMarketingPeriod] = useState("month");
  const [selectedMarketingPeriod, setSelectedMarketingPeriod] = useState(null);

  const [bookingRows, setBookingRows] = useState(null);
  const [precomputedMetrics, setPrecomputedMetrics] = useState(null);

  // ── Settings ────────────────────────────────────────────────────────────────
  const tenantId = authClient?.clientId || "default";
  // ── Location selector ─────────────────────────────────────────────────────
  const tenantLocations = authClient?.locations || [];
  const isMultiLocation = tenantLocations.length > 1;
  const [selectedLocationId, setSelectedLocationId] = useState("all");
  const [locCompExpanded, setLocCompExpanded] = useState(false);
  const selectedLocation = tenantLocations.find(l => l.id === selectedLocationId) || null;
  const selectedLocationCode = selectedLocation?.locationCode || null; // null = All Locations
  // Set tenant-specific display labels globally so label() helper works
  useEffect(() => {
    const labels = isPKTenantUser ? {
      "Daycare": "Day Camp", "Boarding": "Boarding", "Grooming": "Grooming",
      "Dog Training": "Training", "Retail / Food": "Retail",
      "Miscellaneous": "Miscellaneous", "Refunds": "Refunds", "Deposits": "Deposits",
    } : null;
    setTenantLabels(labels);
    setTenantHeaderBg(T_HEADER);
  }, [isPKTenantUser, T_HEADER]);
  const [showSettings, setShowSettings] = useState(false);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [driveLinks, setDriveLinks] = useState(
    authClient?.driveLinks || { qb:"", calls:"", customers:"", bookings:"" }
  );
  const [kennelCapacity, setKennelCapacity] = useState(
    authClient?.kennelCapacity || { daycare:35, boarding:50 }
  );
  const [driveLoading, setDriveLoading] = useState(false);
  const [driveStatus, setDriveStatus] = useState("");
  const [savedToast, setSavedToast] = useState(false);
  const [showDriveConfirm, setShowDriveConfirm] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);

  // ── Customer Master Table — built once when all 3 files loaded ──────────────
  const customerMaster = useMemo(
    () => buildCustomerMaster(rows, propetRows, callRows),
    [rows, propetRows, callRows]
  );
  const callFileRef = useRef();
  const fileRef = useRef();

  // ── PDF Export ─────────────────────────────────────────────────────────────
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const loadScript = (src) => new Promise((res, rej) => {
        if (document.querySelector(`script[src="${src}"]`)) return res();
        const s = document.createElement("script");
        s.src = src; s.onload = res; s.onerror = rej;
        document.head.appendChild(s);
      });
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");

      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const PW = 297, PH = 210;
      let pageNum = 0;

      // ── Period label ─────────────────────────────────────────────────────────
      let activePeriod = selectedPeriod;
      let activePeriodMode = period;
      if (!activePeriod && rows) {
        const dates = rows.map(r => parseDate(r.Date)).filter(Boolean);
        const latest = dates.reduce((a,b) => b > a ? b : a, dates[0]);
        if (latest) { activePeriod = moKey(latest); activePeriodMode = "month"; }
      }
      const periodLabel = activePeriod
        ? (activePeriodMode === "month" ? fmtMo(activePeriod) : activePeriodMode === "week" ? fmtWE(activePeriod) : activePeriod)
        : "All Data";
      const today = new Date().toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" });
      const filename = `Waggles_Scorecard_${periodLabel.replace(/\s+/g,"_").replace(/\//g,"-")}.pdf`;

      // ── Helpers ─────────────────────────────────────────────────────────────
      const wait = (ms) => new Promise(r => setTimeout(r, ms));

      const switchTab = async (t) => {
        setTab(t);
        await wait(600);
      };

      // Wait for AI insight cards to finish loading
      const waitForAI = async () => {
        const maxWait = 30000; // 30 second timeout
        const start = Date.now();
        while (Date.now() - start < maxWait) {
          const analyzing = document.querySelector("[data-ai-loading='true']");
          if (!analyzing) break;
          await wait(500);
        }
      };

      const captureEl = async (el, y, maxW, maxH) => {
        if (!el) return false;
        const canvas = await window.html2canvas(el, {
          scale: 1.8, useCORS: true, backgroundColor: "#f4f5f7",
          logging: false, windowWidth: 1280,
        });
        const imgData = canvas.toDataURL("image/jpeg", 0.88);
        const ratio = canvas.width / canvas.height;
        let w = maxW, h = w / ratio;
        if (h > maxH) { h = maxH; w = h * ratio; }
        pdf.addImage(imgData, "JPEG", (PW - w) / 2, y, w, h);
        return true;
      };

      // Capture element that has overflow/maxHeight constraints — temporarily remove them
      // Only touches the direct outer container, NOT sticky inner divs (avoids header misplacement)
      const captureFullEl = async (el, y, maxW, maxH) => {
        if (!el) return false;
        const origMaxH   = el.style.maxHeight;
        const origOverflowY = el.style.overflowY;
        el.style.maxHeight  = "none";
        el.style.overflowY  = "visible";
        // Remove overflow only on direct scrollable children (not sticky headers)
        const saved = [];
        el.childNodes.forEach(child => {
          if (child.style && child.style.overflowY && child.style.overflowY !== "visible") {
            saved.push([child, child.style.overflowY, child.style.maxHeight]);
            child.style.overflowY = "visible";
            child.style.maxHeight = "none";
          }
        });
        await wait(150);
        const result = await captureEl(el, y, maxW, maxH);
        el.style.maxHeight  = origMaxH;
        el.style.overflowY  = origOverflowY;
        saved.forEach(([d, ovy, mh]) => { d.style.overflowY = ovy; d.style.maxHeight = mh; });
        return result;
      };

      const captureMain = async (y) => {
        const main = document.querySelector("main");
        return captureEl(main, y, PW - 16, PH - y - 10);
      };

      const addHeader = (title) => {
        pdf.setFillColor(27, 58, 107);
        pdf.rect(0, 0, PW, 13, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(9); pdf.setFont("helvetica", "bold");
        pdf.text(title, PW/2, 8.5, { align: "center" });
        pdf.setFontSize(7); pdf.setFont("helvetica", "normal");
        pdf.setTextColor(180, 200, 230);
        pdf.text(periodLabel, PW - 6, 8.5, { align: "right" });
      };

      const addFooter = () => {
        pageNum++;
        pdf.setFillColor(245, 246, 248);
        pdf.rect(0, PH - 7, PW, 7, "F");
        pdf.setFontSize(6.5); pdf.setFont("helvetica", "normal");
        pdf.setTextColor(138, 148, 166);
        pdf.text("Confidential", 6, PH - 2.5);
        pdf.text(`Page ${pageNum}`, PW/2, PH - 2.5, { align: "center" });
        pdf.text(today, PW - 6, PH - 2.5, { align: "right" });
      };

      const addDivider = (title, subtitle) => {
        if (pageNum > 0) pdf.addPage();
        pdf.setFillColor(255, 255, 255); pdf.rect(0, 0, PW, PH, "F");
        pdf.setFillColor(27, 58, 107); pdf.rect(0, 0, 6, PH, "F");
        try { pdf.addImage(LOGO_SRC, "PNG", PW/2 - 30, 50, 60, 23); } catch(e) {}
        pdf.setTextColor(27, 58, 107); pdf.setFontSize(24); pdf.setFont("helvetica", "bold");
        pdf.text(title, PW/2, 95, { align: "center" });
        pdf.setFontSize(12); pdf.setFont("helvetica", "normal");
        pdf.setTextColor(100, 120, 150);
        pdf.text(subtitle, PW/2, 107, { align: "center" });
        pdf.setFillColor(232, 237, 245);
        pdf.roundedRect(PW/2 - 30, 115, 60, 10, 2, 2, "F");
        pdf.setFontSize(10); pdf.setFont("helvetica", "bold");
        pdf.setTextColor(27, 58, 107);
        pdf.text(periodLabel, PW/2, 122, { align: "center" });
        pdf.setFontSize(8); pdf.setFont("helvetica", "normal");
        pdf.setTextColor(150, 165, 185);
        pdf.text(`Generated: ${today}`, PW/2, 138, { align: "center" });
        addFooter();
      };

      const prevTab = tab;

      // ── COVER PAGE ───────────────────────────────────────────────────────────
      addDivider("Scorecard & Summary", "Financials · Bookings · Customers · Marketing");

      // ── SECTION 1: FINANCIALS ─────────────────────────────────────────────────
      await switchTab("scorecard");
      addDivider("Financials", "Revenue · Reconciliation · Trends");

      // Widgets + recon
      pdf.addPage(); addHeader("Financials — " + periodLabel); addFooter();
      const stickyBar = document.querySelector("[data-sticky='scorecard']");
      await captureEl(stickyBar, 15, PW - 16, 60);
      const reconTable = document.querySelector(".recon-table-pdf");
      if (reconTable) await captureEl(reconTable, 78, PW - 16, PH - 90);

      // Revenue Over Time
      pdf.addPage(); addHeader("Revenue Over Time"); addFooter();
      const timePivot = document.querySelector(".time-pivot-pdf");
      await captureEl(timePivot, 15, PW - 16, PH - 28);

      // Revenue Breakdown
      pdf.addPage(); addHeader("Revenue Breakdown — " + periodLabel); addFooter();
      const revBreakdown = document.querySelector(".rev-breakdown-pdf");
      await captureEl(revBreakdown, 15, PW - 16, PH - 28);

      // ── SECTION 2: CUSTOMER KPIs ──────────────────────────────────────────────
      await switchTab("customers");
      addDivider("Customer KPIs", "Retention · Cross-Service Adoption · Lifetime Value");

      pdf.addPage(); addHeader("Customer KPIs — Overview"); addFooter();
      const custWidgets = document.querySelector("[data-sticky='customers']");
      await captureEl(custWidgets, 15, PW - 16, 46);
      const retentionChart = document.querySelector(".retention-chart-pdf");
      await captureEl(retentionChart, 63, PW - 16, PH - 75);

      pdf.addPage(); addHeader("Cross-Service Adoption"); addFooter();
      const crossService = document.getElementById("cross-service-section");
      await captureEl(crossService, 15, PW - 16, PH - 28);

      pdf.addPage(); addHeader("Lifetime Value by Acquisition Service"); addFooter();
      const ltvSection = document.getElementById("ltv-section");
      await captureEl(ltvSection, 15, PW - 16, PH - 28);

      // ── SECTION 3: BOOKINGS ───────────────────────────────────────────────────
      if (bookingRows && bookingRows.length > 0) {
        await switchTab("bookings");
        addDivider("Bookings", "Pipeline · Kennel Operations · Occupancy");

        // KPI widgets + pipeline on same page
        pdf.addPage(); addHeader("Bookings — KPIs & Pipeline"); addFooter();
        const bkgSticky = document.querySelector("[data-pdf='bookings-kpis']");
        if (bkgSticky) await captureEl(bkgSticky, 15, PW - 16, PH - 28);

        // Kennel Ops — captureFullEl removes outer maxHeight but keeps sticky headers
        pdf.addPage(); addHeader("Kennel Operations — Occupancy & Performance"); addFooter();
        const kennelOps = document.querySelector("[data-pdf='kennel-ops']");
        if (kennelOps) await captureFullEl(kennelOps, 15, PW - 16, PH - 28);

        // Force overview mode for revenue chart — wait longer for chart to paint
        const overviewBtn = document.querySelector("[data-viewmode='overview']");
        if (overviewBtn) { overviewBtn.click(); await wait(800); }
        const bkgChart = document.querySelector("[data-pdf='bookings-chart']");
        if (bkgChart) {
          pdf.addPage(); addHeader("Bookings — Revenue by Period"); addFooter();
          await captureEl(bkgChart, 15, PW - 16, PH - 28);
        }

        // Force pipeline/future mode for pipeline chart + top customers
        const futureBtn = document.querySelector("[data-viewmode='future']");
        if (futureBtn) { futureBtn.click(); await wait(400); }

        const pipelineChart = document.querySelector("[data-pdf='pipeline-chart']");
        if (pipelineChart) {
          pdf.addPage(); addHeader("Bookings — Confirmed Pipeline"); addFooter();
          await captureEl(pipelineChart, 15, PW - 16, PH - 28);
        }

        const topCust = document.querySelector("[data-pdf='top-customers']");
        if (topCust) {
          pdf.addPage(); addHeader("Bookings — Top 10 Customers by Pipeline Value"); addFooter();
          await captureEl(topCust, 15, PW - 16, PH - 28);
        }
      }

      // ── SECTION 4: MARKETING ROI ──────────────────────────────────────────────
      if (callRows && callRows.length > 0) {
        await switchTab("marketing");
        addDivider("Marketing ROI", "Call Attribution · Campaign Performance · Acquisition");

        // Marketing: sticky widgets + campaign table on same page
        pdf.addPage(); addHeader("Marketing ROI — Overview & Campaign Performance"); addFooter();
        const mktSticky = document.querySelector("[data-sticky='marketing']");
        if (mktSticky) await captureEl(mktSticky, 15, PW - 16, 52);
        const campTable = document.querySelector("[data-pdf='campaign-table']");
        if (campTable) await captureEl(campTable, 69, PW - 16, PH - 82);

        // Acquisition metrics on next page
        const acqTable = document.querySelector("[data-pdf='acquisition-table']");
        if (acqTable) {
          pdf.addPage(); addHeader("Marketing ROI — Acquisition Metrics"); addFooter();
          await captureEl(acqTable, 15, PW - 16, PH - 28);
        }


      }

      // ── SECTION 5: SCORECARD SUMMARY ─────────────────────────────────────────
      await switchTab("summary");
      await wait(800); // let page render
      await waitForAI(); // wait for all AI cards to complete
      addDivider("Scorecard Summary", "AI Insights · Executive Summary");

      // Capture the 2-col grid of insight cards as one element
      pdf.addPage(); addHeader("Scorecard Summary — Insights"); addFooter();
      const summaryContent = document.querySelector("[data-pdf='summary-content']");
      if (summaryContent) {
        await captureEl(summaryContent, 15, PW - 16, PH - 28);
      }

      // Executive Summary full width below
      const execSummary = document.querySelector("[data-pdf='exec-summary']");
      if (execSummary) {
        pdf.addPage(); addHeader("Executive Summary"); addFooter();
        await captureEl(execSummary, 15, PW - 16, PH - 28);
      }

      // Metric Definitions & Assumptions — switch to summary tab to find it
      const metricDefs = document.querySelector("[data-pdf='metric-definitions']");
      if (metricDefs) {
        pdf.addPage(); addHeader("Metric Definitions & Assumptions"); addFooter();
        await captureFullEl(metricDefs, 15, PW - 16, PH - 28);
      }

      // ── Restore + save ────────────────────────────────────────────────────────
      setTab(prevTab);
      pdf.save(filename);

    } catch(err) {
      alert("PDF export failed: " + err.message);
      console.error(err);
    } finally {
      setExporting(false);
    }
  };


  const handleUniversalFile = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const name = file.name.toLowerCase();
    const isCSV = name.endsWith(".csv");
    const isXLSX = name.endsWith(".xlsx") || name.endsWith(".xls");

    if (isCSV) {
      setCallLoading(true); setLoadMsg("Reading CSV…");
      const reader = new FileReader();
      reader.onload = ev => {
        setTimeout(() => {
        try {
          const text = ev.target.result;
          // Peek at headers to detect file type
          const firstLine = text.slice(0, 500);
          const peekHeaders = firstLine.split("\n")[0].split(",").map(h => h.replace(/"/g,"").trim());

          // PK Combined Bookings CSV — use SheetJS for reliable parsing
          const isPKCombinedCSV = peekHeaders.includes("OrderType") && peekHeaders.includes("ReservationId") && peekHeaders.includes("StoreCode");
          if (isPKCombinedCSV) {
            setLoadMsg("Parsing bookings CSV…");
            const wb = XLSX.read(text, { type:"string", raw:false });
            const raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval:"", raw:false });
            setLoadMsg("Normalizing bookings… 0%");
            normalizePKCombinedBookingsAsync(
              raw,
              (pct) => setLoadMsg(pct < 100 ? `Normalizing bookings… ${pct}%` : "Building index…"),
              (normalized) => {
                setBookingRows(normalized);
                setTab("bookings");
                setCallLoading(false); setLoadMsg(""); e.target.value = "";
              }
            );
            return;
          }

          // PK Customers CSV
          const isPKCustomersCSV = peekHeaders.includes("ID") && peekHeaders.includes("OwnerLastName") && peekHeaders.includes("CreateDate");
          if (isPKCustomersCSV) {
            const wb = XLSX.read(text, { type:"string", raw:false, cellDates:false, dateNF:"yyyy-mm-dd" });
            const raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval:"", raw:false });
            const pkCust = raw.map(r => ({
              id:           String(r.ID || "").trim(),
              first_name:   String(r.FirstName || "").trim(),
              last_name:    String(r.LastName || "").trim(),
              phone_1:      String(r.phone || "").trim(),
              email:        String(r.email || "").trim(),
              created_at:   String(r.CreateDate || "").slice(0, 10),
              total_value:  parseFloat(r.TotalValue) || 0,
              locationCode: String(r.LocationCode || "").trim(),
            }));
            setPropetRows(pkCust);
            setCallLoading(false); setLoadMsg(""); e.target.value = ""; return;
          }

          // Google Calls or ProPet CSV — existing logic
          const lines = text.split("\n").filter(l => l.trim());
          const headers = lines[0].split(",").map(h => h.replace(/"/g,"").trim());
          const isGoogleCalls = headers.includes("Duration") && headers.includes("campaign");
          const isProPet = headers.includes("phone_1") && headers.includes("created_at") && headers.includes("id");
          if (!isGoogleCalls && !isProPet) {
            alert(`Unrecognized CSV format.\nHeaders found: ${headers.slice(0,6).join(", ")}…`);
            setCallLoading(false); setLoadMsg(""); e.target.value = ""; return;
          }
          const wb2 = XLSX.read(text, { type:"string", raw:false, cellDates:false, dateNF:"yyyy-mm-dd" });
          const parsed = XLSX.utils.sheet_to_json(wb2.Sheets[wb2.SheetNames[0]], { defval:"", raw:false });
          if (isGoogleCalls) { setCallRows(parsed); setTab("marketing"); }
          else { setPropetRows(parsed); setTab("marketing"); }
          setCallLoading(false); setLoadMsg(""); e.target.value = "";
        } catch(err) { alert("Error reading CSV: " + err.message); setCallLoading(false); setLoadMsg(""); e.target.value = ""; }
        }, 10);
      };
      reader.readAsText(file);
    } else if (isXLSX) {
      // XLSX = QuickBooks transactions
      setLoading(true); setReady(false); setLoadMsg("Reading file…");
      const reader = new FileReader();
      reader.onload = (ev) => {
        // Defer heavy XLSX parsing so browser doesn't block on read
        setTimeout(() => {
        try {
          setLoadMsg("Parsing rows…");
          const data = new Uint8Array(ev.target.result);
          const wb = XLSX.read(data, { type:"array", cellDates: false, raw: false, dateNF: "yyyy-mm-dd" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const raw = XLSX.utils.sheet_to_json(ws, { defval:"", raw: false });
          // Detect file type: PK Financial, QB, ProPet, or Bookings
          const hdrs = Object.keys(raw[0]||{});
          const isPKFinancial = hdrs.includes("FinalMasterBucket") && hdrs.includes("InvoiceID") && hdrs.includes("OwnerID");
          const isPKBookings  = wb.SheetNames.includes("Boarding") && wb.SheetNames.includes("DayCare");
          const isPKCombined  = hdrs.includes("OrderType") && hdrs.includes("ReservationId") && hdrs.includes("StoreCode");
          const isQB          = hdrs.some(h => /product.*service|memo.*desc/i.test(h));
          const isPP          = hdrs.includes("phone_1") && hdrs.includes("created_at") && hdrs.includes("id");
          const isPKCustomers = (hdrs.includes("ID") && hdrs.includes("FirstContactDate") && hdrs.includes("LocationCode")) ||
                                 (hdrs.includes("ID") && hdrs.includes("OwnerLastName") && hdrs.includes("CreateDate"));
          const isBookings    = hdrs.includes("booking_value") && hdrs.includes("start_date") && hdrs.includes("order_type");

          // PK Combined Bookings — single flat sheet
          if (isPKCombined) {
            setLoadMsg("Normalizing bookings… 0%");
            normalizePKCombinedBookingsAsync(
              raw,
              (pct) => setLoadMsg(pct < 100 ? `Normalizing bookings… ${pct}%` : "Building index…"),
              (normalized) => {
                setBookingRows(normalized);
                setTab("bookings");
                setLoading(false); setLoadMsg(""); e.target.value = "";
              }
            );
            return;
          }

          // PK Bookings — multi-tab workbook (legacy)
          if (isPKBookings) {
            setLoadMsg("Normalizing PK bookings…");
            setTimeout(() => {
              const normalized = normalizePKBookings(wb);
              setBookingRows(normalized);
              setTab("bookings");
              setLoading(false); setLoadMsg(""); e.target.value = "";
            }, 60);
            return;
          }

          // PK Customers
          if (isPKCustomers && !isPP) {
            const pkCust = raw.map(r => ({
              id:           String(r.ID || "").trim(),
              first_name:   String(r.FirstName || "").trim(),
              last_name:    String(r.LastName || r.OwnerLastName || "").trim(),
              phone_1:      String(r.phone || "").trim(),
              email:        String(r.email || "").trim(),
              created_at:   String(r.FirstContactDate || r.CreateDate || "").slice(0, 10),
              total_value:  parseFloat(r.TotalValue) || 0,
              locationCode: String(r.LocationCode || "").trim(),
            }));
            setPropetRows(pkCust);
            setTab("marketing");
            setLoading(false); setLoadMsg(""); e.target.value = ""; return;
          }

          if (!isPKFinancial && !isQB && !isPP && !isBookings) {
            alert("Unrecognized XLSX format.\nExpected PK Financial, Financial File, QuickBooks transactions, or Bookings export.");
            setLoading(false); setLoadMsg(""); e.target.value = ""; return;
          }
          if (isBookings) {
            // Check for pre-computed Kennel_Metrics sheet
            const metricsSheet = wb.SheetNames.find(n => n === "Kennel_Metrics");
            if (metricsSheet) {
              const mRaw = XLSX.utils.sheet_to_json(wb.Sheets[metricsSheet]);
              // Build nested lookup: { month: {period: metrics}, week: {...}, year: {...} }
              const preMetrics = { month:{}, week:{}, year:{} };
              mRaw.forEach(r => {
                const mode = r.mode;
                if (preMetrics[mode]) preMetrics[mode][r.period] = r;
              });
              setPrecomputedMetrics(preMetrics);
              console.log("✓ Loaded pre-computed Kennel Metrics:", Object.keys(preMetrics.month).length, "months,", Object.keys(preMetrics.week).length, "weeks");
            }
            const today = new Date().toISOString().slice(0,10);
            const cleaned = raw.map(r => ({
              customer_id:        String(r.customer_id || "").trim(),
              customer_name:      String(r.customer_name || ""),
              reservation_string: String(r.reservation_string || "").replace("#","").trim(),
              order_type:         String(r.order_type || ""),
              status:             String(r.status || ""),
              date_created:       String(r.date_created || "").slice(0,10),
              start_date:         String(r.start_date || "").slice(0,10),
              end_date:           String(r.end_date || "").slice(0,10),
              rate:               String(r.rate || ""),
              booking_quantity:   parseFloat(r.booking_quantity || 0),
              booking_value:      parseFloat(r.booking_value || 0),
              online_request:     Boolean(r.online_request),
              is_future:          String(r.is_future || "") === "TRUE" || String(r.start_date || "").slice(0,10) >= today,
            }));
            // Deduplicate
            const seen = new Set();
            const deduped = cleaned.filter(r => {
              const key = `${r.reservation_string}|${r.customer_id}|${r.start_date}|${r.rate}`;
              if (seen.has(key)) return false;
              seen.add(key); return true;
            });
            setBookingRows(deduped);
            setTab("bookings");
            setLoading(false); setLoadMsg(""); e.target.value = ""; return;
          }
          if (isPP && !isQB) {
            setPropetRows(raw);
            setTab("marketing");
            setLoading(false); setLoadMsg(""); e.target.value = ""; return;
          }
          setLoadMsg("Running scrubbing pipeline…");
          setTimeout(() => {
            const scrubbed = isPKFinancial ? scrubPKRows(raw) : scrubRows(raw);
            setRows(scrubbed);
            const dates = scrubbed.map(r=>parseDate(r.Date)).filter(Boolean);
            const latest = dates.reduce((a,b)=>b>a?b:a, dates[0]);
            if (latest) setSelectedPeriod(moKey(latest));
            setTab("import"); setReady(true); setLoading(false); setLoadMsg("");
          }, 60);
        } catch(err) { alert("Error: "+err.message); setLoading(false); setLoadMsg(""); }
        }, 10); // end setTimeout defer
      };
      reader.readAsArrayBuffer(file);
      e.target.value = "";
    } else {
      alert("Unsupported file type. Please upload a .xlsx (QuickBooks) or .csv (Google Calls / ProPet) file.");
      e.target.value = "";
    }
  }, []);

  // ── Load all 4 files from saved Google Drive links ──────────────────────────
  const handleLoadFromDrive = useCallback(async () => {
    // Use driveLinks state, falling back to authClient in case state hasn't initialized yet
    const links = (driveLinks?.qb) ? driveLinks : (authClient?.driveLinks || driveLinks);
    const { qb, calls, customers, bookings } = links;
    const missing = [];
    if (!qb) missing.push("QuickBooks");
    if (!calls) missing.push("Google Calls");
    if (!customers) missing.push("Customers");
    if (!bookings) missing.push("Bookings");
    if (missing.length) {
      alert(`Missing Drive links for: ${missing.join(", ")}.\n\nPlease open Settings (⚙) and add the links.`);
      return;
    }

    const fetchDrive = async (url) => {
      const res = await fetch(`${API_BASE}/api/fetch-drive?url=${encodeURIComponent(url)}`);
      if (!res.ok) throw new Error(`Drive fetch failed: ${res.status}`);
      return res.arrayBuffer();
    };

    const processCSV = (text) => {
      // Full RFC-4180 CSV parser — handles multi-line quoted fields, commas in values
      const parseCSV = (str) => {
        const rows = [];
        let row = [];
        let field = "";
        let inQuotes = false;
        let i = 0;
        while (i < str.length) {
          const ch = str[i];
          if (inQuotes) {
            if (ch === '"' && str[i+1] === '"') { field += '"'; i += 2; }
            else if (ch === '"') { inQuotes = false; i++; }
            else { field += ch; i++; }
          } else {
            if (ch === '"') { inQuotes = true; i++; }
            else if (ch === ',') { row.push(field.trim()); field = ""; i++; }
            else if (ch === '\n') {
              row.push(field.trim()); rows.push(row);
              row = []; field = ""; i++;
              if (str[i] === '\r') i++;
            }
            else if (ch === '\r') {
              row.push(field.trim()); rows.push(row);
              row = []; field = ""; i++;
              if (str[i] === '\n') i++;
            }
            else { field += ch; i++; }
          }
        }
        if (field || row.length) { row.push(field.trim()); rows.push(row); }
        return rows;
      };

      const rows = parseCSV(text).filter(r => r.some(v => v));
      if (rows.length < 2) return { parsed: [] };
      const headers = rows[0].map(h => h.replace(/^"|"$/g, "").trim());
      const parsed = rows.slice(1).map(vals => {
        const obj = {};
        headers.forEach((h, i) => { obj[h] = (vals[i] || "").replace(/^"|"$/g, "").trim(); });
        return obj;
      }).filter(r => Object.values(r).some(v => v));
      return { parsed };
    };

    setDriveLoading(true);
    setDriveStatus("Connecting to Google Drive…");
    const isPKTenant = authClient?.clientId === "petkennect";
    try {
      // ── 1. Financial Data ───────────────────────────────────────────────────
      setDriveStatus("Loading Financial Data…");
      const qbBuf = await fetchDrive(qb);
      const qbWB = XLSX.read(new Uint8Array(qbBuf), { type:"array", cellDates:false, raw:false, dateNF:"yyyy-mm-dd" });
      const qbWS = qbWB.Sheets[qbWB.SheetNames[0]];
      const qbRaw = XLSX.utils.sheet_to_json(qbWS, { defval:"", raw:false });
      // Route to tenant-appropriate scrubber
      const scrubbed = isPKTenant ? scrubPKRows(qbRaw) : scrubRows(qbRaw);
      const dates = scrubbed.map(r=>parseDate(r.Date)).filter(Boolean);
      const latest = dates.reduce((a,b)=>b>a?b:a, dates[0]);
      if (latest) setSelectedPeriod(moKey(latest));
      setRows(scrubbed);
      setReady(true);

      // ── 2. Google Calls CSV ─────────────────────────────────────────────────
      setDriveStatus("Loading Google Calls data…");
      const callsBuf = await fetchDrive(calls);
      const callsWB = XLSX.read(new Uint8Array(callsBuf), { type:"array", raw:false, cellDates:false, dateNF:"yyyy-mm-dd" });
      const callsParsed = XLSX.utils.sheet_to_json(callsWB.Sheets[callsWB.SheetNames[0]], { defval:"", raw:false });
      setCallRows(callsParsed);

      // ── 3. Customers ────────────────────────────────────────────────────────
      setDriveStatus("Loading Customer Data…");
      const custBuf = await fetchDrive(customers);
      const custWB = XLSX.read(new Uint8Array(custBuf), { type:"array", raw:false, cellDates:false, dateNF:"yyyy-mm-dd" });
      const custRaw = XLSX.utils.sheet_to_json(custWB.Sheets[custWB.SheetNames[0]], { defval:"", raw:false });
      // Route to tenant-appropriate customer normalization
      const custParsed = isPKTenant
        ? custRaw.map(r => ({
            id:           String(r.ID || "").trim(),
            first_name:   String(r.FirstName || "").trim(),
            last_name:    String(r.LastName || "").trim(),
            phone_1:      String(r.phone || "").trim(),
            email:        String(r.email || "").trim(),
            created_at:   String(r.CreateDate || "").slice(0, 10),
            total_value:  parseFloat(r.TotalValue) || 0,
            locationCode: String(r.LocationCode || "").trim(),
          }))
        : custRaw;
      setPropetRows(custParsed);

      // ── 4. Bookings ─────────────────────────────────────────────────────────
      setDriveStatus("Loading Bookings data…");
      const bookBuf = await fetchDrive(bookings);
      const bookWB = XLSX.read(new Uint8Array(bookBuf), { type:"array", cellDates:false, raw:false, dateNF:"yyyy-mm-dd" });

      if (isPKTenant) {
        // PK: check if it's the new combined format or legacy multi-tab
        const firstSheetData = XLSX.utils.sheet_to_json(bookWB.Sheets[bookWB.SheetNames[0]], { defval:"", raw:false });
        const firstHdrs = Object.keys(firstSheetData[0] || {});
        const isCombined = firstHdrs.includes("OrderType") && firstHdrs.includes("ReservationId");
        if (isCombined) {
          const normalized = normalizePKCombinedBookings(firstSheetData);
          setBookingRows(normalized);
        } else {
          // Legacy multi-tab
          const normalized = normalizePKBookings(bookWB);
          setBookingRows(normalized);
        }
      } else {
        // Waggles: single sheet with optional Kennel_Metrics tab
        const metricsSheet = bookWB.SheetNames.find(n => n === "Kennel_Metrics");
        if (metricsSheet) {
          const mRaw = XLSX.utils.sheet_to_json(bookWB.Sheets[metricsSheet]);
          const preMetrics = { month:{}, week:{}, year:{} };
          mRaw.forEach(r => { if (preMetrics[r.mode]) preMetrics[r.mode][r.period] = r; });
          setPrecomputedMetrics(preMetrics);
        }
        const bookWS = bookWB.Sheets[bookWB.SheetNames[0]];
        const bookRaw = XLSX.utils.sheet_to_json(bookWS, { defval:"", raw:false });
        const today = new Date().toISOString().slice(0,10);
        const cleanedBook = bookRaw.map(r => ({
          customer_id:        String(r.customer_id || "").trim(),
          customer_name:      String(r.customer_name || ""),
          reservation_string: String(r.reservation_string || "").replace("#","").trim(),
          order_type:         String(r.order_type || ""),
          status:             String(r.status || ""),
          date_created:       String(r.date_created || "").slice(0,10),
          start_date:         String(r.start_date || "").slice(0,10),
          end_date:           String(r.end_date || "").slice(0,10),
          rate:               String(r.rate || ""),
          booking_quantity:   parseFloat(r.booking_quantity || 0),
          booking_value:      parseFloat(r.booking_value || 0),
          online_request:     Boolean(r.online_request),
          is_future:          String(r.start_date || "").slice(0,10) >= today,
        }));
        setBookingRows(cleanedBook);
      }

      setDriveStatus("✓ All files loaded!");
      setTab("import");
      setTimeout(() => setDriveStatus(""), 3000);
    } catch(err) {
      alert("Error loading from Drive: " + err.message + "\n\nMake sure your links are set to 'Anyone with the link can view' and the proxy is running.");
      setDriveStatus("");
    } finally {
      setDriveLoading(false);
    }
  }, [driveLinks]);

  // Build period lists from data
  const { weekPeriods, monthPeriods, yearPeriods } = useMemo(() => {
    if (!rows) return { weekPeriods:[], monthPeriods:[], yearPeriods:[] };
    const wks=new Set(), mos=new Set(), yrs=new Set();
    rows.forEach(r => {
      const dwe = parseWE(r.WeekEnding);
      if (dwe) wks.add(weKey(dwe));
      const dt = parseDate(r.Date);
      if (dt) { mos.add(moKey(dt)); yrs.add(yrKey(dt)); }
    });
    const weekPeriods = [...wks].sort((a,b)=>b.localeCompare(a)).map(k=>({key:k,label:fmtWE(k)}));
    const monthPeriods = [...mos].sort((a,b)=>b.localeCompare(a)).map(k=>({key:k,label:fmtMo(k)}));
    const yearPeriods = [...yrs].sort((a,b)=>b.localeCompare(a)).map(k=>({key:k,label:k}));
    return { weekPeriods, monthPeriods, yearPeriods };
  }, [rows]);

  const currentPeriods = period==="week"?weekPeriods:period==="month"?monthPeriods:yearPeriods;

  // Sync selectedPeriod when period mode changes
  const handlePeriodMode = (mode) => {
    setPeriod(mode);
    if (!rows) return;
    if (mode === "week") {
      const dates = rows.map(r=>parseWE(r.WeekEnding)).filter(Boolean);
      const latest = dates.reduce((a,b)=>b>a?b:a, dates[0]);
      if (latest) setSelectedPeriod(weKey(latest));
    } else if (mode === "month") {
      const dates = rows.map(r=>parseDate(r.Date)).filter(Boolean);
      const latest = dates.reduce((a,b)=>b>a?b:a, dates[0]);
      if (latest) setSelectedPeriod(moKey(latest));
    } else {
      const dates = rows.map(r=>parseDate(r.Date)).filter(Boolean);
      const latest = dates.reduce((a,b)=>b>a?b:a, dates[0]);
      if (latest) setSelectedPeriod(yrKey(latest));
    }
  };

  // ── Location filtering — applied upstream before period filtering ──────────
  const locationRows = useMemo(() => {
    if (!rows) return null;
    if (!selectedLocationCode) return rows; // All Locations
    return rows.filter(r => r.LocationCode === selectedLocationCode);
  }, [rows, selectedLocationCode]);

  const locationBookingRows = useMemo(() => {
    if (!bookingRows) return null;
    if (!selectedLocationCode) return bookingRows;
    return bookingRows.filter(r => r.locationCode === selectedLocationCode);
  }, [bookingRows, selectedLocationCode]);

  const locationPropetRows = useMemo(() => {
    if (!propetRows) return null;
    if (!selectedLocationCode) return propetRows;
    return propetRows.filter(r => r.locationCode === selectedLocationCode);
  }, [propetRows, selectedLocationCode]);

  const filteredRows = useMemo(() => {
    if (!locationRows || !selectedPeriod) return [];
    return filterRows(locationRows, period, selectedPeriod);
  }, [locationRows, period, selectedPeriod]);

  const revenueRows = useMemo(() => filteredRows, [filteredRows]);

  const widgets = useMemo(() => buildWidgets(revenueRows), [revenueRows]);
  const tableData = useMemo(() => buildBucketTable(filteredRows), [filteredRows]);
  const quality = useMemo(() => rows ? buildQuality(rows) : null, [rows]);

  // Prior period widgets for indicators
  const priorPeriodKey = useMemo(() => selectedPeriod ? getPriorPeriodKey(period, selectedPeriod) : null, [period, selectedPeriod]);
  const yoyPeriodKey = useMemo(() => selectedPeriod ? getYoYPeriodKey(period, selectedPeriod) : null, [period, selectedPeriod]);
  const priorRows = useMemo(() => (locationRows && priorPeriodKey) ? filterRows(locationRows, period, priorPeriodKey) : [], [locationRows, period, priorPeriodKey]);
  const yoyRows = useMemo(() => (locationRows && yoyPeriodKey) ? filterRows(locationRows, period, yoyPeriodKey) : [], [locationRows, period, yoyPeriodKey]);
  const priorWidgets = useMemo(() => buildWidgets(priorRows), [priorRows]);
  const yoyWidgets = useMemo(() => buildWidgets(yoyRows), [yoyRows]);

  // Reconciliation status: compare our net revenue vs QB net
  const reconStatus = useMemo(() => {
    if (!filteredRows.length) return null;
    const qbNet = filteredRows.reduce((sum,r) => sum + (parseFloat(r.Amount)||0), 0);
    const ourNet = widgets.Total?.revenue || 0;
    const diff = Math.abs(ourNet - qbNet);
    if (diff < 1) return { ok: true, label: "✅ Data fully reconciled" };
    // Count rows where QB PS bucket ≠ our bucket (meaningful mismatches only)
    const unmapped = filteredRows.filter(r => {
      const ps = String(r["Product/Service"]||"").trim();
      const b = r.FinalMainBucket;
      if (ps === "Refunds" && b === "Refunds") return false;
      if (ps === "Daycare" && b === "Daycare") return false;
      if (ps === "Daycare Packages Income" && b === "Daycare") return false;
      if ((ps === "Activities" || ps === "Enrichment") && b === "Boarding") return false; // Lodging in enrichment building
      if (ps === "Transportation Packages Income" && b === "Transport") return false;
      if (ps === "Boarding" && (b === "Boarding" || b === "Dog Training" || b === "Enrichment")) return false;
      if (ps === "Activities" && (b === "Boarding" || b === "Enrichment")) return false;
      if (ps === "Enrichment" && (b === "Enrichment" || b === "Boarding")) return false;
      if (ps === "Dog Training" && b === "Dog Training") return false;
      if (ps === "Grooming" && b === "Grooming") return false;
      if (ps === "Transportation Income" && b === "Transport") return false;
      if (ps === "Discounts" && b === "Discounts") return false;
      return true;
    }).length;
    return { ok: false, label: `⚠️ ${fmt(diff)} gap · ${unmapped.toLocaleString()} unmapped rows` };
  }, [filteredRows, widgets]);

  // Lift BucketAccordion open state to App so widgets can trigger it
  const [accordionBucket, setAccordionBucket] = useState(null);
  const accordionRef = useRef(null);

  const handleWidgetClick = (bucketName) => {
    setAccordionBucket(bucketName);
    setTimeout(() => {
      if (accordionRef.current) {
        // Measure the actual sticky bar height dynamically
        const stickyEl = document.querySelector('[data-sticky="scorecard"]');
        const stickyHeight = stickyEl ? stickyEl.offsetHeight : 60;
        const top = accordionRef.current.getBoundingClientRect().top + window.scrollY - stickyHeight - 16;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }, 80);
  };

  return (
    <div style={{ minHeight:"100vh", background:GRAY_BG, fontFamily:FONT }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* Header */}
      <header style={{ position:"sticky", top:0, zIndex:100, background: WHITE, height:60, padding:"0 28px", display:"flex", alignItems:"center", gap:12, boxShadow:`0 2px 16px rgba(27,58,107,0.12)`, borderBottom:`2px solid ${T_PRIMARY}` }}>
        {/* Logo */}
        {T_LOGO
          ? <img src={T_LOGO} alt={T_NAME} style={{ height:isPKTenantUser ? 48 : 36, objectFit:"contain", flexShrink:0 }} onError={e=>{ e.target.style.display="none"; e.target.nextSibling.style.display="block"; }} />
          : null}
        <span style={{ display:T_LOGO?"none":"block", color:T_PRIMARY, fontWeight:800, fontSize:16, fontFamily:FONT, letterSpacing:"0.05em", flexShrink:0 }}>{T_NAME}</span>
        {T_LOGO && <span style={{ display:"none", color:T_PRIMARY, fontWeight:800, fontSize:16, fontFamily:FONT, letterSpacing:"0.05em", flexShrink:0 }}>{T_NAME}</span>}
        {/* Spacer to push tabs to align with content */}
        <div style={{ width:16, flexShrink:0 }} />
        {/* Nav tabs */}
        <div style={{ display:"flex", gap:2, background:T_NAV_BG, borderRadius:8, padding:3, flexShrink:0 }}>
          {[["import","Import Summary"],["scorecard","Financials"],["bookings","Bookings"],["customers","Customer KPIs"],["marketing","Marketing ROI"],["summary","Scorecard Summary"]].map(([k,l])=>(
            <button key={k} onClick={()=>{ setTab(k); window.scrollTo({top:0,behavior:"instant"}); }} style={{ padding:"6px 10px", borderRadius:6, border:"none", cursor:"pointer", fontSize:11, fontWeight:600, fontFamily:FONT, background:tab===k?T_PRIMARY:T_NAV_BG, color:tab===k?WHITE:T_PRIMARY, transition:"all 0.15s", whiteSpace:"nowrap" }}>{l}</button>
          ))}
        </div>
        {/* Spacer */}
        <div style={{ flex:1 }} />
        {/* Right side controls */}
        <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
          {/* Location selector — only shown for multi-location tenants */}
          {isMultiLocation && (
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <select
                value={selectedLocationId}
                onChange={e => setSelectedLocationId(e.target.value)}
                style={{ fontSize:12, fontWeight:600, padding:"6px 10px", borderRadius:6, border:`1px solid ${T_PRIMARY}`, fontFamily:FONT, background:WHITE, color:T_PRIMARY, cursor:"pointer", outline:"none" }}
              >
                {tenantLocations.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Upload dropdown */}
          <div style={{ position:"relative" }}>
            <button
              onClick={() => setShowUploadMenu(v => !v)}
              style={{ background:T_PRIMARY, color:WHITE, border:"none", borderRadius:6, padding:"6px 12px", fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:FONT, display:"flex", alignItems:"center", gap:4 }}
            >
              Upload <span style={{ fontSize:9 }}>▾</span>
            </button>
            {showUploadMenu && (
              <div style={{ position:"absolute", top:"calc(100% + 4px)", right:0, background:WHITE, border:`1px solid #e2e8f0`, borderRadius:8, boxShadow:"0 4px 16px rgba(27,58,107,0.15)", zIndex:200, minWidth:160, overflow:"hidden" }}
                onMouseLeave={() => setShowUploadMenu(false)}>
                <div onClick={() => { setShowUploadMenu(false); fileRef.current.click(); }}
                  style={{ padding:"10px 16px", fontSize:13, fontWeight:600, fontFamily:FONT, cursor:"pointer", color:T_PRIMARY, display:"flex", alignItems:"center", gap:8 }}
                  onMouseEnter={e=>e.currentTarget.style.background="#f1f5f9"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  📂 New File
                </div>
                <div style={{ height:1, background:"#e2e8f0" }} />
                <div onClick={() => { setShowUploadMenu(false); setShowDriveConfirm(true); }}
                  style={{ padding:"10px 16px", fontSize:13, fontWeight:600, fontFamily:FONT, cursor:"pointer", color:T_PRIMARY, display:"flex", alignItems:"center", gap:8 }}
                  onMouseEnter={e=>e.currentTarget.style.background="#f1f5f9"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  ☁️ From File Links
                </div>
              </div>
            )}
          </div>
          <input ref={fileRef} data-universal="1" type="file" accept=".xlsx,.xls,.csv" onChange={handleUniversalFile} style={{ display:"none" }} />

          {/* Export PDF */}
          {rows && !loading && (
            <button onClick={handleExportPDF} disabled={exporting} style={{ background:"#f0fdf4", color:GREEN, border:`1px solid ${GREEN}`, borderRadius:6, padding:"6px 12px", fontSize:11, fontWeight:600, cursor:exporting?"wait":"pointer", fontFamily:FONT, opacity:exporting?0.7:1 }}>
              {exporting ? "⏳" : "⬇ PDF"}
            </button>
          )}

          {/* Data status badges */}
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            {(loading||callLoading) && <span style={{ fontSize:11, color:T_PRIMARY, fontFamily:FONT }}>⏳ {loadMsg||"Reading…"}</span>}
            {rows && !loading && !driveLoading && <span style={{ fontSize:11, color:GREEN, fontWeight:600, fontFamily:FONT }}>✓ Fin</span>}
            {callRows && !callLoading && <span style={{ fontSize:11, color:"#7C3AED", fontWeight:600, fontFamily:FONT }}>✓ Calls</span>}
            {propetRows && <span style={{ fontSize:11, color:"#059669", fontWeight:600, fontFamily:FONT }}>✓ Cust</span>}
            {bookingRows && <span style={{ fontSize:11, color:"#2563eb", fontWeight:600, fontFamily:FONT }}>✓ Bkgs</span>}
          </div>

          {/* Account menu */}
          <div style={{ position:"relative" }}>
            <button
              onClick={() => setShowAccountMenu(v => !v)}
              title="Account"
              style={{ background:"transparent", border:`1px solid #cbd5e1`, borderRadius:6, padding:"5px 10px", cursor:"pointer", fontSize:16, lineHeight:1, color:T_PRIMARY, display:"flex", alignItems:"center", gap:4 }}
            >
              👤 <span style={{ fontSize:9, color:T_PRIMARY }}>▾</span>
            </button>
            {showAccountMenu && (
              <div style={{ position:"absolute", top:"calc(100% + 4px)", right:0, background:WHITE, border:`1px solid #e2e8f0`, borderRadius:8, boxShadow:"0 4px 16px rgba(27,58,107,0.15)", zIndex:200, minWidth:180, overflow:"hidden" }}
                onMouseLeave={() => setShowAccountMenu(false)}>
                {/* Client name header */}
                <div style={{ padding:"10px 16px", borderBottom:"1px solid #e2e8f0", fontSize:12, color:"#64748b", fontFamily:FONT }}>
                  {authClient?.clientName || ""}
                </div>
                <div onClick={() => { setShowAccountMenu(false); setShowSettings(true); }}
                  style={{ padding:"10px 16px", fontSize:13, fontWeight:600, fontFamily:FONT, cursor:"pointer", color:NAVY, display:"flex", alignItems:"center", gap:8 }}
                  onMouseEnter={e=>e.currentTarget.style.background="#f1f5f9"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  ⚙ Settings
                </div>
                <div onClick={() => { setShowAccountMenu(false); onPortal(); }}
                  style={{ padding:"10px 16px", fontSize:13, fontWeight:600, fontFamily:FONT, cursor:"pointer", color:NAVY, display:"flex", alignItems:"center", gap:8 }}
                  onMouseEnter={e=>e.currentTarget.style.background="#f1f5f9"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  ⌂ Portal
                </div>
                <div style={{ height:1, background:"#e2e8f0" }} />
                <div onClick={() => { setShowAccountMenu(false); onLogout(); }}
                  style={{ padding:"10px 16px", fontSize:13, fontWeight:600, fontFamily:FONT, cursor:"pointer", color:"#dc2626", display:"flex", alignItems:"center", gap:8 }}
                  onMouseEnter={e=>e.currentTarget.style.background="#fef2f2"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  → Sign Out
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Drive Loading Progress Banner ───────────────────────────────────── */}
      {driveLoading && (
        <div style={{ position:"fixed", top:0, left:0, right:0, zIndex:2000, background:NAVY, boxShadow:"0 4px 24px rgba(27,58,107,0.4)" }}>
          <div style={{ height:4, background:"rgba(255,255,255,0.15)" }}>
            <div style={{ height:"100%", background:"#60a5fa", transition:"width 0.5s ease",
              width: (driveStatus.includes("Financial") || driveStatus.includes("QuickBooks")) ? "15%" : driveStatus.includes("Calls") ? "40%" : driveStatus.includes("Customer") ? "65%" : driveStatus.includes("Bookings") ? "85%" : driveStatus.startsWith("✓") ? "100%" : "5%"
            }} />
          </div>
          <div style={{ padding:"14px 28px", display:"flex", alignItems:"center", gap:16 }}>
            <div style={{ width:22, height:22, border:"3px solid rgba(255,255,255,0.3)", borderTop:"3px solid #fff", borderRadius:"50%", animation:"spin 0.8s linear infinite", flexShrink:0 }} />
            <div>
              <div style={{ color:WHITE, fontWeight:700, fontSize:15, fontFamily:FONT }}>Loading from Google Drive…</div>
              <div style={{ color:"#93c5fd", fontSize:13, fontFamily:FONT, marginTop:2 }}>{driveStatus}</div>
            </div>
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* ── Drive Confirm Modal ─────────────────────────────────────────────── */}
      {showDriveConfirm && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:WHITE, borderRadius:14, boxShadow:"0 8px 40px rgba(27,58,107,0.22)", width:420, maxWidth:"95vw" }}>
            <div style={{ background:NAVY, borderRadius:"14px 14px 0 0", padding:"18px 24px" }}>
              <span style={{ color:WHITE, fontWeight:700, fontSize:16, fontFamily:FONT }}>☁️ Load from Google Drive</span>
            </div>
            <div style={{ padding:"24px" }}>
              <p style={{ margin:"0 0 16px", fontSize:14, color:"#334155", fontFamily:FONT, lineHeight:1.6 }}>
                This will fetch all 4 files from your saved Google Drive links and replace any currently loaded data.
              </p>
              <div style={{ background:"#f8fafc", borderRadius:8, border:"1px solid #e2e8f0", padding:"12px 16px", marginBottom:20 }}>
                {[
                  { icon:"💰", label:"Financial Transactions" },
                  { icon:"📞", label:"Google Calls / CTM" },
                  { icon:"👥", label:"Customers" },
                  { icon:"📅", label:"Bookings Combined" },
                ].map(({ icon, label }) => (
                  <div key={label} style={{ display:"flex", alignItems:"center", gap:10, padding:"5px 0", fontSize:13, fontFamily:FONT, color:"#475569" }}>
                    <span>{icon}</span><span>{label}</span>
                  </div>
                ))}
              </div>
              <div style={{ display:"flex", justifyContent:"flex-end", gap:10 }}>
                <button onClick={() => setShowDriveConfirm(false)}
                  style={{ padding:"8px 20px", borderRadius:6, border:"1px solid #cbd5e1", background:WHITE, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:FONT, color:"#475569" }}>
                  Cancel
                </button>
                <button onClick={() => { setShowDriveConfirm(false); handleLoadFromDrive(); }}
                  style={{ padding:"8px 22px", borderRadius:6, border:"none", background:NAVY, color:WHITE, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>
                  Begin Upload ☁️
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Save Toast ──────────────────────────────────────────────────────── */}
      {savedToast && (
        <div style={{ position:"fixed", bottom:28, right:28, zIndex:3000, background:"#022c22", color:"#bbf7d0", padding:"12px 22px", borderRadius:10, fontSize:14, fontWeight:700, fontFamily:FONT, boxShadow:"0 4px 20px rgba(0,0,0,0.25)", display:"flex", alignItems:"center", gap:10 }}>
          ✓ Settings applied for this session!
        </div>
      )}

      {/* ── Settings Modal ──────────────────────────────────────────────────── */}
      {showSettings && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={e => { if (e.target === e.currentTarget) setShowSettings(false); }}>
          <div style={{ background:WHITE, borderRadius:14, boxShadow:"0 8px 40px rgba(27,58,107,0.22)", width:520, maxWidth:"95vw", maxHeight:"90vh", overflowY:"auto", padding:0 }}>
            {/* Modal header */}
            <div style={{ background:NAVY, borderRadius:"14px 14px 0 0", padding:"18px 24px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <span style={{ color:WHITE, fontWeight:700, fontSize:16, fontFamily:FONT }}>⚙ Settings</span>
              <button onClick={() => setShowSettings(false)} style={{ background:"transparent", border:"none", color:WHITE, fontSize:20, cursor:"pointer", lineHeight:1 }}>×</button>
            </div>
            <div style={{ padding:"24px" }}>

              {/* Section: Google Drive File Links */}
              <div style={{ marginBottom:28 }}>
                <div style={{ fontWeight:700, fontSize:14, color:NAVY, fontFamily:FONT, marginBottom:4 }}>☁️ Google Drive File Links</div>
                <div style={{ fontSize:12, color:"#64748b", fontFamily:FONT, marginBottom:14 }}>Paste your "Anyone with the link" Google Drive share URLs. Used by the "From File Links" option.</div>
                {[
                  { key:"qb",        label:"Financial Transactions (.xlsx)",  icon:"💰" },
                  { key:"calls",     label:"Google Calls / CTM (.csv)",         icon:"📞" },
                  { key:"customers", label:"Customers (.csv)",                icon:"👥" },
                  { key:"bookings",  label:"Bookings Combined (.xlsx)",         icon:"📅" },
                ].map(({ key, label, icon }) => (
                  <div key={key} style={{ marginBottom:12 }}>
                    <label style={{ fontSize:12, fontWeight:600, color:"#475569", fontFamily:FONT, display:"block", marginBottom:4 }}>{icon} {label}</label>
                    <input
                      type="text"
                      value={driveLinks[key]}
                      onChange={e => setDriveLinks(prev => ({ ...prev, [key]: e.target.value.trim() }))}
                      placeholder="https://drive.google.com/file/d/..."
                      style={{ width:"100%", padding:"8px 10px", borderRadius:6, border:"1px solid #cbd5e1", fontSize:12, fontFamily:FONT, boxSizing:"border-box", color:NAVY }}
                    />
                  </div>
                ))}
              </div>

              <div style={{ height:1, background:"#e2e8f0", marginBottom:24 }} />

              {/* Section: Kennel Capacity */}
              <div style={{ marginBottom:28 }}>
                <div style={{ fontWeight:700, fontSize:14, color:NAVY, fontFamily:FONT, marginBottom:4 }}>🐕 Kennel Capacity</div>
                <div style={{ fontSize:12, color:"#64748b", fontFamily:FONT, marginBottom:14 }}>Maximum dogs per service type. Used for future occupancy % calculations.</div>
                <div style={{ display:"flex", gap:16 }}>
                  <div style={{ flex:1 }}>
                    <label style={{ fontSize:12, fontWeight:600, color:"#475569", fontFamily:FONT, display:"block", marginBottom:4 }}>🐾 Daycare (max dogs/day)</label>
                    <input type="number" min={1} max={200}
                      value={kennelCapacity.daycare}
                      onChange={e => setKennelCapacity(prev => ({ ...prev, daycare: Math.max(1, parseInt(e.target.value)||1) }))}
                      style={{ width:"100%", padding:"8px 10px", borderRadius:6, border:"1px solid #cbd5e1", fontSize:13, fontFamily:FONT, boxSizing:"border-box", color:NAVY }}
                    />
                  </div>
                  <div style={{ flex:1 }}>
                    <label style={{ fontSize:12, fontWeight:600, color:"#475569", fontFamily:FONT, display:"block", marginBottom:4 }}>🏠 Boarding (max dogs/night)</label>
                    <input type="number" min={1} max={200}
                      value={kennelCapacity.boarding}
                      onChange={e => setKennelCapacity(prev => ({ ...prev, boarding: Math.max(1, parseInt(e.target.value)||1) }))}
                      style={{ width:"100%", padding:"8px 10px", borderRadius:6, border:"1px solid #cbd5e1", fontSize:13, fontFamily:FONT, boxSizing:"border-box", color:NAVY }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ height:1, background:"#e2e8f0", marginBottom:24 }} />

              {/* Section: Monthly Ad Spend */}
              <div style={{ marginBottom:28 }}>
                <div style={{ fontWeight:700, fontSize:14, color:NAVY, fontFamily:FONT, marginBottom:4 }}>💰 Monthly Ad Spend</div>
                <div style={{ fontSize:12, color:"#64748b", fontFamily:FONT, marginBottom:14 }}>Used for CAC, ROI, and payback period calculations in Marketing ROI and Scorecard.</div>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:14, color:NAVY, fontFamily:FONT }}>$</span>
                  <input type="number" min={0}
                    value={monthlySpend}
                    onChange={e => setMonthlySpend(Math.max(0, parseInt(e.target.value)||0))}
                    style={{ width:140, padding:"8px 10px", borderRadius:6, border:"1px solid #cbd5e1", fontSize:13, fontFamily:FONT, color:NAVY }}
                  />
                  <span style={{ fontSize:12, color:"#64748b", fontFamily:FONT }}>/ month</span>
                </div>
              </div>

              {/* Save button */}
              <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:8 }}>
                <div style={{ display:"flex", gap:10 }}>
                  <button onClick={() => setShowSettings(false)}
                    style={{ padding:"8px 20px", borderRadius:6, border:"1px solid #cbd5e1", background:WHITE, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:FONT, color:"#475569" }}>
                    Cancel
                  </button>
                  <button onClick={() => {
                      setSavedToast(true);
                      setTimeout(() => { setSavedToast(false); setShowSettings(false); }, 1500);
                    }}
                    style={{ padding:"8px 20px", borderRadius:6, border:"none", background:NAVY, color:WHITE, fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>
                    {savedToast ? "✓ Applied!" : "Apply for Session"}
                  </button>
                </div>
                <div style={{ fontSize:11, color:"#94a3b8", fontFamily:FONT }}>
                  Changes apply to this session only. Contact PetKennect to update permanent settings.
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Scorecard ready banner */}
      {ready && tab==="import" && (
        <div style={{ position:"sticky", top:60, zIndex:99, background:"#f0fdf4", borderBottom:`1px solid #bbf7d0`, padding:"9px 28px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontSize:13, color:GREEN, fontWeight:600, fontFamily:FONT }}>✓ Scrubbing complete — Scorecard is ready</span>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={() => {
              try {
                // Define the columns we care about — avoids building giant objects
                const EXPORT_COLS = [
                  "Num","Date","WeekEnding","Customer",
                  "FinalMainBucket","Product/Service","FinalProduct",
                  "OriginalService",
                  "Service","ServiceDisplay",
                  "Qty","Rate","Amount",
                  "Is_Refund","Service_Amount_Mismatch","Exclude_From_Revenue"
                ];
                // Build array-of-arrays — much more memory efficient than json_to_sheet
                const header = EXPORT_COLS;
                const dataRows = rows.map(r => EXPORT_COLS.map(k => r[k] ?? ""));
                const ws = XLSX.utils.aoa_to_sheet([header, ...dataRows]);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Scrubbed");
                const wbout = XLSX.write(wb, { bookType:"xlsx", type:"array" });
                const blob = new Blob([wbout], { type:"application/octet-stream" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = "Transactions_Scrubbed.xlsx";
                document.body.appendChild(a); a.click();
                document.body.removeChild(a); URL.revokeObjectURL(url);
              } catch(e) { alert("Download failed: " + e.message); }
            }} style={{ background:WHITE, color:GREEN, border:`1px solid ${GREEN}`, borderRadius:6, padding:"5px 14px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>
              ↓ Download Scrubbed File
            </button>
            <button onClick={()=>setTab("scorecard")} style={{ background:GREEN, color:WHITE, border:"none", borderRadius:6, padding:"5px 14px", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>View Financial Scorecard →</button>
          </div>
        </div>
      )}

      <main style={{ padding:"20px 28px", maxWidth:1200, margin:"0 auto" }}>
        {!rows && tab !== "marketing" && tab !== "bookings" && tab !== "summary" && (
          <div style={{ background:WHITE, border:`2px dashed ${GRAY_B}`, borderRadius:12, padding:60, textAlign:"center" }}>
            <div style={{ fontSize:36, marginBottom:12 }}>📂</div>
            <div style={{ fontSize:17, fontWeight:700, color:NAVY, fontFamily:FONT, marginBottom:6 }}>No data loaded</div>
            <div style={{ fontSize:13, color:GRAY_MID, fontFamily:FONT, marginBottom:16 }}>Upload your Financial File — scrubbing runs automatically</div>
            <button onClick={() => {
              // Generate template XLSX with required column headers
              const headers = isPKTenantUser
                ? ["LocationCode","OwnerID","OwnerLastName","InvoiceID","TotalInvAmount","PaidInFull","InvDate","FinalMasterBucket","FinalProduct","ItemName","PackageName","ListPrice","SalePrice","Quantity","Subtotal","Tax","IsRefunded","RefundAmount","Comments"]
                : ["Date","Name","Num","Product/Service","Memo/Description","Qty","Rate","Amount"];
              const ws = XLSX.utils.aoa_to_sheet([headers]);
              const wb2 = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb2, ws, "Financial Template");
              const out = XLSX.write(wb2, { bookType:"xlsx", type:"array" });
              const blob = new Blob([out], { type:"application/octet-stream" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a"); a.href = url;
              a.download = "Financial_File_Template.xlsx";
              document.body.appendChild(a); a.click();
              document.body.removeChild(a); URL.revokeObjectURL(url);
            }} style={{ background:NAVY, color:WHITE, border:"none", borderRadius:6, padding:"8px 20px", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:FONT }}>
              ⬇ Download Template
            </button>
          </div>
        )}

        {/* IMPORT TAB */}
        {rows && tab==="import" && quality && <ImportSummary quality={quality} rows={rows} onGoScorecard={()=>setTab("scorecard")} authClient={authClient} />}

        {/* CUSTOMER KPIs TAB */}
        {tab==="customers" && !rows && (
          <div style={{ background:WHITE, border:`2px dashed ${GRAY_B}`, borderRadius:12, padding:60, textAlign:"center" }}>
            <div style={{ fontSize:36, marginBottom:12 }}>📊</div>
            <div style={{ fontSize:17, fontWeight:700, color:NAVY, fontFamily:FONT, marginBottom:6 }}>No transaction data loaded</div>
            <div style={{ fontSize:13, color:GRAY_MID, fontFamily:FONT }}>Upload your Financial Transactions file to populate Customer KPIs</div>
          </div>
        )}
        {tab==="customers" && rows && (
          <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
            {(() => {
              const allProfiles = (locationRows || rows) ? buildCustomerProfiles(locationRows || rows) : [];
              const cohortYears = [...new Set(allProfiles.map(p => p.firstYear))].sort();
              const fromYear = cohortFrom === "all" ? null : parseInt(cohortFrom);
              const toYear = cohortTo === "all" ? null : parseInt(cohortTo);
              const filteredProfiles = allProfiles.filter(p => {
                if (fromYear && p.firstYear < fromYear) return false;
                if (toYear && p.firstYear > toYear) return false;
                return true;
              });
              const isFiltered = fromYear || toYear;
              const filterLabel = !isFiltered ? "All Cohorts"
                : fromYear && toYear && fromYear === toYear ? `${fromYear} Cohort`
                : fromYear && toYear ? `${fromYear}–${toYear}`
                : fromYear ? `${fromYear} onwards`
                : `Up to ${toYear}`;
              const isRecentOnly = toYear && toYear >= new Date().getFullYear() - 1;

              return (
                <>
                  {/* Global Cohort Filter */}
                  <div data-sticky="customers-filter" style={{ position:"sticky", top:60, zIndex:91, background:GRAY_BG, paddingBottom:8 }}>
                    <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, padding:"12px 18px", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
                      <span style={{ fontSize:12, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT }}>Customer Cohort</span>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT }}>From</span>
                        <select value={cohortFrom} onChange={e=>{ setCohortFrom(e.target.value); if (e.target.value!=="all" && cohortTo!=="all" && parseInt(e.target.value)>parseInt(cohortTo)) setCohortTo(e.target.value); }}
                          style={{ fontSize:12, padding:"5px 10px", borderRadius:6, border:`1px solid ${GRAY_B}`, fontFamily:FONT, background:WHITE, color:NAVY, cursor:"pointer" }}>
                          <option value="all">All years</option>
                          {cohortYears.map(y=><option key={y} value={y}>{y}</option>)}
                        </select>
                        <span style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT }}>To</span>
                        <select value={cohortTo} onChange={e=>{ setCohortTo(e.target.value); if (e.target.value!=="all" && cohortFrom!=="all" && parseInt(e.target.value)<parseInt(cohortFrom)) setCohortFrom(e.target.value); }}
                          style={{ fontSize:12, padding:"5px 10px", borderRadius:6, border:`1px solid ${GRAY_B}`, fontFamily:FONT, background:WHITE, color:NAVY, cursor:"pointer" }}>
                          <option value="all">All years</option>
                          {cohortYears.map(y=><option key={y} value={y}>{y}</option>)}
                        </select>
                        {isFiltered && (
                          <button onClick={()=>{ setCohortFrom("all"); setCohortTo("all"); }}
                            style={{ fontSize:11, color:GRAY_MID, background:"none", border:`1px solid ${GRAY_B}`, borderRadius:4, padding:"4px 8px", cursor:"pointer", fontFamily:FONT }}>
                            ✕ Clear
                          </button>
                        )}
                      </div>
                      <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:10 }}>
                        <span style={{ fontSize:12, fontWeight:700, color:NAVY, fontFamily:FONT }}>{filterLabel}</span>
                        <span style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT }}>·</span>
                        <span style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT }}>{filteredProfiles.length.toLocaleString()} customers</span>
                        {isRecentOnly && toYear && toYear >= new Date().getFullYear() - 1 && (
                          <span style={{ fontSize:11, color:AMBER, fontWeight:600, fontFamily:FONT }}>⚠ Recent cohorts have partial CLTV</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <CustomerWidgets profiles={filteredProfiles} />
                  <div className="retention-chart-pdf"><CustomerRetentionChart rows={locationRows || rows} cohortFrom={fromYear} cohortTo={toYear} /></div>
                  <CrossServiceMatrix profiles={filteredProfiles} />
                  <LTVByService profiles={filteredProfiles} cohortLabel={filterLabel} />
                </>
              );
            })()}
          </div>
        )}

        {/* SCORECARD TAB */}
        {rows && tab==="scorecard" && (
          <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

            {/* Sticky filter + widgets */}
            <div data-sticky="scorecard" style={{ position:"sticky", top:60, zIndex:90, background:GRAY_BG, paddingBottom:8, marginBottom:-8 }}>

            {/* Date filter bar */}
            <div style={{ background:WHITE, border:`1px solid ${GRAY_B}`, borderRadius:10, padding:"14px 18px", display:"flex", alignItems:"center", gap:16, flexWrap:"wrap", marginBottom:12 }}>
              <span style={{ fontSize:12, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.07em", fontFamily:FONT }}>Filter by</span>
              <div style={{ display:"flex", gap:0, border:`1px solid ${GRAY_B}`, borderRadius:6, overflow:"hidden" }}>
                {[["month","Month"],["week","Week"],["year","Year"]].map(([m,l])=>(
                  <button key={m} onClick={()=>handlePeriodMode(m)} style={{ padding:"6px 14px", border:"none", borderRight:`1px solid ${GRAY_B}`, cursor:"pointer", fontSize:12, fontWeight:600, fontFamily:FONT, background:period===m?NAVY:WHITE, color:period===m?WHITE:GRAY_MID, transition:"all 0.1s" }}>{l}</button>
                ))}
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT }}>Period:</span>
                <select value={selectedPeriod||""} onChange={e=>setSelectedPeriod(e.target.value)} style={{ fontSize:13, fontWeight:600, color:NAVY, padding:"5px 10px", borderRadius:6, border:`1px solid ${GRAY_B}`, fontFamily:FONT, cursor:"pointer", background:WHITE }}>
                  {currentPeriods.map(p=><option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
              <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:12 }}>
                <span style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT }}>
                  {revenueRows.length.toLocaleString()} transactions
                </span>
                {reconStatus && (
                  <span style={{
                    fontSize:12, fontWeight:600, fontFamily:FONT,
                    color: reconStatus.ok ? GREEN : AMBER,
                    background: reconStatus.ok ? "#f0fdf4" : "#fffbeb",
                    border: `1px solid ${reconStatus.ok ? "#bbf7d0" : "#fde68a"}`,
                    borderRadius:6, padding:"4px 10px"
                  }}>{reconStatus.label}</span>
                )}
              </div>
            </div>

            {/* Location Comparison View — always shown for multi-location when All selected */}
            {isMultiLocation && selectedLocationId === "all" && (() => {
              const locs = tenantLocations.filter(l => l.locationCode);
              return (
                <div style={{ marginBottom:8 }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:GRAY_MID, textTransform:"uppercase", letterSpacing:"0.08em", fontFamily:FONT }}>
                      Location Comparison — {selectedPeriod}
                    </div>
                    <button onClick={() => setLocCompExpanded(v => !v)}
                      style={{ background:"transparent", border:`1px solid ${GRAY_B}`, borderRadius:6, padding:"3px 10px", fontSize:11, fontWeight:600, color:GRAY_MID, cursor:"pointer", fontFamily:FONT }}>
                      {locCompExpanded ? "▲ Collapse" : "▼ Expand"}
                    </button>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:`repeat(${locs.length}, 1fr)`, gap:12 }}>
                    {locs.map(loc => {
                      const locData = (locationRows || rows || []).filter(r => r.LocationCode === loc.locationCode);
                      const locFiltered = filterRows(locData, period, selectedPeriod);
                      const locWidgets = buildWidgets(locFiltered);
                      const buckets = ["Daycare","Boarding","Dog Training","Grooming","Retail / Food"];
                      return (
                        <div key={loc.id} style={{ background:WHITE, border:`2px solid ${T_PRIMARY}20`, borderRadius:10, padding:"10px 14px", borderTop:`3px solid ${T_PRIMARY}` }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                            <div style={{ fontSize:12, fontWeight:800, color:T_PRIMARY, fontFamily:FONT }}>{loc.name}</div>
                            <div style={{ fontSize:15, fontWeight:800, color:NAVY, fontFamily:FONT }}>
                              {locWidgets.Total?.revenue ? `$${Math.round(locWidgets.Total.revenue).toLocaleString()}` : "—"}
                            </div>
                          </div>
                          {locCompExpanded && (
                            <div style={{ marginTop:8 }}>
                              {buckets.map(b => (
                                <div key={b} style={{ display:"flex", justifyContent:"space-between", padding:"3px 0", borderBottom:`1px solid ${GRAY_B}` }}>
                                  <span style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT }}>{label(b)}</span>
                                  <span style={{ fontSize:11, fontWeight:600, color:NAVY, fontFamily:FONT }}>
                                    {locWidgets[b]?.revenue ? `$${Math.round(locWidgets[b].revenue).toLocaleString()}` : "—"}
                                  </span>
                                </div>
                              ))}
                              <div style={{ marginTop:4, fontSize:10, color:GRAY_MID, fontFamily:FONT }}>
                                {locFiltered.length.toLocaleString()} transactions
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* KPI Widgets — Row 1 */}
            <div style={{ display:"grid", gridTemplateColumns:`repeat(${isPKTenantUser ? 4 : 5},1fr)`, gap:12 }}>
              <Widget label="Net Revenue" value={widgets.Total?.revenue} qty={widgets.Total?.qty} accent={NAVY}
                prevValue={priorWidgets.Total?.revenue} yoyValue={yoyWidgets.Total?.revenue} period={period} />
              <Widget label={label("Daycare")} value={widgets.Daycare?.revenue} qty={widgets.Daycare?.qty} accent={BUCKET_COLORS["Daycare"]}
                prevValue={priorWidgets.Daycare?.revenue} yoyValue={yoyWidgets.Daycare?.revenue} period={period}
                onClick={()=>handleWidgetClick("Daycare")} />
              <Widget label={label("Boarding")} value={widgets.Boarding?.revenue} qty={widgets.Boarding?.qty} accent={BUCKET_COLORS["Boarding"]}
                prevValue={priorWidgets.Boarding?.revenue} yoyValue={yoyWidgets.Boarding?.revenue} period={period}
                onClick={()=>handleWidgetClick("Boarding")} />
              <Widget label={label("Dog Training")} value={widgets["Dog Training"]?.revenue} qty={widgets["Dog Training"]?.qty} accent={BUCKET_COLORS["Dog Training"]}
                prevValue={priorWidgets["Dog Training"]?.revenue} yoyValue={yoyWidgets["Dog Training"]?.revenue} period={period}
                onClick={()=>handleWidgetClick("Dog Training")} />
              {!isPKTenantUser && <Widget label="Enrichment" value={widgets.Enrichment?.revenue} qty={widgets.Enrichment?.qty} accent={BUCKET_COLORS["Enrichment"]}
                prevValue={priorWidgets.Enrichment?.revenue} yoyValue={yoyWidgets.Enrichment?.revenue} period={period}
                onClick={()=>handleWidgetClick("Enrichment")} />}
            </div>
            {/* KPI Widgets — Row 2 */}
            <div style={{ display:"grid", gridTemplateColumns:`repeat(${isPKTenantUser ? 4 : 5},1fr)`, gap:12 }}>
              <Widget label={label("Grooming")} value={widgets.Grooming?.revenue} qty={widgets.Grooming?.qty} accent={BUCKET_COLORS["Grooming"]}
                prevValue={priorWidgets.Grooming?.revenue} yoyValue={yoyWidgets.Grooming?.revenue} period={period}
                onClick={()=>handleWidgetClick("Grooming")} />
              {!isPKTenantUser && <Widget label="Transport" value={widgets.Transport?.revenue} qty={widgets.Transport?.qty} accent={BUCKET_COLORS["Transport"]}
                prevValue={priorWidgets.Transport?.revenue} yoyValue={yoyWidgets.Transport?.revenue} period={period}
                onClick={()=>handleWidgetClick("Transport")} />}
              <Widget label={isPKTenantUser ? "Retail" : "Misc / Retail"} value={widgets[isPKTenantUser ? "Retail / Food" : "Misc / Retail"]?.revenue} qty={widgets[isPKTenantUser ? "Retail / Food" : "Misc / Retail"]?.qty} accent={BUCKET_COLORS["Miscellaneous"]}
                prevValue={priorWidgets[isPKTenantUser ? "Retail / Food" : "Misc / Retail"]?.revenue} yoyValue={yoyWidgets[isPKTenantUser ? "Retail / Food" : "Misc / Retail"]?.revenue} period={period} />
              <Widget label="Discounts" value={widgets.Discounts?.revenue} qty={widgets.Discounts?.qty} accent={BUCKET_COLORS["Discounts"]}
                prevValue={priorWidgets.Discounts?.revenue} yoyValue={yoyWidgets.Discounts?.revenue} period={period}
                onClick={()=>handleWidgetClick("Discounts")} invertTrend={true} />
              <Widget label={label("Refunds")} value={widgets.Refunds?.revenue} qty={widgets.Refunds?.qty} accent={BUCKET_COLORS["Refunds"]}
                prevValue={priorWidgets.Refunds?.revenue} yoyValue={yoyWidgets.Refunds?.revenue} period={period}
                onClick={()=>handleWidgetClick("Refunds")} invertTrend={true} />
            </div>

            </div>{/* end sticky */}

            {/* 1. Revenue Over Time */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:4 }}>
              <h2 style={{ margin:0, fontSize:14, fontWeight:700, color:NAVY, fontFamily:FONT }}>Revenue Over Time</h2>
              <span style={{ fontSize:12, color:GRAY_MID, fontFamily:FONT }}>Click › to drill into products and services</span>
            </div>
            <div className="time-pivot-pdf"><TimePivot rows={locationRows || rows} /></div>

            {/* 2. Service Category */}
            <div ref={accordionRef} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:8 }}>
              <div>
                <h2 style={{ margin:0, fontSize:14, fontWeight:700, color:NAVY, letterSpacing:"0.02em", fontFamily:FONT }}>Service Category</h2>
                <div style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, marginTop:2 }}>Click › to drill: Bucket → Product → Service</div>
              </div>
            </div>
            <div className="rev-breakdown-pdf"><BucketAccordion tableData={tableData} bucketColors={BUCKET_COLORS} externalOpenBucket={accordionBucket} onBucketChange={setAccordionBucket} accordionRef={accordionRef} /></div>

            {/* 3. Financial File Reconciliation */}
            <div style={{ marginTop:8 }}>
              <h2 style={{ margin:"0 0 4px 0", fontSize:14, fontWeight:700, color:NAVY, fontFamily:FONT }}>Financial File Reconciliation</h2>
              <div style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, marginBottom:12 }}>QB Product/Service vs our classification — filtered by selected period</div>
            </div>
            <div className="recon-table-pdf"><ReconciliationTable filteredRows={filteredRows} /></div>

            {/* 4. Product Reclassification */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:8 }}>
              <div>
                <h2 style={{ margin:0, fontSize:14, fontWeight:700, color:NAVY, fontFamily:FONT }}>Product Reclassification</h2>
                <div style={{ fontSize:11, color:GRAY_MID, fontFamily:FONT, marginTop:2 }}>Click › to drill: QB category → Our bucket → Service display</div>
              </div>
            </div>
            <ReclassificationPivot rows={filteredRows} />

          </div>
        )}

        {/* MARKETING ROI TAB */}
        {tab==="marketing" && (
          <MarketingROI
            callRows={callRows}
            setCallRows={setCallRows}
            callLoading={callLoading}
            setCallLoading={setCallLoading}
            marketingPeriod={marketingPeriod}
            setMarketingPeriod={setMarketingPeriod}
            selectedMarketingPeriod={selectedMarketingPeriod}
            setSelectedMarketingPeriod={setSelectedMarketingPeriod}
            callFileRef={callFileRef}
            propetRows={locationPropetRows || propetRows}
            rows={locationRows || rows}
            monthlySpend={monthlySpend}
            setMonthlySpend={setMonthlySpend}
            customerMaster={customerMaster}
            selectedLocationCode={selectedLocationCode}
          />
        )}

        {/* SCORECARD SUMMARY TAB */}
        {tab==="summary" && (
          <ScorecardSummary
            rows={locationRows || rows}
            callRows={callRows}
            propetRows={locationPropetRows || propetRows}
            monthlySpend={monthlySpend}
            customerMaster={customerMaster}
            bookingRows={locationBookingRows || bookingRows}
            authClient={authClient}
          />
        )}

        {/* BOOKINGS TAB */}
        {tab==="bookings" && (
          <BookingsTab bookingRows={locationBookingRows || bookingRows} precomputedMetrics={precomputedMetrics} isPKTenantUser={isPKTenantUser} />
        )}
      </main>

    </div>
  );
}
